/**
 * The string catalogue.
 *
 * Voice, per design-direction §1 — copy laws with force of law, enforced by
 * `lintCatalogue` in ./lint.ts:
 *   1. second person, present tense
 *   2. short sentences
 *   3. no exclamation marks in system copy (celebrations may earn one)
 *   4. no praise adjectives about the person — acknowledgement attaches to the ACT
 *
 * Casing decision (recorded, per implementation-brief §12 "material deviations get an
 * objection/resolution entry"): the product concept quotes acknowledgement copy in
 * lowercase ("fence closed. that one took some coming back to"). We ship sentence
 * case instead. Objection: lowercase is a style signal that costs legibility and
 * predictability, and design-direction §2's tiebreaker resolves surface-vs-structure
 * conflicts to legibility. Resolution: sentence case everywhere; the wryness survives
 * in word choice, not in typography.
 */

import type { CopyEntry } from './registers';

/** Metaphor -> literal noun map. Every metaphor term in the app appears here. */
export const terms = {
  loop: { warm: 'loop', literal: 'task' },
  loops: { warm: 'loops', literal: 'tasks' },
  capsule: { warm: 'capsule', literal: 'saved context' },
  cairn: { warm: 'cairn', literal: 'session history' },
  /**
   * ADA review C6: this used to read "inactive", while the surfaces that NAME the compost
   * destination — `compost.title` ("Long-idle tasks"), `home.compostRow`, `home.compostRow.hint`
   * ("Opens the list of long-idle tasks"), `compost.gradientLabel`, `compost.empty` — all say
   * "long-idle tasks". Matches `composting`'s own "long idle" below, hyphenated the way
   * `compost.title` hyphenates it (attributive, before the noun) rather than `temp.composting`'s
   * unhyphenated predicate form ("Long idle") — the same distinction those two live strings
   * already draw.
   *
   * THE WORD "INACTIVE" IS NOW GONE FROM THE CATALOGUE, and it had to be, because the first
   * version of this fix shipped a comment claiming it already was while five keys — six forms —
   * still said it: `return.bulkCompostOffer` (both counts), `return.bulkCompostAccept`,
   * `return.bulkCompostDone`, `plus.feature.compost.label` and `plus.feature.compost.explain`.
   * C6 asks for the audit ("audit the other glossary rows against their live surfaces"), and
   * moving the glossary row while the live surfaces kept the old word would have moved the
   * disagreement rather than closed it. "Inactive" was the literal form of nothing — `warm` is
   * "active", `cooling` is "idle", `composting` is "long idle" — so it was an orphan word with
   * no row behind it, and the bulk-move strings now name the destination the way
   * `home.compostRow.hint` names it: the long-idle list.
   * `test/glossary-agreement.test.ts` pins the absence, so this comment cannot go stale again.
   */
  compost: { warm: 'compost', literal: 'long-idle tasks' },
  warm: { warm: 'warm', literal: 'active' },
  cooling: { warm: 'cooling', literal: 'idle' },
  composting: { warm: 'composting', literal: 'long idle' },
  /**
   * Warm is the idiom, literal is the functional term — the same direction as every other
   * entry in this map. It was the other way round, which made this the one row where
   * literal mode handed the reader the metaphor (RULINGS R75).
   *
   * "Let go" is a phrase about a decision; "released" is the state's own name in the domain
   * (`packages/core` `TerminalRecord.kind`), and product-concept §11 defines literal mode as
   * swapping playful copy for "precise functional labels". `celebrate.released` already reads
   * warm "Let go: {title}" / literal "Released: {title}" (R24), so the glossary was also the
   * one place disagreeing with the celebration it names.
   */
  released: { warm: 'let go', literal: 'released' },
  toss: { warm: 'toss', literal: 'transfer' },
  ask: { warm: 'ask', literal: 'request' },
  storm: { warm: 'storm mode', literal: 'triage mode' },
  /**
   * ADA review C6: this used to read "review inbox" — the exact phrase the sorting block's own
   * comment condemns ("an instruction to do work... the inbox-zero pattern §8.3 exists to
   * refuse"), and the one `sorting.title` itself replaced with "Captured items" for that
   * reason. The glossary is exempt from the dead-key test because it is "looked up by term
   * name from data" (`test/references.test.ts`), which is exactly why nothing caught this row
   * disagreeing with the live surface title it names — no `term(` call site renders it for a
   * screenshot to catch. Fixed to agree with `sorting.title`'s literal form, and
   * `test/glossary-agreement.test.ts` now pins the two together so a future rename of one
   * cannot silently leave the other behind.
   */
  sorting: { warm: 'sorting', literal: 'captured items' },
} as const satisfies Record<string, CopyEntry>;

export const copy = {
  // -------------------------------------------------------------------------
  // Navigation — fixed spatial grammar, so these labels never move or rename
  // (design-direction §4).
  // -------------------------------------------------------------------------
  /**
   * The TAB label, which is not the same string as the screen title.
   *
   * Design review caught "Where was I?" rendering as "Where w…" in the tab bar: five fixed
   * tabs on a phone leave roughly seven characters each, and an ellipsis in permanent
   * navigation is the opposite of the fixed, learnable spatial grammar design-direction §4
   * demands. The signature question keeps its full form at `home.title`, where the screen
   * has the width for it — and that is also the surface §12 films for the hero demo.
   */
  /**
   * The literal tab label is 'Now', not 'Active'.
   *
   * critique 7.3: in literal mode the screen title was "Active tasks", every card's
   * temperature pill said "Active", the over-cap row said "active tasks beyond the display
   * limit" — and the tab said "Active" too. Literal mode exists so a literal-minded reader is
   * not made to disambiguate; it had become the register that needed the most. The tab names
   * WHERE you are, so it takes the one word that is about place in the app rather than about
   * a task's state. `lintCatalogue` now refuses a repeat across `nav.*`, `home.title` and
   * `temp.*` within one register, so this cannot silently come back.
   */
  'nav.home': { warm: 'Pick up', literal: 'Now' },
  'nav.capture': { warm: 'Capture', literal: 'Capture' },
  // 'Everything' is 10 characters and truncates to "Everyt…" on an iPhone SE, whose five
  // tabs get ~64pt each. The screen still titles itself "Everything"; the tab does not have
  // the room, and an ellipsis in permanent navigation defeats the fixed spatial grammar
  // design-direction §4 demands. The ceiling in test/laws.test.ts is now SE-calibrated.
  'nav.all': { warm: 'All', literal: 'All' },
  // 'Shopping list' is 13 characters and truncates in the tab bar — caught by the
  // tab-length assertion in test/laws.test.ts, in the literal register, which the design
  // review screenshots had not yet been read closely enough to notice.
  'nav.shopping': { warm: 'Shop', literal: 'Shop' },
  'nav.settings': { warm: 'Settings', literal: 'Settings' },
  // Not a tab: the back control on a pushed surface (the loop detail route). The five
  // tabs are the whole navigation surface (design-direction §4); this only returns.
  'nav.back': { warm: 'Back', literal: 'Back' },
  /**
   * DISMISSES rather than navigates — `BackControl`'s `close` direction (docs/RULINGS.md
   * naming, `packages/ui/src/components/BackControl.tsx`). "Back" is a false statement about
   * a control that closes an inline disclosure, a sheet or a modal in place rather than
   * moving the navigation stack: there is nowhere it "goes back" to, because it never left.
   * product-owner report: the shopping list's "Add an item" field used `nav.back`, which is
   * this exact mistake.
   */
  'nav.close': { warm: 'Close', literal: 'Close' },

  // -------------------------------------------------------------------------
  // The resumption surface (product-concept §6.3)
  // -------------------------------------------------------------------------
  'home.title': {
    warm: 'Where was I?',
    literal: 'Active tasks',
    allow: ['tense', 'first-person'],
    why: 'Same signature question as nav.home, in the user\'s voice. product-concept §6.3.',
  },
  'home.empty.title': {
    warm: 'Nothing warm right now.',
    literal: 'No active tasks.',
  },
  // critique 5.4.2: "That is a fine place to be" is reassurance about the user's
  // SITUATION — therapist-adjacent, which design-direction §1 forbids by name — and it
  // would be shown to people whose situation is not fine. State the fact and stop.
  'home.empty.body': {
    warm: 'Catch something when it turns up.',
    literal: 'Capture a task when you need to.',
  },
  // Two distinct rows, because `TemperatureReason` distinguishes two distinct facts
  // (core model.ts). Saying "look when you are ready" about a loop touched four minutes
  // ago is a false statement, and this audience notices (design-direction §1, obj 2).
  'home.coolingRow': {
    warm: '{count} cooling — look when you’re ready',
    literal: '{count} idle tasks',
    oneWarm: '1 cooling — look when you’re ready',
    oneLiteral: '1 idle task',
    slots: ['count'],
  },
  /**
   * The `reason: 'over-cap'` row (RULINGS R19).
   *
   * It keeps the COUNT and the INVITATION and drops the coldness claim. product-concept §5's
   * own phrase for these loops is "warm-adjacent but not shouting": they were touched minutes
   * ago and are behind the cap, so "cooling" is false about them and "beyond the display
   * limit" was true but described the app's own layout rather than the user's work.
   */
  /**
   * SHORTENED TO ONE LINE — docs/RULINGS.md R108, R115's lever.
   *
   * This row is fixed furniture below the scroller, so every line it wraps to is a line taken off
   * the surface's fold. Measured on a 320pt phone it was 62pt in BOTH registers where a one-line
   * row is 56, and those 6pt come straight out of the leading card's budget. The count and the
   * invitation — the two things R19 says this row keeps — are untouched; what went is the second
   * clause restating that opening is the user's choice, which the row's own hint
   * (`home.overCapRow.hint`) says in full.
   */
  'home.overCapRow': {
    warm: '{count} more on the go — look anytime',
    literal: '{count} more active tasks',
    oneWarm: '1 more on the go — look anytime',
    oneLiteral: '1 more active task',
    slots: ['count'],
  },
  'home.coolingRow.hint': {
    warm: 'Opens the cooling list. Look when you like.',
    literal: 'Opens the list of idle tasks.',
  },
  /**
   * The over-cap row's own hint (RULINGS R19).
   *
   * `QuietRow` used to speak `home.coolingRow.hint` for this row — "Opens the cooling list" —
   * so a screen-reader user met the exact coldness claim R19 removes from the visible line,
   * about loops touched minutes ago. The row opens the full list, which is what it says.
   */
  'home.overCapRow.hint': {
    warm: 'Opens the full list. Look when you like.',
    literal: 'Opens the full list of active tasks.',
  },
  /**
   * THE ONE QUIET ROW (product-concept §6.3; docs/DEVIATIONS.md D45).
   *
   * §6.3 specifies one row — *"Below the warm set, one quiet row: '3 loops cooling — look when
   * you're ready.' Nothing else"* — and the surface rendered two, because R19 correctly refused
   * to let the over-cap loops be called cold. A third group (`reason: 'caught'`) would have made
   * it three, and every row is 56pt of fixed furniture taken off the leading card's fold
   * (docs/DEVIATIONS.md §card-heights H4 names those 112pt as one of the only two ways out of an
   * escalated residual).
   *
   * R19'S REQUIREMENT IS MET BY CONSTRUCTION HERE, not by a second row: this line keeps the
   * COUNT and the INVITATION and makes no claim about temperature at all, so there is no
   * coldness claim to be false about a loop touched four minutes ago. The distinction R19 built
   * has not gone — it names the GROUPS on `/all`, which is where the groups are actually shown
   * and where `home.coolingRow` and `home.overCapRow` still speak.
   */
  'home.moreRow': {
    warm: '{count} more — look when you’re ready',
    literal: '{count} more tasks',
    oneWarm: '1 more — look when you’re ready',
    oneLiteral: '1 more task',
    slots: ['count'],
  },
  'home.moreRow.hint': {
    warm: 'Opens the full list. Look when you like.',
    literal: 'Opens the full list of tasks.',
  },
  /**
   * NOTHING STARTED, BUT SOMETHING CAUGHT (docs/DEVIATIONS.md D44).
   *
   * `home.empty.title` — "No active tasks." — is a false statement in front of a user who caught
   * four things this morning, and it becomes reachable the moment captures stop being cards. So
   * the surface says what is true and says where the things went: the rule ("start one and it
   * comes here") is stated once, where a user first meets it, rather than left to be inferred
   * from an absence. No prompt, no guilt, no count — the quiet row below carries the number
   * (product-concept §11).
   */
  'home.empty.caught.title': {
    warm: 'Nothing on the go.',
    literal: 'No tasks in progress.',
  },
  'home.empty.caught.body': {
    warm: 'What you’ve caught is in the full list. Start one and it comes here.',
    literal: 'Captured tasks are in the full list. Start one to show it here.',
    allow: ['tense'],
    why: 'The word the tense heuristic reads is "caught", naming captures the user already made; the sentence around it is present ("is in the full list"). design-direction §1 permits the past where it states a fact about an act already done (ADA review C5).',
  },
  /**
   * THE QUIET ROW INSIDE THE EMPTY WARM SET (`docs/DEVIATIONS.md` `D44` objection 3, restored;
   * `docs/roadmap/onboarding.md` item 4).
   *
   * `home.moreRow` IS THE WRONG STRING FOR THIS STATE, which is the whole reason this key exists.
   * "{count} more" is a comparative, and in the empty warm set there is nothing to be more THAN:
   * the surface has just said "Nothing on the go", so a row reading "4 more" invites the reader
   * to look for the four it is counting beyond, and there are none. The same row in the same
   * position needs a different sentence when the set above it is empty.
   *
   * IT CARRIES THE COUNT AND IS A DOOR, and nothing else. Without it the flagship states there is
   * nothing on the go, gives no number, and offers no way to the things the user caught this
   * morning — design-direction §4 objection 1's "beautiful forgetting machine", which that
   * document calls its strongest objection, live on the day the user's model forms. The number is
   * the honesty; the press is the door.
   *
   * IT DOES NOT REPEAT THE DESTINATION. `home.empty.caught.body` renders directly above it and
   * already says the full list is where captures are, so this line says how many rather than
   * saying where twice. `home.moreRow.hint` is the right `accessibilityHint` for it — that string
   * says what pressing does ("Opens the full list") and is unchanged by which visible line sits
   * above it, so the two rows share one hint rather than keeping two copies of one sentence in
   * step (`home.overCapRow.hint`'s note records what it cost the last time they drifted).
   *
   * NO PROMPT, NO GUILT, NO INVENTED URGENCY: "look when you're ready" is product-concept §6.3's
   * own words for a quiet row, carried by `home.coolingRow` and `home.moreRow` already. The
   * literal register drops it, as it does on both of those, because permission to act at a time
   * of one's choosing is warmth rather than a functional label (product-concept §11).
   */
  'home.empty.caught.row': {
    warm: '{count} caught — look when you’re ready',
    literal: '{count} captured tasks',
    oneWarm: '1 caught — look when you’re ready',
    oneLiteral: '1 captured task',
    slots: ['count'],
    allow: ['tense'],
    why: 'Counts captures the user already made — "caught" names a past act, which design-direction §1 permits where the sentence states a fact about something already done. Same shape as `sorting.inboxRow` and `home.empty.caught.body` (ADA review C5).',
  },
  'home.compostRow': {
    warm: '{count} in compost',
    literal: '{count} long-idle tasks',
    oneWarm: '1 in compost',
    oneLiteral: '1 long-idle task',
    slots: ['count'],
  },
  'home.compostRow.hint': {
    warm: 'Opens compost. Nothing is thrown away.',
    literal: 'Opens the list of long-idle tasks.',
  },

  // -------------------------------------------------------------------------
  // The daily suggestion (product-concept §8.1, §9.2; docs/DEVIATIONS.md TD1).
  //
  // EVERY STRING HERE IS ABOUT THE WORK, NEVER ABOUT THE PERSON, AND NEVER ABOUT A DAY.
  // The card is one loop offered once a day and waved away for free, and the whole risk in
  // shipping it is that it reads as the daily goal product-concept §3 refuses. So the copy
  // may never: name a target, mention yesterday, acknowledge having been dismissed, praise
  // taking it up, or change its tone when it is ignored. There is exactly one line, it
  // states what the card is, and it is the same line on the first day and the hundredth.
  // -------------------------------------------------------------------------
  'today.label': {
    warm: 'One to look at today',
    literal: 'One suggested task for today',
  },
  'today.hint': {
    warm: 'Opens it. Nothing changes until you do something.',
    literal: 'Opens the task. Looking changes nothing.',
  },
  // The dismissal. "Not today" is the same phrase the energy self-report already uses for
  // declining, deliberately: one gesture, one word for it, wherever the app asks anything.
  'today.dismiss': { warm: 'Not today', literal: 'Not today' },
  'today.dismiss.hint': {
    warm: 'Puts the card away until tomorrow. Nothing else changes.',
    literal: 'Hides the suggestion until tomorrow. Nothing else changes.',
  },

  // -------------------------------------------------------------------------
  // The full list (product-concept §6.3: "the full list exists but is a place
  // you visit, not a wall you face")
  // -------------------------------------------------------------------------
  // critique 6.3: this surface shipped as a placeholder in permanent navigation — a
  // developer note ("route: app/(tabs)/all.tsx — full list UI pending") inlined in a
  // component, and ALL-CAPS raw model keys (WARM, COMPOSTING) as headings, which bypassed
  // the catalogue entirely and showed a literal-mode reader the exact metaphor vocabulary
  // that mode exists to remove. Every quiet row and the compost sliver point here.
  'all.intro': {
    warm: 'Everything you have going, coolest at the bottom.',
    literal: 'All tasks, grouped by activity. Least active last.',
  },
  'all.empty': { warm: 'Nothing here yet.', literal: 'No tasks yet.' },

  /**
   * SEARCH (`RULINGS R169`), reinstating what `docs/DEVIATIONS.md` D35 removed.
   *
   * D35 deleted a `search` Waymark and this key as dead vocabulary — "if the answer is ever
   * yes they are one import and one entry away". R169 is that yes: product-concept §5
   * objection 3 promises released loops are "archived with their history and **searchable**",
   * and this is the field's own visible caption (`TextField` draws a PERSISTENT label, never a
   * placeholder standing in for one — CLAUDE.md: every control carries a text label).
   *
   * ONE WORD, IDENTICAL IN BOTH REGISTERS, and that is not laziness. product-concept §11 makes
   * literal mode "precise functional labels": "Search" already is one, it carries no metaphor
   * to strip, and inventing a warm synonym ("Go looking") would make the warm register the one
   * that has to be decoded. `nav.settings` and `nav.capture` are the precedent.
   *
   * `nav.` is the right prefix for the same reason those two are: `lint.ts`'s
   * `collidesWithinSurface` checks every `nav.` key against the state vocabulary printed on top
   * of it, which is exactly the check a field caption sitting above five temperature headings
   * needs. It is NOT a sixth tab, and the five-tab grammar (design-direction §4) is untouched —
   * `test/laws.test.ts`'s tab-length assertion names the five keys explicitly.
   */
  'nav.search': { warm: 'Search', literal: 'Search' },
  /**
   * The query matched nothing. Second person, present tense, and about the SEARCH rather than
   * about the user (design-direction §1: acknowledgement attaches to the act, never the human —
   * "you have not written that down" would be a statement about their omission).
   *
   * Shared by `EverythingSurface` and `ListSurface` because it is the same fact on both, and a
   * second key would be the same sentence twice waiting to drift apart.
   */
  'search.none': {
    warm: 'Nothing here matches that.',
    literal: 'No matches for that search.',
  },
  /**
   * THE OTHER REASON A SEARCH FINDS NOTHING: there is nothing to search
   * (`docs/roadmap/onboarding.md` item 7).
   *
   * `search.none` is a statement about the QUERY, and it is the right sentence when a populated
   * device fails to match. On a day-one device it is a false one, and it is what the surface
   * says: `EverythingSurface.tsx` branches on whether a query is live, not on whether anything
   * exists, so a new user who types into the field is told their query failed when the truth is
   * that they have not captured anything yet. Telling somebody with nothing that their search
   * was the problem is the same class of false statement `search.none`'s own note refuses in the
   * other direction (design-direction §1, objection 2 — this audience notices).
   *
   * IT IS ABOUT THE CATALOGUE, NOT THE PERSON, and it states no absence as a fault: "yet" here
   * is the same "yet" `all.empty` carries, which product-concept §11 permits because nothing is
   * being asked for and nothing is owed. It also does not tell the user to go and capture
   * something — an empty search result is not the place for a prompt.
   *
   * SEPARATE FROM `all.empty`, which speaks when no query is live. Both facts can be true at
   * once, and the one the user needs is the one about the field they just typed into.
   */
  'search.nothingYet': {
    warm: 'Nothing here to search yet.',
    literal: 'No tasks to search yet.',
  },
  /**
   * THE ARCHIVE'S "SHOW THE REST" CONTROL (`RULINGS R154`).
   *
   * Only rendered above R154's threshold — when the compact row's measured height has met the
   * `LoopCard` height it replaces, so the archive has stopped being cheaper to list than the
   * cards it replaced. Below it there is no control, because there is nothing hidden.
   *
   * IT NAMES THE NUMBER IT IS HIDING. "Show the rest" alone would be the app declining to say
   * how much of the user's own history it has folded away, on a surface whose whole promise is
   * that nothing is taken away (CLAUDE.md; product-concept §5 objection 3). The count is the
   * honesty.
   *
   * THE LITERAL PLURAL CARRIES THE NOUN THE SINGULAR CARRIES. It read "Show the remaining
   * {count}" against a singular "Show the remaining 1 item", so every plural rendering was
   * "Show the remaining 4" — a number with nothing after it, in the register whose whole job is
   * "precise functional labels" (product-concept §11). `home.coolingRow` and `home.moreRow` are
   * the house form: same noun in both branches, literal names the thing, warm does not.
   */
  'all.showTheRest': {
    warm: 'Show the other {count}',
    literal: 'Show the remaining {count} items',
    oneWarm: 'Show the other one',
    oneLiteral: 'Show the remaining 1 item',
    slots: ['count'],
  },
  'all.showTheRest.hint': {
    warm: 'Opens the rest of this group.',
    literal: 'Shows the rest of this group.',
  },

  // Temperature labels. Every temperature is rendered as icon + LABEL + colour, so
  // colour is structurally redundant (design-direction §3) — these are the label channel.
  // 'Recent', not 'Active' (critique 7.3): `warm` means "among the few most recently
  // touched, up to the cap" (core `deriveTemperatures`), so 'Recent' is the more precise
  // literal label AND it stops the pill restating the screen's own title. See `nav.home`.
  'temp.warm': { warm: 'Warm', literal: 'Recent' },
  'temp.cooling': { warm: 'Cooling', literal: 'Idle' },
  'temp.composting': { warm: 'Composting', literal: 'Long idle' },
  'temp.closed': { warm: 'Closed', literal: 'Complete' },
  // RULINGS R75 inverted `terms.released` back the right way round; this was the SECOND site
  // carrying the same two words in the same wrong direction, and it is the live one — the
  // Temperature badge (TemperatureMark.tsx, EverythingSurface.tsx). Warm carries the idiom,
  // literal carries the state's own name in `@carn/core`, like every row above it. If a third
  // site is ever added, grep for `Let go` and `Released` together before trusting either.
  'temp.released': { warm: 'Let go', literal: 'Released' },

  // The capsule, shown as the loop's bookmark — never as evidence of mess
  // (product-concept §6, objection 3).
  // "Saved context" named the DATA STRUCTURE, and a heading that names the app's own tables is the
  // app talking about itself instead of about the work (design-direction §1: Carn "talks about the
  // work"). It also headed a card whose empty state read "No saved context." — a heading and a body
  // saying the same nothing twice. §6.1 states what a capsule answers, in the user's terms:
  // "Where was I, and what's the very next physical thing?"
  'capsule.label': { warm: 'Your bookmark', literal: 'Where you left off' },
  'capsule.next': { warm: 'Next', literal: 'Next step' },
  'capsule.lastStep': {
    warm: 'Where you got to',
    literal: 'Last completed step',
    allow: ['tense'],
    why: 'A capsule field names past state by definition — that is what a capsule is for (product-concept §6.1).',
  },
  'capsule.where': { warm: 'Left it at', literal: 'Location' },
  'capsule.parkedWhen': {
    warm: 'Parked {ago}',
    literal: 'Paused {ago}',
    slots: ['ago'],
    allow: ['tense'],
    why: 'The bookmark\'s own status word: this capsule was parked at a time, stated as a fact rather than narrated. Same shape as `interrupt.parked` (ADA review C5).',
  },
  /** The bare label, for places that pair the clock glyph with a word (design-direction §6). */
  'capsule.timeLabel': { warm: 'Time in', literal: 'Time spent' },
  'capsule.timeInvested': {
    warm: '{duration} in so far',
    literal: 'Time spent: {duration}',
    slots: ['duration'],
  },
  'capsule.interruptedBy': {
    warm: 'Interrupted by {what}',
    literal: 'Interrupted by {what}',
    slots: ['what'],
  },
  'capsule.voiceNote': { warm: 'Voice note', literal: 'Audio note' },
  'capsule.playVoiceNote': { warm: 'Play voice note', literal: 'Play audio note' },

  /**
   * PLAYBACK HAS TO BE VISIBLE, OR IT LOOKS BROKEN.
   *
   * "Play audio note" produced no rendered change of any kind: not while it played, not when it
   * finished, and — the case that matters — not when it could not play at all. The failure branch
   * called `announce()` and stopped there, which reaches a screen reader and nobody else, so on a
   * silent phone, a phone in a pocket, or a build with no audio module, the button was a control
   * that did nothing and said nothing. design-direction §3's law is that state is never carried by
   * one channel; here it was carried by none.
   *
   * Three sentences, one per state the control can be in. `capsule.voiceNoteUnplayable` states the
   * fact AND where the content still is, because product-concept §7.3's "every channel has a silent
   * equivalent of equal speed" is satisfied by the transcript above it — audio is an addition, and
   * losing an addition is not losing the note.
   */
  'capsule.stopVoiceNote': { warm: 'Stop', literal: 'Stop playback' },
  'capsule.voiceNotePlaying': { warm: 'Playing.', literal: 'Playing.' },
  'capsule.voiceNoteUnplayable': {
    warm: 'This one will not play on this phone. The words are just above.',
    literal: 'This audio cannot be played on this device. The transcript is above.',
  },

  'capsule.photo': { warm: 'Photo of where you left it', literal: 'Photo' },
  /**
   * THE PHOTO, AT THE SIZE IT WAS TAKEN FOR.
   *
   * product-concept §6.1's photo is "the half-disassembled pump, exactly as left" — the whole value
   * is in detail a reader can look at, and every rendering of it in the app was a wide, shallow
   * strip about three touch targets tall, cropped `cover`. That is right for recognition on a card
   * and wrong on the one surface whose header says looking at it is the point.
   *
   * Opening it is a deliberate act with an obvious way out, never a gesture to discover: the strip
   * is a labelled control, and the full view carries a close control at the full touch target.
   */
  'capsule.photoOpen': { warm: 'See the photo full size', literal: 'View photo at full size' },
  'capsule.photoClose': { warm: 'Close the photo', literal: 'Close the photo' },
  /**
   * SHORTENED BY MEASUREMENT, NOT BY TASTE (docs/RULINGS.md R115; docs/DEVIATIONS.md L7).
   *
   * The warm register was "No bookmark on this one yet." — 28 characters, which wraps to two lines
   * inside a `LoopCard` at 320pt in dial 4, making the card 88pt instead of 62pt. That 26pt was the
   * whole of the 1.0pt overflow that put the compost review's THIRD option — "let it rest", the
   * deferral clause product-concept §8.3 exists to protect — past the fold on an iPhone SE
   * (measured: bottom 569.0 against a 568pt viewport).
   *
   * The alternative was `numberOfLines={1}` on `LoopCard`'s capsule line, and R115 rejects it in
   * terms: that would ellipsis a REAL capsule sentence on the home recognition card, which
   * product-concept §6.1 makes the entire recognition mechanism and D14 spent a 240pt height budget
   * defending. Clipping the one thing the card exists to show, to recover 1pt on a different
   * surface, trades the wrong way.
   *
   * WHAT IT COSTS: four words of the warm register's "on this one" specificity, on every capsule-less
   * card (home recognition, `/all`, the loop detail, the Sorting card). Nothing anywhere gets a
   * TALLER string, so no layout can regress. The literal register is untouched — it was already one
   * line, and R115's other half is `compost.reviewTitle`.
   */
  /**
   * (continued) AND THE LITERAL REGISTER WAS FALSE WHENEVER IT RENDERED WITH ITS OWN SIBLINGS.
   *
   * "No saved context." is printed under the condition `tier === 0 && !nextAction && !lastStep` —
   * and the `Fact` rows immediately below it are unconditional on that, so a loop parked in a hurry
   * showed "No saved context." directly above "Time spent: 1 min" and "Paused less than an hour
   * ago", which IS saved context. Tier 0 is guaranteed (product-concept §6.1), so on this branch
   * there is ALWAYS context; the only thing missing is something the user wrote. The warm register
   * was already scoped correctly — a bookmark is a thing you write — and the literal register said
   * "context", which covers the timestamps too. Found in a product-owner screenshot.
   *
   * Same character count as the string it replaces, so R115/N8a's measured 1pt on the compost
   * review's fold cannot regress from this.
   */
  /**
   * THE LITERAL FORM SAID "NOTE", AND THE LOOP NOW HAS ONE (docs/DEVIATIONS.md LA1).
   *
   * It read "No note here yet." — fine while `Loop.note` had no writer and nothing rendered it,
   * and false the moment one did: the harness screenshot of `loop/seed-loop-rhythm-feed` at the
   * reference rendering showed "No note here yet." with the user's own two-sentence note directly
   * beneath it, under a heading reading "Details". Two keys, neither wrong on its own, assembling
   * into a contradiction — the species `lintCompositions` exists for, found by looking.
   *
   * The warm form was already right, because it named the object ("bookmark") rather than a
   * generic word for text. The literal register cannot borrow that metaphor, so it names the
   * thing the capsule actually records instead. Both forms are now about the CAPSULE, which is
   * what this key has always been about — see `capsule.notStarted` below on the distinction.
   */
  'capsule.none': {
    warm: 'No bookmark yet.',
    literal: 'Nothing recorded about where you stopped.',
  },
  /**
   * NEVER WORKED ON, AS OPPOSED TO WORKED ON AND NOTHING RECORDED (docs/DEVIATIONS.md top/D38;
   * core `hasBeenWorked`).
   *
   * `capsule.none` above is a statement about a CAPSULE — there is one, guaranteed
   * (product-concept §6.1), and it happens to carry nothing. "No bookmark yet" is honest about a
   * loop that was put down in a hurry. Said about a loop that was caught this morning and never
   * opened, it quietly asserts that there was a place to bookmark, which is the same false
   * premise the "resume" wording carried.
   *
   * NEITHER REGISTER IMPLIES A DEBT. It is a fact about the work, in the present tense, with no
   * second clause — no "yet you should", no elapsed time, no prompt. "Nothing is ever taken away
   * by time. Absence costs nothing" (CLAUDE.md): a loop can sit here for a year and this line
   * will read exactly the same on the last day as on the first.
   */
  'capsule.notStarted': {
    warm: 'Not started yet.',
    literal: 'Not started.',
  },
  'capsule.addNote': { warm: 'Add a note while it is fresh', literal: 'Add a note' },
  /**
   * THE TWO PULLED MODEL TASKS (`RULINGS R203`; implementation-brief §6's `first-physical-action`
   * and `capsule-summarise`). Every string here is written for a control the user presses on
   * purpose, which is why none of them announces itself or explains what AI is — the shipped
   * disclosure `privacy.ai.what` already does that, in these same words: "Ask for AI sorting or a
   * breakdown, and that text is sent to a model to answer it."
   *
   * `capsule.assist.basis` IS THE LOAD-BEARING ONE. It is the capsule's equivalent of
   * `sorting.basis.model` and it must never be dropped for brevity: a suggestion that does not say
   * it is a suggestion is not one (product-concept §7.2, "AI never acts silently"). It also says
   * the choice is the user's, because at the moment it renders, nothing has been written.
   */
  'capsule.assist.askNextAction': {
    warm: 'Ask for a first step',
    literal: 'Request a suggested first step',
  },
  'capsule.assist.askSummary': {
    warm: 'Sum up the recording',
    literal: 'Summarise the voice note',
  },
  'capsule.assist.working': {
    warm: 'Asking a model. This takes a moment.',
    literal: 'Waiting for the model.',
  },
  'capsule.assist.basis': {
    warm: 'A model suggests this. Keep it, or leave it.',
    literal: 'Suggested by a model. Nothing is saved until you keep it.',
  },
  'capsule.assist.keep': { warm: 'Keep this', literal: 'Keep this' },
  'capsule.assist.discard': { warm: 'Leave it', literal: 'Discard' },
  /**
   * The honest line for every failure at once — refused, unreachable, unparseable, over-long.
   * `PLUS_AI_ABUSE_CEILING`'s doc comment forbids presenting its 429 as a paywall, and one line
   * covering all outcomes is the surest way to keep that true. It states the consequence
   * (nothing changed) because that is the only part the user needs.
   */
  'capsule.assist.nothing': {
    warm: 'Nothing came back. Nothing changed.',
    literal: 'No suggestion returned. Nothing was saved.',
    allow: ['tense'],
    why: 'Reports what just happened to a request the user made — an acknowledgement of an event, which is the case the tense law exempts.',
  },
  'capsule.assist.keptNextAction': {
    warm: 'Kept as the first step.',
    literal: 'Saved as the first step.',
    allow: ['tense'],
    why: 'Acknowledges a write the user just confirmed, on the same grounds as capsule.noteAdded.',
  },
  'capsule.assist.keptSummary': {
    warm: 'Kept as where you got to.',
    literal: 'Saved as the last step.',
    allow: ['tense'],
    why: 'Acknowledges a write the user just confirmed, on the same grounds as capsule.noteAdded.',
  },
  // Text-only capsules are a user preference (product-concept §6, objection 3). The
  // photo is kept, never discarded — the app says so rather than silently hiding it.
  'capsule.photoHidden': {
    warm: 'Photo kept, not shown. Your choice, in settings.',
    literal: 'Photo hidden by your setting.',
  },
  /**
   * THE PICTURE IS NOT ON THIS PHONE ANY MORE, AND THE APP SAYS SO.
   *
   * What this replaces: an empty bordered frame with "Photo" and "See the photo full size"
   * underneath it — a control offering to show at full size something it does not have. Every
   * capsule photo captured before `src/native/mediaFiles.ts` existed was stored in the app's cache
   * directory under an absolute container path, and both halves of that stopped being true on the
   * next app update, so the strip rendered nothing and said nothing about why.
   *
   * NOT AN ERROR, AND NOT THE USER'S FAULT — design-direction §1's register, and CLAUDE.md's law
   * that acknowledgement attaches to the act and never to the person. It states the fact and stops:
   * no "failed to load", no red, no retry control for something no press can bring back. The rest
   * of the capsule — the next action, the transcript, the words the photo was standing beside — is
   * untouched and is still on the screen, which is why this is one quiet line and not a surface.
   */
  'capsule.photoMissing': {
    warm: 'The photo is not on this phone any more.',
    literal: 'This photo is no longer available on this device.',
  },

  /**
   * Tier-2 prompt, solicited only at detected calm (product-concept §6.1, RULINGS R42).
   *
   * THE TITLE COMES LAST, for the reason `warmCap.letCool` records and this key had not yet
   * applied. It read "You parked {title} without a note. Want to add one while it is fresh?",
   * which against the shipped fixture renders "You parked Check the water trough in the top field
   * without a note" — a user's own sentence wedged between a verb and its object, so the reader has
   * to find where the title ends before the sentence means anything. A slot holding an arbitrary
   * user sentence belongs at the end of the string, never in the middle of a clause (the class of
   * error R24 was written about; `warmCap.letCool` was fixed for exactly this and this one could not
   * be touched in the same pass).
   *
   * The question now comes first, which is also the right reading order: the prompt's job is to ask,
   * and the title is which thing it is asking about. "this one" carries the reference until the title
   * lands, so the sentence is complete in both directions.
   */
  'capsule.calmPrompt': {
    warm: 'Want to add a note while it is fresh? You parked this one without one: {title}',
    literal: 'Add a note? You paused this one without context: {title}',
    slots: ['title'],
    allow: ['tense'],
    why: 'States that this loop was parked without a note, as the premise for the prompt that follows — a fact about the past (ADA review C5).',
  },

  'loop.resume': { warm: 'Pick it up', literal: 'Resume task' },
  'loop.resume.hint': {
    warm: 'Opens it where you left off.',
    literal: 'Opens the task at the saved step.',
  },
  /**
   * The OUTCOME of resuming, for the `rise` moment's announcement (critique 5.3).
   *
   * The moment used to announce `loop.resume` — the button's own label — so VoiceOver said
   * "Pick it up" as the result of having pressed "Pick it up". A moment's announcement is the
   * only channel a screen-reader user has for it (design-direction §5), so it must state what
   * happened, the way `celebrate.stoneAdded` does.
   */
  'loop.resumed': {
    warm: 'Picked up where you left off.',
    literal: 'Task resumed at the saved step.',
    allow: ['tense'],
    why: 'An acknowledgement names the act that just happened. design-direction §1 permits past tense there.',
  },

  /**
   * THE SAME BUTTON, ON A LOOP NOBODY HAS EVER WORKED ON (docs/DEVIATIONS.md top/D38; core
   * `hasBeenWorked`).
   *
   * `loop.resume` above and this pair are one control with two truths, chosen by a fold over the
   * loop's own append-only log. Nothing is filed at capture and no state is stored — a loop with
   * an empty cairn and an empty capsule log has never been started, and that absence is the whole
   * fact (`hasBeenWorked` in `@carn/core` has the argument in full).
   *
   * IT IS NOT A SECOND ACTION. It presses the same code path, places the same first stone when the
   * session ends, and occupies the same one primary slot on the card. product-concept §6.3 gives
   * the surface exactly one primary action and this does not add one — a second button reading
   * "start" beside one reading "pick it up" would be the invented vocabulary RULINGS R25 was
   * written about.
   *
   * THE HINT IS THE POINT AS MUCH AS THE LABEL. `loop.resume.hint` promises "opens it where you
   * left off", which is the specific false claim: there is no `where`. This one says what actually
   * happens, and says it in the same shape.
   */
  'loop.start': { warm: 'Start it', literal: 'Start task' },
  'loop.start.hint': {
    warm: 'Opens it and starts the clock.',
    literal: 'Opens the task and starts a work session.',
  },
  /**
   * The OUTCOME of starting, for the `rise` moment's announcement — the same contract
   * `loop.resumed` above states, for the same reason: a moment's announcement is the only channel
   * a screen-reader user has for it, so it must name what happened rather than echo the label
   * that was pressed (critique 5.3).
   *
   * "at the saved step" is exactly what it cannot say. There is no saved step.
   */
  'loop.started': {
    warm: 'Started.',
    literal: 'Task started.',
    allow: ['tense'],
    why: 'An acknowledgement names the act that just happened. design-direction §1 permits past tense there, as it does for loop.resumed.',
  },
  /** The card's own hint: what opening it does. critique 7.4 — it used to be a heading. */
  'loop.open.hint': {
    warm: 'Opens everything saved on this one.',
    literal: 'Opens all saved details for this task.',
  },
  'loop.park': { warm: 'Park it', literal: 'Pause task' },
  'loop.park.hint': {
    warm: 'Saves your place and stops the clock.',
    literal: 'Saves context and pauses the task.',
  },
  'loop.activeNow': { warm: 'On this now', literal: 'In progress' },
  // Prominence, never threat (design-direction §3, objection 3): a hard date is stated
  // plainly and given size and position. There is no alarm string for it anywhere.
  //
  // critique 6.2 read the warm form "The world cares: {when}" as the capture-time
  // DISAMBIGUATION QUESTION (product-concept §5, "does the world care when this happens, or
  // just you?") reused verbatim as a display label, and replaced it with "Set for {when}".
  //
  // RULINGS R25 keeps the diagnosis and rejects the cure: "Set for {when}" says nothing about
  // WHICH of the two kinds of date this is, which is the one distinction §5 makes at capture,
  // so the card lost the only fact the toggle exists to record. The fix is to stop the phrase
  // being a bare claim — a full clause about this piece of work, not a two-word verdict — and
  // to give the literal register the mechanism's name. The question still lives at
  // `capture.deadline.world`, where it is a question.
  //
  // RULINGS R151 SHORTENS THE WARM CLAUSE, and it is the only term on the resumption card that
  // could be shortened without breaking a law. R123 measured the leading card at 268pt against a
  // 246pt scroller at 320×568 and found every other term to be a law (56pt targets) or a document
  // requirement (§5's date kind, §6.3/§9.1's payload line, R115's no-truncation). Measured in the
  // app's own type at 320×568, in the 236pt column the card gives this line: the old warm form
  // rendered 290.2pt — two lines — and this one renders 196.4pt, one line, at every dial setting.
  //
  // R25's requirement survives the cut. What R25 rejected was "Set for {when}", a two-word verdict
  // saying nothing about WHICH of §5's two kinds of date this is; "the world cares" is R25's own
  // vocabulary for the hard kind and it is still here. What went is "about this one", which named
  // nothing the card was not already showing — the clause sits directly under the loop's title.
  'loop.hardDate': {
    warm: 'The world cares — {when}.',
    literal: 'Hard deadline: {when}.',
    slots: ['when'],
  },
  /**
   * A hard date that has already passed (critique 3.5, 6.1).
   *
   * "The world cares: Wed, Jul 1, 10:00 AM", present tense, about an appointment four weeks
   * gone, was simply a false statement — and the user was left to know today's date and
   * subtract, which is exactly the reconstruction cost §6.3 exists to abolish, for an
   * audience design-direction §4 calls time-blind.
   *
   * It states the fact and stops. No threat, no colour, no "overdue" — the word does not
   * exist in this product (product-concept §5) and the state does not either: core's
   * `deadlineProminence` decays over a two-day grace window, so a passed date stops being
   * pinned to the top instead of being marked as a failure.
   */
  'loop.hardDatePassed': {
    warm: 'That was {when}',
    literal: 'This date has passed: {when}',
    slots: ['when'],
    allow: ['tense'],
    why: 'States a date that is by definition in the past. Present tense would make it untrue (critique 3.5).',
  },
  'loop.close': { warm: 'Close it', literal: 'Mark complete' },
  /**
   * EQUAL STANDING, CARRIED BY THE VERB (product-concept §6.4).
   *
   * The literal register was "Complete at good enough", which is not English — `complete` reads as
   * an adjective and its complement here is an adjective phrase — and it was the longest label in
   * the endings block, so on the reference rendering (level 0, literal, iPhone SE) it was also the
   * WIDEST control on the surface. §6.4 requires good-enough be equal to closure; width is the only
   * weight channel left once colour is gone by law, so the widest ending read as the most emphatic
   * rather than as a peer.
   *
   * `Mark good enough` shares its verb with `loop.close`'s `Mark complete`. That makes the parity
   * STRUCTURAL — a property of the words, true at every dial setting and every text scale — rather
   * than a property of a layout that has to keep two controls on one line to say it. The "this is
   * still a completion" fact is not lost: `loop.closeGoodEnough.explain` carries it as the hint.
   */
  'loop.closeGoodEnough': { warm: 'Good enough', literal: 'Mark good enough' },
  // critique 5.4.3: "That counts" is a fact about the SCORING SYSTEM — the app arguing
  // with the user's guilt. The model sentence in design-direction §1 is a fact about the
  // WORK. critique 5.5: the literal register had drifted into clerical jargon
  // ("Marked complete at a partial standard") — literal mode is for users who need
  // precision, not for auditors.
  'loop.closeGoodEnough.explain': {
    warm: 'Done enough to stop. It closes here.',
    literal: 'Closes the task at a partial standard. This is a completion.',
  },
  'loop.close.hint': {
    warm: 'Marks it done. One more stone on it.',
    literal: 'Marks the task complete.',
  },
  /**
   * THE SHORT VISIBLE PAIR — painted on the Now surface's card, spoken as the full pair above
   * (docs/DEVIATIONS.md `now-card/NC3`, amended).
   *
   * *Founder instruction: "can we make 'good enough' smaller, it's getting a little crowded".*
   *
   * The same visible/accessible split `shopping.removeItem`/`shopping.removeItemFull` uses, and
   * for a sharper version of the same reason. `LoopCard` draws the two closes side by side, so on
   * the 320pt phone every fold argument here is fought at, each control is 115pt wide and holds
   * 81pt of text. `Mark good enough` measures 159pt at that type, so it wrapped to THREE lines —
   * and because the pair is stretched to one height (§6.4: neither may be the smaller control),
   * the longer label was setting the height of both. 96pt of card for two words.
   *
   * SHORTENED IN PAIR, NEVER ONE OF THEM. The instruction names good-enough, and shortening only
   * good-enough is the one thing §6.4 forbids: `loop.closeGoodEnough`'s own entry above records
   * that the shared verb is what makes the parity STRUCTURAL rather than a property of a layout.
   * So both lose the verb together and the parity moves to the other symmetry available — two
   * parallel words for the standard the work is being closed at, `Complete` and `Good enough`,
   * the same part of speech at the same length class.
   *
   * NOTHING IS LOST FROM THE SPOKEN CHANNEL OR FROM THE OTHER TWO SURFACES. `Button.visibleLabel`
   * changes only the painted text: `label` stays `loop.close`/`loop.closeGoodEnough`, so a
   * VoiceOver user hears `Mark complete` and `Mark good enough` exactly as they do on
   * `LoopDetail` and in the quick-actions menu, both of which are untouched — they have full card
   * width and never had this problem. `loop.close.hint` and `loop.closeGoodEnough.explain` still
   * carry "this is a completion".
   *
   * THE WARM REGISTER IS ALREADY SHORT, so its values are its long ones. `Close it` measures 61pt
   * and fits on one line; that register never wrapped to three and nothing about it changes.
   */
  'loop.close.short': { warm: 'Close it', literal: 'Complete' },
  'loop.closeGoodEnough.short': { warm: 'Good enough', literal: 'Good enough' },
  /**
   * GIVING A LOOP A NAME, OR A BETTER ONE (product-concept §7.2 objection 2, "keep your own
   * words"; RULINGS R27 — an unnamed capture is named from the catalogue at render time, so there
   * is always a name to change).
   *
   * SEPARATE FROM `sorting.fixName*`, AND THE HINT IS THE REASON. That family's hint promises the
   * wording is "kept for future suggestions" — true on the Sorting card, where a proposal is on
   * screen to diff against, and FALSE here: `RenameControl` learns nothing, deliberately, because
   * there is no "what the app heard" on this surface and inventing one would be the silent
   * inference §7.2 forbids. The loop detail surface rendered `sorting.fixNameHint` as the rename
   * control's hint AND as the loop title's accessibility hint, so a screen-reader user pressing the
   * loop's own name was told the app would remember their wording. It does not. A string asserting
   * behaviour the code does not have is the R80 species in the register that exists to be trusted,
   * so the two acts get two families and neither has to lie for the other.
   */
  'loop.rename': { warm: 'Fix the name', literal: 'Rename this task' },
  'loop.rename.hint': {
    warm: 'Changes what this one is called. The old name is kept.',
    literal: 'Changes the task name. The previous name is kept.',
  },
  'loop.rename.apply': { warm: 'Use those words', literal: 'Use this name' },
  /**
   * THE WAY OUT OF THE RENAME EDITOR, which did not exist.
   *
   * `setOpen(false)` was reachable only from inside `apply()`, behind a guard that returns early on
   * an empty field — and the labelled button that opened the editor is replaced BY the editor. So
   * opening it, including by mis-tapping the title, left the user with the keyboard up and the
   * route's Back control as the only exit. R14's instinct generalises: a control the user cannot
   * find is bad, and a state the user cannot leave is worse.
   *
   * "Leave the name alone", not "Cancel", in the warm register: it says what survives rather than
   * what is abandoned, which is the same choice `loop.remove.cancel` makes with "Keep it".
   */
  'loop.rename.cancel': { warm: 'Leave the name alone', literal: 'Cancel' },
  'loop.rename.cancelHint': {
    warm: 'Closes this without changing the name.',
    literal: 'Closes the editor. The name is unchanged.',
  },
  /** It landed. Said, because silence is not an answer to an act the user performed. */
  'loop.renamed': {
    warm: 'Your words, then.',
    literal: 'This task is renamed.',
    allow: ['tense'],
    why: 'The rename\'s own outcome acknowledgement — the same shape as `sections.renamed`, `lists.renamed` (ADA review C5).',
  },
  'loop.rewarm': { warm: 'Bring it back', literal: 'Make active' },
  'loop.rewarm.hint': {
    warm: 'Puts it back in front of you. Its history stays.',
    literal: 'Makes the task active again. Its history is kept.',
  },
  /**
   * The OUTCOME of bringing one back, spoken (docs/DEVIATIONS.md D43).
   *
   * The quick-actions menu closes on this act, and the visible answer is a card that has moved
   * groups on a list the user may not be looking at — so a screen reader gets the same fact the
   * eye does, in the shape `loop.resumed` already uses: what happened, never the button's label
   * (critique 5.3).
   */
  'loop.rewarmed': {
    warm: 'Back in front of you: {title}',
    literal: 'Task is active again: {title}',
    slots: ['title'],
  },
  /**
   * The literal register said "Let go of task" — handing the reader the idiom on an ACTION
   * BUTTON, in the one mode that exists to remove it (RULINGS R81, the third site after R75's
   * `terms.released` and `temp.released`). Its own sibling `loop.rewarm` already runs
   * warm "Bring it back" / literal "Make active", so this row was also the one disagreeing
   * with the pair it sits in.
   */
  'loop.release': { warm: 'Let it go', literal: 'Release task' },
  'loop.release.hint': {
    warm: 'A decided ending. It stays findable.',
    literal: 'Ends the task. It stays searchable.',
  },
  // -------------------------------------------------------------------------
  // Tending a repeating chore (product-concept §8.1, the rhythm axis; the "Recurring work"
  // section of docs/OPEN-QUESTIONS.md is the authorised design)
  //
  // THIS IS THE ONE ENDING THAT IS NOT AN ENDING, and the copy is most of what stops a reader
  // mistaking it for one. `loop.close` is "you finished it", `loop.release` is "you decided to
  // stop", and both write a terminal record. Tending writes none: it places a session stone,
  // advances `lastTouchedAt`, and leaves the task open so its repeating time condition
  // (`surfacing.rhythm`) brings it back in its next window. "feed the chickens" survives being
  // fed.
  //
  // WHAT NO STRING HERE MAY SAY, in either register. A missed window creates nothing — there is
  // no Tuesday object to be late, no counter, no gap — so there is nothing to report about one.
  // No lateness, no elapsed-time judgement, no count of times done, no run, no rate, no
  // percentage. That is the streak/cairn distinction (design-direction §6, objection 1) applied
  // to a feature that would otherwise be the easiest place in the app to smuggle a streak back
  // in. The linter's `no-streaks` and `no-shame` lists catch the words; the silence about
  // frequency is the part only a reader can hold.
  // -------------------------------------------------------------------------
  /**
   * "Done this time" NAMES THE OCCURRENCE RATHER THAN THE LOOP (`RULINGS R163`, CHANGED).
   *
   * The shipped candidate was "Done for now", and R124 itself flagged the weakness that R163 then
   * ruled on: *"for now"* can be read as *"I'm stopping for now"*, which is `loop.park` — a
   * different act, on the same surface, one control away. "This time" cannot be read that way,
   * because it is about the OCCURRENCE, which is exactly what `tend` records: the chore is done
   * this time and it comes round again.
   *
   * Pitched at the length and register of `loop.close` ("Close it") and `loop.release` ("Let it
   * go") because it sits in the same action set. The literal register names the mechanism the way
   * `celebrate.stoneAdded` does, and takes "repeats" from `surfacing.rhythm`'s own literal form
   * ("Repeating time condition", RULINGS R25) rather than inventing a second word for one
   * condition.
   *
   * Copy-law check, made explicitly because R163 makes one: about the act, no praise adjective
   * about the person, and no claim about a schedule (design-direction §1). `R166` makes the warm
   * register unreachable in the shipped app — this is corrected for correctness and for the
   * harness's register axis, not because a user reads it tomorrow.
   */
  'loop.tend': { warm: 'Done this time', literal: 'Mark done, task repeats' },
  // -------------------------------------------------------------------------
  // TAKING A TEND BACK (`RULINGS R160`)
  //
  // `tend` was the only irreversible act on the loop detail surface, and a mis-press does two
  // things at once: it writes a permanent session stone, and it stops the chore coming round in
  // this window — the exact failure the feature exists to prevent, achieved by pressing the
  // feature. R160's answer is an undo rather than a confirmation, so `tend` stays a two-second tap.
  //
  // THESE STRINGS ARE `loop.remove.undo`'S GRAMMAR, DELIBERATELY: plain, timerless, no threat, no
  // warning, nothing red (there is none in the token system to reach for). And nothing here may
  // say that the work did not happen — it says the RECORD is taken back, which is the true and
  // narrower claim. Nothing is deleted: the stone stays in the append-only log for ever and the
  // derivations stop counting it (CLAUDE.md).
  // -------------------------------------------------------------------------
  /**
   * The control. It answers "I pressed that by mistake" and nothing else.
   *
   * NOT "UNDO" ALONE in the warm register: a bare "Undo" beside four other controls says nothing
   * about which act it reverses, and this surface can hold a removal undo at the same moment. The
   * literal register says the mechanism plainly, as its siblings do.
   */
  'loop.tend.withdraw': { warm: 'Not done after all', literal: 'Take back the last one' },
  /**
   * BOTH FACTS, AND ONLY THESE TWO. The pile stops counting it, and the task comes round again.
   *
   * No count, no rate, no "you have taken back 2" — the block above `loop.tend` forbids the whole
   * family. And nothing says "deleted": `standingStones` in @carn/core stops counting the stone and
   * the stone stays in the log, which is why "stops counting" is the honest verb in both registers.
   */
  'loop.tend.withdraw.hint': {
    warm: 'Takes the last one back off the pile. This one comes round again.',
    literal: 'Stops counting the last completion. The task can be brought round again.',
  },
  /**
   * The acknowledgement, said out loud through a live region — the same discipline
   * `loop.remove.undone` keeps, because a control that did something and said nothing is the
   * defect `CapsuleDetail` records against its own play control.
   */
  'loop.tend.withdrawn': {
    warm: 'Taken back. It comes round again.',
    literal: 'The completion is taken back. The task can be brought round again.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },
  /**
   * THE HINT IS LOAD-BEARING HERE IN A WAY IT IS NOT FOR ITS SIBLINGS. Every other action in
   * this set has a prior model a reader can lean on — closing, releasing, pausing, deferring and
   * removing are all things other apps do. A disposition that records the work and refuses to end
   * is new, and it sits directly beside `loop.closeGoodEnough.explain` ("Done enough to stop. It
   * closes here."), so without this line the least familiar control would be the only one whose
   * meaning had to survive on a button label alone.
   *
   * IT ECHOES `loop.close.hint`'S OPENING ON PURPOSE, THEN BREAKS FROM IT. That hint reads "Marks
   * it done. One more stone on it."; this one reads "Marks it done without closing it." The shared
   * first three words are the shared fact — the work is recorded either way — and the divergence
   * arrives at word four, which is where a reader comparing two adjacent buttons is looking.
   *
   * NEITHER FORM NAMES A SCHEDULE OR A NUMBER. "on its own" is the promise the mechanism actually
   * keeps (the task stays open, so `evaluateSurfacing` sees it and its condition fires in its next
   * window); "next Sunday", "every week" and "tomorrow" are all schedules a weekday-plus-window
   * condition may not have, and a count of any kind is forbidden outright — see the block above.
   * The literal register reuses `surfacing.rhythm`'s own words ("Repeating time condition",
   * RULINGS R25) rather than minting a second name for one condition, and states what STAYS
   * rather than what does not happen: "it is not completed" is a true sentence that reads as a
   * negation of the work the user has just done, which is not shippable at the moment they read it.
   */
  'loop.tend.hint': {
    warm: 'Marks it done without closing it. It comes round again on its own.',
    literal: 'Records the task as done. It stays open, and its repeating time condition still applies.',
  },
  /**
   * The quiet fact line, and deliberately the most boring string in this file.
   *
   * `{ago}` is `formatAgo` (`@carn/ui`), the same slot and the same coarse vocabulary as
   * `capsule.parkedWhen` ("Parked {ago}") — "yesterday", "3 days ago", "2 weeks ago". Coarse is
   * the safety property here, not a limitation: a weekday name is ambiguous past seven days, and
   * anything finer invites the reader to do the subtraction that produces a verdict.
   *
   * ONE FACT, IN THE PAST, ABOUT AN ACT. Never "due today", never "3 days late", never "12 in a
   * row". Prominence never escalates with elapsed time either — a hard date gets prominence via
   * core's `deadlineProminence` (design-direction §3, objection 3) and a rhythm loop is not that.
   * No `allow: ['tense']`: "done" is a past participle inside a timestamp, exactly like
   * `capsule.parkedWhen`, which declares none.
   */
  'loop.lastTended': {
    warm: 'Last done {ago}',
    literal: 'Last marked done {ago}',
    slots: ['ago'],
  },
  /**
   * A REPEATING LOOP, STATED AS A FACT ABOUT THE LOOP (product-concept §8.1; RULINGS R25).
   *
   * The loop detail surface used to render `surfacing.rhythm` here — "When it comes round" /
   * "Repeating time condition" — which is an AXIS LABEL from the Sorting card, where it heads the
   * proposal for that axis. Rendered beside sentences like "Parked 2 days ago" and "Time spent: 40
   * minutes" it reads as a field name in a row of statements. R125's species, inverted: reusing an
   * authority whose shape is different. The literal register takes its wording from
   * `celebrate.tended`, which already says "This task repeats", so no second vocabulary is minted
   * for one condition.
   *
   * WHAT IS STILL NOT HERE, and will not be: no schedule, no weekday, no next window, no count, no
   * run, no rate. See `RhythmBlock`'s own docblock in apps/mobile — every one of those is either
   * unauthorised scope or a streak with a beard, and "Due Sunday" would make a missed Sunday into
   * something that had been missed. The honest statement available today is that it repeats.
   */
  'loop.repeats': { warm: 'This one comes round again', literal: 'This task repeats' },
  // -------------------------------------------------------------------------
  // WHEN IT COMES ROUND, IN WORDS (`RULINGS R162`)
  //
  // `surfacing.rhythm` renders as a bare axis label and the condition's `weekday`, `hourFrom` and
  // `hourTo` were shown to the user NOWHERE — so the app could say a thing repeats and could not
  // say when. R162 authorises the formatter, and product-concept §8.1's own example is
  // "Sunday-morning loops on Sunday morning", so the vocabulary is quoted, not invented.
  //
  // CAPPED AT `RULINGS R39`'s GRANULARITY — THE DAY AND A PART OF IT, NEVER A CLOCK TIME. R39
  // stopped the app rendering "11:59 PM" because it "implies a precision the user did not state",
  // and a rhythm window is that case again: Sorting PROPOSES these conditions, so the user often did
  // not author the hours at all. There is no slot here that could hold an hour.
  //
  // AND NOTHING HERE IS A CLAIM ABOUT THE FUTURE. These describe the CONDITION — "Sunday mornings"
  // — never a next occurrence. "Due Sunday" would make a missed Sunday into something that had been
  // missed, and no object in this model can be late.
  //
  // THE WEEKDAY IS NOT IN THIS FILE. `{day}` is filled from the platform's own locale
  // (`formatRhythm` in @carn/ui), for the reason `formatDate` gives: a date is data, not copy, and
  // the user's locale already knows how they read one. What IS copy is the grammar around it, which
  // is why the plural "s" lives in these templates.
  // -------------------------------------------------------------------------
  /**
   * A weekday and a part of it. The R162 case: "Sunday mornings".
   *
   * The warm register pluralises, because the condition is a habit rather than an appointment. The
   * literal register says "Every" instead, which states the same fact without leaning on plurality
   * to carry the recurrence.
   */
  'loop.repeats.dayAndPart': {
    warm: '{day} {part}s',
    literal: 'Every {day} {part}',
    slots: ['day', 'part'],
  },
  /** A weekday with no usable hours — an all-day Sunday condition, or one straddling two parts. */
  'loop.repeats.dayOnly': { warm: '{day}s', literal: 'Every {day}', slots: ['day'] },
  /** A part of the day with no weekday — the daily chore. Never "every day": that is a streak word. */
  'loop.repeats.partOnly': { warm: 'Every {part}', literal: 'Every {part}', slots: ['part'] },
  /**
   * The four parts of the day, as NOUNS.
   *
   * Distinct from `time.band.*`, which are the ADVERBIAL forms ("in the evening") because the only
   * sentence that uses those puts them where an adverb goes ("Caught in the evening"). These go in
   * a noun's slot — "Sunday mornings" — and jamming an adverbial phrase into it would be the class
   * of grammatical error RULINGS R24 was written about. Same four bands, from `TIME_OF_DAY_BANDS`
   * in @carn/core, so no second vocabulary is minted.
   *
   * Identical in both registers, for the reason `time.band.*` are: there is no metaphor in them and
   * no wryness to be had, and a part of the day the user recognises is the only unit R39's cap
   * leaves available.
   */
  'loop.repeats.part.morning': { warm: 'morning', literal: 'morning' },
  'loop.repeats.part.afternoon': { warm: 'afternoon', literal: 'afternoon' },
  'loop.repeats.part.evening': { warm: 'evening', literal: 'evening' },
  'loop.repeats.part.night': { warm: 'night', literal: 'night' },
  /**
   * THE RHYTHM IS RESTING (`RULINGS R161`).
   *
   * R161 ruled that settling a repeating task SUSPENDS its rhythm rather than terminating the task:
   * "rest suspends the rhythm and the task returns to composting, recoverable by rewarm". Before
   * this line the loop detail surface said NOTHING about the rhythm of a settled repeating loop,
   * which reads as "this does not repeat" — the termination R161 says settling is not.
   *
   * IT STATES THE WAY BACK, because a suspension the user cannot see the end of is a termination
   * with better manners. Nothing here is a warning and nothing escalates: rest is legitimate for
   * ever (product-concept §8.3, objection 2), so this is a fact, not a nudge.
   *
   * The literal register carries no metaphor: "resting" is the metaphor layer and `settled` is on
   * the linter's own list, so the literal form names the mechanism instead.
   */
  'loop.repeats.resting': {
    warm: 'Resting. Bring it back and it comes round again.',
    literal: 'The repeating time condition is paused. Reopening the task resumes it.',
  },
  // -------------------------------------------------------------------------
  // SAYING SO YOURSELF — the manual door to a repeating task (`RULINGS R204`)
  //
  // Until R204 the ONLY way a task became repeating was to accept the Sorting proposal, which
  // fires on the fourth capture of a job already finished three times (`RULINGS R128` Part 1).
  // A job whose name drifts between captures never triggers it, and capture may not ask
  // (product-concept §7.1) — so a user who simply knew their chore repeats had no way to say so.
  // R204 authorises the control; these are its four strings.
  //
  // THE PAIR STATES THE CONSEQUENCE THE PRODUCT CAN ACTUALLY KEEP, and no more. Not "it will
  // appear every morning": a bare rhythm condition carries no day and no hour (`R128` Part 2),
  // and `evaluateSurfacing`'s arrival on a device is not something a button can promise. What is
  // guaranteed by shipped code is the disposition — marking it done places a stone and leaves the
  // task open — which is the same sentence `sorting.rhythmKeepsGoing` already makes at the moment
  // of confirmation, deliberately, so the two doors say one thing.
  //
  // AND NOTHING HERE IS A JUDGEMENT IN EITHER DIRECTION. Turning a rhythm off is not a lapse, a
  // failure or a giving-up — "it just means the task intervals have come to a conclusion"
  // (R204's own framing) — so the off label names the act and stops, the way `loop.release`
  // does. No "are you sure", no consequence dressed as a warning.
  // -------------------------------------------------------------------------
  'loop.repeats.mark': { warm: 'Make it come round', literal: 'Set as a repeating task' },
  /**
   * `sorting.rhythmKeepsGoing`'s second sentence, said at the other door. Deliberately the same
   * promise in the same shape: the two paths to one condition must not describe it differently.
   */
  'loop.repeats.mark.hint': {
    warm: 'Mark it done and it stays, ready for next time.',
    literal: 'Marking it done will not end the task.',
  },
  /**
   * Said out loud after the press, for `loop.renamed`'s reason: a control that wrote something
   * and said nothing leaves the user to infer the state from a re-render.
   */
  'loop.repeats.marked': { warm: 'It comes round now.', literal: 'This task now repeats.' },
  'loop.repeats.stop': { warm: 'Stop it coming round', literal: 'Set as a one-off task' },
  /**
   * THE SECOND SENTENCE IS THE ONE THAT MATTERS (product-concept §5, §9: "nothing is ever taken
   * away by time"; design-direction §6 objection 1). Stopping a rhythm removes a CONDITION and
   * touches nothing in the log: every session already recorded stays exactly where it is, because
   * history is append-only and a cairn is a pile that can only be added to. A user who thinks the
   * off switch might erase their record will not press it, and the record is the thing this
   * product is built to keep.
   */
  'loop.repeats.stop.hint': {
    warm: 'It stops coming round. Everything you have done stays.',
    literal: 'Removes the repeating time condition. The completed sessions are kept.',
  },
  'loop.repeats.stopped': {
    warm: 'It will not come round again.',
    literal: 'This task no longer repeats.',
  },

  'loop.rest': { warm: 'Let it rest', literal: 'Decide later' },
  // Deferral is legitimate and may repeat forever (product-concept §8.3, objection 2:
  // "some users will hoard and never release, and that is fine"), so the hint promises
  // nothing about a next time and applies no pressure toward one.
  'loop.rest.hint': {
    warm: 'No decision today. It can come round again.',
    literal: 'Defers the decision. It may be offered again later.',
  },
  // -------------------------------------------------------------------------
  // Removal (docs/DEVIATIONS.md D32)
  //
  // THE COPY HERE IS DOING THE MOST IMPORTANT PART OF THIS FEATURE'S JOB: keeping removal
  // distinct from the two endings beside it. `loop.close` is "you finished it", `loop.release`
  // is "you decided to stop", and both are outcomes the product celebrates. Removal is neither.
  // If these strings blur into the release strings, a user clearing a mis-heard voice note will
  // reach for release instead, and the archive will record a decision they never made.
  //
  // So every line below states the same two facts and no others: it leaves, and you can undo it.
  // Nothing says "gone forever" (untrue — the row survives and the undo restores it), nothing
  // warns, nothing asks why, and nothing thanks the user for tidying up.
  // -------------------------------------------------------------------------
  'loop.remove': { warm: 'Remove it', literal: 'Remove task' },
  'loop.remove.hint': {
    warm: 'Takes it out of the app. You can undo this.',
    literal: 'Removes the task from every screen. You can undo this.',
  },
  /** The question the confirmation asks. Removal is one press away, never one press. */
  'loop.remove.question': { warm: 'Remove this?', literal: 'Remove this task?' },
  /**
   * What removal is NOT, said plainly, because the two controls beside it are the endings and a
   * user needs to know this is not a third one (product-concept §6.4, §5).
   *
   * AND HOW LONG THE UNDO LASTS, WHICH THIS COULD NOT SAY BEFORE (docs/RULINGS.md R135). D32 made
   * removal a tombstone with a total undo and no end date, so the honest thing to say about the
   * window was nothing. R135 gives it one — thirty days, the same number as the recovery journal,
   * after which the loop's content and its media files are purged from the device — and requires
   * this string to say so, "because there is finally something true to say". `loop.remove.hint`'s
   * "takes it out of the app" stays honest and is no longer the whole story.
   *
   * THE NUMBER IS A SLOT, not a word, so it is read from `REMOVED_LOOP_PURGE_DAYS` (@carn/core) at
   * the call site rather than typed here twice in two registers. `privacy.keeping.recovery` does
   * the same thing with the same number for the same reason.
   */
  'loop.remove.explain': {
    warm: 'It leaves every screen. It does not count as done or let go. You can put it back for {days} days.',
    literal:
      'The task leaves every screen. It is not recorded as complete or released. You can restore it for {days} days.',
    slots: ['days'],
  },
  'loop.remove.confirm': { warm: 'Yes, remove it', literal: 'Remove' },
  'loop.remove.cancel': { warm: 'Keep it', literal: 'Keep the task' },
  /** The acknowledgement. A statement of fact, not a moment — see `discardLoop` in engine.ts. */
  'loop.removed': { warm: 'Removed: {title}', literal: 'Removed: {title}', slots: ['title'] },
  'loop.remove.undo': { warm: 'Put it back', literal: 'Undo removal' },
  'loop.remove.undoHint': {
    warm: 'Brings it back with everything on it.',
    literal: 'Restores the task and its history.',
  },
  'loop.remove.undone': {
    warm: 'Back where it is: {title}',
    literal: 'Restored: {title}',
    slots: ['title'],
  },
  /** Dismisses the acknowledgement. It has no timer: an undo that expires unseen is not one. */
  'loop.remove.dismiss': { warm: 'That is all', literal: 'Dismiss' },

  /**
   * The heading over the quick-actions menu a long press opens on a card (docs/DEVIATIONS.md D43).
   *
   * IT USED TO HEAD THE LOOP DETAIL SURFACE'S ACTION SET TOO, and that was one key serving two
   * different acts. "Actions" is right for a menu — a menu IS a list of actions — and wrong for a
   * passage of a surface, where it names an affordance category rather than anything about the work.
   * The detail surface's acts now sit directly under the capsule with no heading at all (the control
   * that resumes work needs no label above it saying that controls follow), and its endings passage
   * has its own words: `loop.endings`.
   */
  'loop.actions': { warm: 'What next?', literal: 'Actions' },
  /**
   * The heading over the acts that END a loop, on its own surface (product-concept §6.4, §5, §10).
   *
   * WHY THE PASSAGE IS NAMED AT ALL. It is the group whose members are near-synonyms — close, good
   * enough, let go, hand over — and design-direction §3's redundancy rule leaves colour unavailable
   * to say "these four are the same kind of act, and it is the kind that stops". A heading is the
   * cheapest channel that survives level 0 unchanged. `loop.tend` is deliberately OUTSIDE it and
   * above it (RULINGS R25): "Done for now" records the work and leaves the loop open, so it is not
   * one of these, and the heading is now part of what says so.
   *
   * NEITHER REGISTER WARNS. §3 objection 3 fixes prominence as the only lever and there is no red in
   * the token system to reach for; "ends" is a statement about what the acts do, not a caution about
   * doing them. Release is one of them and is a success (§5's special rule), so a heading that read
   * as a danger zone would break the equal-standing law on the one channel a screenshot shows first.
   */
  'loop.endings': { warm: 'How this one ends', literal: 'Ending this task' },
  /**
   * WHAT THE HOLD DOES, for the rotor and for switch control (RULINGS R14: "a hold is never a
   * control's only input", so the gesture has to be named for people who cannot perform it).
   *
   * It names the MENU, not any act inside it, because holding a card writes nothing at all — the
   * menu's own answers are where anything happens.
   */
  'loop.actions.open': { warm: 'Show what you can do', literal: 'Show actions' },
  /** Said once at the top of the menu, because a menu over someone's own work should not look
   *  like a thing that has already changed it. */
  'loop.actions.explain': {
    warm: 'Nothing changes until you pick one.',
    literal: 'No change is made until you choose an action.',
  },
  /** Walking away from the menu. Writes nothing, records nothing. */
  'loop.actions.dismiss': { warm: 'Nothing right now', literal: 'Close the menu' },
  /**
   * Closing the menu after it has said what an act DID — the warm cap's "that one is cooling now"
   * acknowledgement, which the detail surface can leave inline on the page and a panel cannot.
   * Deliberately not `loop.actions.dismiss`: nothing was declined here, something happened.
   */
  'loop.actions.done': { warm: 'That is all', literal: 'Dismiss' },
  /** Going to the loop's own surface, from the menu. The tap on a card does the same thing —
   *  this is the labelled route to it, for a menu that has to be complete on its own. */
  'loop.open': { warm: 'Open it', literal: 'Open the task' },
  /** A loop id that no longer resolves — a stale deep link, never a user's fault. */
  'loop.notFound': { warm: 'That one is not here.', literal: 'Task not found.' },
  'loop.origin': {
    warm: 'Caught',
    literal: 'Captured',
    allow: ['tense'],
    why: 'The channel a capture already happened through, stated as a fact (design-direction §6) — the same acknowledgement shape "Caught."/"Captured." uses elsewhere (ADA review C5).',
  },
  // Origin is preserved "because origin is often the strongest memory hook for ND recall"
  // (product-concept §5), so the loop detail surface states the channel in words. One key
  // per `CaptureChannel` in @carn/core — a channel with no word would be an icon to guess at
  // (design-direction §6).
  'origin.voice': { warm: 'Spoken aloud', literal: 'Voice capture' },
  'origin.photo': { warm: 'A photo', literal: 'Photo capture' },
  'origin.text': { warm: 'Typed in', literal: 'Typed' },
  'origin.shareSheet': { warm: 'Shared from another app', literal: 'Share sheet' },
  'origin.interrupt': { warm: 'The interrupt button', literal: 'The interrupt button' },
  'origin.walkaround': { warm: 'A walkaround', literal: 'Walkaround mode' },
  'origin.tossedToMe': {
    warm: 'Tossed to you',
    literal: 'Transferred to you',
    allow: ['tense'],
    why: 'Names how this loop arrived — a fact about a past toss, the same shape as `share.tossFrom` (ADA review C5).',
  },
  'origin.askReply': { warm: 'An answer to your ask', literal: 'A reply to your request' },
  // "Session history" was BOTH a table name and untrue. `loop.sessionCount` directly below it
  // already spent a docblock fixing exactly this claim — a chore tended four hundred times must not
  // render as four hundred work sessions, because a two-second tap is not a session — and left the
  // heading above it saying "Session history". These words are true of the whole mixed pile.
  'loop.history': { warm: 'Cairn', literal: 'Work recorded on this task' },
  /**
   * THE PILE IS MIXED, AND THESE WORDS HAVE TO BE TRUE OF ALL OF IT.
   *
   * `Cairn.tsx` renders this from `sessionCount(loop)`, which counts every stone including
   * `endedBy: 'tended'` — and a tend is a two-second tap with `durationMs: 0` (`SessionStone`
   * in @carn/core; `RULINGS R124` item 2 puts the stone here deliberately, because for a loop
   * that never ends the pile is the only record that the work happened at all). So a chore
   * tended four hundred times rendered as "400 work sessions recorded — every session you gave
   * it", which is a fabricated work history, and the more damaging half of one: the claim was
   * about the user's own effort. `report.ts` names that exact failure as the thing to avoid.
   *
   * THE COUNT IS RIGHT AND DOES NOT CHANGE — only the noun did. Excluding tends here would
   * break `lastTendedAt` and `onTend`'s refusal signal, which both depend on the stone being
   * placed unconditionally. So the string is now true of a pile holding both a forty-minute
   * session and a two-second tap: "turned up to it" and "worked on or marked done" are true of
   * a park, an interrupt, a closure and a tend alike, and neither claims a duration.
   *
   * "every session you gave it" also had to go on its own merits. Acknowledgement attaches to
   * the act, never to the person (design-direction §1), and the user's *generosity* is a
   * property of the person. The subject is now the pile.
   *
   * WHAT THE LINE IS STILL FOR is unchanged: design-direction §6 makes the cairn "partial
   * effort made permanently visible", the anti-"it doesn't count unless finished" device. The
   * warm register still means *you came back to this, and here is the proof* — it just no longer
   * says how long you stayed.
   *
   * NOTE THE DELIBERATE DIVERGENCE FROM `reflection.cairn.count`, which keeps "work sessions".
   * That is the aggregate, and its fold in `report.ts` EXCLUDES tends, so "work sessions" is
   * exactly what it holds. Two different numbers with two different definitions get two
   * different words, on purpose; giving them one word is how this defect happened.
   */
  'loop.sessionCount': {
    warm: '{count} stones — one for every time you turned up to it',
    literal: '{count} times worked on or marked done',
    oneWarm: '1 stone — the first time you turned up to it',
    oneLiteral: '1 time worked on or marked done',
    slots: ['count'],
  },
  /** The same control at zero, so it must name the same unit the first arrival will be. */
  'loop.cairnEmpty': {
    warm: 'No stones yet. The first one goes on when you get to it.',
    literal: 'Nothing recorded on this task yet.',
  },
  // The streak/cairn distinction, said out loud (design-direction §6, objection 1):
  // purely accumulative, absence costs nothing.
  //
  // "Sessions are kept" became "Records are kept" for the reason above: this renders in the
  // Cairn's own accessible name (per-loop, tends included) AND on the reflection surface
  // (aggregate, tends excluded), so the one noun it uses has to be true of both piles. The
  // promise itself — nothing is removed — was always true of every stone regardless of kind,
  // and the warm form was already unit-neutral.
  'loop.cairnNothingLost': {
    warm: 'Stones stay. Time away costs nothing.',
    literal: 'Records are kept. Time away removes nothing.',
  },
  /**
   * The capsule log, rendered (critique 4.1).
   *
   * `Loop.capsules` is append-only and every renderer read only the newest entry, so
   * "the last three times you put this down" was write-only data. On screen a Loop was a
   * title, a next step, an attachment and a modified date — a Todoist task with a label.
   * This heading introduces the earlier bookmarks on the loop detail surface.
   */
  'capsule.previous': {
    warm: 'Where you left it before',
    literal: 'Earlier saved context',
    allow: ['tense'],
    why: 'Names past states of the work. A capsule log is a record of what already happened (product-concept §6.1).',
  },

  // -------------------------------------------------------------------------
  // The Interrupt Button (product-concept §6.2) — agency, not failure
  // -------------------------------------------------------------------------
  // Past tense on purpose: the button names the thing that just happened TO the user and
  // hands them the agency to act on it (product-concept §6.2 — "pressing the button is
  // agency"). An imperative here ("Park it") would make it a chore instead.
  'interrupt.button': {
    warm: 'Life happened',
    literal: 'Interrupt',
    allow: ['tense'],
    why: 'Names the interrupting event, which is by definition already past. product-concept §6.2.',
  },
  'interrupt.hint': {
    warm: 'One tap parks what you are on. Hold to also catch what interrupted you.',
    literal: 'Tap to pause the current task. Hold to also capture the interruption.',
  },
  'interrupt.parked': {
    warm: '{title} is parked. It kept your place.',
    literal: '{title} paused. Context saved.',
    slots: ['title'],
    allow: ['tense'],
    why: 'The model sentence design-direction §1 itself quotes for this exception ("It kept your place") — an acknowledgement of a park that just happened (ADA review C5 adds \'parked\' to the checked verb list).',
  },
  'interrupt.captureInterrupter': {
    warm: 'What happened?',
    literal: 'Describe the interruption',
    allow: ['tense'],
    why: 'Asks about the interruption that just occurred. product-concept §6.2.',
  },
  'interrupt.nothingActive': {
    warm: 'Nothing running to park. Catch the new thing instead?',
    literal: 'No task in progress. Capture a new task?',
  },
  /**
   * THE SECOND GESTURE, SAID UNDER THE FINGER THAT IS ALREADY MAKING IT.
   *
   * product-concept §6.2 gives the control two gestures — "one tap says: life just happened",
   * and "hold the button, speak three words" — and the second was written down nowhere a
   * sighted user would meet it before performing it. `interrupt.hint` is the
   * `accessibilityHint`, which VoiceOver reads and a screen never shows.
   *
   * It replaces the named target on the plate for as long as a finger rests there and is gone
   * the instant it lifts (`InterruptButton`), so a tap never sees it: the words arrive only for
   * someone who is already holding, which is the only moment they are useful. Not a
   * substitute for the rotor action of the same name — that is how a hold is reachable without
   * holding (RULINGS R14), and it is unchanged.
   */
  'interrupt.holdHint': {
    warm: 'Keep holding to say what happened',
    literal: 'Keep holding to describe the interruption',
    allow: ['tense'],
    why: 'Refers to the interruption that has just occurred, like interrupt.captureInterrupter.',
  },
  /**
   * Tier 1, reachable at last (critique 2.3).
   *
   * `capsuleTier` returns 1 only when the capsule carries `interruptedBy`, and no UI path
   * ever passed it — so a named tier of product-concept §6.1's tiering system existed in the
   * model, in the renderer and in the fixture, and was not producible by the product. §6.2
   * describes the gesture exactly: "hold the button, speak three words". These are the words
   * around the three words.
   *
   * The park has ALREADY happened by the time this field appears — it is never a gate in
   * front of it. Tier 0 is guaranteed with zero input (§6.1) and nothing here can take that
   * away; skipping costs the user nothing but the context.
   */
  'interrupt.interrupterPlaceholder': {
    warm: 'Three words is plenty',
    literal: 'What interrupted the task',
  },
  'interrupt.interrupterSave': { warm: 'Catch that too', literal: 'Save the interruption' },
  'interrupt.interrupterSkip': { warm: 'Not now', literal: 'Not now' },
  'interrupt.interrupterSaved': {
    warm: 'Caught, and noted on what you put down.',
    literal: 'Saved as a new task, and recorded on the paused one.',
    allow: ['tense'],
    why: 'Acknowledges that the interrupting thing was just caught and saved as its own loop — a fact about what just happened (ADA review C5).',
  },
  /** Offered on the acknowledgement, so the second gesture survives a plain tap. */
  'interrupt.addInterrupter': {
    warm: 'Say what happened',
    literal: 'Add the interruption',
    allow: ['tense'],
    why: 'Refers to the interruption that has just occurred, like interrupt.captureInterrupter.',
  },
  /**
   * THE THREE WORDS, SPOKEN — §6.2's own sentence is "hold the button, **speak** three words".
   *
   * The panel's field is the silent equivalent §7.2 objection 3 requires and it stays; this is
   * the channel §7.3's reference user (gloves, rain, a chicken under one arm) can actually
   * reach at the moment the feature exists for. Same control, same machine, this surface's
   * words — the rule `loop.addVoiceNote` already follows, because only the destination differs.
   */
  'interrupt.speakInterrupter': {
    warm: 'Hold and say what happened',
    literal: 'Hold to record the interruption',
    allow: ['tense'],
    why: 'Names the interruption that has just occurred, like interrupt.captureInterrupter.',
  },
  'interrupt.speakInterrupter.hint': {
    warm: 'Hold, speak, let go. It becomes a loop of its own.',
    literal: 'Hold the button and speak. The recording saves when you release.',
  },
  'interrupt.speakInterrupter.unavailable': {
    warm: 'No microphone here. Type it instead.',
    literal: 'Speech recognition is unavailable. Use the text field.',
  },

  // -------------------------------------------------------------------------
  // Capture (product-concept §7) — zero decisions at capture time
  // -------------------------------------------------------------------------
  // The screen is named for what it is; `capture.save` below names the ACT. They used to be
  // the same three characters in the warm register, so the page heading and the primary
  // button 1,200px below it both read "Catch it" — the first thing an audience
  // design-direction §1 objection 2 describes will notice. `lintCollisions` now checks this
  // pair, so they cannot converge again.
  'capture.title': { warm: 'Catch', literal: 'Capture' },
  'capture.placeholder': { warm: 'One line is plenty', literal: 'Describe the task' },
  /** The field's accessible name. The placeholder is a hint, never a label. */
  'capture.fieldLabel': { warm: 'What is it?', literal: 'Task description' },
  'capture.fieldHint': {
    warm: 'One line is plenty. Press enter to save.',
    literal: 'Enter a short description. Press enter to save.',
  },
  /** The save control. `capture.title` names the screen; this names the act. */
  'capture.save': { warm: 'Catch it', literal: 'Save task' },
  'capture.saveHint': {
    warm: 'Saves it here, now. Nothing else to decide.',
    literal: 'Saves the task on this device. No other input is needed.',
  },
  'capture.saved': {
    warm: 'Caught.',
    literal: 'Saved.',
    allow: ['tense'],
    why: 'The capture confirmation itself — "Caught."/"Saved." — is the catalogue\'s shortest acknowledgement of an act just done (ADA review C5).',
  },
  // The "Caught." prefix went, because the confirmation already says it on the line above and
  // the app was saying it twice (found in the review screenshots). What is left is the part
  // that is news: the local write is the real write (implementation-brief §2).
  //
  // THE SYNC CLAUSE WAS REMOVED BECAUSE NO CODE PERFORMED IT. This string used to end "It
  // syncs when you have signal." / "It syncs when online." — and `@carn/sync` is not a
  // dependency of `apps/mobile`, there is no drain, and there is no account to sync to.
  // `offline-capture.test.ts` asserts the outbox is deliberately left undrained. So the one
  // reassurance sentence on the capture surface was a statement about a mechanism that does
  // not exist, shown to every user on every capture. What is left is true, and it is still
  // the part that is news to someone standing in a barn with no signal. `test/copy-claims.
  // test.ts` fails if the sync clause comes back while the dependency is still absent.
  'capture.savedOffline': {
    warm: 'It is here now. Nothing is waiting on a signal.',
    literal: 'Saved on this device. Nothing is pending.',
  },
  'capture.voice': { warm: 'Hold and speak', literal: 'Hold to record' },
  'capture.voiceHint': {
    warm: 'Hold and speak. It saves as soon as you stop.',
    literal: 'Hold to record. Saves as soon as you stop.',
  },
  'capture.voiceUnavailable': {
    warm: 'No microphone on this device. Type it instead.',
    literal: 'Voice capture is not available on this device.',
  },
  /**
   * REFUSED IS NOT ABSENT (docs/roadmap/onboarding.md item 6).
   *
   * `capture.voiceUnavailable` is a claim about the DEVICE — "no microphone on this device" —
   * and saying it to someone who has a microphone and declined the permission is a false
   * statement on the surface product-concept §14 risk 1 says this audience reads most carefully.
   * Until this key existed the control simply went quiet and returned to "Hold and speak", which
   * is `RULINGS R72`'s defect exactly: "a control that fails silently is worse than one that says
   * what stopped it".
   *
   * It names the thing that stopped it and where the switch is, and it does not ask, apologise,
   * or suggest the user made a mistake. The other channels are untouched and equally fast, which
   * is product-concept §7.1's rule that voice is a path and never the path.
   */
  /**
   * THE DEMONSTRATION CARD (docs/RULINGS.md R198; docs/DEVIATIONS.md D57).
   *
   * The flagship surface is empty until the user has parked something, which takes six
   * deliberate acts the product never asks for — so a new user's first session can end without
   * them ever seeing a capsule, which is the one thing the product exists to show them. R198
   * answers that with a demonstration rather than a walkthrough.
   *
   * THESE STRINGS ARE RENDERED, NEVER STORED. That is the whole of why the ruling could
   * authorise them: a title written into the repository would be frozen in the register in
   * force on the day it was written, would sync to the user's other device as something they
   * wrote, and could be closed — a demonstration that can be half-finished is a chore the
   * product handed the user. Resolved here at read time, they switch register with everything
   * else and vanish the moment a real loop is warm.
   *
   * THE CONTENT IS STILL THE DOCUMENTS' OWN EXAMPLE — BUT §9.1'S, NOT §6.1'S.
   *
   * The first draft used §6.1's capsule ("the half-disassembled pump... 'bolts are in my jacket
   * pocket, need a 13mm'") and §2's fence. Both are written around Persona A, the smallholder,
   * who is the DESIGN ANCHOR for capture and resumption (§3) but is not the median reader of
   * this card — and this card is the first thing a stranger sees. A demonstration that says
   * "fix the fence in the top field" tells most people the product is not for them, at the exact
   * moment it is trying to say the opposite.
   *
   * §9.1's own example of a first physical action is domestic and universal: "stand up and put
   * the drill on the kitchen table". So the scenario moves to that one — still the documents'
   * words rather than invented product, and it now serves Persona B (the knowledge worker at
   * home) as readily as Persona A. It stays PHYSICAL because §9.1 requires the next action to be
   * bodily and concrete, and because a physical half-done thing is what makes a capsule legible
   * at a glance.
   */
  'home.demo.label': {
    warm: 'An example, so the shape of it is visible before you have one.',
    literal: 'Example only. This is not one of your tasks.',
  },
  'home.demo.title': {
    warm: 'Put up the shelf in the hall',
    literal: 'Put up the shelf in the hall',
  },
  'home.demo.nextAction': {
    warm: 'Put the drill on the kitchen table',
    literal: 'Put the drill on the kitchen table',
  },
  'home.demo.place': { warm: 'the hall', literal: 'the hall' },
  'capture.voiceRefused': {
    warm: 'The microphone is off for Carn. Your phone\u2019s settings can turn it back on.',
    literal: 'Microphone access is denied. Enable it in the system settings for Carn.',
  },
  /**
   * A voice capture's title before the transcript exists. Capture never blocks on
   * transcription (implementation-brief §6), so the loop is saved with this and the
   * transcript arrives afterwards as a PROPOSAL the user confirms.
   */
  'capture.voiceTitle': {
    warm: 'A voice line, not written down yet',
    literal: 'Voice capture, no transcript yet',
  },
  'capture.photo': { warm: 'Photo', literal: 'Photo' },
  /** The image IS the task (product-concept §7.1), so this is its title until named. */
  'capture.photoTitle': {
    warm: 'A photo, not named yet',
    literal: 'Photo capture, no description yet',
  },
  'capture.photoHint': {
    warm: 'The photo is the thing. Name it later if you want to.',
    literal: 'The image is the task. A description can be added later.',
  },
  /**
   * What a share-sheet capture is called when it arrived with no words at all — an image sent
   * straight from Photos, or a share whose text this build could not read.
   *
   * THE THIRD MEMBER OF `untitledKeyFor`'s FAMILY, AND IT EXISTS BECAUSE THE OTHER TWO WOULD LIE
   * HERE. Before the share-sheet channel was built (docs/DEVIATIONS.md DP9) that function had two
   * branches — photo, and everything else resolving to `capture.voiceTitle` — and its own comment
   * justified the fallback by asserting that "`text`, `share-sheet`, `tossed-to-me` and `ask-reply`
   * all carry words by construction". A photo shared from another app falsifies that for exactly
   * one channel: it carries no words, and it was never spoken, so "A voice line, not written down
   * yet" would be a sentence about something that never happened.
   *
   * IT NAMES THE GESTURE, NOT THE CONTENT, because the gesture is what the user will recognise.
   * product-concept §5: origin is preserved "because origin is often the strongest memory hook for
   * ND recall" — six weeks later, "something you sent over" is the hook, and the app has no idea
   * what the picture is of.
   */
  'capture.sharedTitle': {
    warm: 'Sent over from somewhere else, not named yet',
    literal: 'Shared item, no description yet',
  },
  /**
   * THE TWO LINES THE iOS SHARE EXTENSION DRAWS, and the only words that surface has.
   *
   * They travel to native code inside the widget payload's string table
   * (`SHARE_EXTENSION_KEYS` in ./widgetStrings.ts) for the same reason every widget word does:
   * `docs/specs/widgets-architecture.md` §3 law 1 — native code looks a key up and composes
   * nothing. An extension is a separate process with its own bundle and no access to `useCopy()`,
   * so the register the user chose reaches it the only way it can.
   *
   * `capture.sharedLanded` SAYS WHERE THE THING WENT, because that is the part that is news. The
   * user is in Messages, the sheet is about to close, and they will not see Carn again for hours:
   * "Caught." alone leaves them wondering whether to check. It is the same job
   * `capture.savedOffline` does on the in-app surface, and it is worded to the same rule — state
   * the mechanism, promise nothing about a network (implementation-brief §2: the local write is
   * the real write).
   *
   * `capture.sharedFailed` IS THE HONEST FAILURE, and it exists because the alternative is the
   * worst shape this product has. `capture.notSaved`'s own note states the rule: "Capture must
   * never half-succeed… and the other half of never-half-succeeding is never-silently"
   * (docs/RULINGS.md R111). A share extension that could not reach the container and dismissed
   * with a tick would be a capture the user believes in and does not have — for the audience whose
   * second-ranked pain is capturing before forgetting. It names the recovery, and the recovery is
   * the app itself, which is always there.
   *
   * No exclamation marks, no praise, nothing about the person (design-direction §1) — the sentence
   * is about the item.
   */
  'capture.sharedLanded': {
    warm: 'It is in Carn. It will be waiting when you open it.',
    literal: 'Saved to Carn on this device. It appears in the inbox at next launch.',
  },
  'capture.sharedFailed': {
    warm: 'That is not in Carn. Open the app and catch it there.',
    literal: 'The item is not saved. Open Carn and capture it directly.',
  },
  'capture.photoUnavailable': {
    warm: 'No camera on this device. Type it or speak it instead.',
    literal: 'Photo capture is not available on this device.',
  },
  'capture.listening': { warm: 'Listening', literal: 'Recording' },
  'capture.transcribing': { warm: 'Writing it down', literal: 'Transcribing' },
  'capture.transcriptFailed': {
    warm: 'Could not make out the words. The recording is safe on the loop.',
    literal: 'No transcript available. The audio is attached to the task.',
  },
  /** The transcript is an inference, so it arrives as a proposal (product-concept §7.2). */
  'capture.transcriptProposed': {
    warm: 'Heard this. Use it as the name?',
    literal: 'Transcript ready. Use it as the description?',
  },
  'capture.audioKept': {
    warm: 'The recording stays on this one either way.',
    literal: 'The audio stays attached either way.',
  },
  /**
   * The three `MediaBreach` strings (RULINGS R21), keyed `media.*` rather than
   * `capture.media.*` because a breach is not a capture-surface event: a voice note imported
   * onto an existing loop, or a photo added from the loop's own surface, reaches the same
   * three caps in `@carn/core`'s `capsuleMediaBreach`.
   *
   * EVERY ONE OF THEM SAYS THE LOOP IS SAVED FIRST. They used to open with the file's problem
   * ("Longer than fifteen seconds, so…"), which reads as a failed capture to anyone who does
   * not already know that the write happened before the attachment was attempted. A breach is
   * never a refusal to capture (product-concept §6.1, §7.1's zero-decision guarantee), and the
   * only sentence that makes that true for the reader is the one that says so.
   *
   * `media.voiceTooLong` now describes an IMPORTED file: R4 stops the recorder at fifteen
   * seconds rather than rejecting the result afterwards. `media.photoTooLarge` is a last
   * resort, because R5 downscales rather than refusing.
   */
  'media.voiceTooLong': {
    warm: 'The loop is saved. That recording runs past fifteen seconds, so it is not attached.',
    literal: 'The task is saved. The audio is longer than the fifteen-second limit, so it is not attached.',
  },
  'media.voiceTooLarge': {
    warm: 'The loop is saved. That recording is too big to keep, so it is not attached.',
    literal: 'The task is saved. The audio is over the size limit, so it is not attached.',
  },
  'media.photoTooLarge': {
    warm: 'The loop is saved. That photo is too big to keep, so it is not attached.',
    literal: 'The task is saved. The image is over the size limit, so it is not attached.',
  },
  'capture.deadlineToggle': {
    warm: 'Does the world care when this happens, or just you?',
    literal: 'Is there a fixed external deadline?',
  },
  'capture.deadline.world': { warm: 'The world cares', literal: 'Fixed deadline' },
  'capture.deadline.me': { warm: 'Just you', literal: 'Intended date only' },
  /** The §5 toggle is a refinement AFTER the save, never a gate before it. */
  'capture.deadlineOptional': {
    warm: 'Answer it or leave it. It is saved either way.',
    literal: 'Optional. The task is already saved.',
  },
  'capture.deadlineWhen': { warm: 'Roughly when?', literal: 'Approximately when?' },
  'capture.deadlineWhen.today': { warm: 'Today', literal: 'Today' },
  'capture.deadlineWhen.tomorrow': { warm: 'Tomorrow', literal: 'Tomorrow' },
  'capture.deadlineWhen.week': { warm: 'Within the week', literal: 'Within seven days' },
  'capture.deadlineSet': {
    warm: 'The world cares: {when}.',
    literal: 'Fixed deadline: {when}.',
    slots: ['when'],
  },
  /**
   * "Within the week", stated as a DAY (RULINGS R39).
   *
   * The capture surface used to render the stored instant through `formatDate`, which produced
   * "Wed, Aug 6, 11:59 PM" — an instant the user never said, at a precision they never chose,
   * on the one decision product-concept §5 authorises at capture. R39: "stop rendering the
   * instant... the day, never '11:59 PM', which is honest and reads oddly and implies a
   * precision the user did not state." Today and tomorrow name themselves
   * (`capture.deadlineWhen.today` / `.tomorrow`); the third choice lands on a weekday, and a
   * weekday name is data the platform already knows how to render (see `formatDate`'s own note:
   * "a date is data, not copy").
   */
  'capture.deadlineSetDay': {
    warm: 'The world cares: by {when}.',
    literal: 'Fixed deadline: by {when}.',
    slots: ['when'],
  },
  'capture.deadlineSoft': {
    warm: 'Just you, then. It can slide.',
    literal: 'No fixed deadline recorded.',
  },
  // Development-only instrumentation of the two-second rule (implementation-brief §9,
  // §11: "capture-to-saved is under two seconds offline"). Shown in dev so a breach is
  // visible in review rather than left to rot; never rendered in a release build.
  //
  // "SAVED IN {write} MS" WAS NOT MEASURING A SAVE. The number was the interval around a
  // `Map.set` plus the synchronous part of firing an unawaited promise — 0–1ms, every time —
  // while the field it came from was documented as "durable local write". Two numbers now,
  // because there are two facts: the write is ENQUEUED synchronously (which is what makes
  // the surface instant, brief §2) and it reaches disk a moment later. An honest number
  // under budget is worth more than a flattering one.
  'capture.timing': {
    warm: 'Ready in {ready} ms. Queued in {queued} ms.',
    literal: 'Ready in {ready} ms. Queued in {queued} ms.',
    slots: ['ready', 'queued'],
  },
  'capture.timingDurable': {
    warm: 'On the phone’s disk after {durable} ms.',
    literal: 'Written to device storage after {durable} ms.',
    slots: ['durable'],
  },
  /**
   * NOT development-only, and not a reassurance.
   *
   * The write-behind adapter (`src/data/sqlite.ts`) updates an in-memory mirror
   * synchronously and fires the SQL write behind it. When that write does not land, the app
   * used to print `console.error` and leave `capture.savedOffline` — "It is here now." — on
   * screen over the top of it. For a product whose second-ranked user pain is capturing
   * before forgetting, a silent-loss path with an on-screen reassurance over it is the worst
   * possible shape, so the surface says what is true instead.
   */
  'capture.notOnDisk': {
    warm: 'On the screen, not yet on the phone. It is being written again.',
    literal: 'Not yet written to device storage. The write is being retried.',
  },
  /**
   * THE CAPTURE COULD NOT BE MADE AT ALL — a different fact from `capture.notOnDisk`, and
   * docs/RULINGS.md R111 is why both exist.
   *
   * `notOnDisk` is a write that happened and has not landed yet; this is a write that never
   * started, because the outbox entry was refused before the first local write (the privacy
   * invariant, `assertNoRawLocation`, or a storage refusal). R111: "Capture must never
   * half-succeed" — and the other half of never-half-succeeding is never-silently: a capture
   * used to throw out of the save handler, so nothing cleared, nothing was announced, and the
   * inbox count did not move while the loop sat unsyncable on disk.
   *
   * It says the three facts a user in a barn needs and no more: it is not saved, nothing is on
   * the phone (so there is nothing to go and find), and the words they typed are still in the
   * field, so retyping is not the price of the failure.
   */
  'capture.notSaved': {
    warm: 'Not saved. Nothing is on the phone. The words are still in the field.',
    literal: 'Not saved. Nothing is written to storage. The text is still in the field.',
  },
  'capture.timingOverBudget': {
    warm: 'That is over the {budget} ms budget.',
    literal: 'That is over the {budget} ms budget.',
    slots: ['budget'],
  },

  // -------------------------------------------------------------------------
  // The refine sheet (RULINGS R38) — everything the acknowledgement no longer holds
  // -------------------------------------------------------------------------
  // The confirmation used to be a card carrying the §5 date question, the transcript proposal
  // with its two buttons, the media notice and the photo. Measured on a real iPhone 15 with the
  // keyboard up (393×516) the visible part of it was the word "Saved.", the first line of the
  // title, and a second line sliced horizontally through the middle of a word — and the §5
  // toggle's SECOND option was off screen, so the one decision the documents authorise rendered
  // as a choice with one visible option (docs/reviews/capture-critique.md §1.4).
  //
  // So the acknowledgement is one compact row and the rest moved behind this control. Nothing is
  // lost and NOTHING GATES THE SAVE: the loop is already written, the field already has focus
  // back, and a user who never opens this has made a complete, correct capture (product-concept
  // §7.1).
  'capture.refine': { warm: 'Add to this one', literal: 'Add details' },
  'capture.refineHint': {
    warm: 'Opens a date question, and anything else waiting. It is saved either way.',
    literal: 'Opens the deadline question and any pending items. The task is already saved.',
  },
  'capture.refineTitle': { warm: 'Add to this one', literal: 'Task details' },
  /** Leaves the sheet. Nothing in it needs answering, so this is never a cancel. */
  'capture.refineDone': { warm: 'Back to catching', literal: 'Close' },
  'capture.refineDoneHint': {
    warm: 'Goes back to the field. Anything left unanswered stays that way.',
    literal: 'Returns to the capture field. Unanswered items are left as they are.',
  },
  /**
   * A transcript landed while the sheet was shut.
   *
   * The words are an inference and stay one (§7.2), so they wait behind the control rather than
   * renaming anything — but a proposal nobody can see is a proposal nobody confirmed, and the
   * critique's §1.4 finding was precisely that this was pushed off screen. One line on the
   * acknowledgement says where it is.
   */
  'capture.transcriptWaiting': {
    warm: 'Some words came back. They are in the details.',
    literal: 'A transcript is ready. It is in the task details.',
  },
  /**
   * The refine control's OWN LABEL while a transcript is waiting behind it.
   *
   * `capture.transcriptWaiting` above is the hint (and therefore what a screen reader hears); this
   * is what a sighted user sees, and it is on the control rather than under it because a line under
   * it was measured landing across the fold. Same words, one less row.
   */
  'capture.refineWaiting': { warm: 'Words waiting', literal: 'Transcript ready' },

  // -------------------------------------------------------------------------
  // Sorting session (product-concept §7.2) — approve or flick away, never file
  // -------------------------------------------------------------------------
  // The literal register said "Review inbox", which is an instruction to do work, and it sat
  // above a count and a "done" state that congratulated emptiness — the inbox-zero pattern
  // §8.3 exists to refuse. The screen is named for what it holds.
  'sorting.title': { warm: 'Sorting', literal: 'Captured items' },
  // critique 5.4.5: "bin" is deletion language in a product whose §8.3 promise is
  // "nothing is ever thrown away", and it contradicted compost.intro two screens away.
  // The gesture is named as "one" rather than as "a swipe": the shipped surface confirms
  // with one full-size tap per item, because a swipe-away needs a fourth motion moment and
  // design-direction §5's MVP contract is exactly three (settle, rise, place). Recorded in
  // docs/OPEN-QUESTIONS.md. §7.2's requirement is one gesture per item and no filing, which
  // a tap satisfies — and a tap is also the one gesture a glove can hit reliably (§7.3).
  // Shortened to ONE LINE on a 320pt-wide phone, and stripped of "to review". Two reasons, both
  // measured: on an iPhone SE the literal form wrapped to two lines and pushed the suggestion —
  // the thing the accept button below refers to — off the fold; and "captured items to review"
  // repeated the screen title directly above it while framing the pile as work owed.
  'sorting.intro': {
    warm: '{count} to look at. One touch each.',
    literal: '{count} items. One action each.',
    oneWarm: '1 to look at. One touch.',
    oneLiteral: '1 item. One action.',
    slots: ['count'],
  },
  // critique 5.4.8: "A guess, {confidence} sure" was ungrammatical, and product-concept
  // §7.2 makes honest confidence display a TRUST requirement — broken syntax at the
  // trust moment is not a style nit.
  // "A guess — quite confident" is a sentence that argues with itself, and it fired over the
  // user's OWN taught words at VOCABULARY_CONFIDENCE — the app calling the user's private
  // dictionary a guess. The band is still the whole of the claim; the editorial verdict on
  // top of it is gone.
  /**
   * HOW SURE THE APP IS, IN A SENTENCE RATHER THAN A READING (founder review, 2026-08-24;
   * `docs/DEVIATIONS.md` §retrospective-capture RT5).
   *
   * The literal register said "Confidence: Medium" — a machine reporting a score, in the same
   * vocabulary the disclosure above it was renamed out of. §7.2 objection 2 makes the honest band
   * a TRUST instrument ("confidence is shown honestly"), and a trust instrument that reads as
   * telemetry is doing the opposite of its job: it invites the user to calibrate against a scale
   * nobody defined for them rather than to judge the guess in front of them.
   *
   * SO BOTH REGISTERS SAY THE SAME SENTENCE, and the identity is allowed rather than a skipped
   * pass — `lintEntry`'s identical-register check fires only where the warm form reads
   * metaphorically, and "Not very sure of this" is plain in any register. There is nothing for a
   * literal pass to strip.
   *
   * WHAT IS LOST, because a three-step scale had a real virtue: it was scannable and identical
   * across every suggestion, and some literal-minded readers prefer a fixed ladder to a sentence.
   * The founder weighed that and chose the sentence. It is the ND co-design panel's to overturn
   * under design-direction §7, like every other question of this kind.
   */
  'sorting.suggestionConfidence': {
    warm: '{confidence} sure of this',
    literal: '{confidence} sure of this',
    slots: ['confidence'],
  },
  // THE QUESTION WENT BECAUSE THERE WAS NO ANSWER TO IT. "Same thing?" had three controls
  // under it and none of them was a reply: accept applies title/surfacing/listId, and a
  // duplicate-only proposal has none of those, so the primary button changed nothing but
  // `sorted`. There is no merge, no "open the other one", and no way to record "no, they are
  // different". Until those verdicts exist this is a fact the user can use, not a question
  // the app cannot hear the answer to. The remaining work is in docs/OPEN-QUESTIONS.md.
  'sorting.duplicate': {
    warm: '{count} things here look close to this.',
    literal: 'Similar to {count} existing tasks.',
    oneWarm: 'One thing here looks close to this.',
    oneLiteral: 'Similar to 1 existing task.',
    slots: ['count'],
  },
  // "Inbox clear." is only meaningful against a prior state of unclear, and it was ALSO false
  // whenever the user reached it by deferring: `sorting.doneForNow` below is that state, and
  // the two are now different sentences because they are different facts.
  'sorting.done': { warm: 'Nothing waiting.', literal: 'No captured items.' },
  'sorting.doneBody': {
    warm: 'Anything you catch lands here.',
    literal: 'New captures appear here.',
  },
  // Deferring every item used to render `sorting.done` — "Inbox clear." — while the capture
  // tab, two taps away, still said "3 caught and not looked at yet". Two surfaces,
  // contradictory claims, in one session, and the false one was the celebratory one.
  'sorting.doneForNow': {
    warm: 'That is everything for now. {count} are still here.',
    literal: 'No further items in this session. {count} remain.',
    oneWarm: 'That is everything for now. One is still here.',
    oneLiteral: 'Nothing further in this session. 1 remains.',
    slots: ['count'],
  },
  'sorting.skip': { warm: 'Leave it for now', literal: 'Skip' },
  /** The quiet row into Sorting. A place you visit, never a count that nags (§7.2). */
  // Re-voiced: "not looked at yet" / "to review" are statements about something the USER has
  // not done, in fixed navigation, at every count above zero — the same semantic shape as
  // "untouched", which the linter's SHAME_WORDS already carries and which this slipped past
  // as a paraphrase. `home.overCapRow` is the model to follow: it describes the app's own
  // display choice, not the user's omission.
  'sorting.inboxRow': {
    warm: '{count} caught, here when you want them',
    literal: '{count} captured items. Review is optional.',
    oneWarm: '1 caught, here when you want it',
    oneLiteral: '1 captured item. Review is optional.',
    slots: ['count'],
    allow: ['tense'],
    why: 'The count of items already caught, sitting unsorted — a fact about past captures, the same shape as `home.empty.caught.body` (ADA review C5).',
  },
  'sorting.inboxRow.hint': {
    warm: 'Opens sorting. Look when you like.',
    literal: 'Opens the inbox review.',
  },
  'sorting.remaining': {
    warm: '{count} left to look at',
    literal: '{count} items remaining',
    oneWarm: '1 left to look at',
    oneLiteral: '1 item remaining',
    slots: ['count'],
  },
  // `sorting.yourWords` was REMOVED — docs/RULINGS.md R84 build consequence 1, made by R107.
  // "Drop the 'What you captured' label — the title's own prominence already says that." It cost
  // 18pt of label and 8pt of gap at the very top of a card that has to fit its decision above a
  // 320pt phone's fold, and the boundary it marked is now carried by the hairline above the
  // proposal section, the per-block headings R31 requires, and the verdict pair's own words.
  'sorting.proposalHeading': { warm: 'A suggestion', literal: 'Suggestion' },
  // `sorting.noSuggestion` was REMOVED with the block it lived in. In manual mode the card
  // rendered the heading "A suggestion" over the sentence "No suggestion for this one." — the app
  // narrating its own absence, above a control asking the user to reject the thing it had just
  // said did not exist. The suggestion block now does not render at all when there is no
  // proposal, so there is nothing for this string to say.
  // "description" named a field the model does not have, and the warm register beside it already
  // said "name" — see `sorting.fixName`'s docblock for the whole argument.
  'sorting.proposedTitle': { warm: 'Suggested name', literal: 'Suggested task name' },
  // The literal register said "Suggested triggers" directly under "Suggestion", which read as
  // the app saying the same word twice (found in the review screenshots).
  'sorting.proposedSurfacing': {
    warm: 'Where it would turn up',
    literal: 'Where it would appear',
  },
  /**
   * The same suggestion, on a device that cannot tell where you are.
   *
   * A review found that the `place` axis — the only axis this heuristic ever proposes — was
   * evaluated by no code anywhere: the on-device evaluator had zero call sites and the
   * scheduled server job correctly refuses the axis, because geofence evaluation is
   * on-device only (implementation-brief §3, a privacy invariant with force of law). The
   * evaluator is now wired (src/data/hooks/useSurfacing.ts), but the geofence registration
   * it needs is gated on verification spike #4, which needs a device. So when there is no
   * place signal the heading does not promise arrival, and the footnote below says why.
   * Saying "Where it would turn up" over a trigger nothing watches for is the confident
   * wrong claim §7.2 objection 2 says loses this audience at the first offence.
   */
  'sorting.proposedSurfacingRecorded': {
    warm: 'Where this belongs',
    literal: 'Recorded location',
  },
  'sorting.placeNotWatched': {
    warm: 'Nothing on this phone watches for places yet, so it will not turn up on its own.',
    literal: 'Location monitoring is not set up on this device, so it will not appear on its own.',
  },
  /**
   * THE SURFACING BLOCK'S OTHER HEADING: a proposed RHYTHM rather than a proposed place
   * (`RULINGS R124` item 8; product-concept §8.1's rhythm axis, §7.2's "suggested surfacing
   * context").
   *
   * `sorting.proposedSurfacing` asks "Where it would turn up", and a rhythm is not a where. The
   * form follows `sorting.proposedList`'s ("Looks like shopping") because this is the same shape
   * of claim — the app has recognised a KIND of job, not a location — and the verdict pair below
   * it answers exactly that claim.
   *
   * IT DOES NOT SAY "COMES ROUND" IN THE WARM REGISTER, though that is the obvious phrase, because
   * `surfacing.rhythm` renders immediately underneath it and already says "When it comes round".
   * Reviewing the two as one utterance (see `compositions`) is what makes the repetition visible:
   * "Looks like a job that comes round When it comes round" is the app saying the same words twice
   * in two lines, which is the defect `sorting.proposedSurfacing`'s own comment records ("Suggested
   * triggers" directly under "Suggestion", found in the review screenshots).
   */
  'sorting.proposedRhythm': {
    warm: 'Looks like a job that repeats',
    literal: 'Suggested repeating task',
  },
  /**
   * WHAT A PROPOSED RHYTHM ACTUALLY COMMITS TO — and, first, what it does NOT know.
   *
   * "No day or hour is set" is the honest whole of the proposed condition: the heuristic proposes
   * a bare `{ axis: 'rhythm' }` and nothing else, because the interval vocabulary is deferred
   * (`RULINGS R124` item 2) and there is no schedule formatter or copy for one
   * (`docs/OPEN-QUESTIONS.md`, "Recurring work"). A user confirming a trigger deserves to know
   * that no schedule is hiding behind the label, which is the same duty of disclosure
   * `sorting.placeNotWatched` discharges one entry above.
   *
   * THE SECOND SENTENCE IS THE ONLY CONSEQUENCE THIS CAN PROMISE, and it is chosen for that
   * reason. It does NOT say "it will appear each day": on a shipped device
   * `src/data/hooks/useSurfacing.ts` returns an empty result whenever place presence is
   * unavailable, which is every device until verification spike #4, so a daily-arrival promise
   * would be `sorting.placeNotWatched`'s defect rebuilt for a second axis. What IS guaranteed by
   * shipped code is the disposition: a confirmed rhythm is what `isRepeating` reads, and that is
   * what puts `loop.tend` on the loop's own surface — "the absence of a terminal record is the
   * whole feature" (`RULINGS R124` item 1). Marking it done keeps it.
   */
  'sorting.rhythmKeepsGoing': {
    warm: 'No day or hour is set. Mark it done and it stays, ready for next time.',
    literal: 'No day or hour is recorded. Marking it done does not end the task.',
  },
  /**
   * THE SURFACING BLOCK'S FOURTH HEADING: a proposed ENERGY FIT (`RULINGS R165`;
   * product-concept §9.2's third axis, §7.2's "suggested surfacing context").
   *
   * `sorting.proposedSurfacing` asks "Where it would turn up" and a job's size is not a where;
   * `sorting.proposedRhythm` says "Looks like a job that repeats" and this is not a when either.
   * The form follows `sorting.proposedRhythm`'s, which follows `sorting.proposedList`'s, because
   * all three are the same shape of claim — the app has recognised a KIND of job — and the
   * verdict pair below answers exactly that claim.
   *
   * IT DESCRIBES THE JOB, NOT THE DAY AND NOT THE PERSON. "Looks like a small job" is a statement
   * about the captured line; "for when you are low" would be a statement about the user, which
   * design-direction §1 forbids and which the whole energy vocabulary was rewritten once already
   * to avoid (see `energy.foggy` and the note above it on "Capacity: Low").
   *
   * IT DOES NOT SAY "SMALL" IN THE LITERAL REGISTER, though that is the obvious word, because
   * `energy.foggy` renders literally as "Small jobs" on the settings surface and `surfacing.energy`
   * renders literally as "Job-size trigger" immediately underneath this heading. "Suggested small
   * job Job-size trigger" is the app saying the same thing twice in two lines — the defect
   * `sorting.proposedSurfacing`'s own comment records from the review screenshots.
   */
  'sorting.proposedEnergy': {
    warm: 'Looks like a small job',
    literal: 'Suggested job-size condition',
  },
  /**
   * WHAT A PROPOSED ENERGY FIT ACTUALLY COMMITS TO — and, as with the rhythm above it, first what
   * it does NOT depend on.
   *
   * THE DAYS ARE NAMED because the bare axis label does not name them. `surfacing.energy` renders
   * as "How you are" / "Job-size trigger", and a user confirming a trigger is entitled to know
   * which days it fires on before they confirm it — the same duty of disclosure
   * `sorting.placeNotWatched` and `sorting.rhythmKeepsGoing` discharge for the other two axes. The
   * words are `energy.foggy` and `energy.steady`'s own, in each register, so the sentence and the
   * self-report control agree about what they are called.
   *
   * THE SECOND SENTENCE IS §9.2's GRACEFUL DECAY, STATED RATHER THAN IMPLIED. §9.2's objection 2
   * accepts that most people stop logging their energy within a week, and core's
   * `evaluateCondition` answers `unknown` — never `no-match` — for an energy condition with no
   * current self-report, so the loop keeps surfacing. Saying so is what stops a user reading this
   * block as "answer the daily question or lose this task", which would make a feature depend on
   * the self-report existing in the user's head even though nothing in the code does.
   */
  'sorting.energyFits': {
    warm: 'It turns up on foggy and steady days. Set nothing and it still turns up.',
    literal: 'This appears on small-jobs and usual-jobs days. With nothing set, it still appears.',
  },
  /**
   * THE FIFTH BLOCK: THE LINE WAS WRITTEN ABOUT WORK THAT HAD ALREADY STARTED
   * (`docs/DEVIATIONS.md` §retrospective-capture RT1; product-concept §7.2, §2 axiom 1, §6).
   *
   * The form follows `sorting.proposedRhythm`'s and `sorting.proposedEnergy`'s, which follow
   * `sorting.proposedList`'s, because all four are the same shape of claim — the app has
   * recognised a KIND of captured line — and the controls below answer exactly that claim.
   *
   * IT ASKS RATHER THAN ASSERTS, IN BOTH REGISTERS. "Looks like" is the hedge every other
   * proposal heading uses and it is doing more work here than anywhere else: this claim is about
   * the USER'S OWN PAST, and the app has one word of evidence for it. §7.2 objection 2 was
   * accepted on the grounds that "autistic users in particular will lose trust fast at the first
   * wrong guess", and there is no faster way to lose it than to tell someone what they have been
   * doing.
   *
   * IT SAYS NOTHING ABOUT THE PERSON (design-direction §1). "One you already started" is a
   * statement about a task. "You never finished this" would be a statement about the human, and it
   * is the sentence this whole feature has to not be — the product's own anti-thesis is that
   * "nothing may represent unfinished work as failure — not visually, not linguistically, not
   * structurally" (product-concept §2, axiom 3).
   */
  'sorting.proposedUnderway': {
    warm: 'Looks like one you already started',
    literal: 'Suggested: already started',
  },
  /**
   * WHAT ANSWERING THIS ACTUALLY COMMITS TO — the same duty of disclosure `sorting.placeNotWatched`,
   * `sorting.rhythmKeepsGoing` and `sorting.energyFits` discharge for the three surfacing axes.
   *
   * THE SECOND SENTENCE IS THE ONLY CONSEQUENCE THIS CAN PROMISE, and it is the true one:
   * `hasBeenWorked` in `@carn/core` is what `reentryCopy` reads, so recording this changes the
   * task's one primary action from "start" to "pick up" and changes the line where a bookmark
   * would be. Nothing else moves — no date pressure, no position change, no reminder.
   *
   * IT IS AN INSTRUCTION RATHER THAN A QUESTION, and that is the copy law rather than a
   * preference: "Roughly when did you start?" puts the sentence in the past tense, which
   * design-direction §1 reserves for acknowledgement copy. An imperative in the second person
   * carries the same ask in the register the law asks for.
   *
   * IT SAYS "ROUGHLY" ON PURPOSE. The person this exists for is the person who cannot remember
   * which day it was, and a question that only accepts an exact answer is a question they cannot
   * answer. "Not sure" is a complete reply and the controls offer it.
   */
  'sorting.underwayWhen': {
    warm: 'Say roughly when you started. It will offer to pick it up, not to begin it.',
    literal: 'Record roughly when you started. The task then offers to resume, not to start.',
  },
  'sorting.underwayToday': { warm: 'Today', literal: 'Today' },
  'sorting.underwayYesterday': { warm: 'Yesterday', literal: 'Yesterday' },
  'sorting.underwayLastWeek': { warm: 'Last week', literal: 'Last week' },
  'sorting.underwayUnsure': { warm: 'Not sure when', literal: 'Not sure when' },
  'sorting.underwayDayHint': {
    warm: 'Records the day you started. Press another to change it.',
    literal: 'Records the start day. Select another to change it.',
  },
  'sorting.underwayUnsureHint': {
    warm: 'Records that you started it, with no day.',
    literal: 'Records that the task is already started, with no date.',
  },
  /**
   * THE ANSWER, SAID BACK — the day, never the instant (`RULINGS R39`).
   *
   * R39's rule is that the app renders the DAY it was given, because quoting a minute back at
   * someone who chose a day "implies a precision the user did not state". The three dated controls
   * name themselves here, in the words the user pressed, exactly as `setDateLine` in
   * `apps/mobile`'s `RefineSheet` does for a deadline.
   */
  'sorting.underwayRecorded': {
    warm: 'Started {when}.',
    literal: 'Start recorded: {when}.',
    slots: ['when'],
    allow: ['tense'],
    why: 'States a fact about work the user has already done, which is the acknowledgement shape design-direction §1 permits past tense for (same exception as `capture.saved` and `interrupt.parked`). The whole claim is about the past; a present-tense form would say something false.',
  },
  'sorting.underwayRecordedUnknown': {
    warm: 'Already started. No day recorded.',
    literal: 'Already started. No date recorded.',
  },
  'sorting.underwayUndo': { warm: 'No, it is new', literal: 'No, this is a new task' },
  'sorting.underwayUndoHint': {
    warm: 'Takes that back. The task keeps everything else.',
    literal: 'Removes the start record. Nothing else changes.',
  },
  /**
   * THE GROUNDS FOR "LOOKS LIKE ONE YOU ALREADY STARTED" (R74's rule that every basis has words,
   * R31's that every part names its own).
   *
   * IT NAMES THE PHRASE, on `sorting.basis.keyword`'s and `sorting.basis.head-verb`'s precedent:
   * `packages/copy/src/resumptionWords.ts` matches on the head phrase and on nothing else, so
   * naming that phrase is a complete account of why the app thinks this, and the user can disagree
   * with it in one glance.
   *
   * IT QUOTES THE USER, WHICH IS WHAT MAKES THIS BASIS DIFFERENT FROM THE OTHER EIGHT. The app is
   * not reporting something it noticed about the world; it is reading back a word the person typed
   * and asking whether they meant it the ordinary way.
   */
  'sorting.basis.resumption-verb': {
    warm: 'You wrote “{word}”, which usually means it is already going.',
    literal: 'The description starts with “{word}”, which usually means work has started.',
    slots: ['word'],
  },
  /**
   * THE GROUNDS FOR A CLAIM THE APP DID NOT MAKE (`ProposalBasis`'s `chosen` member in
   * `@carn/core`; product-concept §7.2).
   *
   * Every other line in this family answers "why does the app think this?". This one answers it
   * by saying the app does not think it at all — the user named the list themselves during
   * Sorting, and the part exists so the choice travels the same confirmed path every inference
   * does rather than a second, unrecorded one.
   *
   * IT IS WRITTEN AS A DENIAL BECAUSE THAT IS THE USEFUL FACT. `sorting.basis.vocabulary`'s own
   * note is the precedent — "`vocabulary` is NOT a guess and must not read as one. The app called
   * the user's own taught phrase 'a guess' once (capture-critique §4.2) — worse than saying
   * nothing." A basis line reading like a hedge over the user's own decision would be the same
   * mistake with the volume up.
   */
  'sorting.basis.chosen': {
    warm: 'Your own choice, not a guess.',
    literal: 'Your choice, not a suggestion.',
  },
  'sorting.duplicateOf': {
    warm: 'Looks like {title}',
    literal: 'Similar to {title}',
    slots: ['title'],
  },
  'sorting.accept': { warm: 'That is it', literal: 'Accept the suggestion' },
  'sorting.acceptHint': {
    warm: 'Takes the suggested name and where it turns up.',
    literal: 'Applies the suggested description and triggers.',
  },
  // "Leave it as it is" was one word away from `sorting.skip`'s "Leave it for now", and two
  // adjacent controls that read almost identically is a precision failure this audience
  // notices (design-direction §1, objection 2).
  'sorting.reject': { warm: 'Not that. Keep your words', literal: 'Reject the suggestion' },
  // "Nothing changes" is now true of the DATA as well as of the title: neither verdict moves
  // `lastTouchedAt`, so rejecting a suggestion no longer relocates the loop on the
  // resumption surface (packages/core/src/sorting.ts).
  'sorting.rejectHint': {
    warm: 'Keeps your own words. Nothing changes.',
    literal: 'Keeps the original wording. Nothing changes.',
  },
  /**
   * MANUAL MODE'S OWN PRIMARY VERDICT.
   *
   * With suggestions off there is no proposal, so the accept control was not rendered and the
   * only way to mark an item reviewed was `sorting.reject` — "Not that. Keep your words" —
   * under a heading that said "No suggestion for this one." The app was asking the user to
   * reject a suggestion the same card had just said did not exist, and it landed hardest on
   * exactly the cohort §7.2 objection 2 names: people who chose manual mode BECAUSE they do
   * not trust guesses.
   */
  'sorting.markReviewed': { warm: 'Looked at it', literal: 'Mark as reviewed' },
  'sorting.markReviewedHint': {
    warm: 'Marks it as seen. Your words stay exactly as they are.',
    literal: 'Marks the item reviewed. The description is unchanged.',
  },
  // The trust requirements of §7.2 objection 2, said out loud on the surface itself.
  // One line on a 320pt-wide phone, in both registers. The literal form wrapped to two, and
  // those 22pt were measured against the suggestion being on screen at all.
  'sorting.nothingSilent': {
    warm: 'Nothing changes until you say so.',
    literal: 'Nothing changes until you confirm.',
  },
  'sorting.onThisDevice': {
    warm: 'These guesses are made on this phone. Nothing is sent anywhere.',
    literal: 'Suggestions are made on this device. No data is sent.',
  },
  'sorting.skippingIsFine': {
    warm: 'Leave this for days if you like. Nothing breaks.',
    literal: 'This can be left. Unsorted items still appear, with less confidence.',
  },
  // "Sorting can be turned fully manual" (§7.2, objection 2).
  'sorting.suggestionsOff': { warm: 'Sort these yourself', literal: 'Turn suggestions off' },
  'sorting.suggestionsOn': { warm: 'Let it suggest again', literal: 'Turn suggestions on' },
  'sorting.suggestionsAreOff': {
    warm: 'No guesses. You decide each one.',
    literal: 'Suggestions are off. You review each item.',
  },
  /**
   * THE DAILY PROMPT, RESTORED (RULINGS R46).
   *
   * These four were deleted for promising a notification nothing scheduled, and
   * `apps/mobile/test/copy-claims.test.ts` held them out until delivery existed. It does now
   * (R7), so they come back — re-voiced, because the removed versions said "nudge", and
   * product-concept §11 makes these invitations rather than nudges.
   *
   * DEFAULT OFF, and the copy must not imply otherwise: product-concept §7.2 is "a daily gentle
   * prompt, **default off until the user opts in**". `promptOn` / `promptOff` state which it is;
   * `promptOptIn` / `promptOptOut` are what pressing the switch does.
   */
  'sorting.promptOn': {
    warm: 'One invitation a day, at {hour}. Turn it off whenever you like.',
    literal: 'One reminder a day at {hour}. It can be turned off at any time.',
    slots: ['hour'],
  },
  'sorting.promptOff': {
    warm: 'No daily invitation unless you ask for one.',
    literal: 'The daily reminder is off until you turn it on.',
  },
  'sorting.promptOptIn': { warm: 'Ask daily', literal: 'Turn the daily reminder on' },
  'sorting.promptOptOut': { warm: 'Stop asking daily', literal: 'Turn the daily reminder off' },
  /**
   * The per-user vocabulary loop (§7.2; implementation-brief §6 makes it phase-1 mandatory).
   *
   * "DESCRIPTION" IS GONE FROM THIS FAMILY, AND THE NOUN WAS THE DEFECT. `Loop` has
   * `title: string | null` and no body, notes or description field anywhere in the schema — so the
   * literal register named an object the product does not have, while the warm register next door
   * (`sorting.proposedTitle`) said "name". Two registers naming different objects is exactly what
   * `slot-parity` exists to prevent and the one dimension it cannot inspect: nouns. product-concept
   * §11 makes literal mode "precise functional labels", and precision about the wrong field is not
   * precision. "Wording" is what the user actually supplies, in both registers.
   *
   * THIS FAMILY IS NOW THE SORTING CARD'S ALONE. It used to serve the loop detail surface too,
   * where the same words meant "rename this loop" — and the HINT is why that could not stand:
   * `fixNameHint` promises the wording is "kept for future suggestions", which is true here (a
   * proposal is on screen, `apply` runs `diffCorrection` and offers `sorting.learnAsk`) and false
   * there (`RenameControl` learns nothing, deliberately — inventing a correction with nothing to
   * diff against would be the silent inference §7.2 forbids). One hint cannot be true of both
   * acts, so there are two families: `loop.rename*` is the other.
   */
  /**
   * PUTTING A CAPTURE ON A LIST, AT SORTING (product-concept §7.2, §8.2; `RULINGS R23`, R125).
   *
   * WHY THE WORDS ARE ABOUT THE ITEM AND NOT ABOUT FILING. §7.2's design is that "the user never
   * files; they only approve or flick away", and this control is deliberately written to stay on
   * the right side of that line: it does not say "file", "move", "assign" or "categorise", it
   * says where the thing goes, in the same shape §7.3 already uses for the fast path ("'we're out
   * of milk' spoken at the fridge lands on the shopping list with no further routing"). One tap
   * to open, one to pick a list, one to place it.
   *
   * THE CONTROL IS NOT A QUESTION AND ITS ABSENCE COSTS NOTHING. It sits behind a disclosure
   * beside the rename one, closed, so a card the user has no list thought about is exactly the
   * card it was before — §7.2's fifteen seconds (R84) is a claim about the ordinary pass, and
   * this must not be on it.
   *
   * `sorting.putOnListWhere` READS DIFFERENTLY IN THE TWO REGISTERS ON PURPOSE. Warm asks where
   * on the list it goes, which is how somebody thinks about a gift list with a section per person;
   * literal names the mechanism, because "section" is the word the section manager, the move
   * control and the list surface all use, and a literal reader is owed the same noun everywhere
   * (design-direction §1's literal-language mode).
   */
  'sorting.putOnList': { warm: 'Put it on a list', literal: 'Add this to a list' },
  'sorting.putOnListHint': {
    warm: 'Opens your lists. It becomes an item on the one you pick.',
    literal: 'Opens your lists. This becomes an item on the list you choose.',
  },
  'sorting.putOnListWhich': { warm: 'Which list', literal: 'Which list' },
  'sorting.putOnListWhere': { warm: 'Where on it', literal: 'Which section' },
  'sorting.putOnListApply': {
    warm: 'Put it on {list}',
    literal: 'Add it to {list}',
    slots: ['list'],
  },
  /**
   * NOTHING TO PUT IT ON. A statement of fact, not a refusal and not a sales pitch for making a
   * list: R12's posture is "prefer absence to disablement", and the honest thing to say to
   * somebody who opened a picker with nothing in it is that there is nothing in it.
   */
  'sorting.putOnListNone': {
    warm: 'You have no lists yet.',
    literal: 'There are no lists yet.',
  },
  /**
   * SPOKEN, BECAUSE THE CARD IS ABOUT TO BE REPLACED. Placing an item sorts the loop, so the next
   * card takes the screen and the acknowledgement has nowhere to sit — `controls.tsx` rule 4's
   * case exactly. Identical in both registers: it is two pieces of the user's own content in a
   * frame with nothing else in it.
   */
  'sorting.putOnListDone': {
    warm: '{title} is on {list}.',
    literal: '{title} is on {list}.',
    slots: ['title', 'list'],
  },
  'sorting.fixName': { warm: 'Put it in your own words', literal: 'Enter your own wording' },
  'sorting.fixNameApply': { warm: 'Use those words', literal: 'Use this wording' },
  'sorting.fixNameHint': {
    warm: 'Your wording wins, and it is kept for next time.',
    literal: 'Your wording is used, and kept for future suggestions.',
  },
  /**
   * THE CONFIRMATION THE VOCABULARY LOOP DID NOT HAVE.
   *
   * Pressing "Use those words" used to apply the title AND silently commit a substitution
   * rule to the user's private dictionary, which is then applied to the title of every
   * subsequent capture at "quite confident". A review reproduced
   * `diffCorrection('Buy 2 bags', 'Buy 3 bags')` recording the permanent rule `2 → 3`.
   *
   * §7.2 objection 2 is not negotiable about this: *"AI never acts silently — every
   * inference is a proposal confirmed by swipe."* The extracted phrase is a separate
   * inference from the title the user typed, so it gets its own confirmation. The title is
   * applied either way — declining teaches nothing, it does not undo the rename.
   */
  'sorting.learnAsk': {
    warm: 'Remember that “{heard}” means “{meant}”?',
    literal: 'Save “{heard}” as “{meant}” for future suggestions?',
    slots: ['heard', 'meant'],
  },
  'sorting.learnYes': { warm: 'Yes, remember it', literal: 'Save the correction' },
  'sorting.learnNo': { warm: 'Just this one', literal: 'Do not save it' },
  'sorting.learnDeclined': {
    warm: 'Just this one, then. Nothing is remembered.',
    literal: 'The correction is not saved.',
  },
  /** The rename landed, and nothing was learned from it. Said, so silence is not the answer. */
  'sorting.fixNameApplied': {
    warm: 'Your words, then.',
    literal: 'Your wording is used.',
  },
  'sorting.fixNameSaved': {
    warm: 'Noted. Your words from now on.',
    literal: 'Correction saved. Your wording is used from now on.',
  },
  'sorting.vocabularyOnDevice': {
    warm: 'Your words stay on this phone.',
    literal: 'Corrections are stored on this device.',
  },
  // Honest confidence, as a band rather than a false-precision percentage. Fills the
  // {confidence} slot of `sorting.suggestionConfidence`.
  // Capitalised, because these words BEGIN the rendered sentence in the warm register
  // ("Fairly sure of this") and a line starting lowercase reads as a fragment. Found by reading
  // the review screenshot, not by a law.
  // THE THREE BANDS FILL `sorting.suggestionConfidence`'s ONE SLOT AND ARE RENDERED NOWHERE ELSE
  // — four call sites, all of them that sentence (`CONFIDENCE_COPY` in `apps/mobile`). So the
  // ladder and the sentence move together or not at all: "Low" inside "{confidence} sure of this"
  // would read "Low sure of this". See that entry for why the literal register lost the ladder.
  'sorting.confidence.low': { warm: 'Not very', literal: 'Not very' },
  'sorting.confidence.medium': { warm: 'Fairly', literal: 'Fairly' },
  'sorting.confidence.high': { warm: 'Quite', literal: 'Quite' },

  // -------------------------------------------------------------------------
  // ONE BLOCK PER INFERENCE (RULINGS R31, R74) — the basis, the verdict pair,
  // and the disclosure the trigger line and the confidence line sit behind (R41).
  // -------------------------------------------------------------------------
  /**
   * WHY THE APP THINKS THIS, per inference (RULINGS R74).
   *
   * product-concept §7.2 objection 2 was accepted on the grounds that "autistic users in
   * particular will lose trust fast at the first wrong guess", and a user cannot confirm an
   * inference whose grounds they cannot see. `ProposalBasis` in @carn/core is the identifier;
   * these six are its words, one per member, exhaustively.
   *
   * `vocabulary` MUST NOT READ AS A GUESS. It is the user's own taught phrase being handed
   * back — the app called that "a guess" once (capture-critique §4.2) and calling the user's
   * own dictionary a guess is worse than saying nothing at all.
   */
  'sorting.basis.vocabulary': {
    warm: 'These are your words for it.',
    literal: 'From wording you saved yourself.',
  },
  'sorting.basis.transcript': {
    warm: 'This is what the recording sounded like.',
    literal: 'From the speech transcript.',
  },
  'sorting.basis.tidy': {
    warm: 'Same words. Spacing and a capital letter, nothing else.',
    literal: 'Spacing and capitalisation only. No wording changed.',
  },
  'sorting.basis.origin-place': {
    warm: 'This phone noted the place when you caught it.',
    literal: 'From the location label recorded at capture.',
    allow: ['tense'],
    why: 'States that this phone noted the place when the item was caught — a fact about what happened at capture (ADA review C5).',
  },
  // The one basis that names the evidence, because a single noun is the whole of it and the
  // user can judge a noun (RULINGS R23). {item} is the matched word, not a department.
  'sorting.basis.keyword': {
    warm: '“{item}” is usually something you buy.',
    literal: 'Matched the shopping word “{item}”.',
    slots: ['item'],
  },
  'sorting.basis.title-overlap': {
    warm: 'Most of the same words as the other one.',
    literal: 'The two descriptions share most of their words.',
  },
  /**
   * THE ONE BASIS THAT CANNOT NAME ITS EVIDENCE (`RULINGS R199`, R200; product-concept §7.2).
   *
   * `keyword` names its noun and `head-verb` names its verb, because in both cases a single word
   * IS the whole of the reasoning. Here there is no such word to name: the proxy returns text, and
   * a device that invented grounds for it — "because it mentions the pump" — would be describing a
   * reasoning process it never saw. So the line states the fact and stops, which is what §7.2's
   * "confidence is shown honestly" asks for when the honest answer is short.
   *
   * It says a model, not Carn, and not a brand. The user is told a machine elsewhere answered;
   * which machine is an implementation detail (`ai-gateway.ts`'s `DEFAULT_MODEL_BY_TASK` may
   * change per task without this string becoming a lie).
   */
  'sorting.basis.model': {
    warm: 'A model read this one and suggested it.',
    literal: 'Suggested by a model, not by this phone.',
  },
  /**
   * THE GROUNDS FOR "THIS LOOKS LIKE A SMALL JOB" (`RULINGS R165`; R74's rule that every basis
   * has words, R31's that every part names its own).
   *
   * IT NAMES THE WORD, on `sorting.basis.keyword`'s precedent — that entry's comment is "the one
   * basis that names the evidence, because a single noun is the whole of it and the user can judge
   * a noun", and here a single VERB is the whole of it. `packages/copy/src/smallJobWords.ts`
   * matches on the head word and on nothing else, so naming the head word is a complete account of
   * why the app thinks this, and the user can disagree with it in one glance.
   *
   * IT DESCRIBES THE ACT, NEVER THE PERSON (design-direction §1). "A couple of minutes on your
   * feet" is a claim about the job; "easy" or "manageable" would be a claim about what the user
   * can manage, which is the sentence this entry exists to not be.
   */
  'sorting.basis.head-verb': {
    warm: 'It starts with “{word}” — a couple of minutes on your feet.',
    literal: 'The description starts with “{word}”, a short physical action.',
    slots: ['word'],
  },
  /**
   * THE SAME BASIS, ABOUT A DIFFERENT CLAIM — the grounds for "this one repeats"
   * (`RULINGS R124` item 8, R74).
   *
   * `ProposalBasis` has six members and no seventh is being invented here: the evidence for a
   * proposed rhythm genuinely IS title overlap. But `sorting.basis.title-overlap` above is
   * written about a PAIR ("the other one"), because until now the only claim resting on that
   * basis was the duplicate question, and a rhythm rests on three or four earlier notes rather
   * than one. So the surfacing block reads this line instead (see `PartDetails`).
   *
   * IT NAMES THE COUNT, on `sorting.basis.keyword`'s precedent — that entry's comment is "the one
   * basis that names the evidence, because a single noun is the whole of it and the user can judge
   * a noun", and here the whole of the evidence is a number the user can check against their own
   * memory. The confidence band deliberately does not move with that number (see
   * `RHYTHM_CONFIDENCE`): extra evidence is SHOWN rather than converted into a claim of
   * certainty, which is what §7.2's "confidence is shown honestly" asks of an inference this
   * large.
   *
   * The `one` forms are unreachable while `REPEAT_MIN_PRIOR` is three, and they are written
   * properly anyway: a threshold is a constant somebody will change, and "1 times" is exactly
   * the imprecision this audience notices (critique 5.3).
   */
  'sorting.basis.repeatCapture': {
    warm: 'You have caught this same job {count} times before, and finished it.',
    literal: 'This description matches {count} earlier tasks you completed.',
    oneWarm: 'You have caught this same job once before, and finished it.',
    oneLiteral: 'This description matches 1 earlier task you completed.',
    slots: ['count'],
    allow: ['tense'],
    why: 'States how many times this same job was caught and finished before — a fact about the past, the evidence line for a suggestion (ADA review C5).',
  },
  /** The list binding, proposed as its own inference with its own confidence (R23, R31). */
  'sorting.proposedList': { warm: 'Looks like shopping', literal: 'Suggested list' },
  /**
   * THE VERDICT PAIR FOR ONE INFERENCE (RULINGS R31).
   *
   * §7.2's clause is that "**every** inference is a proposal confirmed by" the user, so each
   * block owns its own two answers and the bar below answers whatever is left. These are
   * deliberately shorter and flatter than the bar's own labels: they are about one claim.
   */
  /**
   * SHORTENED TO ONE LINE — docs/RULINGS.md R107, and R115's lever ("shortening copy is the
   * cheapest honest fix, and it was predicted to the point").
   *
   * The pair now sits on ONE row (`verdictPairBasis`), so each control is ~116pt wide on a 320pt
   * phone. "Apply this one" wrapped to two lines there and "Do not apply this one" to three,
   * which made a 56pt row 94pt — 38pt of the fold spent on wrapping. The verb is what the user
   * has to read; "this one" was carried by the block the pair sits inside, and the full sentence
   * still reaches a screen-reader user through `sorting.partYesHint` / `sorting.partNoHint`.
   */
  'sorting.partYes': { warm: 'Use this', literal: 'Apply' },
  'sorting.partYesHint': {
    warm: 'Only this one. The rest still wait for you.',
    literal: 'Applies this suggestion only. Others are unaffected.',
  },
  /**
   * One line at ~116pt, same reasoning as `sorting.partYes` above. The literal register takes the
   * same verb the bar's own refusal uses (`sorting.reject`), so the two refusals on the surface
   * read as the same act at two scopes rather than as two different ones — and "Do not apply"
   * measured at two lines where "Reject" measures at one.
   */
  'sorting.partNo': { warm: 'Not this', literal: 'Reject' },
  'sorting.partNoHint': {
    warm: 'Only this one is refused. Nothing else moves.',
    literal: 'Refuses this suggestion only. Others are unaffected.',
  },
  /** What the card says once a block has an answer, so the choice is words and not a colour. */
  'sorting.partTaken': { warm: 'Taking this one.', literal: 'This one will be applied.' },
  'sorting.partLeft': { warm: 'Leaving this one.', literal: 'This one will not be applied.' },
  /**
   * The disclosure R41 puts the trigger line and the confidence line behind.
   *
   * IT SAID "BASIS AND CONFIDENCE", AND THAT IS THE MODEL'S VOCABULARY RATHER THAN A PERSON'S
   * (founder review, 2026-08-24; `docs/DEVIATIONS.md` §retrospective-capture RT5). Both words are
   * precise and neither is a word this audience uses about their own to-do list. design-direction
   * §1 asks the literal register for "precise functional labels" — precise about the FUNCTION, and
   * the function here is answering "why is the app telling me this". `R166` made literal the
   * default and hid the wording control, so this was the label essentially every user would meet.
   *
   * IT NAMES THE REASON AND NOT THE CONFIDENCE, WHICH IS A DELIBERATE LOSS. Both lines are behind
   * the control; the label promises one. `R41`'s clause is that the confidence is "one tap away
   * rather than gone" and that is untouched — a label is not the tap. The hint below names both,
   * so a screen-reader user is told what the shorter label leaves out, and the founder took the
   * shorter label knowing the cost. The two registers drop the same half, because a register that
   * promises something the other does not is a structural difference and §11 makes the metaphor
   * layer "skin, never structure".
   */
  'sorting.whyThis': { warm: 'Why this came up', literal: 'Why this is suggested' },
  'sorting.whyThisHint': {
    warm: 'Shows what each suggestion rests on, and how sure it is.',
    literal: 'Shows the reason for each suggestion, and how sure it is.',
  },
  /**
   * THE BAR ANSWERS WHAT THE BLOCKS DID NOT.
   *
   * Said out loud, because two sets of verdicts on one screen is otherwise a guess about
   * scope, and this surface is the one place in the product where a guess about scope is the
   * thing being fixed.
   */
  'sorting.barAnswersRest': {
    warm: 'These answer anything still waiting above.',
    literal: 'These apply to suggestions with no answer above.',
  },
  'sorting.acceptAll': { warm: 'All of it', literal: 'Apply all the suggestions' },
  'sorting.acceptRestHint': {
    warm: 'Takes everything still waiting above.',
    literal: 'Applies every suggestion not yet answered.',
  },
  'sorting.rejectAll': { warm: 'None of it. Keep your words', literal: 'Reject all the suggestions' },

  // -------------------------------------------------------------------------
  // Duplicate detection gets TWO REAL VERDICTS (RULINGS R32)
  // -------------------------------------------------------------------------
  /**
   * "You've captured 'fix gate latch' three times; same thing?" (§7.2) — asked again, because
   * there is now an answer to it.
   *
   * The question was removed once, correctly: it had three controls under it and none of them
   * was a reply (capture-critique §4.7). `mergeInto` and `recordDuplicateVerdict` in @carn/core
   * are the two replies, so the question comes back with them and not before.
   */
  'sorting.duplicateHeading': {
    warm: 'Caught twice?',
    literal: 'Possible duplicate',
    allow: ['tense'],
    why: '"Caught twice?" asks about a past event — whether the same thing was captured more than once (ADA review C5).',
  },
  'sorting.duplicateSame': { warm: 'Same thing', literal: 'Same task' },
  'sorting.duplicateSameHint': {
    warm: 'Joins them. The older one keeps both notes and both cairns. Nothing is lost.',
    literal: 'Merges them into the older task. Notes and sessions are kept.',
  },
  'sorting.duplicateDifferent': { warm: 'Different jobs', literal: 'Different tasks' },
  'sorting.duplicateDifferentHint': {
    warm: 'Keeps both. This pair is never raised again.',
    literal: 'Keeps both. This pair is not suggested again.',
  },
  'sorting.duplicateOpenHint': {
    warm: 'Opens the other one, so you can see what it is.',
    literal: 'Opens the other task.',
  },
  'sorting.mergedInto': {
    warm: 'Joined. {title} holds everything from both.',
    literal: 'Merged into {title}. Nothing was removed.',
    slots: ['title'],
    allow: ['tense'],
    why:
      'An acknowledgement of an act the user has just performed. design-direction §5 allows ' +
      'past tense in acknowledgement copy, and "nothing was removed" is the reassurance the ' +
      'merge exists to earn — present tense would read as a promise about the future instead.',
  },
  'sorting.duplicateDismissed': {
    warm: 'Kept apart. That pair will not come up again.',
    literal: 'Recorded as different. This pair is not suggested again.',
  },
  // THE DAILY-PROMPT STRINGS ARE GONE, WITH THEIR SWITCH.
  //
  // §7.2's daily prompt is "default off until the user opts in", and the default was right —
  // but the opt-in wrote `Settings.sortingPrompt.enabled` and NOTHING in the repository read
  // it. No local notification, no `expo-notifications`, no scheduled handler. So "Ask daily"
  // was a live control that promised a recurring notification and produced none, and
  // `sorting.promptOn` then told the user "A daily nudge is on." A control that misreports
  // its own effect is worse than an absent feature in the product whose moat is trust.
  //
  // The stored preference stays in the model (core `Settings.sortingPrompt`, default off), so
  // the scheduler has a shape to write against. These four strings and their two controls
  // come back when a notification actually fires. Recorded in docs/OPEN-QUESTIONS.md.

  // -------------------------------------------------------------------------
  // Compost (product-concept §8.3) — a lifecycle, not a graveyard
  // -------------------------------------------------------------------------
  'compost.title': { warm: 'Compost', literal: 'Long-idle tasks' },
  'compost.intro': {
    warm: 'Things settle here when they sit a while. Nothing is thrown away.',
    literal: 'Tasks with no activity for several weeks. Nothing is deleted.',
  },
  // critique 5.2: the warm register omitted {count} the literal register used, so
  // switching register changed the INFORMATION CONTENT and a caller passing `count` had
  // it silently discarded in the default voice.
  'compost.reviewInvite': {
    warm: '{count} things have settled. Want a look?',
    literal: '{count} tasks are long idle. Review them?',
    oneWarm: '1 thing has settled. Want a look?',
    oneLiteral: '1 task is long idle. Review it?',
    slots: ['count'],
  },
  // WIRED, not decorative: `CompostSurface` swaps the surface's title for this one while a
  // review is running, so the review has a name of its own instead of the room's. A swap
  // rather than an extra line, because §7.3 needs every pixel of that surface's vertical
  // budget for the three options on a 320pt phone.
  /**
   * THE LITERAL REGISTER WAS THE ONE NOBODY MEASURED (docs/RULINGS.md R115).
   *
   * It was "Review long-idle tasks" — 22 characters, which at `variant="title"` (21px) beside the
   * `ground-layers` glyph in 280pt of content width sets on TWO lines: a 70pt heading where the warm
   * register's "Compost review" is 35pt. That extra 35pt, minus the 26pt the shorter literal capsule
   * line already saves, is what turned L7's recorded 1pt overflow into a measured **10.0pt** one
   * (bottom 578.0 against a 568pt viewport on an iPhone SE at dial 4) — and it is the third option,
   * "Decide later", that goes over. §8.3's deferral clause is the one option that must not need a
   * scroll, and `lists-critique.md` §7.3 says so in terms.
   *
   * R115: "that is the second time in this build a defect has hidden in the literal register (R16's
   * dial wrap was the first), and it is the register a literal-minded autistic user is most likely to
   * be reading — product-concept §11 makes literal mode a hard requirement, not a variant."
   *
   * "Long-idle review" is 16 characters and keeps the literal register's own vocabulary for this
   * state, which is already fixed by its neighbours: `compost.empty` literal is "No long-idle tasks.",
   * `compost.gradientLabel` literal is "Long-idle tasks are below", `compost.reviewInvite` literal is
   * "{count} tasks are long idle." A noun phrase, matching the warm register's shape, because both are
   * the name of a thing the user is doing rather than an instruction.
   */
  'compost.reviewTitle': { warm: 'Compost review', literal: 'Long-idle review' },
  'compost.empty': { warm: 'Nothing settled down here.', literal: 'No long-idle tasks.' },
  // `compost.released` was here — "Let go. A decided ending." — with zero call sites, which is
  // `celebrate.released` minus its {title}. A release is voiced by `celebrate.released` (the
  // celebration, equal in warmth to a closure per design-direction §5) plus
  // `compost.releasedFindable`. A purpose-built third string for the same moment is a
  // maintenance tax that the 100%-literal-coverage test counts as covered, so it is deleted
  // rather than kept warm. `packages/copy/test/references.test.ts` now fails on new dead keys.
  'compost.releasedFindable': {
    warm: 'Still findable if you ever want it back.',
    literal: 'Archived and searchable.',
  },
  // The always-perceptible sliver at the screen bottom (design-direction §4, objection 1:
  // "the interface admits depth"). Compost is literally downward (§4).
  'compost.gradientLabel': { warm: 'Compost is below', literal: 'Long-idle tasks are below' },
  'compost.gradientHint': {
    warm: 'Tap to go down there.',
    literal: 'Tap to open long-idle tasks.',
  },
  'compost.restNote': {
    warm: 'It sinks quietly. No decision needed today.',
    literal: 'Deferred. No action taken.',
  },

  // -- The review itself (product-concept §8.3) ------------------------------
  // Tone: gardening, not accounting. The invitation is an invitation: `compost.reviewLater`
  // is a real answer, and taking it changes nothing at all.
  /**
   * The count of what is down here, on the surface that IS down here.
   *
   * Identical wording to `home.compostRow`, and a separate key on purpose: the compost surface
   * was rendering the HOME surface's key, so a `home.*` string was the sentence a non-home
   * screen stated its own contents in. The two are never on screen together, so the collision
   * law does not apply; what applied was ownership.
   */
  'compost.count': {
    warm: '{count} in compost',
    literal: '{count} long-idle tasks',
    oneWarm: '1 in compost',
    oneLiteral: '1 long-idle task',
    slots: ['count'],
  },
  'compost.reviewStart': { warm: 'Have a look', literal: 'Start review' },
  'compost.reviewLater': { warm: 'Not now', literal: 'Not now' },
  /**
   * It used to say "Nothing changes", and that was true in the worst way: declining wrote
   * nothing, so the invitation returned on the next visit and the only way to stop being asked
   * was to comply — §8.3's "nothing ever demands review" failing as a demand dressed as an
   * invitation. "Not now" now closes the asking for the rest of the day and touches nothing
   * else, and the hint says exactly that, because a control's hint is a promise about what the
   * press does.
   */
  'compost.reviewLater.hint': {
    warm: 'Closes the invitation for today. Nothing else changes.',
    literal: 'Closes the invitation until tomorrow. Nothing else changes.',
  },
  /** Why a review shows a handful and not the pile (`REVIEW_BATCH_SIZE` in @carn/core). */
  'compost.reviewBatchNote': {
    warm: 'A few at a time, every couple of weeks. Only if you want to.',
    literal: 'A small batch, offered every two weeks. Optional.',
  },
  'compost.reviewRemaining': {
    warm: '{count} more in this batch',
    literal: '{count} tasks left in this review',
    oneWarm: '1 more in this batch',
    oneLiteral: '1 task left in this review',
    slots: ['count'],
  },
  'compost.reviewNext': { warm: 'Next one', literal: 'Next task' },
  // "Nothing else needs a look today" is a claim about the world, rendered beside a count of
  // things still in compost — two true statements that contradict each other when read
  // together. It is a statement about the BATCH, so it says so.
  'compost.reviewDone': {
    warm: 'That is the batch. Nothing else in this one.',
    literal: 'Review complete. Nothing else in this batch.',
  },
  /** Compost is literally downward (design-direction §4), so the way out reads upward. */
  'compost.backUp': { warm: 'Up to the warm set', literal: 'Back to active tasks' },
  'compost.rewarmed': {
    warm: '{title} is back in front of you.',
    literal: '{title} is active again.',
    slots: ['title'],
  },
  /**
   * §8.3's "a fresh capsule prompt included" with rewarm — as its own string.
   *
   * The surface used to render `capsule.calmPrompt` here: "You parked {title} without a note.
   * Want to add one while it is fresh?" That is the Tier-2 CALM-MOMENT string, and on this path
   * its preconditions cannot hold. The loop was never parked (a composting loop has no `parked`
   * event; it cooled), nothing about it is fresh (it had been idle 33 days in the shipped
   * fixture), and "without a note" attributes an omission to the user — the same class of defect
   * the copy review fixed for `celebrate.closed` by splitting out `celebrate.closedAfterReturn`
   * and gating it on a real park. The lesson was learned in one place and not applied in the
   * next.
   *
   * So this one talks about the work and the present, claims nothing about the user's history,
   * and contains no form of "park". `apps/mobile/test/announcements.test.ts` asserts that no key
   * reachable from the compost surface mentions parking.
   */
  'compost.rewarmPrompt': {
    warm: 'Nothing saved on this one. Want to add where to start?',
    literal: 'This task has no saved context. Add a note?',
  },
  'compost.openLoop': { warm: 'Open it', literal: 'Open task' },
  'compost.seeEverything': {
    warm: 'The full list is a place you visit.',
    literal: 'The full task list is on the all-tasks screen.',
  },

  // -- Release observations (product-concept §8.3) ---------------------------
  // @carn/core `releaseObservation` returns a STRUCTURED FACT plus a kind, never English,
  // so the phrasing lives here in both registers. It is a CANDIDATE the user accepts or
  // discards — never a claim asserted at them — hence the keep/discard pair.
  // "Worth noticing" invited the user to reflect on their own inaction, and stacked above
  // `compost.observation.noSessions` it rebuilt the sentence two earlier critiques
  // condemned: "Worth noticing. No sessions on this one." Neither half tripped a law alone,
  // which is why `lintCompositions` and the declared groups below now exist. The label
  // states where the sentence CAME FROM instead of what the user should feel about it.
  'compost.observationLabel': { warm: 'From the record', literal: 'From the task record' },
  'compost.observationExplain': {
    warm: 'A guess from the record. Keep it only if it fits.',
    literal: 'Derived from the task record. Keep it only if it is correct.',
  },
  'compost.observationKeep': { warm: 'Keep this note', literal: 'Keep this note' },
  'compost.observationDiscard': { warm: 'Leave it out', literal: 'Discard this note' },
  /**
   * The observation itself is keyed `release.*`, not `compost.*` (RULINGS R20, R28).
   *
   * The words belong to the ENDING, not to the room the ending happened in: R28 makes the
   * structured `ReleaseObservation` part of the loop's terminal record, and R57 moves the
   * offer off the compost card and onto the loop's own surface. Two surfaces render the same
   * three sentences, so neither surface owns them. The framing above them
   * (`compost.observationLabel` and its keep/discard pair) stays with the surface that frames
   * it.
   *
   * Every branch talks about the WORK, never about the person's failure to act — the exact
   * defect @carn/core's compost.ts header records ("Never got a session started").
   */
  'release.observation.noSessions': {
    warm: 'No sessions were recorded on this one.',
    literal: 'No work sessions were recorded.',
    allow: ['tense'],
    why: 'Reports what the record holds about work that is over. Present tense ("no sessions are recorded") would claim it about now, on a task that has just ended. design-direction §1 permits past tense in acknowledgement copy; RULINGS R20 fixes this wording.',
  },
  'release.observation.interrupted': {
    warm: 'Interrupted {interrupts} times. Sessions on it: {sessions}.',
    literal: 'Interruptions: {interrupts}. Work sessions: {sessions}.',
    slots: ['interrupts', 'sessions'],
  },
  /**
   * The two comparative observations (RULINGS R20, R37), and why each is only HALF a sentence.
   *
   * The old `caught-at-place` branch was deleted for firing on the common case and reading the
   * loop's own metadata back under a heading claiming insight — "Caught at Barn. Sessions on it:
   * 1.", both facts already on the card above. R37 rebuilds it as a COMPARISON: this loop's
   * origin place, or its time-of-day band, against the distribution across the user's decided
   * loops, gated in core on at least five closed loops and a 2× ratio. That is what
   * product-concept §8.3's own example is ("language apps in the evening don't stick for you") —
   * a pattern across loops rather than a restatement of one.
   *
   * So these two keys still state only the fact, and the clause that EARNS the block is
   * `release.pattern.*` below, rendered immediately after. That is deliberate: core's
   * `ObservationComparison` carries a direction, and a fact key that could not express it would
   * silently drop the one thing that made the observation worth saying — the same defect the
   * slot-parity law exists to catch. The two halves are declared as a composition below, so the
   * linter judges the sentence the user actually reads.
   *
   * `caught-at-time` has no ruling of its own: R20 named one comparative key and core's R37
   * work shipped two axes. Recorded in docs/DEVIATIONS.md §copy.
   */
  'release.observation.caughtAtPlace': {
    warm: 'Caught at {placeLabel}. Sessions on it: {sessions}.',
    literal: 'Captured at {placeLabel}. Work sessions: {sessions}.',
    slots: ['placeLabel', 'sessions'],
    allow: ['tense'],
    why: 'Names where and how many sessions this loop was caught and worked — RULINGS R37\'s own observation strings, stating facts about the past (ADA review C5).',
  },
  'release.observation.caughtAtTime': {
    warm: 'Caught {band}. Sessions on it: {sessions}.',
    literal: 'Captured {band}. Work sessions: {sessions}.',
    slots: ['band', 'sessions'],
    allow: ['tense'],
    why: 'Names when this loop was caught — RULINGS R37\'s own observation strings, stating a fact about the past (ADA review C5).',
  },
  /**
   * Which way the comparison runs (`ObservationComparison.direction` in @carn/core).
   *
   * Both directions ship, because §8.3's "extracts value where possible" is served by either:
   * "this kind of work tends to finish" is as useful as its opposite, and shipping only the
   * negative one would make the block a place that only ever says something disappointing.
   *
   * NEITHER FORM MENTIONS THE PERSON. The subject of every sentence here is the work, which is
   * design-direction §1's law and the reason the earlier version of this feature was deleted.
   * "Work caught like this" is a statement about a category of task; "you do not finish things
   * you start at the barn" is a statement about a human being, and it is not shippable in any
   * register at any Intensity.
   */
  'release.pattern.doesNotStick': {
    warm: 'Work caught like this does not often reach an ending.',
    literal: 'Tasks captured like this are completed less often than average.',
    allow: ['tense'],
    why: 'States a pattern about work caught this way — a fact about the past distilled into a trend (ADA review C5).',
  },
  'release.pattern.sticks': {
    warm: 'Work caught like this usually reaches an ending.',
    literal: 'Tasks captured like this are completed more often than average.',
    allow: ['tense'],
    why: 'States a pattern about work caught this way — a fact about the past distilled into a trend (ADA review C5).',
  },

  // -------------------------------------------------------------------------
  // Closure and release celebrations — equal in warmth (design-direction §5)
  // -------------------------------------------------------------------------
  // critique 5.4.4: the wry acknowledgement was asserted for EVERY closure, including a
  // loop captured and closed ninety seconds later with zero parks. The app making a false
  // statement about the user's own history is the precise opposite of axiom 2 ("the app
  // carries the state"), and it turns the wryness into a tic rather than an observation.
  //
  // The caller selects: `celebrate.closed` when the loop was never parked, and
  // `celebrate.closedAfterReturn` when it was. `@carn/core` exposes the event log and
  // `sessionCount()` as a fold, so the condition is knowable — check for a `parked` or
  // `interrupted` event on the loop.
  'celebrate.closed': {
    warm: '{title} closed.',
    literal: '{title} complete.',
    slots: ['title'],
  },
  'celebrate.closedAfterReturn': {
    warm: '{title} closed. That one took some coming back to.',
    literal: '{title} complete.',
    slots: ['title'],
    allow: ['tense'],
    why: 'An acknowledgement of work already done. design-direction §1 permits past tense here and models this exact sentence.',
  },
  /**
   * ADA review C4: the literal register used to read "{title} complete at a partial standard."
   * — "at a partial standard" is a GRADE, the residue of the clerical phrasing critique 5.5
   * condemned in `loop.closeGoodEnough.explain` ("Marked complete at a partial standard"). The
   * sibling explain string found the dignified shape long ago ("This is a completion."); this
   * is the line shown at the moment itself, in the shipped default register, and it must not
   * audit the ending it is celebrating (product-concept §6.4: good-enough is "a dignified
   * checkbox", "a real ending").
   *
   * VERB-FIRST, matching R24's grammar precedent below: "Complete: {title}." states the ending
   * before naming it, the same shape "Released: {title}." uses, so the two literal endings read
   * as peers rather than one of them reading as an audit.
   */
  'celebrate.closedGoodEnough': {
    warm: '{title} closed at good enough. Good place to stop.',
    literal: 'Complete: {title}. Closed at good enough.',
    slots: ['title'],
  },
  /**
   * RULINGS R24: this read as a grammatical error, and at the exact moment design-direction §5
   * requires "equal in warmth" to a completion.
   *
   * "{title} let go." makes the title the SUBJECT of "let go" — "Clean out the gutters on the
   * barn let go." — because every other celebration key puts the title first and those all
   * happen to take a participle that agrees ("{title} closed."). Releasing is the one ending
   * the user performs on the loop rather than the loop reaching, so the verb comes first and
   * the title is named after it. A sentence that reads as broken grammar is not equal in
   * standing to one that reads correctly.
   */
  'celebrate.released': {
    warm: 'Let go: {title}. A decided ending.',
    literal: 'Released: {title}. Recorded as a decision.',
    slots: ['title'],
  },
  /**
   * ADA review C3: closure already has a history-aware family — plain, "took some coming back
   * to" (`celebrate.closedAfterReturn`), and good-enough — and release, the state CLAUDE.md
   * calls "equal in standing", rendered one fixed line regardless of whether the loop was never
   * touched or carried months of stones and interrupts. Equal in warmth was not yet equal in
   * craft. Gated on `everWorked` (`hasBeenWorked(loop)`, `./celebrationCopy.ts` `releasedKey`),
   * the same event-log-fold shape `cameBackTo` uses for the closure family — so this is spoken
   * only when it is true, never assumed.
   *
   * ACKNOWLEDGES THE ACT, NOT THE PERSON (design-direction §1): "the work it got stays on the
   * record" is a fact about the loop's own log, not a grade on the person who kept it. Verb
   * first, for the same reason `celebrate.released` is: releasing is a thing the user does to
   * the loop, not a thing the loop reaches.
   */
  'celebrate.releasedAfterWork': {
    warm: 'Let go: {title}. The work it got stays on the record.',
    literal: 'Released: {title}. Recorded work is kept.',
    slots: ['title'],
    allow: ['tense'],
    why: 'Acknowledges that the loop\'s work stays on the record — the release celebration\'s history-aware line, gated on `hasBeenWorked` (ADA review C3, C5).',
  },
  /**
   * Tending, celebrated exactly as closing is (product-concept §8.1; the "Recurring work"
   * section of docs/OPEN-QUESTIONS.md). Same machinery, same moment, scaled by the same
   * Intensity dial — design-direction §5's "proportional and finite" applies unchanged.
   *
   * THE VERB COMES FIRST, for RULINGS R24's reason and more sharply than R24 needed it: the
   * titles this key renders are imperative chores — "feed the chickens", "check the water
   * troughs" — so the `{title} closed.` shape used by `celebrate.closed` would produce "feed
   * the chickens done.", which is broken English. `celebrate.released` already solved this
   * ("Let go: {title}.") and this follows it.
   *
   * The second sentence is the one that does the work: it states, at the only moment the user
   * is definitely reading, that the thing did not just get destroyed by being done. "Comes
   * round" is `surfacing.rhythm`'s own warm idiom ("When it comes round"), which this transition
   * is what finally makes true. It says the task returns; it says nothing about how often, how
   * many, or how long since — see the block at `loop.tend`.
   *
   * No `celebratory: true` and no exclamation mark: the whole `celebrate.*` family is calm at
   * every Intensity, and a repeating chore is the last place in the app that wants a party.
   * That narrowing is a decision, not a default, and it is recorded rather than left to this
   * comment alone: docs/DEVIATIONS.md, Workstream: ada-copy, AC1 (ADA review C8).
   *
   * THIS IS THE CELEBRATION COPY, NOT THE ONLY THING A TEND SAYS. `celebrationCopy.ts` maps the
   * `tended` outcome here, and separately `Cairn.tsx` announces `celebrate.stoneAdded` for the
   * `place` moment, because a tend does place a stone. Two channels, one press, and both have to
   * be true of a two-second tap — which is why `celebrate.stoneAdded`'s literal form no longer
   * says "work session". An earlier draft of this comment claimed the caller fires this one
   * INSTEAD of `celebrate.stoneAdded`; that was wrong, and it is how the defect survived review.
   */
  'celebrate.tended': {
    warm: 'Done: {title}. That one comes round again.',
    literal: 'Marked done: {title}. This task repeats.',
    slots: ['title'],
  },
  /**
   * The `place` moment's announcement, and it fires on a TEND as well as on a session end —
   * `Cairn.tsx` passes it as the moment's `meaning` whenever `justPlaced` is true, and
   * `LoopDetail`'s `onTend` always passes `justPlaced: true` because a tend always banks a
   * stone. So "Work session recorded." was false for a two-second tap, in the register that
   * exists to be precise. See `loop.sessionCount` for the whole argument.
   *
   * THE WARM FORM IS UNCHANGED AND WAS NEVER WRONG. "One more stone on it" is a statement about
   * the pile, and a tend does put one more stone on it. Only the literal register named a unit,
   * and only the named unit was the lie.
   */
  'celebrate.stoneAdded': {
    warm: 'One more stone on it.',
    literal: 'Recorded on this task.',
  },

  // -------------------------------------------------------------------------
  // Shopping mode (product-concept §8.2)
  // -------------------------------------------------------------------------
  'shopping.title': { warm: 'Shopping', literal: 'Shopping list' },
  'shopping.enterMode': { warm: 'Start shopping', literal: 'Start shopping mode' },
  'shopping.nearStore': {
    warm: 'You are near {store}. Open the list?',
    literal: 'Near {store}. Open shopping list?',
    slots: ['store'],
  },
  // Identical in both registers — so the warm voice said nothing at all on the one state a new
  // user sees first — and a bare negation with no next step. `packages/copy`'s law tests count an
  // identical pair as covered, which is how it passed.
  // The second answer to the arrival invitation. `useStoreProximity.clear()` existed with no
  // caller, so the banner stood for the life of the screen however the user answered it — §11's
  // invitation semantics mean an answer ends the asking, and "no" is an answer.
  'shopping.nearStoreLater': { warm: 'Not now', literal: 'Not now' },
  'shopping.nearStoreLater.hint': {
    warm: 'Closes this. The way in stays below.',
    literal: 'Dismisses this prompt. The list is still openable below.',
  },
  'shopping.empty': {
    warm: 'Nothing on the list yet. Say it at the fridge and it lands here.',
    literal: 'The shopping list is empty. Add items as you notice them.',
  },
  // The same moment on a list that is not a shopping list (RULINGS R126). `shopping.empty` names
  // a fridge and a shopping list, which is the right thing to say to somebody standing in their
  // kitchen and the wrong thing to say to somebody keeping present ideas for their family. Two
  // keys rather than one neutral one, because the shopping sentence is GOOD — it teaches the
  // capture habit §8.2 is built around — and flattening it to serve both surfaces would trade a
  // specific, useful line for a vague one. Same split, same reason, as
  // `sections.explain` / `sections.explainShopping`.
  'lists.emptyItems': {
    warm: 'Nothing on this list yet. Add things as you think of them.',
    literal: 'This list is empty. Add items as you think of them.',
  },
  /**
   * R127's OTHER HALF OF THE COUNT LINE, for the kind that has no trolley to carry it.
   *
   * The ruling's build consequence is one sentence: "a `plain` list's resting surface renders
   * checked items in place, marked, and the count line says how many are done." The surface said
   * only `shopping.remaining`, so nobody could see how many of their gift ideas were bought
   * (docs/reviews/lists-plural-critique.md S2.3). On a `shopping` list the same fact is already
   * on screen — `shopping.checkedRow` counts what is in the trolley — so this is what that key
   * says where there is no trolley, not a second reading of anything.
   *
   * A FACT ABOUT THE LIST, NOT A SCORE ABOUT THE PERSON (product-concept §11; docs/DEVIATIONS.md
   * L4 of the shopping workstream, "no completion percentage is computed on any shopping
   * surface"). It is a count and never a proportion: "3 done" beside "5 left", never "3 of 8" and
   * never a percentage, because the moment the two numbers are divided the list is grading the
   * week. Nothing here is per-day, nothing is compared with a previous list, and the number only
   * ever goes up — checking is not a deadline met, it is a thing you now have.
   */
  'lists.doneCount': {
    warm: '{count} done',
    literal: '{count} items done',
    oneWarm: '1 done',
    oneLiteral: '1 item done',
    slots: ['count'],
  },

  /**
   * ADDING ONE, HERE, WITH A KEYBOARD (product-concept §7.2, objection 3).
   *
   * The whole surface was read-only. `shopping.empty` said "add items as you notice them" and the
   * screen it said that on had no way to add an item at all — every route in was Capture, or the
   * review-only partner trigger, or a partner's sync. §7.2 objection 3 is categorical: "every
   * channel has a silent equivalent of equal speed; voice is a path, never THE path", and a
   * silent equivalent that lives on a different tab is not of equal speed when the user is
   * standing at the fridge with the list open.
   *
   * NO DECISIONS AT ADD TIME (§7.1: "zero decisions... no project, date, priority or list
   * picker"). One field, one press. The item lands in the unsorted department, which is the same
   * place an unclassified capture lands, and the user can move the aisle order whenever they like.
   */
  /**
   * SHORTENED FROM "Add it to the list" (docs/DEVIATIONS.md D35 S2-10; AddItemRow.tsx).
   *
   * That string, plus `nav.close`'s "Close", is what sits in `AddItemRow.tsx`'s button row
   * next to each other. `Button`'s skin (`packages/ui/src/primitives/Pressable.tsx`) puts
   * `theme.space.lg` (16pt) padding on each side of a button plus `theme.space.strokeWidth`
   * border, and the add button also carries an 18pt icon and `theme.space.sm` (8pt) gap to
   * it; the row itself has an 8pt gap between the two buttons. On a 320pt phone, sparse
   * density puts `screenPadding` and `cardPadding` at 20pt each, so the row's own available
   * width is 320 − 2×20 (screen) − 2×20 (the `Surface` card) = 240pt (D35 S2-10 stated this
   * as "~248pt available" against the same two token values; the arithmetic above is the
   * corrected figure, checked against `packages/tokens/src/theme.ts`'s own `DENSITY` table
   * rather than taken on faith — it makes the old string's overflow larger, not smaller). The
   * old warm string, "Add it to the list", plus the icon and its gap (added in the same
   * review pass that first flagged this row, D35), measured past that ceiling — reported by
   * D35 S2-10 and not re-measured pixel-for-pixel here, since this tree has no rendering rig
   * this pass could invoke — so the two 56pt targets stacked instead of sitting side by side
   * (+64pt on the sheet, in the warm register only; the literal register's "Add an item" /
   * "Close" pair was always short enough). Shortened here rather than by changing the layout:
   * the row's whole point
   * is two full-width-ish targets side by side, and the icon stays (design-direction §6's
   * "every icon has a text label" pairing is exactly what this task's brief exists to ship).
   * Both registers still mean the same thing — add the typed item to the list.
   */
  'shopping.addItem': { warm: 'Add to list', literal: 'Add an item' },
  'shopping.addItem.hint': {
    warm: 'Puts it straight on the list. Nothing else to answer.',
    literal: 'Adds the item to this list. No further input is required.',
  },
  'shopping.addItemName': { warm: 'What to get', literal: 'Item name' },
  'shopping.addItemAdded': {
    warm: '{item} is on the list.',
    literal: '{item} is on the list.',
    slots: ['item'],
  },
  'shopping.remaining': {
    warm: '{count} left',
    literal: '{count} items remaining',
    oneWarm: '1 left',
    oneLiteral: '1 item remaining',
    slots: ['count'],
  },
  // critique 5.4.6: "Go home" is an imperative about the user's next movement that the
  // app cannot know is right, and drill-sergeant-adjacent — which design-direction §1
  // forbids by name.
  'shopping.complete': {
    warm: 'List worked.',
    literal: 'All items checked.',
    celebratory: true,
  },
  // `shopping.reorderDepartments` was REMOVED here (RULINGS R129) along with the rest of
  // `DepartmentOrder.tsx`'s strings — see the block further down that used to hold them for
  // where each one went, and which two were PORTED rather than deleted.
  // `shopping.partnerAdded` ("{who} added {item}") was here with zero call sites. It cannot have
  // one: this build has no account identities (`items.ts` — "`memberIds` is empty because this
  // build has no account identities yet"), so the app cannot name the person, and
  // `shopping.itemArrived` exists precisely because "the app must not name a person it cannot
  // identify". It comes back with the member list, phrased against whatever that actually
  // carries.

  // -- Shopping mode: the dedicated surface (product-concept §8.2) -----------
  // "A high-contrast, huge-tap-target, one-handed checklist — nothing else on screen."
  // The mode names itself in place terms rather than task terms: the user is IN the shop,
  // and that is the whole reason this surface exists.
  'shopping.modeTitle': { warm: 'In the shop', literal: 'Shopping mode' },
  'shopping.enterMode.hint': {
    warm: 'Big targets, one hand, nothing else on screen.',
    literal: 'Opens a high-contrast checklist with large targets.',
  },
  'shopping.exitMode': { warm: 'Stop shopping', literal: 'Exit shopping mode' },
  'shopping.exitMode.hint': {
    warm: 'Closes this screen. The list keeps everything.',
    literal: 'Closes shopping mode. The list is unchanged.',
  },
  // Checked items VANISH (§8.2) — so the hint says where the item goes, because a row that
  // disappears with no explanation is indistinguishable from a bug.
  // Both registers used to describe the vanish as SUBTRACTION — "it comes off the list" / "the
  // item leaves the list view" — which is the framing §8.3 spends a whole section refusing
  // everywhere else ("nothing is thrown away"). The trolley is where the item goes, and the copy
  // already had the word.
  //
  // THESE TWO ARE THE TRIP'S AND THE TROLLEY'S SENTENCES, NOT EVERY LIST'S (RULINGS R127). They
  // render in shopping mode, in the trolley, and on a `shopping` list's resting surface — the
  // three places a checked item really does move. `item.check.hint` / `item.uncheck.hint` say the
  // true thing for a `plain` list, where nothing moves and no trolley is drawn.
  'shopping.check.hint': {
    warm: 'Tap when it is in the trolley. It moves there.',
    literal: 'Tap to check off. The item moves to the checked list.',
  },
  'shopping.checkedRow': {
    warm: '{count} in the trolley',
    literal: '{count} items checked off',
    oneWarm: '1 in the trolley',
    oneLiteral: '1 item checked off',
    slots: ['count'],
  },
  'shopping.checkedRow.hint': {
    warm: 'Opens what you have picked up, in case one is wrong.',
    literal: 'Shows checked items so one can be unchecked.',
  },
  // Unchecking is a correction and it must STICK — the reason `ShoppingItem.uncheck` exists
  // in @carn/core at all ("the app undid their correction every time, forever").
  'shopping.uncheck': { warm: 'Put it back on the list', literal: 'Uncheck this item' },
  'shopping.uncheck.hint': {
    warm: 'Your correction stands. Nothing overwrites it.',
    literal: 'The item returns to the list. A later sync does not undo it.',
  },
  /**
   * The same guarantee as CONTENT, which is a different job from a hint.
   *
   * `Trolley` rendered `shopping.uncheck.hint` as a visible `Text` node while `ItemRow` passed the
   * same key as every row's `accessibilityHint`, so a screen-reader user heard it once per row
   * plus once as prose. One string doing two jobs. The hint stays a hint; this is the sentence the
   * trolley panel states once, at the top, where correcting a mis-tap is the whole activity.
   */
  'shopping.uncheck.note': {
    warm: 'Tap one to put it back on the list. Your correction stands.',
    literal: 'Tap an item to return it to the list. A later sync does not undo it.',
  },
  /** The one way out of the trolley panel, worded as a return rather than a dismissal. */
  'shopping.trolleyClose': { warm: 'Back to the list', literal: 'Back to the shopping list' },
  // -------------------------------------------------------------------------
  // CLEARING A WORKED LIST (docs/DEVIATIONS.md LC1)
  //
  // §8.2's sentence is "checked items vanish; the list ends". `R127` ruled the clauses after the
  // semicolon belong to the trip, and the mode does close itself — but the LIST never ended, so a
  // weekly shop accumulated every item ever bought and `shopping.checkedRow` read "247 in the
  // trolley" with no way out. These strings are that way out.
  //
  // THE TWO GUARANTEES EVERY ONE OF THEM CARRIES, in both registers, because the product owner's
  // own phrasing put "clear out" and "Archive list" in one sentence and that is the confusion
  // these words exist to prevent: THE LIST ITSELF STAYS, and NOTHING IS THROWN AWAY. The wording
  // of the second deliberately matches `lists.archiveExplain` ("Nothing on the list is thrown
  // away") and `return.bulkCompostDone` ("Nothing is deleted") — three different acts, one
  // guarantee, one phrasing, so a user who has read any of them has read all three.
  //
  // NOT `shopping.remove.*`, WHICH IS THE NEIGHBOUR ABOVE AND A DIFFERENT ACT. That one takes one
  // item off the list for good, on the `discardLoop` path, with `R135`'s thirty-day purge behind
  // it. This one writes one field on the list row and touches no item at all.
  //
  // NOTHING HERE IS `celebratory` AND NOTHING CARRIES AN EXCLAMATION MARK, deliberately. Every
  // item in a clear-out was already ended when it was checked (checking IS `close()`), and the
  // trip's own ending is already acknowledged by `shopping.complete`. A second warm moment over
  // housekeeping would be `D32`'s "congratulating somebody for cleaning up", and repeated weekly it
  // is a streak by another name (design-direction §6 objection 1).
  // -------------------------------------------------------------------------
  /**
   * THE INVITATION, at the moment the list is fully worked — the one thing on this surface that
   * says a shop has ended.
   *
   * Modelled on `shopping.nearStore` and offered the same way, which is this surface's own
   * ratified grammar (`R53`: "the arrival raises a prominent invitation with the primary action
   * one tap away… it never replaces the screen under the user's thumb"). It states a fact and
   * offers two answers; `shopping.clearLater` is the other one, and an invitation that only
   * accepts yes is not an invitation (product-concept §11).
   */
  'shopping.allWorked': {
    warm: 'Nothing left to get on this one.',
    literal: 'Every item on this list is checked off.',
  },
  'shopping.clearLater': { warm: 'Leave them there', literal: 'Leave them as they are' },
  'shopping.clearLater.hint': {
    warm: 'Nothing changes. The trolley stays as it is.',
    literal: 'No change. Checked items stay on the list.',
  },
  /**
   * THE CONTROL, which is always there whenever anything is checked — not only at the moment
   * above.
   *
   * Two entry points for one act, on purpose and by this surface's existing precedent: the
   * geofence raises `shopping.nearStore` at a moment, and `shopping.enterMode` sits below as the
   * same act available always, because "you do not need a geofence to walk into a shop"
   * (`ListSurface.tsx`). The invitation is the moment; the control is the capability. Gating the
   * capability on the moment would make it unreachable on the ordinary list — one that is fully
   * worked for the few seconds at the end of a trip and never again, because §8.2 has items
   * "captured all week at the moment of noticing".
   */
  'shopping.clear': { warm: 'Empty the trolley', literal: 'Clear the checked items' },
  /**
   * "CLEARS", NOT "REMOVES", AND THE VERB IS THE POINT (docs/DEVIATIONS.md LC2).
   *
   * The literal register shipped as "Removes checked items from this list" — the verb this very
   * block reserves for the other act twenty lines up, whose own string is "The item leaves the
   * list." So a literal-register reader met "Removes…" on the button and "Nothing is deleted; each
   * item keeps its own record" in the panel it opened: the two halves of one gesture contradicting
   * each other, in the register that exists to be precise. The warm form was already right.
   */
  'shopping.clear.hint': {
    warm: 'Takes the ticked ones off the list. The list itself stays.',
    literal: 'Clears checked items off this list. The list itself is kept.',
  },
  /** The question, carrying the count so the user is told what they are about to act on. */
  'shopping.clear.question': {
    warm: '{count} in the trolley. Clear them off the list?',
    literal: '{count} items are checked off. Clear them from this list?',
    oneWarm: '1 in the trolley. Clear it off the list?',
    oneLiteral: '1 item is checked off. Clear it from this list?',
    slots: ['count'],
  },
  /**
   * Shown BEFORE the confirming press, never after — `R130`'s rule for `lists.archiveExplain`:
   * "a reassurance that arrives once the act is done is not a reassurance."
   */
  'shopping.clear.explain': {
    warm:
      'The list stays, with anything still to get on it. Nothing is thrown away — each of these keeps its own history.',
    literal:
      'The list is kept, along with any items still to buy. Nothing is deleted; each item keeps its own record.',
  },
  'shopping.clear.confirm': { warm: 'Yes, clear them', literal: 'Clear them' },
  'shopping.clear.cancel': { warm: 'Keep them there', literal: 'Keep them' },
  /**
   * The statement of what happened. Drops the `.clear.` segment to match the other three acts
   * that report themselves — `loop.removed`, `lists.archived`, `lists.renamed`.
   */
  'shopping.cleared': {
    warm: 'Trolley empty. Nothing is thrown away.',
    literal: 'Checked items are cleared. Nothing is deleted.',
  },
  /**
   * The undo IN THE PANEL, where the acknowledgement it answers is the line directly above it.
   * "Undo that" has its antecedent on screen here, which is why it works — and why it does not
   * work in the standing row below (`shopping.clear.putBack`).
   */
  'shopping.clear.undo': { warm: 'Put them back', literal: 'Undo that' },
  /**
   * THE STANDING ROW'S OWN LABEL, which must read alone (docs/DEVIATIONS.md LC2).
   *
   * `ClearCheckedUndoRow` sits in the `R129` band between the store controls and "Rename this
   * list", with nothing above it referring to a clear. Reusing `shopping.clear.undo` there put
   * "Undo that" in that band — a demonstrative with no antecedent anywhere on the surface, which is
   * exactly the sort of thing the literal register exists to refuse. Found by reading the rendered
   * screenshot rather than the diff, which is the same instrument the critic pass needed.
   *
   * The warm form survived that test ("Put them back" is a whole sentence about a knowable them);
   * both are restated here anyway rather than one key doing two jobs, because the two positions ARE
   * two utterances — one answers a statement, the other names an act from cold.
   */
  'shopping.clear.putBack': {
    warm: 'Put the cleared ones back',
    literal: 'Restore the cleared items',
  },
  'shopping.clear.undo.hint': {
    warm: 'Puts them back in the trolley, history and all.',
    literal: 'Returns the items to the checked list. Their records are unchanged.',
  },
  'shopping.clear.undone': {
    warm: 'Back in the trolley.',
    literal: 'Returned to the checked list.',
  },
  /**
   * The way out of the acknowledgement, once the undo is used or not wanted.
   *
   * `loop.remove.dismiss`'s pair ("That is all" / "Dismiss") is the precedent and this now matches
   * its shape. It shipped as "Right then" / "Close", which failed register parity in two ways the
   * linter cannot see: one is an interjection and the other names an act, and "Right then" is the
   * app talking to itself rather than to the user (design-direction §1's second-person law in
   * spirit, if not in a form `lint.ts` can match).
   */
  'shopping.clear.dismiss': { warm: 'That is all', literal: 'Dismiss' },
  /**
   * THE LIST AFTER IT HAS BEEN WORKED AND CLEARED — a third true sentence (LC2).
   *
   * `shopping.empty` says "nothing on the list YET", which is right for a list nothing has ever been
   * on and wrong for one the user has just finished with; `ListSurface` already carries a comment
   * naming that string's failure mode as "the wrong one is the one that teaches the user their items
   * are gone", and after a clear it rendered directly above `shopping.cleared`'s "Nothing is
   * deleted". Both registers state the same two facts: there is nothing to get, and what was here is
   * kept.
   */
  'shopping.emptyCleared': {
    warm: 'Nothing to get. The last shop is put away, not thrown out.',
    literal: 'This list has no items to buy. The cleared items are kept.',
  },
  // -------------------------------------------------------------------------
  // REMOVING AN ITEM — a third, tap-reachable route beside the hold and the rotor action
  // (RULINGS R14; the shopping-critique S1-4 fix). `shopping.removeItem` is the SHORT visible
  // word on the per-row control (`ItemRow.tsx`, the same visible/accessible split
  // `sections.moveUpWord`/`sections.moveUpNamed` uses — this comment used to name `shopping.moveUp`/`Full`,
  // which were that split's first instance and are now deleted); `shopping.removeItemFull` is the
  // spoken name, because
  // a row of unlabelled "Remove" buttons is useless to a screen-reader user moving by rotor.
  'shopping.removeItem': { warm: 'Remove', literal: 'Remove' },
  'shopping.removeItemFull': {
    warm: 'Remove {item} from the list',
    literal: 'Remove {item} from the list',
    slots: ['item'],
  },
  /**
   * `RemoveLoop.tsx`'s confirmation, overridden for a SHOPPING item (`RemoveRequest.copy`) —
   * the shared `loop.remove.question`/`.explain` literal strings state a consequence that is
   * FALSE for a checked item: checking an item off IS `close()` (`engine.ts`'s own comment,
   * "checking an item off is a close"), so `loop.remove.explain`'s "It is not recorded as
   * complete" is untrue in the one direction the literal register exists to get right. These two
   * read correctly whether or not the item was checked, without branching on it: removing an
   * item always undoes a check if there was one, so the explanation states that plainly instead
   * of asserting there was never anything to undo.
   */
  'shopping.remove.question': { warm: 'Remove this from the list?', literal: 'Remove this item from the list?' },
  'shopping.remove.explain': {
    // design-direction §1: present tense outside acknowledgement copy — this is a
    // confirmation, not an acknowledgement, so "had"/"was" are not allowed here.
    warm: 'It comes off the list. If it is ticked off, that comes undone too — nothing stays counted.',
    literal:
      'The item leaves the list. If it is checked off, that check is undone as well; it is not recorded as bought.',
  },
  // Origin as a memory hook (product-concept §5): items are noticed at the fridge, the barn,
  // the bathroom (§8.2), and where you were is often how you remember what you meant.
  'shopping.capturedAt': {
    warm: 'Noticed at {place}',
    literal: 'Captured at {place}',
    slots: ['place'],
  },
  // A shared list syncs live, so a partner's addition can land mid-shop (§8.2, §10.1).
  // Used when the actor is unknown: the app must not name a person it cannot identify.
  // Phrased so the item name never has to agree with a verb: "{item} is on the list now"
  // renders "Bin bags is on the list now", which design review caught reading as a typo.
  'shopping.itemArrived': {
    warm: 'New on the list: {item}',
    literal: 'Added to the list: {item}',
    slots: ['item'],
  },

  // -- The one-time department re-order (§8.2, and the route-learning decision note) --
  //
  // NINE KEYS LIVED HERE AND NONE DOES NOW. `DepartmentOrder.tsx` was subsumed by the section
  // manager (RULINGS R129), which reorders the sections of ANY list rather than the twelve
  // departments of the one shopping list, so every string that named a DEPARTMENT was either
  // false on a gift list or already said by a `sections.*` key. What happened to each:
  //
  //   `shopping.reorderDepartments`, `shopping.reorderExplain`  → `sections.explainShopping` /
  //     `sections.explain`, which say the aisle thing for the kind that has aisles and the other
  //     thing for the kind that does not (R126).
  //   `shopping.reorderFinish`, `shopping.reorderSaved`         → `sections.done`. Nothing on that
  //     page is staged — every gesture is committed when it is made — so there is no "save" to
  //     name and no "saved" to announce; the control is the return trip and says so.
  //   `shopping.moveUp` / `shopping.moveDown`                   → `sections.earlier` /
  //     `sections.later` (the printed words).
  //   `shopping.moveUpFull` / `shopping.moveDownFull`           → `sections.moveUpNamed` /
  //     `sections.moveDownNamed` (the spoken names). Same visible/accessible split, `{section}` in
  //     place of a `{department}` slot that is a lie about a list of people.
  //   `shopping.reorderDrag`                                    → `sections.drag`.
  //   `shopping.reorderMoved`                                   → `sections.moved`.
  //
  // THE LAST TWO ARE A PORT, NOT A DELETION, and that distinction is the whole of
  // docs/DEVIATIONS.md L7. They are main's drag affordance and its announcement (D41); the
  // feature survived the move to the section manager, so its strings had to as well. Deleting
  // them because the file they were written for is gone would have laundered a lost feature into
  // a tidy-up.
  //
  // Deleted rather than left: `packages/copy/test/references.test.ts` treats a shopping key with
  // no call site as a failure, on the grounds that "a dual-register string nothing renders is a
  // tax the 100%-literal-coverage test counts as covered".

  // -- The geofence, stated plainly (implementation-brief §3, product-concept §14) ---
  // "This audience reads the permissions screen", so the privacy invariant is said out loud
  // on the surface it applies to, not buried in a policy.
  /**
   * A FALSE CLAIM WAS ATTACHED TO THE PRIVACY SENTENCE. Both halves of "The list opens itself
   * when you reach the shop" were untrue: no geofence is wired on any platform
   * (`useStoreProximity` reports "not near a store" unconditionally on native, awaiting
   * verification spike #4), and even by design the arrival is OFFERED, not performed — the
   * screen rendered the promise directly below "You are near Farm shop. Open the list?", so it
   * contradicted the control beside it.
   *
   * §14 risk 1 says this audience reads the privacy surface closely, which makes it the worst
   * place in the product to be approximate. So the sentence now states what the build does, in
   * the same register `sorting.placeNotWatched` already uses for the same gap
   * (docs/DEVIATIONS.md D17), and the invariant is stated as what holds when the geofence lands.
   */
  // -- The shop this list belongs to (product-owner report; RULINGS R8, R129; §8.1, §8.2) ------
  //
  // WHAT CHANGED AND WHY. The foot of the shopping list used to hold an attach control, an
  // unconditional three-sentence privacy statement, a conditional geofence paragraph and up to five
  // outcome lines — all of it rendered to users who had never asked for arrival at all. It is now
  // the ONE question this surface can answer — which shop is this list for — plus a line of state
  // only when there is something true to say. Setting where a shop actually IS stays in Places
  // (settings), because that is where a user can set a shop they are not currently standing in.
  //
  // The permission prompt moved with it, which keeps R8's rule intact rather than bending it: the
  // ask still happens at the gesture that needs it, and that gesture is "set where this place is"
  // rather than "open a shopping list" or "say which shop this is for".
  //
  // THE BINDING WAS MISSING AND THAT WAS THE DEFECT. For a while this row was a signpost and
  // nothing else — the user could add shop after shop in settings and never say which list went
  // with which, so `CarnList.storePlaceId` stayed empty and no arrival ever matched a list.
  // `shopping.storePick` is the control that closes it.
  'shopping.storePick': {
    warm: 'Which shop is this list for?',
    literal: 'Select the store for this list',
  },
  'shopping.storeNone': {
    warm: 'No shop on this list yet, so arriving somewhere says nothing about it.',
    literal: 'No store is assigned to this list, so arrival does not surface it.',
  },
  'shopping.storeSet': {
    warm: 'This list is for {store}.',
    literal: 'This list is assigned to {store}.',
    slots: ['store'],
  },
  /**
   * The bound shop has no location yet — a real state and a quiet one.
   *
   * Binding a list to a place and telling the app where that place is are two separate acts, on
   * two separate surfaces, and neither is a precondition of the other. Saying so is the honest
   * alternative to a control that refuses the binding until the location exists.
   */
  'shopping.storeNoLocation': {
    warm: 'Where {store} is, is not set yet, so arriving there is not noticed.',
    literal: 'No location is set for {store}, so arrival is not detected.',
    slots: ['store'],
  },
  'shopping.storeClear': {
    warm: 'Take {store} off this list',
    literal: 'Remove {store} from this list',
    slots: ['store'],
  },
  /**
   * The `{store}` slot's fallback, for the one case where the list is bound and this phone does
   * not hold the place: a shared list bound on a partner's device before that pull has landed.
   * The control still has to be operable and still has to name what it does, and "the shop" is
   * the truthful thing to call a shop whose name this device has not been told.
   */
  'shopping.storeThisShop': { warm: 'the shop', literal: 'the store' },
  /** No places of kind shop exist at all: the picker has nothing to pick from, so it says so. */
  'shopping.storeNoneRegistered': {
    warm: 'No shops set up yet. Add one in settings and it turns up here.',
    literal: 'No stores are set up. Add one in the settings to select it here.',
  },
  'shopping.attachStore': {
    warm: 'Set up your shops',
    literal: 'Manage stores',
  },
  'shopping.attachStore.hint': {
    warm: 'Opens Places in settings, where you name your shops and say where they are.',
    literal: 'Opens the Places settings, where stores are named and located.',
  },
  /** Something on this phone is watching. Rendered only when that is true. */
  'shopping.arrivalReady': {
    warm: 'This phone watches for the shops you have set.',
    literal: 'This device monitors the store locations you have set.',
  },
  /**
   * The "while using the app" state, said plainly (product-owner report 2).
   *
   * iOS delivers a region crossing to a background task, which needs the Always grant. A
   * while-using device will not be woken, so telling this user that the list turns up when they
   * arrive would be a promise the operating system will not keep.
   */
  'shopping.arrivalForegroundOnly': {
    warm: 'Location is set to “while using the app”, so arrival is only noticed while the app is open.',
    literal: 'Location permission is “while using the app”. Arrival is detected only while the app is open.',
  },
  /** Location refused outright. A legitimate answer, and the list is unchanged. */
  'shopping.attachDenied': {
    warm: 'Location is off on this phone, so the shop goes unnoticed. The list works the same.',
    literal: 'Location permission is not granted, so arrival cannot be detected. The list is unchanged.',
  },
  /** `reason: 'unavailable'` — the browser review build, and any platform without regions. */
  'shopping.attachUnavailable': {
    warm: 'This build cannot notice shops at all. Open the list yourself.',
    literal: 'This build cannot monitor location. Open the list manually.',
  },
  // Review-build affordances (docs/DEVIATIONS.md D3): a browser has no geofence and no
  // second account, so the two moments §8.2 and §7 describe are unreviewable without a
  // trigger. Catalogued because they are user-visible strings, and labelled so no reviewer
  // can mistake them for product.
  'shopping.reviewTools': { warm: 'Review build only', literal: 'Review build only' },
  'shopping.reviewNearStore': {
    warm: 'Review only: act as if you are at the shop',
    literal: 'Review only: simulate arriving at the store',
  },
  'shopping.reviewPartnerAdd': {
    warm: 'Review only: add an item as someone else',
    literal: 'Review only: add an item from another member',
  },

  // -------------------------------------------------------------------------
  // Department names (product-concept §8.2; RULINGS R22)
  // -------------------------------------------------------------------------
  // `DEFAULT_DEPARTMENTS` in `@carn/core` is a list of IDS — `produce`, `bakery`, `meat-fish`
  // — and these are their names. Twelve English strings in the domain package was the same
  // violation as an inlined string in a component with more distance between the crime and the
  // law: CLAUDE.md says every user-visible string ships here, in both registers, or it is not a
  // shipped string. `reorderDepartments` and `shoppingModeGroups` now sort ids, so the user's
  // saved aisle order survives a locale change and a rename.
  //
  // THE TWO REGISTERS ARE CLOSE ON PURPOSE. R22: "a department name is not a place for
  // wryness." The only differences below are where the warm form is a British colloquial
  // shortening and the literal form is the complete noun — the register exists for precision,
  // not for a second joke. UK English throughout, and the vocabulary is a UK supermarket's own
  // signage rather than an invented taxonomy, because the reference user is reading it while
  // walking (§8.2's huge-target aisle list).
  //
  // The id order is core's, and it is the default aisle order. Do not reorder here.
  'dept.produce': { warm: 'Fruit and veg', literal: 'Fruit and vegetables' },
  'dept.bakery': { warm: 'Bakery', literal: 'Bakery' },
  'dept.meat-fish': { warm: 'Meat and fish', literal: 'Meat and fish' },
  'dept.dairy-eggs': { warm: 'Dairy and eggs', literal: 'Dairy and eggs' },
  'dept.chilled': { warm: 'Chilled', literal: 'Chilled food' },
  'dept.frozen': { warm: 'Frozen', literal: 'Frozen food' },
  'dept.cupboard': { warm: 'Cupboard', literal: 'Cupboard and dry goods' },
  'dept.drinks': { warm: 'Drinks', literal: 'Drinks' },
  'dept.household': { warm: 'Household', literal: 'Household' },
  'dept.health-beauty': { warm: 'Health and beauty', literal: 'Health and beauty' },
  'dept.pet-animal': { warm: 'Pet and animal feed', literal: 'Pet and animal feed' },
  /**
   * The catch-all, and `UNSORTED_DEPARTMENT` in core.
   *
   * An item whose department is unknown is never hidden (core `lists.ts`), so this group is
   * where a capture with no guess lands. It is deliberately not called "Unsorted": the item is
   * not waiting on the user to file it, and product-concept §8.2 has no filing step.
   */
  'dept.other': { warm: 'Other', literal: 'Other' },

  // -------------------------------------------------------------------------
  // Lists, plural (RULINGS R124, R126, R128, R130) — a shopping list is one kind of
  // list among several now. R128 turns the fourth tab into an index of every list the
  // device holds; R126 gives a list exactly two kinds, chosen once at creation; R130
  // says a list is archived, never deleted.
  // -------------------------------------------------------------------------

  // -- The index (R128): `nav.lists` is the permanent tab, `lists.title` is what it
  // opens on. The two are on screen together, so they must not read the same. --
  'nav.lists': { warm: 'Lists', literal: 'Lists' },
  'lists.title': { warm: 'Your lists', literal: 'Your lists' },
  /**
   * No-shame law: an empty index says what to do next, never what has not happened yet.
   *
   * THE LITERAL FORM NAMES THE CONTROL; THE WARM FORM GRANTS THE PERMISSION
   * (`docs/roadmap/onboarding.md` item 7). It read "Create one when you are ready." — the same
   * permission-granting shape as the warm form beside it. That phrasing is authorised, and only
   * in warm: product-concept §6.3 puts "look when you're ready" in the product's own voice, and
   * `home.coolingRow` and `home.moreRow` carry it for exactly that reason. product-concept §11
   * defines the literal register as "precise functional labels", and permission to act at a
   * time of one's choosing is not a label — it is the wryness-adjacent warmth this register
   * exists to strip, and it leaves a literal-minded reader deciding whether readiness is a
   * precondition the app is checking. The literal form now names the control that does the
   * thing, which is `lists.new`'s own literal label ("Add a list"), so the sentence and the
   * button agree word for word.
   */
  'lists.empty': {
    warm: 'No lists yet. Start one whenever you like.',
    literal: 'No lists yet. Add a list to create one.',
  },
  'lists.new': { warm: 'Start a list', literal: 'Add a list' },
  'lists.new.hint': {
    warm: 'Opens a name and a kind. Two fields, nothing else.',
    literal: 'Opens the two fields needed to create a list: a name and a kind.',
  },
  'lists.open.hint': { warm: 'Opens the list.', literal: 'Opens the list.' },
  // The index row's running total. A different job from `shopping.remaining` above,
  // which counts what is left on an open trip — this counts everything on the list.
  'lists.itemCount': {
    warm: '{count} items',
    literal: '{count} items',
    oneWarm: '1 item',
    oneLiteral: '1 item',
    slots: ['count'],
  },
  // The index row's kind tag. Deliberately short — `lists.kind.*` below is the fuller,
  // warmer choice offered once, at creation; this is the small permanent label.
  'lists.kindShopping': { warm: 'Shopping', literal: 'Shopping list' },
  'lists.kindPlain': { warm: 'General', literal: 'General list' },
  'lists.sharedMark': { warm: 'Shared', literal: 'Shared' },

  // -- Creating a list (R124, R126) --
  'lists.newTitle': { warm: 'New list', literal: 'New list' },
  'lists.nameLabel': { warm: 'What to call it', literal: 'List name' },
  'lists.namePlaceholder': { warm: 'Gift ideas', literal: 'Gift ideas' },
  'lists.kindQuestion': { warm: 'What kind of list is this?', literal: 'Choose a list kind' },
  /**
   * The two options R126 allows, worded as what they are FOR rather than as jargon — "the
   * field is that assertion made checkable", not a taxonomy the user has to parse. The two
   * labels, and the two explains beside them, must read as visibly different choices.
   */
  'lists.kind.shopping': { warm: 'Shopping', literal: 'Shopping list' },
  'lists.kind.shopping.explain': {
    warm: 'Grouped by the aisles of a shop. Attach a shop and the list opens itself when you arrive. Has its own shopping mode too.',
    literal: 'Items are grouped by the departments of a store. A store can be attached so the list opens on arrival. Includes a dedicated shopping mode.',
  },
  'lists.kind.plain': { warm: 'Something else', literal: 'General list' },
  'lists.kind.plain.explain': {
    warm: 'You name the groups yourself. Gifts, packing, jobs — whatever this list is for. No shop, no shopping mode.',
    literal: 'You choose the section names yourself — for gifts, packing, or jobs. No store, and no shopping mode.',
  },
  'lists.create': { warm: 'Make the list', literal: 'Create the list' },
  'lists.create.hint': {
    warm: 'Saves the name and kind. The list opens straight after.',
    literal: 'Saves the list and opens it.',
  },
  'lists.created': { warm: '{name} is ready.', literal: '{name} is created.', slots: ['name'] },
  'lists.nameNeeded': { warm: 'Give it a name first.', literal: 'A name is required.' },

  // -- Managing one list (R129: below the content, beside store, share and re-order) --
  'lists.rename': { warm: 'Give it a new name', literal: 'Rename this list' },
  'lists.rename.hint': {
    warm: 'Opens a field with the current name in it.',
    literal: 'Opens a field pre-filled with the current name.',
  },
  'lists.renameLabel': { warm: 'New name', literal: 'New name' },
  'lists.renamed': {
    warm: 'Renamed to {name}.',
    literal: 'Renamed to {name}.',
    slots: ['name'],
    allow: ['tense'],
    why: 'The rename\'s own outcome acknowledgement (ADA review C5).',
  },
  /**
   * THE HEADING OVER THE BAND OF ACTS ON THE LIST ITSELF (`RULINGS R129`; design-direction §4).
   *
   * R129 fixed what sits below the items — sections, share, store, rename, archive — and called
   * it "the band". `ListSurface`'s `ListToolsBand` draws it, and this is its name.
   *
   * IT NAMES THE OBJECT, NOT THE ACTS. "Manage list", "List options" and "More" were all
   * considered and all describe the CONTROLS; what the reader needs at that point on the surface
   * is to be told that the subject has changed — everything above is what is ON the list, and
   * everything below is about the list itself. So the heading says the subject.
   *
   * The two registers differ here in a way most headings do not. Warm names the thing in the
   * user's own terms; literal names the category the controls belong to, because a literal reader
   * asked repeatedly for the label that says what a control does rather than what it is about
   * (design-direction §1's literal-language mode).
   */
  'lists.tools': { warm: 'The list itself', literal: 'List settings' },
  'lists.sections': { warm: 'Manage sections', literal: 'Manage sections' },
  /**
   * ADA review C5: the literal form used to read "where sections can be added, renamed, or
   * removed" — a modal passive, present tense throughout, which the tense heuristic reads as
   * a past act only because `renamed` is on its verb list. Reworded to the warm form's own
   * active construction rather than annotated with `allow: ['tense']`, because an exemption
   * here would state something false: this sentence is allowed because it obeys the law
   * (design-direction §1's "second person, present tense"), not because it is an
   * acknowledgement.
   */
  'lists.sections.hint': {
    warm: 'Opens the section list — add one, rename one, or remove one.',
    literal: 'Opens the list of sections. You can add, rename, or remove a section there.',
  },
  'lists.archive': { warm: 'Archive this list', literal: 'Archive this list' },
  'lists.archive.hint': {
    warm: 'Moves it off this screen. Nothing on it is thrown away.',
    literal: 'Removes the list from view. Nothing on it is deleted.',
  },
  /**
   * R130: archiving is not deleting. The same guarantee `loop.remove.explain` states for a
   * task, said here for a list — nothing is destroyed, the list just leaves this screen.
   *
   * AND WHAT IT DOES TO SHARING, which this was silent about while being careful about deletion —
   * and access is the half a person archiving a shared list actually needs to know
   * (docs/reviews/lists-plural-critique.md R2-S2.3). A share link hands WRITE access to whoever it
   * is forwarded to (`shareLink.explain` says so at the moment it is minted), so a list that stayed
   * open after being put away is the trust product-concept §12 calls the moat, spent. §14 makes
   * saying it plainly a product requirement rather than a courtesy.
   *
   * "Any" covers zero, so the sentence is true on a list that was never shared and does not need a
   * second string or a condition on the surface. The withdrawal it describes is performed by
   * `apps/mobile/src/data/hooks/useArchivedWithdrawal.ts`, which retries until it succeeds — this
   * says what archiving DOES, in the present tense, and does not promise a moment.
   */
  'lists.archiveExplain': {
    warm: 'This is not deleting. Nothing on the list is thrown away — it just leaves this screen. Any link you gave out for it is withdrawn.',
    literal: 'Archiving does not delete anything. The list and its items are kept; the list leaves this screen. Any share link for this list is revoked.',
  },
  'lists.archiveConfirm': { warm: 'Yes, archive it', literal: 'Archive' },
  'lists.archiveCancel': { warm: 'Keep it here', literal: 'Keep the list' },
  'lists.archived': {
    warm: '{name} is archived. Everything on it is kept.',
    literal: '{name} is archived. Nothing on it is deleted.',
    slots: ['name'],
  },

  // -------------------------------------------------------------------------
  // Sections (RULINGS R125, R129) — `dept.*` above is what a section is called when the
  // list is a grocery list; these strings are the general concept, and the strings the
  // user's OWN section names sit beside. A section name the user types is data on the
  // section row, never a catalogue key (R125.3) — the linter has no opinion on "Mum".
  // -------------------------------------------------------------------------
  'sections.title': { warm: 'Sections', literal: 'Sections' },
  'sections.explain': {
    warm: 'Groups within this list. Add one for each person, place, or category you want to keep apart.',
    literal: 'Groups within this list, for keeping items separate by person, place, or category.',
  },
  'sections.explainShopping': {
    warm: 'For a shopping list, these are the aisles of your shop, in the order you walk them.',
    literal: 'For a shopping list, sections are the departments of a store, ordered to match how you shop there.',
  },
  'sections.add': { warm: 'Add a section', literal: 'Add a section' },
  'sections.add.hint': {
    warm: 'Opens a name field. It joins the list once saved.',
    literal: 'Opens a name field for a new section.',
  },
  'sections.addLabel': { warm: 'Section name', literal: 'Section name' },
  'sections.added': { warm: '{name} is added.', literal: '{name} is added.', slots: ['name'] },
  // `shopping.moveUp` / `shopping.moveDown` moved here and became `sections.moveUpNamed` /
  // `sections.moveDownNamed` (RULINGS R129). Their slot was `{department}`, which is false on a gift
  // list whose sections are people, and they were the last two strings still calling a section a
  // department after the surface stopped doing so.
  //
  // THESE ARE THE SPOKEN NAMES, NOT THE PRINTED ONES. The button prints `sections.earlier` —
  // one word — and carries this as its accessible name, so a screen-reader user hears which
  // section they are about to move while the row stays narrow enough for two controls to share
  // it. The printed word is a SUBSTRING of the spoken name in both registers, which is WCAG
  // 2.5.3 (Label in Name) and not a nicety: a voice-control user says "tap Earlier", and that
  // only works if the visible word appears in the accessible name.
  //
  // THEY ARE ALSO THE ONLY SPOKEN ROUTE THROUGH THE RE-ORDER (RULINGS R14; docs/DEVIATIONS.md
  // D41). `sections.drag` below labels a gesture with no assistive-technology equivalent, so
  // these two are not a fallback beside it — they are the feature, and the drag is the addition.
  /**
   * THE SPOKEN NAMES, and the section comes AFTER the verb rather than inside it.
   *
   * "Move Frozen up" would read better as English and would be a WCAG 2.5.3 (Label in Name)
   * failure: the rule is that the accessible name must contain the visually presented text
   * *word for word, in order*, and "Move up" is not a substring of "Move Frozen up". A
   * voice-control user says "tap Move up", and the platform matches their words against the
   * accessible name — so the natural-sounding phrasing is the one that breaks the control.
   *
   * The previous wording had the same defect and hid it behind a word nobody would say aloud:
   * printed "Earlier", spoken "Move Frozen earlier". `packages/copy/test/laws.test.ts` now
   * asserts the containment for every one of these pairs, so the next person to make the
   * sentence flow better gets a failing test rather than a silently unusable button.
   */
  'sections.moveUpNamed': {
    warm: 'Move up, {section}',
    literal: 'Move up, {section}',
    slots: ['section'],
  },
  'sections.moveDownNamed': {
    warm: 'Move down, {section}',
    literal: 'Move down, {section}',
    slots: ['section'],
  },
  // The printed words. One word each, because four controls that each printed a sentence stacked
  // into a wall — one section filled a phone screen, and a shopping list has twelve of them.
  /**
   * "Move up" / "Move down", NOT "Earlier" / "Later" (founder correction).
   *
   * The earlier wording was temporal and the act is not: nothing about a section's place in a
   * list is about time. A user reading "Earlier" on a list of aisles has to translate it into a
   * direction before they can act, and the direction is the thing they can already see.
   *
   * These are the PRINTED words. Their spoken counterparts are `sections.moveUpNamed` /
   * `sections.moveDownNamed` below, and the split has a rule attached — see those keys.
   */
  'sections.moveUpWord': { warm: 'Move up', literal: 'Move up' },
  'sections.moveDownWord': { warm: 'Move down', literal: 'Move down' },
  'sections.renameWord': { warm: 'Rename', literal: 'Rename' },
  'sections.removeWord': { warm: 'Remove', literal: 'Remove' },
  /**
   * The drag handle's visible word — `shopping.reorderDrag` ported, not renamed for tidiness
   * (docs/DEVIATIONS.md D41 and L7). The handle came across from `DepartmentOrder.tsx` with the
   * feature; only its name changed, because what it drags is a section now.
   *
   * NEVER SPOKEN. The handle is hidden from assistive technology (`Waymark … presentational`)
   * because a raw pan has no equivalent action for VoiceOver or switch control; `sections.moveUpNamed`
   * / `sections.moveDownNamed` beside it are that equivalent and carry their own full spoken names.
   * The word is here at all because design-direction §6 does not let an icon stand alone.
   */
  'sections.drag': { warm: 'Drag', literal: 'Drag' },
  /**
   * Announced once a re-order lands, BY EITHER ROUTE — `shopping.reorderMoved` ported (D41, L7).
   *
   * One sentence for one act, deliberately. A drag that moves something silently is invisible to
   * a screen-reader user (R14), and iOS ships VoiceOver with "Speak Hints" OFF by default, so a
   * button that silently swapped with its neighbour is a button that did nothing. Announcing the
   * two paths differently would make one of them read as second-class; this one names the section
   * as well as where it landed, because "Move up" spoken ten times running with no subject is
   * useless once the row order no longer matches reading order.
   *
   * Present tense throughout, so it needs no `allow: ['tense']` exception even though it reports
   * something that just finished (contrast "fence closed", which the acknowledgement exception
   * exists for) — "is now" states the resulting fact rather than narrating the event.
   *
   * THE SLOT IS `spot`, NOT `position`. `apps/mobile/test/route-learning.test.ts` bans the WORD
   * `position` as an identifier anywhere on this code path (product-concept §8.2's route-learning
   * guard, whole-word, deliberately blunt), because a per-ITEM ordinal is exactly how the cut
   * feature would come back. This is a per-SECTION slot in an order the user re-arranged by hand
   * — the same fact `String(index + 1)` already prints beside every row — but the guard cannot
   * tell the difference from source text alone, so the identifier steps around it and the guard
   * carves out this one call shape explicitly rather than being weakened. The rendered English
   * word "position" in the literal register is unaffected; only the interpolation key differs.
   */
  'sections.moved': {
    warm: '{section} is now number {spot}.',
    literal: '{section} is now in position {spot}.',
    slots: ['section', 'spot'],
  },
  // NAMED, like their row-mates `sections.moveUpNamed` / `sections.moveDownNamed`, and for the same
  // reason. These are the accessible names of per-row buttons whose printed word is one word, so
  // a screen-reader user moving down a list of sections used to hear "Rename it. Remove it.
  // Rename it. Remove it." with no antecedent anywhere — the pronoun referred to a row they
  // could not see. The move controls already took a `{section}` slot to avoid exactly that; the
  // two beside them did not, which made the row half-legible by voice.
  //
  // The printed word stays a SUBSTRING of the name in both registers — "Rename" inside "Rename
  // Mum" — so WCAG 2.5.3 (Label in Name) still holds and "tap Rename" still matches.
  'sections.rename': {
    warm: 'Rename {section}',
    literal: 'Rename the section {section}',
    slots: ['section'],
  },
  'sections.rename.hint': {
    warm: 'Opens a field with its current name in it. Only the name changes.',
    literal: 'Opens a field pre-filled with the section’s name. Only the name changes.',
  },
  'sections.renamed': {
    warm: 'Now called {name}.',
    literal: 'Renamed to {name}.',
    slots: ['name'],
    allow: ['tense'],
    why: 'The rename\'s own outcome acknowledgement (ADA review C5).',
  },
  'sections.remove': {
    warm: 'Remove {section}',
    literal: 'Remove the section {section}',
    slots: ['section'],
  },
  'sections.remove.hint': {
    warm: 'Its items move to the list’s general section. Nothing is lost.',
    literal: 'The section’s items move to the list’s general section. Nothing is deleted.',
  },
  'sections.removed': {
    warm: '{name} is removed. Its items are safe in the general section.',
    literal: '{name} is removed. Its items remain in the list’s general section.',
    slots: ['name'],
  },
  /**
   * THE MOST IMPORTANT STRING IN THIS BLOCK. `removeSection` in `@carn/core` tombstones the
   * section but never the items in it — they land in the catch-all and `listGroups` always
   * renders that group. Say the guarantee plainly, so removing a section does not read as
   * destructive: it isn't (CLAUDE.md — "nothing is ever taken away by time").
   */
  'sections.removeExplain': {
    warm: 'Nothing is lost. Every item in it moves to the general section and stays in view.',
    literal: 'No items are deleted. Every item in the section moves to the general section and remains visible.',
  },
  /**
   * A built-in section (`BUILT_IN_SECTIONS`) is named by the app in both registers, so its
   * name is fixed — R125.2 keeps that compile-time guarantee. Said plainly and without
   * apology: a user who wants their own wording adds their own section instead.
   */
  // A PAGE-LEVEL FACT, not a row-level one, and the wording follows the placement. It printed on
  // every built-in row and said "this section", which on a shopping list was the same sentence
  // twelve times. As one footnote it has to speak about several sections at once, so "this one"
  // and "this section's name" are both wrong now.
  //
  // Neither register names Carn: "some section names are fixed" states the limit without the
  // third-person self-reference the linter treats as an exception to be justified, and without
  // asking the user to care which part of the app decided.
  'sections.builtIn': {
    warm: 'Some sections keep their names. Add your own if you want different wording.',
    literal: 'Some section names are fixed. Add your own section for different wording.',
  },
  // "NONE OF YOUR OWN", never "none". The literal register said "No sections have been added
  // yet" and rendered directly beneath a line counting the sections that exist — a flat
  // contradiction on screen, because a list always has at least the catch-all. Both registers now
  // say what is actually true: the user has authored none, which is a fact about their own
  // structure rather than about the list's.
  //
  // The obvious literal phrasing — "you have not added your own sections yet" — was written
  // first and REFUSED by the linter's `no-shame` law, which lists "you have not" among the
  // constructions that frame an absence as the user's failing. The law is right and the refusal
  // is the system working: an empty state is the single easiest place in a product to imply
  // somebody should have done something by now (product-concept §2 axiom 3). The two registers
  // therefore open with the same five words, which is fine — they are precise words, and
  // precision is what the literal register is for.
  'sections.empty': {
    warm: 'No sections of your own yet. Add one to start splitting up the list.',
    literal: 'No sections of your own yet. Add one to divide the list into groups.',
  },
  'sections.count': {
    warm: '{count} sections',
    literal: '{count} sections',
    oneWarm: '1 section',
    oneLiteral: '1 section',
    slots: ['count'],
  },
  'sections.done': { warm: 'Back to the list', literal: 'Return to the list' },

  // -------------------------------------------------------------------------
  // Moving an item between sections (R125, R127) — `moveItemToSection` in `@carn/core`.
  // Shopping keeps the aisle-flavoured warm register `sections.explainShopping` establishes;
  // the generic `item.*` keys stay plain, because a gift list has no aisles to speak of.
  // -------------------------------------------------------------------------
  'item.section': { warm: 'Section', literal: 'Section' },
  'item.moveTo': { warm: 'Move it to another section', literal: 'Move to a different section' },
  // `item.moveTo.hint` was REMOVED here (RULINGS R125). It was written as the hint for the
  // move control, and the move control has no hint channel: it is an accessibility ACTION
  // (`Pressable`'s `actions`), which carries a name and nothing else, because the row's own hint
  // belongs to the tap — checking the item off — and a row cannot have two.
  //
  // Nothing was lost with it. `item.moveTo` is the action's name and is already a full sentence
  // ("Move it to another section"), and iOS ships VoiceOver with "Speak Hints" OFF by default, so
  // the hint would have been silent for most of the people it was written for. Re-add it only
  // alongside a control that can actually render one.
  'item.moved': {
    warm: '{name} moved to {section}.',
    literal: '{name} moved to {section}.',
    slots: ['name', 'section'],
  },
  /**
   * TICKING A ROW ON A LIST THAT HAS NO TROLLEY (RULINGS R127;
   * docs/reviews/lists-plural-critique.md S2.2).
   *
   * `shopping.check.hint` says the item "moves to the trolley", and that is true of exactly two
   * renderings: shopping mode, where R127 keeps §8.2's vanish, and a `shopping` list's resting
   * surface, where `restingGroups` takes checked items out of the aisles and `ListSurface` draws
   * the `Trolley` they land in. On a `plain` list neither is true — nothing moves, and there is no
   * trolley on that surface at all — so the hint named a destination the screen does not have.
   *
   * Two keys rather than one flattened pair, for the reason `shopping.empty` / `lists.emptyItems`
   * is two keys: the shopping sentence is RIGHT where it renders and teaches the trolley the user
   * is about to look for, and a wording vague enough to be true on both would be worse on both.
   * `ItemRow` picks on `list.kind`, exactly as `ListSurface` picks its empty state.
   */
  'item.check.hint': {
    warm: 'Tap when you have it. It stays here with a tick.',
    literal: 'Tap to mark the item as done. It stays on the list, marked.',
  },
  'item.uncheck.hint': {
    warm: 'Tap again to take the tick off. Your correction stands.',
    literal: 'Tap to remove the mark. A later sync does not undo it.',
  },
  // Targets the quick-add field at one section directly, with no picker (product-concept
  // §7.1: zero decisions at add time) — a row under a section heading, on any list kind.
  'shopping.addToSection': {
    warm: 'Add to {section}',
    literal: 'Add to {section}',
    slots: ['section'],
  },
  // KIND-NEUTRAL, and it was not. This renders in `AddItemRow`, which is the same component on
  // a shopping list and a gift list (RULINGS R124, R126) — so "a different aisle" and "this
  // item's department" both described a shop to somebody choosing which family member a present
  // is for. Neither register may name a thing the list does not have. The aisle vocabulary
  // survives where it is true: `sections.explainShopping` on a shopping list's own section page.
  'shopping.changeSection': {
    warm: 'Put it somewhere else',
    literal: 'Choose a different section',
  },
  // THE HINT IS THE SAME CONTROL'S OTHER HALF, and it was left naming aisles and departments when
  // the label above it was made kind-neutral (docs/reviews/lists-plural-critique.md S2.1). It
  // becomes `accessibilityHint` on that button, so on a gift list it was the sentence a VoiceOver
  // user heard about a shop that is not there. Same law, same reason, same fix as the label: a
  // string that renders on both kinds may only name things both kinds have (R124, R126).
  'shopping.changeSection.hint': {
    warm: 'Opens the list of sections. Pick where this item goes.',
    literal: 'Opens the list of sections so this item can go in a different one.',
  },

  // -------------------------------------------------------------------------
  // Sharing (product-concept §10) — exactly three gestures, no more
  // -------------------------------------------------------------------------
  'share.toss': { warm: 'Toss it to someone', literal: 'Transfer task' },
  /**
   * The delivery-tense description, for when there is a transport.
   *
   * Note what the warm form enumerates: "the photo, the note and the next step". The photo does
   * not travel — `TossPayload` carries media as a boolean because a local file path means nothing
   * on another device — so this sentence made the same false promise the received card's
   * "Photo of where you left it — On" line did (critique 6.1). It stays for the day a transport
   * can carry a file; `share.tossExplainLocal` is what the section says until then, and
   * `SHARING_DELIVERY_WIRED` in `localSharing.ts` picks between them.
   */
  'share.tossExplain': {
    warm: 'They get the photo, the note and the next step. An offer, not an order.',
    literal: 'Sends the task with its saved context. The recipient may decline.',
  },
  'share.tossExplainLocal': {
    warm: 'Hands over the next step and the notes with it. An offer, not an order.',
    literal: 'Records the task with its saved context for one person. Nothing is sent yet.',
  },
  'share.tossReceived': {
    warm: '{who} tossed you {title}',
    literal: '{who} transferred {title} to you',
    slots: ['who', 'title'],
    allow: ['tense'],
    why: 'Acknowledges that a loop was just tossed to the reader — the toss\'s own arrival line (ADA review C5).',
  },
  'share.accept': { warm: 'Take it', literal: 'Accept' },
  'share.bounce': { warm: 'Bounce it back', literal: 'Decline' },
  'share.ask': { warm: 'Ask someone', literal: 'Request information' },
  /** Same pair, same reason: there is no answer path anywhere yet, even locally (critique 6.2). */
  'share.askExplain': {
    warm: 'Their answer lands straight in your inbox.',
    literal: 'The reply is captured to your inbox.',
  },
  'share.askExplainLocal': {
    warm: 'Asks one person for one thing. Nothing is sent from this phone yet.',
    literal: 'Requests one item of information. Nothing is sent from this device yet.',
  },

  'share.title': { warm: 'Sharing', literal: 'Sharing' },
  /** The three-gesture ceiling, stated (product-concept §10, and core `SharingGesture`). */
  'share.onlyThree': {
    warm: 'Three ways to share. That is the whole list.',
    literal: 'Three sharing actions. This is the complete list.',
  },
  /** Honest about the review build: delivery is not wired to a second account yet. */
  'share.localOnly': {
    warm: 'This phone only for now. Nothing leaves it yet.',
    literal: 'Local to this device. Nothing is sent yet.',
  },
  /** product-concept §10's explicit non-goal: household task equity management. */
  'share.noTally': {
    warm: 'No tallies. Nothing here counts who does more.',
    literal: 'No counts or comparisons between people are recorded.',
  },
  'share.sharedList': { warm: 'Shared lists', literal: 'Shared lists' },
  /**
   * Gesture 1's pair, for the same reason as the other two.
   *
   * "Shopping lists sync live. Any member can add items." is a claim about a transport, and it
   * rendered three lines below `share.localOnly` saying nothing leaves the phone — the same
   * prominent-false-statement-over-quiet-true-one shape as `share.tossSent` (critique 6.2). The
   * list itself is real and local; the syncing is not.
   */
  'share.sharedListExplain': {
    warm: 'Shopping, live. Anyone on the list can add to it.',
    literal: 'Shopping lists sync live. Any member can add items.',
  },
  'share.sharedListExplainLocal': {
    warm: 'The shopping list. On this phone for now.',
    literal: 'The shopping list. Stored on this device for now.',
  },
  'share.people': { warm: 'People', literal: 'People' },
  'share.peopleEmpty': {
    warm: 'Add someone to share with.',
    literal: 'Add a person to share with.',
  },
  'share.personAdd': { warm: 'Add someone', literal: 'Add a person' },
  'share.personName': { warm: 'Their name', literal: 'Name' },
  'share.personRemove': { warm: 'Remove {who}', literal: 'Remove {who}', slots: ['who'] },
  'share.tossPick': { warm: 'What to hand over', literal: 'Which task to transfer' },
  // Both composers are on one scroller, so a bare "Recipient" twice is two radiogroups with
  // the same accessible name (lint.ts `SURFACE_MANIFESTS`).
  'share.tossTo': { warm: 'Who to hand it to', literal: 'Transfer recipient' },
  /** §10: "a complete work order, not a bare title" — so the surface lists what travels. */
  'share.tossTravels': { warm: 'What goes with it', literal: 'Included in the transfer' },
  'share.tossNoPressure': {
    warm: 'No reminders their end. No read receipts. No date pressure.',
    literal: 'No reminders for the recipient. No read receipts. No deadline transfer.',
  },
  'share.tossSend': { warm: 'Toss it', literal: 'Transfer it' },
  /**
   * TWO CONFIRMATIONS PER GESTURE, and `SHARING_DELIVERY_WIRED` in
   * `apps/mobile/src/screens/share/localSharing.ts` decides which one renders.
   *
   * "Handed to Sam. An offer, not an order." is a factual claim about another person having
   * received something, and it rendered in body text on the same screen as the tertiary line
   * saying nothing had been sent — so the prominent statement was the false one (critique 6.2).
   * `share.askSent` was worse: it asserted a future event (an answer arriving) for which there
   * is no path at all, even locally.
   *
   * The `*Recorded` forms say what happened. The delivery forms stay because they are correct
   * the day a transport lands, and the flag switches them back in one place.
   */
  'share.tossSent': {
    warm: 'Handed to {who}. An offer, not an order.',
    literal: 'Sent to {who}. The recipient may decline.',
    slots: ['who'],
  },
  'share.tossRecorded': {
    warm: 'Written down for {who} on this phone. Nothing has left it.',
    literal: 'Recorded on this device for {who}. Nothing has been sent.',
    slots: ['who'],
  },
  'share.tossNothing': {
    warm: 'Nothing open to hand over.',
    literal: 'No open tasks to transfer.',
  },
  'share.askTo': { warm: 'Who to ask', literal: 'Request recipient' },
  'share.askWhat': { warm: 'What to ask for', literal: 'What to request' },
  /** On a received toss, where the title is the heading and the sender is the quiet line. */
  'share.tossFrom': {
    warm: 'Handed over by {who}',
    literal: 'Sent by {who}',
    slots: ['who'],
  },
  'share.askPlaceholder': {
    warm: 'One line. Which feed brand do you use?',
    literal: 'Describe what you need.',
  },
  'share.askSend': { warm: 'Ask them', literal: 'Send the request' },
  'share.askSent': {
    warm: 'Asked {who}. Their answer lands in your inbox.',
    literal: 'Sent to {who}. The reply arrives in your inbox.',
    slots: ['who'],
  },
  'share.askRecorded': {
    warm: 'Your question is saved for {who} on this phone. Nothing has left it.',
    literal: 'The request is recorded on this device for {who}. Nothing has been sent.',
    slots: ['who'],
  },
  'share.inbox': { warm: 'Handed to you', literal: 'Received' },
  /**
   * The outbound sections had the SAME headings as the composers above them —
   * `share.toss` twice and `share.ask` twice, at the same heading level, 2,000pt apart on a
   * ~3,000pt surface. For a VoiceOver user navigating by heading, which is the only practical
   * way to traverse a surface this long, the rotor offered two indistinguishable landmarks
   * (critique 6.5). `share.inbox` was already distinct; these two now match it.
   */
  'share.handedOver': { warm: 'Handed over', literal: 'Transferred' },
  'share.askedFor': { warm: 'Asked for', literal: 'Requested' },
  /**
   * What does NOT travel, said rather than silently dropped.
   *
   * §10's claim is that the recipient gets "the photo, the voice note, and the next action — a
   * complete work order". The payload carries media as a BOOLEAN because a local file path is
   * meaningless on another device, which is the right shape — but the received card listed
   * "Photo of where you left it — On" and then acceptance produced a loop with no photo, no
   * voice note and no last step (critique 6.1). `capsule.photoHidden` is the precedent: the app
   * states what it did with the thing instead of losing it quietly.
   */
  'share.mediaNotIncluded': {
    warm: 'The photo and the voice note stay on the phone that holds them.',
    literal: 'Photos and audio notes are not transferred.',
  },
  /**
   * §10 objection 2 forbids "due-date pressure transfer", and `TossPayload` has no date field
   * to carry one — but the picker offers hard-dated loops, so the one fact making a vet
   * appointment urgent disappeared without a word (critique 6.4). The guard is right; the
   * silence was not.
   */
  'share.dateNotIncluded': {
    warm: 'The date stays with you. No deadline is handed over.',
    literal: 'The date is not transferred. No deadline is handed over.',
  },
  'share.inboxEmpty': { warm: 'Nothing handed your way.', literal: 'Nothing received.' },
  'share.accepted': {
    warm: '{title} is yours now.',
    literal: '{title} added to your tasks.',
    slots: ['title'],
  },
  'share.bounced': { warm: 'Sent back to {who}.', literal: 'Returned to {who}.', slots: ['who'] },

  // -------------------------------------------------------------------------
  // Surfacing axes (product-concept §8.1) — place, rhythm, energy. Used as the labels
  // for the three axis icons, which never appear without one (design-direction §6).
  // -------------------------------------------------------------------------
  'surfacing.place': { warm: 'Where you are', literal: 'Location trigger' },
  // RULINGS R25: "Time trigger" named a category, not a mechanism — every one of the three
  // axes is a time-adjacent trigger to a reader who does not already know the model, and this
  // is the axis a literal-minded user meets most often. The literal form now says what the
  // condition IS: it repeats, and it is a clock condition rather than a place or a job size.
  'surfacing.rhythm': { warm: 'When it comes round', literal: 'Repeating time condition' },
  // "Capacity trigger" rated the person, like the rest of the literal energy vocabulary
  // (critique 9.2). The axis fires on what the day suits, so that is what it is called.
  'surfacing.energy': { warm: 'How you are', literal: 'Job-size trigger' },

  // -------------------------------------------------------------------------
  // Energy (product-concept §9.2) — non-clinical, non-judgmental, never mandatory
  // -------------------------------------------------------------------------
  // critique 5.4.1: "How is today going?" is a direct question about the user's emotional
  // state, which design-direction §1 forbids by name. product-concept §9.2 wants the
  // LABELS (Foggy/Steady/Charged) to carry the self-report; the prompt asks about the
  // WORK, which is what "talks about the work, not the feelings" means in practice.
  //
  // THE LITERAL REGISTER DESCRIBES THE WORK, NOT THE PERSON. It used to read "Set today's
  // capacity: Low / Medium / High" — an ordinal rating of the human, in the vocabulary of a
  // functional-capacity assessment, in the register autistic users live in permanently and the
  // one a user reaches by moving the dial to 0. §9.2's words are "non-clinical and
  // non-judgmental"; design-direction §1's copy law is that acknowledgement attaches to the
  // act and never to the human. "Capacity: Low" is a statement about the human and nothing
  // else (critique 9.2).
  //
  // "Small jobs / Usual jobs / Big jobs" is what the self-report is FOR: §9.2 filters what the
  // surface offers ("foggy days show tiny physical loops and errands; charged days offer the
  // meaty ones"), so naming the work is both more literal and more useful than naming a level.
  // It also removes three of the five label collisions on this surface, because the intensity
  // levels are Low/Medium/High in the same register (critique 10.4).
  'energy.prompt': { warm: 'What’s today good for?', literal: 'What today suits' },
  'energy.foggy': { warm: 'Foggy', literal: 'Small jobs' },
  'energy.steady': { warm: 'Steady', literal: 'Usual jobs' },
  'energy.charged': { warm: 'Charged', literal: 'Big jobs' },
  'energy.skip': { warm: 'Not today', literal: 'Skip' },
  // §9.2: "never asked more than once a day, never mandatory, decays gracefully if
  // unset", and objection 2 accepts that users stop logging — so nothing depends on it,
  // and the surface says that rather than implying a cost to leaving it blank.
  'energy.explain': {
    warm: 'One tap, if you want. Nothing depends on it.',
    literal: 'Optional. No feature requires this.',
  },
  'energy.today': { warm: 'Today: {level}', literal: 'Today: {level}', slots: ['level'] },
  'energy.notSet': { warm: 'Nothing set today.', literal: 'Not set today.' },
  'energy.onceADay': {
    warm: 'Asked once a day at most. Never twice.',
    literal: 'Requested at most once per day.',
  },

  // -------------------------------------------------------------------------
  // Warm cap (product-concept §5) — a prompt, never a block
  // -------------------------------------------------------------------------
  'warmCap.prompt': {
    warm: 'That’s {count} warm already. Want to let one cool off?',
    literal: '{count} tasks are active. Set one to idle?',
    oneWarm: 'That’s 1 warm already. Want to let it cool off?',
    oneLiteral: '1 task is active. Set it to idle?',
    slots: ['count'],
  },
  'warmCap.keepAll': { warm: 'Keep them all warm', literal: 'Keep all active' },
  /**
   * THE THIRD ANSWER ON THE TRADE PROMPT (`RULINGS R183`; product-concept §5, objection 2).
   *
   * §5 objection 2 invents storm mode for the mid-crisis day this prompt appears on, and until
   * R183 the only door to it was Settings → Today → Storm. The row is at EQUAL weight with the
   * other two answers, so its words are at equal weight too: it is phrased as an answer to the
   * question above it, not as an apology for needing one.
   *
   * IT DESCRIBES THE DAY, NEVER THE PERSON — R183's own copy note, and design-direction §1's law.
   * "One of those days" is a statement about today; "if you are struggling" would have been a
   * statement about the user, and it is the sentence this entry exists to not be.
   *
   * THE LITERAL REGISTER CANNOT SAY "STORM", and that is the lint rule rather than a preference:
   * `storm` is in `lint.ts`'s `METAPHOR_TERMS`, so the literal form uses the word
   * `settings.stormDeclare` already established for the same act — triage. The two doorways
   * therefore read as the same act in both registers, which is what makes the second one
   * recognisable to a user who has met the first.
   */
  'warmCap.declareStorm': { warm: 'One of those days. Declare a storm', literal: 'Mark today as triage' },
  /**
   * WHAT DECLARING IT DOES, at the control (design-direction §6: an answer whose consequence is
   * only discoverable by pressing it is not an answer).
   *
   * The same two facts `settings.stormExplain` gives on the settings page, in the same order, so
   * the second doorway makes the same promise as the first. It states the lift, not a benefit:
   * §5's guarantee is that the cap stops asking, and nothing here claims the day gets easier.
   */
  'warmCap.declareStormHint': {
    warm: 'Today becomes triage. The limit lifts and this stops asking.',
    literal: 'Marks today as triage. The active limit lifts and this prompt stops.',
  },
  'warmCap.stormMode': {
    warm: 'Storm mode: everything is triage today. No judgment.',
    literal: 'Triage mode: the active limit is suspended.',
  },

  // -------------------------------------------------------------------------
  // Lapse and return (product-concept §11) — a designed moment, never a wall
  // -------------------------------------------------------------------------
  /**
   * The greeting no longer counts (critique 3.2).
   *
   * product-concept §11's own sentence is "good to see you — here's the one warm thing that
   * still matters", and the app shipped it verbatim above SIX warm cards: the lapsed fixture
   * derives one deadline-warm loop plus five filled in by the warm-set floor. A numeric claim
   * the surface immediately contradicts is worse than no claim, and the literal register — the
   * one whose entire purpose is precision for people who will notice — was the version that
   * said "your most recent active task:" and then listed six. Its colon had no object at all,
   * because the next thing on screen was a bulk-disposal question (critique 3.1).
   *
   * So the greeting states what is true of the surface in every case: this is where you were.
   * §11's "one warm thing" is still delivered — by the bulk-compost offer, which now actually
   * clears the stale set (core `bulkCompostOffer`), so accepting makes the sentence true
   * rather than asserting it and hoping.
   *
   * THAT LAST ARGUMENT DOES NOT HOLD, AND THE SECOND CLAUSE IS BACK
   * (`docs/roadmap/onboarding.md` item 7). §11's sentence is two clauses — "good to see you —
   * here's the one warm thing that still matters" — and the app shipped the first and dropped
   * the second. The bulk-compost offer cannot cover it: that offer is about the OLD set, the
   * things that stopped mattering, and it is a question the user answers about disposal. §11's
   * second clause is about the ONE THAT STILL MATTERS, which is a different set, a different
   * direction and not a question at all. A returning user got a greeting, then a disposal
   * prompt, and nothing pointing at the work.
   *
   * IT POINTS AT THE LAYOUT AND ASSERTS NO RANKING, which is the whole of the judgement here.
   * "The one that still matters" is a claim about the user's life; the app cannot know what
   * matters and must not say it does. What it CAN state is the grammar of its own surface, which
   * is fixed by law: warm at the top, cooling below, compost downward (CLAUDE.md;
   * design-direction §4's vertical temperature axis). So the sentence says where the warmest
   * material sits, which is true by construction on every render and stays true whatever
   * `orderWarmSet` does inside the warm group — that function sorts on an active session, then
   * deadline prominence, then recognisability, then recency, none of which this line claims. It
   * also teaches the axis to the one user who has forgotten it, at the one moment they need it.
   *
   * THE LITERAL FORM CANNOT SAY "WARMEST" — `lint.ts` refuses metaphor in that register, and
   * rightly: "active" is the literal vocabulary this catalogue already uses for the same group
   * (`home.title`, `home.overCapRow`). It makes the same layout statement in the same shape.
   *
   * "THIS IS WHERE YOU LEFT THINGS" WENT, RATHER THAN BEING ADDED TO. The greeting is one line
   * beside a glyph on a 320pt phone (`ReturnWelcome.tsx`), and three clauses wrap to three lines
   * above the leading card that critique 3.1 fought to keep above the fold. The pointer says
   * everything the old second clause said and one thing more.
   */
  'return.greeting': {
    warm: 'Good to see you. The warmest one is at the top.',
    literal: 'Welcome back. The most active one is at the top.',
  },
  // critique 5.2: the warm register omitted the {count} the literal register used.
  'return.bulkCompostOffer': {
    warm: 'There are {count} old ones here. Want them tucked into compost so you start clean?',
    literal: '{count} tasks are long idle. Move them to the long-idle list?',
    oneWarm: 'There’s 1 old one here. Want it tucked into compost so you start clean?',
    oneLiteral: '1 task is long idle. Move it to the long-idle list?',
    slots: ['count'],
    allow: ['tense'],
    why: 'Names the compost move being offered in the same words the confirmation later uses ("tucked") — the premise for the question that follows (ADA review C5).',
  },
  'return.bulkCompostAccept': { warm: 'Yes, tuck it away', literal: 'Move to the long-idle list' },
  'return.bulkCompostDecline': { warm: 'Leave it as it is', literal: 'Keep as is' },
  // The offer is reversible by `rewarm` (core `settle`/`SettlementMarker`), so the
  // confirmation says what actually happened and nothing more.
  'return.bulkCompostDone': {
    warm: 'Tucked away. Nothing is thrown out.',
    literal: 'Moved to the long-idle list. Nothing is deleted.',
    allow: ['tense'],
    why: 'Acknowledges that loops were just tucked into compost — the bulk move\'s own outcome line (ADA review C5).',
  },
  /**
   * Undo, on the confirmation (critique 3.4).
   *
   * `acceptBulkCompost` appends `settled` markers and `rewarm` appends `unsettled`, so the
   * gesture was always losslessly reversible — but the only affordance was `loop.rewarm` on
   * each loop's own detail surface, i.e. four navigations and four taps to undo one accept,
   * immediately after a decision a returning user was asked to make in their first ten
   * seconds. The set is already in hand, so the reversal is one control.
   */
  'return.bulkCompostUndo': { warm: 'Bring them back', literal: 'Undo that' },
  'return.bulkCompostUndo.hint': {
    warm: 'Puts them back in front of you. Their history stays.',
    literal: 'Returns them to their previous state. History is kept.',
  },
  'return.bulkCompostUndone': {
    warm: 'Back in front of you.',
    literal: 'Returned to the previous state.',
  },

  // -------------------------------------------------------------------------
  // Notifications — invitations, with "quieter, please" on every one (§11)
  // -------------------------------------------------------------------------
  // critique 5.2: warm used only {place} while literal used both, so the count vanished
  // in the default voice.
  'notify.nearby': {
    warm: '{count} {place} loops are nearby if you want them.',
    literal: '{count} tasks are set to appear near {place}.',
    oneWarm: '1 {place} loop is nearby if you want it.',
    oneLiteral: '1 task is set to appear near {place}.',
    slots: ['place', 'count'],
  },
  /**
   * THE SCHEDULED PUSH ITSELF (`RULINGS R179`).
   *
   * `functions/handlers/scheduled-surfacing-evaluation.ts` built these two lines in the handler,
   * outside this package, so they had no literal register and the linter could not see them —
   * pre-existing, surfaced twice during the recurrence workstream, left alone both times. R179's
   * ruling is that the Function imports `@carn/copy` directly: `packages/copy` is a workspace
   * package and `functions/build.mjs` pre-bundles the API (`docs/DEVIATIONS.md` N4), so the
   * catalogue bundles in with it.
   *
   * SENDING A KEY WOULD NOT WORK, and that is the reason the string travels rather than the
   * reference: the OS renders a notification body *before* the app wakes, so there is nothing on
   * the device to resolve a key against. The **literal** register is what ships, per `R166` —
   * which does not relax the two-register requirement, because a hidden register is not a dead
   * register.
   *
   * IT NAMES NOTHING. No title, no count, no place — a lock screen is the one surface with no
   * privacy control in front of it, and the invitation is complete without saying which job it
   * is about. The user opens the app to find out, which is where every other reading of their
   * work already happens.
   *
   * IT NAMES WHAT IS ON OFFER, NEVER THE READER'S STATE (`docs/roadmap/onboarding.md` item 7).
   * The title read "This might be a good moment" and both bodies ended "ready whenever you are"
   * — claims about the user the app cannot know and did not measure, delivered to a lock screen
   * where they cannot be argued with. Nothing in `evaluateSurfacing` measures a moment's quality
   * or a person's readiness: it matches a condition the user themselves set on a loop, on the
   * rhythm axis (a clock condition) or the energy axis (a job-size condition). product-concept
   * §11 requires notifications to be invitations and gives the model — "the barn loops are
   * nearby if you want them" — which names the THING and its availability, and leaves the
   * wanting entirely to the reader. These do the same: what is on offer, why it is on offer now,
   * and the standing fact that leaving it costs nothing.
   *
   * "IF YOU WANT IT" IS §11'S OWN CLAUSE, kept verbatim from that sentence and already carried by
   * `notify.nearby`, which is the same gesture on the place axis. The two now read as one family.
   */
  'notify.surfacing.title': {
    warm: 'A loop is ready if you want it',
    literal: 'A task is set to appear now',
  },
  'notify.surfacing.body': {
    warm: 'One you set for a time or a day like this. Nothing changes if you leave it.',
    literal: 'A time or job-size condition you set matches now. Nothing changes if you leave it.',
  },
  'notify.quieter': { warm: 'Quieter, please', literal: 'Fewer notifications' },
  'notify.quieterConfirmed': {
    warm: 'Noted. Fewer of these.',
    literal: 'Notification frequency reduced.',
  },
  /**
   * THE SECOND ACTION ON EVERY NOTIFICATION (design-direction §3: the Intensity dial is
   * "adjustable any time from settings **and** from any notification").
   *
   * A DIFFERENT LEVER FROM `notify.quieter`, which stretches one class's cadence. This one
   * opens the dial that governs sensory load, for the user a bright moment has just ambushed.
   * It opens the settings page and writes NOTHING on its own: an accidental tap on a lock
   * screen must not change how the app looks (product-concept §11's interface stability).
   */
  'notify.intensity': { warm: 'Turn it down', literal: 'Open intensity settings' },

  /**
   * THE WAY BACK from "quieter, please" (`louderPlease` in @carn/core).
   *
   * The quieting gesture is on every notification and on every class block, and until this key
   * existed it had no inverse anywhere in the product: a user who tapped it three times on a lock
   * screen had silenced that class with nothing that could raise it again, because the on/off
   * switch deliberately carries `quietLevel` across (R36). product-concept §11's "full sensory
   * settings" is not a one-way ratchet.
   *
   * Deliberately NOT "Louder": nothing about this is volume — sound is off by default and stays a
   * separate switch (design-direction §5) — and the warm form mirrors "Quieter, please" so the two
   * read as one pair of opposites rather than two unrelated controls.
   */
  'notify.louder': { warm: 'More, please', literal: 'More notifications' },
  'notify.louderConfirmed': {
    warm: 'Noted. A few more of these.',
    literal: 'Notification frequency raised.',
  },

  /**
   * ASKING THE PHONE, FROM THE SCREEN THAT KNOWS IT MATTERS (docs/RULINGS.md R7, R72).
   *
   * R72 gave the surface an honest sentence for a refused permission (`notify.blocked.*`) and no
   * way to act on it: the only prompt in the app fires when a class is switched ON, and every
   * class a user cares about is already on by default — so a device that answered "no" once, or
   * answered before the user understood what was being asked, had a settings screen that
   * explained the problem and offered nothing. Saying what stopped you is half the job.
   *
   * TWO STATES, TWO CONTROLS, BECAUSE THEY DO DIFFERENT THINGS. `notify.enable` prompts (the OS
   * will still show its own dialog); `notify.openSettings` is for a phone that will not ask again,
   * where the only honest action is to hand the user to the OS surface that owns the answer.
   * Neither is offered while the permission is granted — a control that would do nothing is a
   * question the user has to answer (R12).
   */
  'notify.enable': { warm: 'Ask your phone to allow these', literal: 'Enable notifications' },
  'notify.enableExplain': {
    warm: 'Your phone decides. It will ask you once.',
    literal: 'The system will show its own permission prompt.',
  },
  /**
   * The answer, said out loud, because saying yes REMOVES the button that was pressed — and a
   * control vanishing with nothing in its place is indistinguishable from a control that broke.
   */
  'notify.permissionGranted': {
    warm: 'Your phone is letting these through now.',
    literal: 'Notification permission is granted.',
  },
  'notify.openSettings': { warm: 'Open your phone settings', literal: 'Open system settings' },
  'notify.openSettingsExplain': {
    warm: 'Your phone will not ask again, so it has to be turned on there.',
    literal: 'The system will not prompt again. Notifications must be enabled in system settings.',
  },

  // One key per member of `NOTIFICATION_CLASSES` in @carn/core. The classes exist
  // because one global switch was a shipping bug (core settings.ts): three taps of
  // "quieter, please" on a compost invitation must never disable a vet-appointment
  // reminder, which product-concept §5 promises "calendar-grade treatment". The names
  // below have to make that difference obvious BEFORE the user taps anything.
  'notify.class.deadline': { warm: 'Things the world cares about', literal: 'Fixed deadlines' },
  // States the RULE, not a delivery. "These arrive whatever else you quiet" / "Always
  // delivered, whatever else is quieted" was the most consequential untrue sentence on these
  // surfaces: `mayNotify` exempts this class from every quieting route and nothing anywhere
  // delivers a notification (critique 5.2, core `NOTIFICATION_DELIVERY_WIRED`). What is true
  // today is the exemption, so that is what it says.
  'notify.class.deadline.explain': {
    warm: 'The vet appointment. Quieting anything else never quiets these.',
    literal: 'External commitments. No other setting quiets this class.',
  },
  'notify.class.surfacing': { warm: 'Nearby and on the day', literal: 'Place and time reminders' },
  'notify.class.surfacing.explain': {
    warm: 'An invitation when you are near, or when the day comes round.',
    literal: 'Delivered by place or by time. Optional.',
  },
  /**
   * THE PRECONDITION ON THE PLACE HALF, STATED (docs/RULINGS.md R93).
   *
   * `notify.class.surfacing.explain` says "Delivered by place or by time", and with
   * `NOTIFICATION_DELIVERY_WIRED` true that reads as a promise about both halves. The place half
   * has a precondition the user controls and cannot guess: nothing is watching for an arrival
   * until a shop is attached to a list (`StoreAttachRow`, R8), because that press is the only
   * thing that registers an OS region.
   *
   * This is R36's situation — two individually correct facts that jointly form a false statement
   * — and R36's resolution was that a surface must not assert a delivery that cannot happen. The
   * class is NOT defaulted off: place invitations are product-concept §8.1's primary organising
   * principle ("a companion that taps your shoulder"), and turning the headline mechanism off to
   * avoid an awkward sentence is the wrong repair.
   *
   * WHAT IT DOES NOT SAY, deliberately. It does not say the class is inert, and it does not
   * promise that attaching a shop is SUFFICIENT — it states a necessary condition and which half
   * of the class carries it. It becomes self-resolving the moment the attach control is used.
   */
  'notify.class.surfacing.needsStore': {
    warm: 'Nearby invitations need a shop attached to a list first. The ones on the day do not.',
    literal: 'Place reminders require a store attached to a shopping list. Time reminders do not.',
  },
  'notify.class.compost': { warm: 'Compost invitations', literal: 'Long-idle reviews' },
  'notify.class.compost.explain': {
    warm: 'A look at what has settled, every so often.',
    literal: 'A periodic review of long-idle tasks.',
  },
  // Named as the DELIVERY of the prompt, not the prompt itself: `Settings.sortingPrompt` is
  // default-off (product-concept §7.2) while this class is default-on, and a row reading
  // "Sorting prompt — On" next to a prompt that is off would be a false statement.
  'notify.class.sorting': { warm: 'Sorting reminders', literal: 'Inbox review reminders' },
  'notify.class.sorting.explain': {
    warm: 'Only arrive when the daily inbox prompt is on. That is off by default.',
    literal: 'Delivered only when the daily inbox prompt is enabled. Disabled by default.',
  },
  /** Rendered for every class in `PROTECTED_NOTIFICATION_CLASSES` (core settings.ts). */
  'notify.protected': {
    warm: 'Always on. Quieting anything else never touches this one.',
    literal: 'Cannot be turned off. Other settings do not affect it.',
  },

  /**
   * CONFIGURED versus WIRED. Two sets of the same four facts, and core's
   * `NOTIFICATION_DELIVERY_WIRED` decides which set the surface renders.
   *
   * The `*Set` forms below are the honest ones for today: nothing in Carn schedules or
   * delivers a notification, so a live budget in tabular figures, a quiet-hours window and
   * "still delivered" were five sentences describing a mechanism with zero callers
   * (critique 5.2). They describe the SETTING, which is real and which the user genuinely
   * controls. The delivery-tense forms above and below stay in the catalogue because they
   * become true the day a scheduler lands, and the flag flips them back in one place.
   *
   * `notify.notDelivered` is the `share.localOnly` of this section — and, unlike
   * `share.localOnly` was, it is rendered in body text at the top rather than as the quietest
   * line under a louder contradiction.
   */
  'notify.notDelivered': {
    warm: 'Nothing is sent yet. These are the settings for when it is.',
    literal: 'No notifications are delivered yet. These settings are stored for when they are.',
  },
  'notify.budget': {
    warm: 'At most {count} a day',
    literal: 'Up to {count} per day',
    oneWarm: 'At most 1 a day',
    oneLiteral: 'Up to 1 per day',
    slots: ['count'],
  },
  'notify.budgetSet': {
    warm: 'Set to {count} a day',
    literal: 'Limit set to {count} per day',
    oneWarm: 'Set to 1 a day',
    oneLiteral: 'Limit set to 1 per day',
    slots: ['count'],
  },
  'notify.classOff': {
    warm: 'Off. Nothing from this one.',
    literal: 'Off. None of these are delivered.',
  },
  'notify.quietHours': { warm: 'Quiet hours', literal: 'Quiet hours' },
  'notify.quietHoursRange': {
    warm: 'Nothing between {from} and {to}.',
    literal: 'No notifications between {from} and {to}.',
    slots: ['from', 'to'],
  },
  'notify.quietHoursRangeSet': {
    warm: 'Set to nothing between {from} and {to}.',
    literal: 'Window set to {from} until {to}.',
    slots: ['from', 'to'],
  },
  'notify.quietHoursProtected': {
    warm: 'Things the world cares about still come through.',
    literal: 'Fixed deadlines are still delivered.',
  },
  'notify.quietHoursProtectedSet': {
    warm: 'Things the world cares about are exempt from the window.',
    literal: 'Fixed deadlines are exempt from this window.',
  },

  /**
   * THE QUIET WINDOW IS EDITABLE (RULINGS R45), so it needs two control labels and a word for
   * the empty window.
   *
   * These are the accessible names of two `StepperRow`s in whole hours. There is no time picker
   * and there will not be one — it would be the only picker in the app, and the stepper
   * vocabulary this surface already owns (warm cap, text size, line height) solves it.
   * `notify.quietHoursNone` is what the surface says when the two hours meet: `withinQuietHours`
   * treats `from === to` as no window at all, so that state is reachable by stepping and has to
   * be sayable.
   */
  'notify.quietStart': { warm: 'Quiet start', literal: 'Quiet hours start' },
  'notify.quietEnd': { warm: 'Quiet end', literal: 'Quiet hours end' },
  'notify.quietHoursNone': {
    warm: 'No quiet hours. Move these apart to have some.',
    literal: 'No quiet window is set. Set two different hours to define one.',
  },

  /**
   * WHY NOTHING IS BEING SENT, WHEN NOTHING IS (RULINGS R72).
   *
   * `ScheduleOutcome.blocked` in `apps/mobile/src/notifications/` has exactly two members, and
   * these are their sentences. A control that fails silently is worse than one that says what
   * stopped it — and this is the per-DEVICE half of the honesty that
   * `NOTIFICATION_DELIVERY_WIRED` cannot express: the flag says a scheduler exists, and a
   * refused permission is still a phone that delivers nothing.
   *
   * Both state that the settings survive, because the user's instinct on reading either sentence
   * is "have I lost what I set?" — and they have not.
   */
  'notify.blocked.noPermission': {
    warm: 'Your phone has not allowed notifications, so none are sent. What you set here is kept.',
    literal: 'Notification permission is not granted, so none are delivered. These settings are stored.',
  },
  'notify.blocked.unavailable': {
    warm: 'This build cannot send notifications. What you set here is kept.',
    literal: 'Notifications are unavailable in this build. These settings are stored.',
  },

  // -------------------------------------------------------------------------
  // What a notification SAYS when it arrives (RULINGS R72)
  // -------------------------------------------------------------------------
  // A notification is a different register of utterance from a screen label: it arrives
  // uninvited, so it must say why it came. Before R72 the scheduler borrowed in-app keys, and
  // every rung of R33's ladder rendered `loop.hardDate` — so the reminder a week out and the
  // reminder in the hour itself read IDENTICALLY. "Escalating" is product-concept §5's own word,
  // and identical text does not escalate.
  //
  // Four rungs, because R33's ladder has four: a week, a day, two hours, the hour itself. The
  // lead time is the only thing that changes; the loop's own title is the notification's TITLE
  // and is never a catalogue string. A custom offset falls back to `loop.hardDate`, which claims
  // no lead time and is therefore true of any rung.
  //
  // NONE OF THESE COUNTS A DAY OF ABSENCE. product-concept §2's anti-thesis forbids "You haven't
  // opened Carn in 3 days!" outright, and §5's law is that nothing is ever taken away by time.
  // Every one of them is an invitation or a plain fact about the world's own clock.
  'notify.deadline.lead.week': {
    warm: 'A week from now — {when}.',
    literal: 'One week from now. {when}.',
    slots: ['when'],
  },
  'notify.deadline.lead.day': {
    warm: 'Tomorrow — {when}.',
    literal: 'Due tomorrow. {when}.',
    slots: ['when'],
  },
  'notify.deadline.lead.hours': {
    warm: 'In two hours — {when}.',
    literal: 'Due in two hours. {when}.',
    slots: ['when'],
  },
  'notify.deadline.lead.now': {
    warm: 'This is the hour — {when}.',
    literal: 'Due now. {when}.',
    slots: ['when'],
  },

  /**
   * COUNT-FREE ON PURPOSE (RULINGS R72).
   *
   * `compost.reviewInvite` carries `{count}`, and this one must not: compost membership is
   * derived from idleness, so a count fixed a fortnight before the notification fires would be
   * read aloud as today's. A false number in a notification is worse than no number, and the
   * user finds the real one on the surface one tap later.
   */
  'notify.compost.invite': {
    warm: 'A few loops are resting. Have a look when you like.',
    literal: 'Some tasks are long idle. Open the review when you choose.',
  },
  /**
   * MAY carry a count (RULINGS R72), because an inbox cannot change while the app is closed:
   * captures and sorting both happen inside Carn, and every launch reconciles the schedule.
   */
  'notify.sorting.invite': {
    warm: '{count} things are waiting whenever you want them.',
    literal: '{count} items are in the inbox. Review them when you choose.',
    oneWarm: '1 thing is waiting whenever you want it.',
    oneLiteral: '1 item is in the inbox. Review it when you choose.',
    slots: ['count'],
  },

  // -------------------------------------------------------------------------
  // Settings
  // -------------------------------------------------------------------------
  'settings.intensity': { warm: 'Intensity', literal: 'Intensity' },
  // The warm register said the dial controls "sound". It does not, and it must not: sound is
  // silent by default and stays a separate switch, because design-direction §5 is explicit
  // that "many autistic users are actively harmed by uninvited audio" — a dial that turned
  // audio on as a side effect of asking for more colour would be the opposite of the control
  // §3 promises. The six channels are colour, texture, movement, vibration, celebrations and
  // wording; the literal register already had it right.
  // Both registers now name the SAME six channels, and neither mentions sound. The warm form
  // omitted celebrations and texture while the literal form omitted texture, so the two
  // registers described two different products (critique 2.2). The channel list is
  // `INTENSITY_CHANNELS` in @carn/tokens and `apps/mobile/test/settings-copy.test.ts` pins
  // these two sentences against it, so a seventh channel cannot arrive without the
  // description noticing.
  'settings.intensityExplain': {
    // "edges" is the `contrast` channel (RULINGS R10). The dial's own description has to name
    // every channel it controls — apps/mobile/test/settings-copy.test.ts asserts exactly that,
    // derived from `INTENSITY_CHANNELS`, so a channel cannot be added without a word for it.
    warm: 'One dial for colour, edges, texture, movement, vibration, celebrations and wording.',
    literal: 'Controls colour, edges, texture, movement, vibration, celebrations and wording.',
  },
  'settings.intensityMixer': { warm: 'Set each one yourself', literal: 'Per-channel settings' },

  // -------------------------------------------------------------------------
  // The Intensity dial and the mixer beneath it (design-direction §3 and its
  // objection 1). `@carn/tokens` holds the same names as design-time constants;
  // these are the shipped, dual-register strings — literal mode must cover the
  // dial too, or the single most important control in the app is untranslated.
  // -------------------------------------------------------------------------
  'intensity.dialHint': {
    warm: 'Move it and the app changes as you go.',
    literal: 'Changes apply immediately.',
  },
  'intensity.level.0': { warm: 'Still', literal: 'Minimal' },
  'intensity.level.1': { warm: 'Quiet', literal: 'Low' },
  // "Balanced", not "Steady": `energy.steady` is also "Steady" in the warm register, and both
  // render on the settings surface at once — a collision in the DEFAULT register that the
  // critique's own list of five missed and the surface manifest in lint.ts catches. §9.2 names
  // "steady" as the energy vocabulary by example; design-direction §3 does not name the dial's
  // midpoint at all, and `intensity.describe.2` already calls it balanced.
  'intensity.level.2': { warm: 'Balanced', literal: 'Medium' },
  'intensity.level.3': { warm: 'Lively', literal: 'High' },
  // "Max", not "Maximum" (RULINGS R16). The five literal names render together in the mixer's
  // thirty options and under the dial, and "Minimal / Low / Medium / High / Maximum" is wider
  // than "Still / Quiet / Balanced / Lively / Vivid" — which is what reflowed the mixer rows at
  // 320pt in the register a literal-minded user is most likely to be on. It is also the shortest
  // form that is still a plain functional label, which is what product-concept §11 asks literal
  // mode to be.
  'intensity.level.4': { warm: 'Vivid', literal: 'Max' },
  // "No sound or vibration" implied the higher levels have sound. They do not, at any level,
  // ever: sound is a separate switch, off by default, because design-direction §5 is explicit
  // that "many autistic users are actively harmed by uninvited audio" (critique 2.2).
  'intensity.describe.0': {
    warm: 'Near-monochrome. No movement. No vibration. Plain words.',
    literal: 'No colour, movement or vibration. Plain words.',
  },
  'intensity.describe.1': {
    warm: 'A little colour. Small movements.',
    literal: 'Low colour. Small movements.',
  },
  'intensity.describe.2': {
    warm: 'Balanced colour and movement.',
    literal: 'Medium colour and movement.',
  },
  'intensity.describe.3': {
    warm: 'Fuller colour. Movement you notice.',
    literal: 'High colour. Visible movement.',
  },
  // NOT "full colour" / "maximum colour". docs/DEVIATIONS.md D7 caps the temperature ramp's
  // separation budget and D6 accepts a dark olive accent in the light scheme, so what the top
  // of the colour channel actually buys is the accent and a slight warmth on the cooler cards —
  // two of the five temperature surfaces barely move (critique 2.3). The copy now describes
  // that rather than contradicting the two deviations.
  'intensity.describe.4': {
    warm: 'The strongest colour and highlight. Lively movement. Full celebrations.',
    literal: 'The strongest colour, movement and celebration this dial offers.',
  },
  'intensity.channel.colour': { warm: 'Colour', literal: 'Colour' },
  // RULINGS R10. Named for what the user sees change — the edges of things — rather than for the
  // measurement, because "contrast" is also what a reader will assume governs text, and this
  // channel deliberately never touches text (design-direction §3).
  'intensity.channel.contrast': { warm: 'Edges and outlines', literal: 'Edges and outlines' },
  'intensity.channel.decor': { warm: 'Texture and depth', literal: 'Texture and depth' },
  'intensity.channel.motion': { warm: 'Movement', literal: 'Movement' },
  'intensity.channel.haptics': { warm: 'Vibration', literal: 'Vibration' },
  'intensity.channel.celebration': { warm: 'Celebrations', literal: 'Celebrations' },
  'intensity.channel.voice': { warm: 'Wording', literal: 'Wording' },
  /**
   * The mixer option that means "I have not overridden this channel".
   *
   * The literal register read "Follow the main setting", which is a five-word instruction where a
   * one-word STATE belongs: it is the first option in six radiogroups of six, so a reader working
   * down the mixer met the same sentence six times, and it was also the longest option in every
   * group — the reflow R16 already trimmed `intensity.level.4` for. "Default" is what the option
   * IS, and product-concept §11 defines literal mode as "precise functional labels".
   *
   * The warm register keeps "Follow the dial", which names the control the value comes from and is
   * the idiom this register exists for.
   *
   * `WordingSettings` renders this SAME key as its first option, so both registers change in both
   * places at once — which is the point of there being one key: "I have not overridden this" is one
   * concept, and it is said one way wherever it appears.
   */
  'intensity.channelAuto': { warm: 'Follow the dial', literal: 'Default' },
  /**
   * The mixer's accessible names, and every announcement of a changed value.
   *
   * The mixer is six radiogroups of six options, and every one of the 36 was named from a
   * vocabulary of six words with no channel on it — so VoiceOver read "Steady" six times and
   * "Vivid" six times, with the channel only on the group container, which iOS announces on
   * entry and switch-control users traversing element by element never hear at all
   * (critique 4.2). `controls.tsx` already solved exactly this for the steppers
   * ("Decrease Text size"); this is the same rule reaching the mixer.
   *
   * It doubles as the shape of every value announcement on the surface ("Sound: On",
   * "Text size: 150%"), because "this control is now that value" is one sentence in both
   * jobs and two keys saying it identically would be two keys to keep in step.
   */
  'settings.labelledValue': {
    warm: '{label}: {value}',
    literal: '{label}: {value}',
    slots: ['label', 'value'],
  },
  // Shown when an OS accessibility switch has taken a channel below the dial. The OS
  // switch wins (tokens/intensity.ts) and the UI must say so rather than look broken.
  'intensity.systemOverride': {
    warm: 'Your phone settings are holding this one down.',
    literal: 'A system accessibility setting is limiting this channel.',
  },

  /**
   * THE WORDING CONTROL IS NOT A SWITCH ANY MORE, and this is why.
   *
   * `Settings.literal` is a boolean and the register it claims to set is derived from a
   * FIVE-LEVEL channel: `CopyProvider` renders plain words when `theme.literal ||
   * theme.levels.voice === 0`. So at the reference rendering — dial 0, nothing else touched —
   * the whole app rendered plain words while a switch labelled "Plain words" read "Off", and
   * pressing it changed nothing visible either way (critique 3.2). A boolean layered over a
   * five-level channel disagrees with it by construction, and a control that lies about its
   * own state is worse than no control.
   *
   * Three options over the `voice` channel cannot disagree with it: follow the dial, or pin it
   * to plain, or pin it to playful. `settings.wording*Now` states the effective register under
   * the control, so "Follow the dial" is never ambiguous about what the dial is currently
   * giving.
   */
  'settings.wording': { warm: 'Playful or plain words', literal: 'Wording style' },
  'settings.literalMode': { warm: 'Plain words', literal: 'Plain words' },
  'settings.playfulWords': { warm: 'Playful words', literal: 'Metaphor labels' },
  'settings.literalModeExplain': {
    warm: 'Swaps every playful label for a plain one.',
    literal: 'Uses functional labels instead of metaphors.',
  },
  'settings.wordingPlainNow': {
    warm: 'Right now: plain words.',
    literal: 'Currently showing plain words.',
  },
  'settings.wordingPlayfulNow': {
    warm: 'Right now: playful words.',
    literal: 'Currently showing metaphor labels.',
  },
  'settings.density': { warm: 'How much to show', literal: 'Information density' },
  // NOT "Minimal" and "Maximum": those are `intensity.level.0` and `intensity.level.4` in the
  // literal register, and all four render on the settings surface at the same time — so a
  // VoiceOver user swiping through heard "Minimal, radio" twice, 700pt apart, with the
  // disambiguating name only on the group container (critique 10.4). `lint.ts`'s
  // `SURFACE_MANIFESTS` lists `settings.density.` for exactly this reason and holds these
  // three against every other label on that surface.
  //
  // AND NOT "COMPACT" / "EVERYTHING" EITHER, WHICH IS WHY THESE CHANGED (docs/DEVIATIONS.md
  // Q1). Those two labelled the ends the wrong way round: `sparse` is the ROOMIEST rendering
  // (`DENSITY` in @carn/tokens gives it 20pt of card padding against `full`'s 12pt), so
  // choosing "Compact" made every card bigger and choosing "Everything" made everything
  // smaller. The internal logic was coherent on its own terms — sparse means fewer things, so
  // more air around each — but "compact" is a universally understood SPACING word and it
  // fought the behaviour, in the register that ships by default (`DEFAULT_VOICE_LEVEL`,
  // docs/DEVIATIONS.md D30). "Show everything" was a second, separate false statement: it
  // promised content the setting did not reach.
  //
  // These three name the one axis both halves of the setting now run along — how much lands
  // on the screen — so no reading of them is inverted. Higher means more items AND tighter
  // spacing, which are the same direction; there is no word here that can be read as
  // promising the opposite of what it does, and none of them claims completeness the quiet
  // rows do not deliver (`DENSITY_NAMED_LOOPS`, @carn/tokens).
  'settings.density.sparse': { warm: 'Just the warm set', literal: 'Least on screen' },
  'settings.density.balanced': { warm: 'A bit more on screen', literal: 'More on screen' },
  'settings.density.full': { warm: 'As much as fits', literal: 'Most on screen' },

  /**
   * `Settings.labels` (design-direction §6, CLAUDE.md; `LabelDensity` in `@carn/core`).
   * Applies to navigation chrome and repeated controls — back, close, the tab bar, add,
   * remove, share, undo. A destructive confirmation, the Interrupt Button, first-run and
   * anything that is the only explanation of a consequence opt out and are unaffected by
   * this setting (see `Waymark`'s `wordsAreLoadBearing` prop, `@carn/ui`).
   *
   * NAMES THE AXIS, NOT ONE OF ITS VALUES — the same rule its sibling `settings.density`
   * follows with "How much to show" over `.sparse`/`.balanced`/`.full`. This heading used to
   * read "Icons and words", one keystroke from `.iconAndWords`'s own "Icon and word", sitting
   * directly above it on the settings surface — the exact "Minimal"/"Maximum" species this
   * file's `SURFACE_MANIFESTS` header (`lint.ts`) exists to catch, missed only because the
   * manifest registered the `.` children and not this bare key (now fixed there too). Reworded
   * on `settings.theme`'s "Light or dark" model: name the choice, don't restate one option of
   * it.
   */
  'settings.labels': { warm: 'Icons, words, or both', literal: 'Icon and word choice' },
  'settings.labels.iconAndWords': { warm: 'Icon and word', literal: 'Icon and label' },
  'settings.labels.wordsOnly': { warm: 'Words only', literal: 'Text only' },
  'settings.labels.iconOnly': { warm: 'Icon only', literal: 'Icon only' },

  /**
   * `Settings.showEmptySections` (design-direction §4's density axis; RULINGS R132). A switch,
   * so the label is the THING that is on or off — the shape `settings.imagery` uses — and not a
   * verb, because "Hide empty sections — On" makes the reader resolve two negatives to work out
   * what they are looking at.
   *
   * IT NAMES SECTIONS, NOT ITEMS, in both registers. "Empty" alone on a list screen is one word
   * away from being read as the items, and the setting has no opinion about those at all.
   */
  'settings.emptySections': {
    warm: 'Sections with nothing in them',
    literal: 'Empty sections',
  },
  'settings.warmCap': { warm: 'How many stay warm', literal: 'Active task limit' },
  'settings.sound': { warm: 'Sound', literal: 'Sound' },
  'settings.soundExplain': {
    warm: 'Off unless you want it.',
    literal: 'Disabled by default.',
  },
  'settings.reading': { warm: 'Reading', literal: 'Text and reading' },
  'settings.textSize': { warm: 'Text size', literal: 'Text size' },
  /**
   * The coarse path. `READING_BOUNDS.textScale` runs 0.85 → 2.5 in steps of 0.1, so reaching the
   * largest text size was seventeen presses with no spoken confirmation between them — worse
   * than the slider the no-sliders rule replaced, for the one person the setting exists for
   * (critique 10.2). Four labelled stops are one tap and announce as a selection.
   */
  'settings.textSizePresets': { warm: 'Jump to a size', literal: 'Preset text sizes' },
  'settings.letterSpacing': { warm: 'Letter spacing', literal: 'Letter spacing' },
  'settings.bolderText': { warm: 'Bolder text', literal: 'Bolder text' },
  /**
   * design-direction §4's "family alternates", the fourth reading lever beside size, spacing
   * and weight (docs/DEVIATIONS.md F1, F3).
   *
   * THE HINTS DESCRIBE, AND THEY DO NOT JUDGE. Each says what the face DOES to the letters and
   * stops there. There is deliberately no line about the evidence for dyslexia-oriented faces
   * being mixed: §4 asks the product to "offer the levers rather than promising the cure", and
   * a description that promises nothing already satisfies that — the section's own
   * `settings.readingExplain` ("Effects differ between people") carries the posture for all
   * four levers, and a per-option caveat on one of them reads as the app arguing with a choice
   * the user is entitled to make (product-owner request; F3).
   *
   * Every option is named for the face it actually draws. `comic` is Comic NEUE — Comic Sans
   * MS is Microsoft's and is not shipped — and naming it for its lookalike would be the one
   * kind of dishonesty a settings screen cannot afford.
   */
  'settings.fontFamily': { warm: 'Typeface', literal: 'Typeface' },
  'settings.fontFamily.sans': { warm: 'Lexend', literal: 'Lexend' },
  'settings.fontFamily.sansHint': { warm: 'The usual one.', literal: 'The default typeface.' },
  'settings.fontFamily.system': { warm: 'Your phone’s own', literal: 'System default' },
  'settings.fontFamily.systemHint': {
    warm: 'Whatever your phone uses everywhere else.',
    literal: 'The typeface your device uses for its own interface.',
  },
  'settings.fontFamily.serif': { warm: 'Serif', literal: 'Serif' },
  'settings.fontFamily.serifHint': {
    warm: 'Letters with feet. Your phone’s own.',
    literal: 'The serif typeface your device provides.',
  },
  'settings.fontFamily.dyslexic': { warm: 'OpenDyslexic', literal: 'OpenDyslexic' },
  'settings.fontFamily.dyslexicHint': {
    warm: 'Heavier at the bottom.',
    literal: 'Weighted letter bottoms.',
  },
  'settings.fontFamily.comic': { warm: 'Comic Neue', literal: 'Comic Neue' },
  'settings.fontFamily.comicHint': {
    warm: 'Rounded and handwritten. Like Comic Sans.',
    literal: 'A rounded, handwriting-style face in the Comic Sans shape.',
  },
  'settings.theme': { warm: 'Light or dark', literal: 'Colour scheme' },
  'settings.theme.system': { warm: 'Follow your phone', literal: 'Follow the system' },
  'settings.theme.light': { warm: 'Light', literal: 'Light' },
  'settings.theme.dark': { warm: 'Dark', literal: 'Dark' },

  /**
   * MATERIALS — the six Welsh-landscape hue-anchor sets, `@carn/core`'s `MATERIAL_IDS`
   * (design-direction §2, §3, §7; product-concept §12 as amended by `docs/DEVIATIONS.md` M11 and
   * RULINGS R182). Two are free — Drystone and Llanw — and four are paid.
   *
   * A material changes COLOUR ONLY — never contrast, text size or motion (`materials.ts`'s own
   * doc comment: every ramp is solved to the Drystone stop's exact WCAG relative luminance, so
   * nothing downstream that carries a contrast obligation ever reads the material's own colour).
   * `material.explain` says this plainly because it is the whole of what makes a PAID colour
   * choice compatible with "sensory and accessibility settings are free forever, categorically"
   * (`settings.freeForever`, above) — the promise this section sits directly beneath.
   *
   * Names are the Welsh nouns the materials are drawn from (`drystone` itself is the one
   * English id, because a drystone wall has no separate Welsh gloss here); `.gloss` is the
   * plain English word, carried as its own key so the Welsh name is never the only
   * identification on screen (literal mode in particular should never require the reader to
   * already know Welsh). `.detail` is one concrete sentence of what the material looks like —
   * not a pitch, since the free materials (Drystone and, since R182, Llanw) get exactly the same
   * shape of sentence as the four paid ones.
   */
  'settings.material': { warm: 'Your colours', literal: 'Colour material' },
  'material.explain': {
    warm: 'Changes colour only. Contrast, text size and movement stay exactly where you set them.',
    literal: 'Changes colour only. Contrast, text size and motion settings are unaffected.',
  },
  'material.current': {
    warm: 'Now: {name}.',
    literal: 'Currently: {name}.',
    slots: ['name'],
  },
  /**
   * docs/DEVIATIONS.md M8: "the gate must never read as a wall." States the fact (Plus) and
   * the working path (preview it, whenever) in the same shape `plus.gate.*` already uses —
   * never a bare "Locked".
   */
  'material.locked': {
    warm: 'Part of Carn Plus. Have a look whenever you like.',
    literal: 'Included with Carn Plus. Preview it whenever you like.',
  },
  'material.preview.active': {
    warm: 'This is a preview of {name}.',
    literal: 'You are previewing {name}.',
    slots: ['name'],
  },
  'material.preview.keep': { warm: 'Get this one', literal: 'Get this material' },
  'material.preview.putBack': { warm: 'Put it back', literal: 'Stop previewing' },
  /**
   * product-concept §12 ethics constraints forbid fake-urgency timers; `materialPreviewable`
   * in `@carn/core` is a function that always returns true precisely so this promise cannot
   * quietly grow a clock later. Said here so the person looking at a preview reads it, not
   * just the type system.
   */
  'material.preview.explain': {
    warm: 'No time limit on this. Nothing changes until you choose it.',
    literal: 'This preview has no time limit. Nothing changes until you choose it.',
  },

  'material.drystone.name': { warm: 'Drystone', literal: 'Drystone' },
  'material.drystone.gloss': { warm: 'wall', literal: 'wall' },
  'material.drystone.detail': {
    warm: 'Slate, moss, wool and oak, with a gorse-yellow accent.',
    literal: 'Slate grey, moss green, wool cream and oak brown, with a gorse-yellow accent colour.',
  },
  'material.calch.name': { warm: 'Calch', literal: 'Calch' },
  'material.calch.gloss': { warm: 'limestone', literal: 'limestone' },
  'material.calch.detail': {
    warm: 'Bleached bone and sea light, bright and high-key, with a steel-blue sea holly accent.',
    literal: 'Pale bone white and sea-light tones, bright and high-key, with a steel-blue accent colour.',
  },
  'material.glo.name': { warm: 'Glo', literal: 'Glo' },
  'material.glo.gloss': { warm: 'coal', literal: 'coal' },
  'material.glo.detail': {
    warm: 'Blue-black and dark, built for low light, with a verdigris accent.',
    literal: 'Dark blue-black tones, designed dark-first, with a verdigris accent colour.',
  },
  'material.grug.name': { warm: 'Grug', literal: 'Grug' },
  'material.grug.gloss': { warm: 'heather', literal: 'heather' },
  'material.grug.detail': {
    warm: 'Bilberry and heather shadow, purple the whole way through, with a magenta accent.',
    literal: 'Bilberry purple and heather-shadow tones, with a magenta accent colour.',
  },
  'material.llanw.name': { warm: 'Llanw', literal: 'Llanw' },
  'material.llanw.gloss': { warm: 'tide', literal: 'tide' },
  'material.llanw.detail': {
    warm: 'Silt, dove-grey and eelgrass. Cool and open, with a kingfisher-blue accent.',
    literal: 'Cool grey-blue and eelgrass green tones, with a kingfisher-blue accent colour.',
  },
  /**
   * Ffion — the foxglove. The one warm-chromatic corner of the set; see the note on the material
   * itself in `packages/tokens/src/materials.ts` for why its accent is violet and not pink.
   */
  'material.ffion.name': { warm: 'Ffion', literal: 'Ffion' },
  'material.ffion.gloss': { warm: 'foxglove', literal: 'foxglove' },
  'material.ffion.detail': {
    warm: 'Blush and mauve, the colour of a hedgerow in June, with a deep violet accent.',
    literal: 'Pale pink and mauve tones, with a deep violet accent colour.',
  },
  /**
   * Pride — the six-stripe flag (`RULINGS R196`; `docs/DEVIATIONS.md` `FD8`). Free, like Drystone
   * and Llanw.
   *
   * THE DETAIL STRING SAYS WHERE THE COLOURS GO, not what they mean. Every other `detail` in this
   * block names materials and an accent; this one names the same thing — which stripe is doing
   * which job — because that is the concrete fact about the rendering, and because §1's copy law
   * puts acknowledgement on the act rather than on the person. A string that told the user what
   * choosing it says about them would be praising the human, one register removed.
   *
   * NO EXCLAMATION MARK, no "celebrate", no second person plural. It is a colour scheme in a list
   * of colour schemes, described the way the other six are.
   */
  'material.pride.name': { warm: 'Pride', literal: 'Pride' },
  'material.pride.gloss': { warm: 'flag', literal: 'flag' },
  'material.pride.detail': {
    warm: 'The six stripes, laid down the temperature: red on top, violet at the compost, blue for the accent.',
    literal:
      'Pride flag colours mapped to states. Red for recent, orange for idle, violet for long idle, green for complete. Blue accent colour.',
  },

  /**
   * THE SURFACE TREATMENT (`RULINGS R188`).
   *
   * Same three-string shape as `material.*` above — name, gloss, one concrete sentence — and
   * deliberately so: they are the two halves of one question ("what does my app look like"), and
   * a user moving between the two sections should not have to learn a second vocabulary.
   *
   * NO `.locked` KEY HERE, and its absence is a statement rather than an oversight. Every
   * treatment is free at every tier — `packages/core/src/treatments.ts` records why charging for
   * one would break `docs/DEVIATIONS.md` M11's premise and unprice the four paid materials — so
   * there is no gate to word calmly, and a future agent adding a lock string should read that
   * file first.
   *
   * `.explain` carries the fact that matters to a sensory-sensitive reader and carries it FIRST:
   * this is decoration, it never touches text, and the dial turns it off. That is not
   * reassurance, it is the mechanism — `resolveField` returns no geometry at decor 0 whatever
   * this setting says.
   */
  /**
   * THE APP ICON (`RULINGS R197`).
   *
   * Its section reuses `material.*.name` and `material.*.gloss` for the rows — an icon IS a
   * material rendered as artwork (`@carn/core`'s `appIcons.ts`), so a second set of seven names
   * would be seven strings to keep in step with the seven one section above. Only the strings that
   * are about the ICON rather than about a colour live here.
   *
   * `appIcon.explain` STATES THE SYSTEM ALERT IN ADVANCE, and that is the whole reason it is a
   * sentence rather than a heading. iOS shows "You have changed the icon for Carn" on every change
   * and there is no supported way to silence it, so the choice is between warning the user and
   * letting a settings tap produce an unexplained system dialogue. product-concept §11's interface
   * stability promise is about the app being predictable; a dialogue nobody mentioned is not.
   *
   * NO PRICE, NO LOCK, NO BADGE — every icon is free at every tier (R197), including the four
   * whose material is not, so there is no `appIcon.locked` and there must not be one.
   */
  'settings.appIcon': { warm: 'Your home screen', literal: 'App icon' },
  'appIcon.explain': {
    warm: 'Which icon sits on your home screen. Any of them, whichever colours you use in the app.',
    literal:
      'Choose the icon shown on your home screen. All icons are available regardless of the material you use.',
  },
  'appIcon.systemAlert': {
    warm: 'Your phone will ask you to confirm the change. That prompt is the operating system, not Carn.',
    literal:
      'The operating system will show a confirmation dialog when the icon changes. This dialog is not part of Carn.',
  },
  'appIcon.current': {
    warm: 'Now: {name}.',
    literal: 'Currently: {name}.',
    slots: ['name'],
  },
  'appIcon.unavailable': {
    warm: 'This phone cannot change its icon, so the app keeps the one it came with.',
    literal:
      'This device does not support alternate app icons. The default icon remains in place.',
  },
  'settings.treatment': { warm: 'Your surfaces', literal: 'Surface treatment' },
  'treatment.explain': {
    warm: 'Pattern on the card itself. Your words keep their own background, and the Still end of the dial turns it off.',
    literal: 'Decorative pattern on the card above the text. Text contrast is unaffected. Set the decoration channel to Still to disable it.',
  },
  'treatment.current': {
    warm: 'Now: {name}.',
    literal: 'Currently: {name}.',
    slots: ['name'],
  },
  'treatment.matte.name': { warm: 'Matte', literal: 'None' },
  'treatment.matte.gloss': { warm: 'plain', literal: 'plain' },
  'treatment.matte.detail': {
    warm: 'No pattern. Flat colour, and where you start unless you choose otherwise.',
    literal: 'No decorative pattern. Flat card colour. This is the default.',
  },
  'treatment.smooth.name': { warm: 'Smooth', literal: 'Gradient' },
  'treatment.smooth.gloss': { warm: 'washed', literal: 'washed' },
  'treatment.smooth.detail': {
    warm: 'A soft wash of your accent colour, pulled down the card in streaks.',
    literal: 'A soft gradient in the accent colour, stretched vertically.',
  },
  'treatment.contour.name': { warm: 'Contour', literal: 'Contour lines' },
  'treatment.contour.gloss': { warm: 'mapped', literal: 'mapped' },
  'treatment.contour.detail': {
    warm: 'Map lines. The closer together they run, the warmer the loop.',
    literal: 'Contour lines, as on a map. Line spacing indicates the temperature.',
  },
  'treatment.posterise.name': { warm: 'Poster', literal: 'Flat bands' },
  'treatment.posterise.gloss': { warm: 'banded', literal: 'banded' },
  'treatment.posterise.detail': {
    warm: 'Hard bands of flat colour, with no blending between them.',
    literal: 'Four bands of flat colour with hard edges and no gradient.',
  },
  'treatment.dither.name': { warm: 'Dither', literal: 'Pixel dither' },
  'treatment.dither.gloss': { warm: 'pixelled', literal: 'pixelled' },
  'treatment.dither.detail': {
    warm: 'Square pixels in a chequered grid, the way old screens faked a gradient.',
    literal: 'A grid of square pixels in a chequered pattern, as used on early displays.',
  },

  /**
   * THE LIVE PREVIEW (`RULINGS R190`).
   *
   * `preview.explain` names the two things the panel CANNOT show, and it does so first, because a
   * preview that quietly omits a channel is worse than no preview: a user who sets motion to Still
   * and sees no change in the panel would reasonably conclude the setting is broken. Motion and
   * haptics are the two, and design-direction §5 is why neither can be faked in a still card.
   *
   * `preview.still` is the notice `TreatmentSettings` shows when the decor channel is at zero and a
   * treatment therefore draws nothing. It states the cause and where the control is, because the
   * dial lives on a different page (`docs/DEVIATIONS.md` D33) and a user cannot be expected to
   * connect two screens they cannot see at once. NO "GO TO" LINK: design-direction §4 fixes the
   * spatial grammar, and a shortcut that jumps between settings pages is the smart rearrangement
   * §4 forbids — the sentence names the place, the user keeps their own thread.
   */
  'preview.title': { warm: 'How it looks', literal: 'Preview' },
  'preview.explain': {
    warm: 'A real card, drawn with your settings as you change them. Movement and buzz are not shown here.',
    literal: 'A sample card rendered with your current settings. Motion and haptics are not represented.',
  },
  'preview.hide': { warm: 'Hide this', literal: 'Hide preview' },
  'preview.show': { warm: 'Show it', literal: 'Show preview' },
  'preview.loop.title': {
    warm: 'Fix the back gate',
    literal: 'Fix the back gate',
  },
  'preview.loop.next': {
    warm: 'Get two hinges from the hardware shop',
    literal: 'Get two hinges from the hardware shop',
  },
  'preview.still': {
    warm: 'Your dial is at Still, so this stays off. Turn up Decoration under Intensity to see it.',
    literal: 'The decoration channel is at level 0, so no pattern is drawn. Raise Decoration in Intensity settings to enable it.',
  },

  /**
   * product-concept §12: "Sensory and accessibility settings are free forever,
   * categorically." Said out loud on the surface itself, because the same section
   * records that this audience is "justifiably hostile to the 'ND tax'" — a promise
   * kept only in a pricing page is a promise nobody sees.
   *
   * SCOPED TO THE CONTROLS, NOT TO THE SCREEN. It used to promise "everything on this screen",
   * which is broader than §12 makes it and already untrue of the page furniture: the surface
   * links to the shared-lists surface, and §12 puts "multiple shared lists" behind Carn Plus
   * (critique 8.2). A promise that has to be walked back the day a paid tier ships is worse
   * than the narrower one §12 actually makes, and the narrower one is categorical.
   */
  'settings.freeForever': {
    warm: 'Every sensory and accessibility setting is free. It always will be.',
    literal: 'All sensory and accessibility settings are free permanently.',
  },

  'settings.on': { warm: 'On', literal: 'On' },
  'settings.off': { warm: 'Off', literal: 'Off' },

  /**
   * THE SETTINGS INDEX — the names of the nine places this area is now divided into
   * (docs/DEVIATIONS.md D33).
   *
   * Settings was one scroller about nine screenfuls long, and design-direction §4's fixed spatial
   * grammar is what made that a problem rather than merely long: "things stay where the user's
   * motor memory put them" is a promise you can only keep about a place a person can FIND, and a
   * control 900pt down an undifferentiated scroll has no place to remember. The division does not
   * reorder anything — the nine groups are the nine numbered sections the single surface already
   * had, in the order it already had them.
   *
   * EACH ROW SAYS WHAT IS INSIDE IT, because a name alone turns navigation into guessing and
   * icon-guessing is the executive load design-direction §6 names. Every `*.contains` line below
   * lists the actual controls on the page, in the order they appear on it, so the row is a
   * contents entry rather than a category.
   *
   * Rows that already had a name keep it — `settings.intensity`, `settings.notifications`,
   * `sorting.title`, `share.title`, `reflection.title`, `auth.account`, `plus.settings.title`.
   * Four groups had no single name because they were sections of a scroll, and those are here.
   */
  'settings.section.appearance': { warm: 'Look and words', literal: 'Appearance and wording' },
  'settings.section.appearance.contains': {
    warm: 'Words, light or dark, your colours, text size.',
    literal: 'Wording, colour scheme, material, density, reading.',
  },
  /**
   * THE LISTS PAGE — a destination of its own (product-owner instruction, 2026-08-03;
   * docs/DEVIATIONS.md §list-settings, which departs from RULINGS R132 build consequence 4).
   *
   * NOT NAMED "YOUR LISTS", which is `lists.title` — the fourth TAB. A settings row wearing the
   * tab's own name would promise the surface holding the lists and deliver a page of preferences
   * about how they are drawn, and product-concept §11's predictable UI is exactly the promise that
   * a row lands where its label says. So the row is named for what it decides, in both registers.
   */
  'settings.section.lists': { warm: 'How your lists read', literal: 'List display' },
  'settings.section.lists.contains': {
    warm: 'Whether a section with nothing in it shows.',
    literal: 'Empty sections.',
  },
  'settings.section.feedback': { warm: 'Sound and pictures', literal: 'Sound and images' },
  'settings.section.feedback.contains': {
    warm: 'Sound, off until you ask. Photos in your bookmark.',
    literal: 'Sound, off by default. Photos in saved context.',
  },
  'settings.section.today': { warm: 'Today', literal: 'Today' },
  // Four items now, not three: the daily suggestion joined this page (docs/DEVIATIONS.md
  // TD1), and an index row that lists three of four is a false statement on the screen this
  // audience reads most carefully.
  'settings.section.today.contains': {
    warm: 'How many stay warm, storm mode, how today is going, the daily one.',
    literal: 'Active task limit, triage mode, energy, daily suggestion.',
  },
  'settings.section.words': { warm: 'Places and your words', literal: 'Places and vocabulary' },
  'settings.section.words.contains': {
    warm: 'The places you name, and the words you want heard right.',
    literal: 'Place labels, and transcript corrections.',
  },
  /** The contents lines for the six groups whose NAME already existed. Same shape, same job. */
  'settings.section.intensity.contains': {
    warm: 'The dial, and each channel on its own.',
    literal: 'The dial, and each channel separately.',
  },
  'settings.section.notifications.contains': {
    warm: 'Each kind on its own, quiet hours, permission.',
    literal: 'Each type, quiet hours, system permission.',
  },
  'settings.section.sorting.contains': {
    warm: 'Whether it guesses at all, and whether it invites you.',
    literal: 'Automatic suggestions, and the daily prompt.',
  },
  'settings.section.reflection.contains': {
    warm: 'Off unless you turn it on. Nothing counts down.',
    literal: 'Off by default. Nothing counts down.',
  },
  'settings.section.account.contains': {
    warm: 'What this device holds, and your subscription.',
    literal: 'Device storage, and subscription status.',
  },

  /**
   * Shown for the channels in `Theme.systemClamped` (@carn/tokens). The OS switch
   * outranks the dial and the mixer, so the surface says so instead of offering a
   * control that springs back.
   */
  'settings.systemHolding': {
    warm: 'Your phone settings hold these down: {channels}',
    literal: 'System accessibility settings limit these: {channels}',
    slots: ['channels'],
  },
  /**
   * THE ONE SENTENCE R62 ADDS, and the whole content of the counter-argument it settles.
   *
   * `settings.systemHolding` names the channels; on its own it is a fact with no consequence
   * attached, and the consequence is the part the user needs: the dial is about to look broken.
   * R62 — "add one sentence stating that raising the dial will not raise that channel while the
   * OS setting is on. That was the counter-argument's whole content, and it costs a line."
   */
  'settings.systemHoldingDial': {
    warm: 'Moving the dial up will not raise these while that phone setting is on.',
    literal: 'Raising the Intensity dial does not raise these while the system setting is enabled.',
  },

  // design-direction §4: offer the dyslexia-oriented LEVERS, never promise the cure —
  // "evidence on dedicated 'dyslexia fonts' is mixed".
  'settings.readingExplain': {
    warm: 'Levers to try, not a cure. Some help some people.',
    literal: 'Adjust these if they help. Effects differ between people.',
  },
  'settings.lineHeight': { warm: 'Line height', literal: 'Line height' },
  'settings.percent': { warm: '{value}%', literal: '{value}%', slots: ['value'] },
  'settings.pixels': { warm: '{value} px', literal: '{value} px', slots: ['value'] },
  // The ACCESSIBLE name of a step button: specific, because a screen reader user hears it
  // out of context ("Less text size", not "Less").
  'settings.less': { warm: 'Less {label}', literal: 'Decrease {label}', slots: ['label'] },
  'settings.more': { warm: 'More {label}', literal: 'Increase {label}', slots: ['label'] },
  // The VISIBLE text on the same buttons. Design review caught "Less How many stay warm"
  // overflowing its card, and the row already states what is being adjusted directly above.
  'settings.stepLess': { warm: 'Less', literal: 'Less' },
  'settings.stepMore': { warm: 'More', literal: 'More' },

  // design-direction §4: higher densities are opt-in, "some ADHD users genuinely prefer
  // everything-visible; hiding can mean ceasing-to-exist".
  //
  // IT NAMES BOTH HALVES, AND IT NAMES THEM IN THE SAME DIRECTION (docs/DEVIATIONS.md Q1).
  // The old pair said "Show more if hidden means gone for you" / "Higher densities show more
  // at once", which was a promise about CONTENT under a setting that reached only
  // `cardGap`/`cardPadding`/`screenPadding` — a false statement on the screen this audience
  // reads most carefully. The setting now moves quantity as well (`DENSITY_NAMED_LOOPS`,
  // @carn/tokens: the quiet count rows name the first few of the loops they count), and the
  // spacing tightens with it rather than against it, so one sentence covers both truthfully.
  // "Least on screen" quotes `settings.density.sparse`, so the explanation and the option it
  // describes cannot drift into disagreeing.
  'settings.densityExplain': {
    warm: 'Least on screen by default. Higher settings fit more, packed tighter.',
    literal: 'Least on screen by default. Higher settings fit more, with tighter spacing.',
  },

  // design-direction §6, CLAUDE.md: icon and word by default; the other two are opt-in,
  // not a default a request can move.
  'settings.labelsExplain': {
    warm: 'Icon and word by default on back, close and the tab bar. Change how much shows.',
    literal: 'Icon and label by default on navigation controls. Adjustable here.',
  },

  /**
   * THE THIRD SENTENCE IS THE LOAD-BEARING ONE (RULINGS R132). design-direction §4's own
   * argument against hiding is that "hiding can mean ceasing-to-exist", and the answer to it is
   * a fact rather than a reassurance: a section that is not on the list is still on the section
   * page, where it can be renamed, reordered and removed. Saying so is what makes the switch
   * safe to try, and it is why the row carries an explanation at all.
   */
  'settings.emptySectionsExplain': {
    warm: 'On shows every section, even the ones with nothing in yet. Off shows only the sections holding something. Either way, the sections page lists them all.',
    literal:
      'On shows every section, including empty ones. Off shows only sections that contain items. All sections remain on the sections page.',
  },

  'settings.volume': { warm: 'Volume', literal: 'Volume' },
  // product-concept §6, objection 3: text-only capsules are a real preference.
  'settings.imagery': { warm: 'Photos in your bookmark', literal: 'Photos in saved context' },
  'settings.imageryExplain': {
    warm: 'Off shows text only. The photo stays saved either way.',
    literal: 'Off shows text only. Photos are kept, not deleted.',
  },
  'settings.warmCapExplain': {
    warm: 'How many sit up front. A prompt, never a block.',
    literal: 'How many tasks show on the main surface. Never enforced.',
  },

  // The daily suggestion's control (docs/DEVIATIONS.md TD1). It lives on the Today page
  // because it is a setting about THIS DAY rather than about how the app renders for ever,
  // which is what that page is for.
  'settings.today': { warm: 'One to look at today', literal: 'Daily suggestion' },
  'settings.todayExplain': {
    warm: 'One a day, from the ones that have gone quiet. Nothing is kept score of.',
    literal: 'One task a day, taken from the idle ones. Nothing is counted or recorded.',
  },
  'settings.todayHour': { warm: 'Offered from', literal: 'Shown from' },
  'today.optIn': { warm: 'Offer one a day', literal: 'Turn the daily suggestion on' },
  'today.optOut': { warm: 'Stop offering one', literal: 'Turn the daily suggestion off' },
  // The state of the setting, with the hour in it — the same shape as `sorting.promptOn`,
  // and for the same reason: stating the hour is what stops "on" being a claim the user
  // cannot check. It also states what the card cannot do, because that is the fact a reader
  // of this page most needs: no notification, no arrival, nothing waiting for them.
  'today.settingOn': {
    warm: 'One card on the home surface from {hour}. Nothing is sent to you.',
    literal: 'One suggestion on the main surface from {hour}. No notification is sent.',
    slots: ['hour'],
  },
  'today.settingOff': {
    warm: 'No suggestion unless you ask for one.',
    literal: 'The daily suggestion is off until you turn it on.',
  },

  // Storm mode (product-concept §5, objection 2). It expires on its own, so the surface
  // states when — a storm the user forgot to end must not become their settings.
  'settings.storm': { warm: 'Storm mode', literal: 'Triage mode' },
  'settings.stormExplain': {
    warm: 'Declares today as triage. The limit lifts, invitations pause.',
    literal: 'Marks today as triage. The active limit lifts and notifications pause.',
  },
  'settings.stormDeclare': { warm: 'Declare a storm', literal: 'Start triage mode' },
  'settings.stormPassed': { warm: 'The storm has passed', literal: 'End triage mode' },
  'settings.stormActive': {
    warm: 'Storm mode is on. Judgment is suspended today.',
    literal: 'Triage mode is on. The active limit is suspended.',
  },
  'settings.stormEnds': {
    warm: 'It lifts on its own {when}.',
    literal: 'Ends automatically {when}.',
    slots: ['when'],
  },

  'settings.notifications': { warm: 'Notifications', literal: 'Notifications' },
  'settings.notificationsExplain': {
    warm: 'Each kind is set on its own. Quieting one leaves the others alone.',
    literal: 'Each type is configured separately. Quieting one does not affect the others.',
  },

  // Places (product-concept §8.1; implementation-brief §3 privacy invariant, §8 "a handful of
  // registered regions"; product-owner report 3 — "manage different stores and their locations").
  //
  // `settings.placesExplain` USED TO SAY "Labels only. No coordinates ever leave your phone." The
  // first half stopped being true the moment a place could be given a location; the second half is
  // still true and is now stated at the moment it matters (`places.locationPrivacy`). A privacy
  // sentence that has gone stale is worse than none, because §14 risk 1 says this audience reads
  // it closely and will be the first to notice it no longer describes the build.
  'settings.places': { warm: 'Places', literal: 'Places' },
  'settings.placesExplain': {
    warm: 'Name the places you go. Give one a location and this phone can notice you arriving.',
    literal: 'Name the places you visit. Adding a location lets this device detect arrival.',
  },
  'settings.placesLimit': {
    warm: 'Up to {count} places.',
    literal: 'Maximum {count} places.',
    oneWarm: 'Up to 1 place.',
    oneLiteral: 'Maximum 1 place.',
    slots: ['count'],
  },
  'settings.placesEmpty': { warm: 'No places yet.', literal: 'No places yet.' },
  /**
   * THE CAP, STATED RATHER THAN ENFORCED IN SILENCE (implementation-brief §8; iOS caps monitored
   * regions per app and drops the overflow without telling anyone). The add control goes when the
   * limit is reached and this line says why, so the missing control is never a mystery.
   */
  'settings.placesFull': {
    warm: 'That is every place this phone can watch for. Remove one to add another.',
    literal: 'The maximum number of places is in use. Remove one to add another.',
    slots: [],
  },
  'settings.placeAdd': { warm: 'Add a place', literal: 'Add a place' },
  'settings.placeName': { warm: 'Name it plainly', literal: 'Place name' },
  'settings.placeKind': { warm: 'What kind of place', literal: 'Place type' },
  'settings.placeRemove': { warm: 'Remove {label}', literal: 'Remove {label}', slots: ['label'] },
  'settings.placeEdit': { warm: 'Change {label}', literal: 'Edit {label}', slots: ['label'] },
  'settings.placeDone': { warm: 'Done with this one', literal: 'Finish editing' },
  'settings.placeRename': { warm: 'What it is called', literal: 'Place name' },
  'place.kind.home': { warm: 'Home', literal: 'Home' },
  'place.kind.store': { warm: 'Shop', literal: 'Store' },
  'place.kind.work': { warm: 'Work', literal: 'Work' },
  'place.kind.other': { warm: 'Somewhere else', literal: 'Other' },

  // -- A place's location (product-owner report 3; RULINGS R8; implementation-brief §3, §8) -----
  //
  // TWO WAYS IN, AND THE NON-VISUAL ONE IS NOT THE LESSER (docs/RULINGS.md R14's principle: a
  // gesture is never a control's only input). "Use where you are" needs the user to be standing in
  // the place; "find it by address" needs only a keyboard, which is what makes it the path that
  // works for a screen-reader user, a switch-control user, a user with a tremor, and anybody
  // setting up a shop from the kitchen table.
  'place.locationUnset': {
    warm: 'No location on this one, so arriving there is not noticed.',
    literal: 'No location set. Arrival at this place is not detected.',
  },
  'place.locationSet': {
    warm: 'Location set. This phone notices when you get there.',
    literal: 'Location set. This device detects arrival at this place.',
  },
  'place.useHere': { warm: 'Use where you are now', literal: 'Use the current position' },
  'place.useHere.hint': {
    warm: 'Asks for location, then takes one reading from where you are standing.',
    literal: 'Requests location permission, then takes a single position reading.',
  },
  'place.byAddress': { warm: 'Find it by address', literal: 'Search by address' },
  'place.byAddress.hint': {
    warm: 'Type an address and pick it from the results. You do not have to be there.',
    literal: 'Enter an address and select a result. Being present is not required.',
  },
  'place.addressField': { warm: 'Address or the name of the shop', literal: 'Address or place name' },
  'place.addressSearch': { warm: 'Look it up', literal: 'Search' },
  'place.addressSearching': { warm: 'Looking…', literal: 'Searching…' },
  'place.addressPick': { warm: 'Pick the right one', literal: 'Select the correct result' },
  'place.addressUse': { warm: 'Use {address}', literal: 'Use {address}', slots: ['address'] },
  'place.addressNoMatch': {
    warm: 'Nothing came back for that. Try the street and the town.',
    literal: 'No results. Try including the street and town.',
  },
  'place.addressFailed': {
    // PRESENT TENSE (design-direction §1): "did not come back" / "did not complete" tripped
    // the copy-law linter's past-tense check (`packages/copy/src/lint.ts`'s `pastVerb` regex
    // matches "did"). Reworded to the same fact in the present tense rather than declaring
    // `allow: ['tense']` — this is a plain state description, not acknowledgement copy, so the
    // exception does not apply.
    warm: 'The lookup gets nothing back. Try again when you have signal.',
    literal: 'The address search returns no result. Try again with a network connection.',
  },
  'place.addressUnavailable': {
    warm: 'Address lookup is not available in this build.',
    literal: 'Address search is not available in this build.',
  },
  'place.clearLocation': {
    warm: 'Forget where {label} is',
    literal: 'Remove the location for {label}',
    slots: ['label'],
  },
  /**
   * THE ONE PRIVACY SENTENCE, AND THE ONLY ONE THAT MAY NOT BE DELETED.
   *
   * implementation-brief §3 objection 2 makes "geofence evaluation happens on-device only… the
   * server never receives raw location" a privacy invariant WITH FORCE OF LAW, and product-concept
   * §14 risk 1 records that this audience reads the permissions screen. The product-owner report
   * that cut every other repetition of this claim was explicit that this one survives, because it
   * is rendered at the moment of GRANTING — beside the controls that trigger the operating
   * system's own permission dialogue — which is the only moment at which a user is deciding
   * anything and therefore the only moment at which the claim does work.
   *
   * Every clause is checkable: `Place` in `@carn/core` has no coordinate field, `centres` and
   * `regionStore.ts` in `apps/mobile/src/native` are the only two things that hold one and neither
   * has a route to the sync engine, and `assertNoRawLocation` in `packages/schema` throws on the
   * outbox if either ever does.
   */
  'places.locationPrivacy': {
    warm: 'Where the place is stays on this phone. It is never sent, and no trail is kept.',
    literal: 'The location is stored on this device only. It is never transmitted, and no location history is kept.',
  },
  // What the operating system has actually granted, in the user's words rather than the platform's
  // (product-owner report 2 — the app must state what it has, not what it wishes it had).
  'places.permission.none': {
    warm: 'Location is off for this app, so nothing is noticed. Your phone’s settings is where you turn it on.',
    literal: 'Location permission is not granted, so arrival is not detected. It can be granted in the device settings.',
  },
  'places.permission.whileUsing': {
    warm: 'Location is set to “while using the app”. Arriving is only noticed while the app is open — “always” is what lets it happen with the app closed.',
    literal: 'Location permission is “while using the app”. Arrival is detected only while the app is open. “Always” is required for detection with the app closed.',
  },
  'places.permission.always': {
    warm: 'Location is set to “always”, so arriving is noticed with the app closed.',
    literal: 'Location permission is “always”. Arrival is detected with the app closed.',
  },
  'places.permission.unavailable': {
    warm: 'This build cannot watch for places at all.',
    literal: 'This build cannot monitor locations.',
  },
  // The outcome of setting a location. `AttachOutcome.reason` is a closed union and every member
  // has a line here, because a control that fails silently is worse than one that says what
  // stopped it (RULINGS R72).
  'place.attach.denied': {
    warm: 'Location stays off, so this place goes unnoticed. Everything else works the same.',
    literal: 'Location permission is not granted, so arrival cannot be detected. Nothing else changes.',
  },
  'place.attach.foregroundOnly': {
    warm: 'Only “while using the app” came back, and noticing an arrival needs “always”. Your phone’s settings is where that lives.',
    literal: 'Only “while using the app” was granted. Arrival detection requires “always”, which is set in the device settings.',
    allow: ['tense'],
    why: 'Reports the answer the operating system just gave. The literal register names the grant that came back, and "was granted" is the plainest way to say which one.',
  },
  'place.attach.noFix': {
    warm: 'No reading here. Try again outside, or find it by address instead.',
    literal: 'No location reading is available here. Try again outdoors, or search by address.',
  },
  'place.attach.full': {
    warm: 'That is every place this phone can watch for. Forget one first.',
    literal: 'The maximum number of monitored regions is in use. Remove one first.',
  },
  'place.attach.noMatch': {
    warm: 'That result is no longer to hand. Look it up again.',
    literal: 'That search result is no longer available. Search again.',
  },
  'place.attach.noRunner': {
    warm: 'Noticing arrivals is not wired up in this build.',
    literal: 'Background region monitoring is not available in this build.',
  },
  'place.attach.unavailable': {
    warm: 'This build cannot watch for places.',
    literal: 'This build cannot monitor locations.',
  },

  // The vocabulary loop (product-concept §7.2, implementation-brief §6: phase-1
  // mandatory). The log is append-only in core, so the copy says that too.
  'settings.vocabulary': { warm: 'Your words', literal: 'Word corrections' },
  'settings.vocabularyExplain': {
    warm: 'Teach it your names. “The girls” means the chickens.',
    literal: 'Corrections applied to transcripts. “The girls” means the chickens.',
  },
  'settings.vocabularyEmpty': { warm: 'No corrections yet.', literal: 'No corrections yet.' },
  'settings.vocabularyHeard': { warm: 'It hears', literal: 'Recognised as' },
  'settings.vocabularyMeans': { warm: 'You mean', literal: 'Replace with' },
  'settings.vocabularyAdd': { warm: 'Add a correction', literal: 'Add a correction' },
  'settings.vocabularyCurrent': {
    warm: 'Now means {meant}',
    literal: 'Currently means {meant}',
    slots: ['meant'],
  },
  'settings.vocabularyConfirmations': {
    warm: 'Set {count} times',
    literal: 'Recorded {count} times',
    oneWarm: 'Set once',
    oneLiteral: 'Recorded once',
    slots: ['count'],
  },
  'settings.vocabularyAppendOnly': {
    warm: 'Corrections stack up. Nothing is overwritten.',
    literal: 'Corrections are appended. Nothing is overwritten.',
  },
  /**
   * THE REVERSAL THE APPEND-ONLY LOG SEEMED TO FORBID.
   *
   * This screen was add-only, and its own header explained why: the log is append-only and
   * "there is no edit-in-place operation". Correct about the log, wrong about the
   * consequence — a rule that silently edits everything the user says afterwards has to be
   * undoable, and a review found the only remedy available to a user taught `2 → 3` was to
   * invent the rule `2 → 2` and then read "2 = 2, set twice" back in their own settings.
   *
   * `forgetCorrection` in core appends a TOMBSTONE state rather than deleting: the rule stops
   * firing, every previous correction stays in the log, and append-only is preserved.
   */
  /**
   * Trust requirement 4 of §7.2 objection 2, now reachable.
   *
   * The switch used to live at the foot of the Sorting session, 454pt below the fold on an
   * iPhone SE, on every card. Preferences belong where preferences are.
   */
  'settings.sortingSuggestions': { warm: 'Suggest names and triggers', literal: 'Show suggestions' },
  // The daily prompt's own control (RULINGS R46). Named as an INVITATION rather than as a
  // reminder in the warm register, because product-concept §11 is explicit that notifications
  // are invitations; the literal register names the mechanism, as literal mode is for.
  'settings.sortingPrompt': { warm: 'A daily invitation to sort', literal: 'Daily inbox reminder' },
  'settings.sortingPromptHour': { warm: 'Invitation hour', literal: 'Reminder hour' },
  'settings.vocabularyForget': { warm: 'Forget this one', literal: 'Stop using this correction' },
  'settings.vocabularyForgotten': {
    warm: 'Not used any more. The history stays.',
    literal: 'No longer applied. The history is kept.',
  },
  'settings.vocabularyForgetHint': {
    warm: 'Stops it changing what you say. Nothing is deleted.',
    literal: 'Stops the substitution. No data is deleted.',
  },

  // NOT "Capacity" — a section heading rating the person (critique 9.2). The section is about
  // today and what today suits, in both registers.
  'settings.energy': { warm: 'Today', literal: 'Today' },

  // -------------------------------------------------------------------------
  // Account (RULINGS R49) — the sign-in sentence returned WITH the integration
  // -------------------------------------------------------------------------
  // The original three strings exist because omission was the defect. A review read all 180
  // accessible nodes of the settings surface and found no account row, no `@carn/auth` import and
  // no `auth.*` key anywhere (settings-critique 1.4), while first run promised "sign in later" —
  // a forward promise with no surface to keep it, made to an audience product-concept §12 calls
  // "justifiably hostile" to being misled.
  //
  // THIS BLOCK'S HEADER USED TO SAY "NOTHING HERE OFFERS A SIGN-IN", AND THAT IS NOW FALSE.
  //
  // It said so because Clerk was implementation-brief §10 verification spike #3, needing deep-link
  // configuration and real keys "this repository does not have", and it promised that "the sign-in
  // sentence returns with the integration, not before". R49's ruling says the same in the same
  // words. The integration has landed at founder instruction (docs/DEVIATIONS.md, Workstream: auth,
  // A1 — the Clerk instance is configured and the keys exist), so this is the ruling's own stated
  // condition being met, not a departure from it. The strings below are that returning sentence.
  //
  // WHAT THESE STRINGS MAY AND MAY NOT PROMISE, WHICH IS THE WHOLE DIFFICULTY.
  //
  // Signing in switches on exactly two things, and both are checkable in this repository today:
  //
  //   1. THE SERVER REPLICA, UPWARDS ONLY. `apps/mobile/src/sync/useDrainKeeper.ts` is already
  //      mounted app-wide and already attempts `POST /api/v1/sync/push` every 60s; every attempt
  //      currently dies at `client.ts`'s `bearerToken()` with `no_session`. A session is the one
  //      thing missing, so signing in genuinely starts replicating local writes off the device.
  //   2. SHARING. `share-link` and the shared-list endpoints require a Clerk JWT — which is exactly
  //      what R76 recorded as blocked ("the §10.1 walk is blocked on spike #3, and must be *said*").
  //
  // AND IT USED NOT TO SWITCH ON DEVICE-TO-DEVICE SYNC — WHICH IS NO LONGER TRUE, SO
  // `auth.backupOnly` NO LONGER SAYS IT (docs/DEVIATIONS.md, Workstream: data-surface, DS1).
  //
  // This paragraph read: "The PULL half is still blocked, for two reasons `src/sync/drain.ts`
  // states and this catalogue must not paper over: `Backend` has no generic row-write path, and
  // nothing on the device carries the `fieldVersions` vector." Both were removed by the pull-sync
  // workstream (P1, P2): `@carn/sync`'s `loop-row.ts` is the mapper, `loops.field_versions` is the
  // vector, `src/sync/pull.ts` merges per field, and `useDrainKeeper` pulls on the same tick it
  // pushes. `copy-claims.test.ts` gates its "not available yet" assertion on exactly that
  // (`pullPossible`), so the sentence stopped being checked the day it stopped being true — which
  // is how a careful, correct limit becomes a stale claim nobody notices. It is corrected here.
  //
  // WHAT IS STILL NOT PROMISED, because the round trip is not total: places and lists are HELD from
  // the wire (`src/sync/outboxMapping.ts`, `PUSHABLE_TABLES`), and surfacing rules and media files
  // are never sent at all. Settings and the vocabulary came OFF that list in the settings-sync
  // workstream (docs/DEVIATIONS.md), which is why `data.travels` names them now. `auth.backupOnly` is one line under a
  // sign-in control and cannot carry that; the `data.*` family below is the surface that can, and
  // the account page links to it. Saying "everything, on all your devices" here would be the
  // forward promise settings-critique 1.4 caught on first run, made about data instead of a button.
  //
  // NONE OF THESE STRINGS SAYS "SYNC", and that rule survives the pull half landing. It is checked
  // unconditionally by `copy-claims.test.ts` — the noun names a round trip that is complete for
  // some tables and absent for others, and a reader cannot tell which from the word.
  'auth.account': { warm: 'Account', literal: 'Account' },
  'auth.deviceOnly': {
    warm: 'There is no account. Everything you keep is on this phone.',
    literal: 'No account exists. All data is stored on this device only.',
  },
  'auth.noSubscription': {
    warm: 'Nothing is paid for, so there is nothing to cancel.',
    literal: 'There is no subscription, so there is nothing to cancel.',
  },
  'auth.signIn': { warm: 'Sign in', literal: 'Sign in' },
  'auth.signInExplain': {
    warm: 'Sign in and a copy of your work is kept off this phone. Shared lists need it too.',
    literal: 'Signing in uploads a backup copy of your data and enables shared lists.',
  },
  // The address is the ONLY thing capture-grade sign-in asks for: §4 makes passwordless the primary
  // flow because "passwords are executive-function taxes and this audience loses them".
  'auth.email': { warm: 'Email address', literal: 'Email address' },
  'auth.sendLink': { warm: 'Send a sign-in link', literal: 'Send a sign-in link' },
  // The pending state. It is a fact about where the link is, not an instruction with a deadline —
  // §12 forbids fake urgency, so nothing here counts down or hurries the reader.
  'auth.linkSent': {
    warm: 'A link is on its way. Open it on this phone and you are in.',
    literal: 'A sign-in link is being sent. Open it on this device to finish signing in.',
  },
  // What the deep-link landing route says while the session is being adopted
  // (`apps/mobile/app/sign-in/callback.tsx`). Brief by design — it is on screen for the length of
  // one local read, and the route leaves for the account page as soon as the answer arrives.
  'auth.linkOpened': {
    warm: 'The link is open. Signing you in.',
    literal: 'The sign-in link is verified. Completing sign-in.',
  },
  // Clerk's own flow resolves on an expired link exactly as it does on a verified one, so this is a
  // state the surface really reaches (`src/auth/clerkAdapter.ts`). Stated as a fact with the next
  // action attached, never as something the reader did wrong.
  'auth.linkExpired': {
    warm: 'That link is no longer good. Send another and open it sooner.',
    literal: 'The sign-in link is no longer valid. Request another link.',
  },
  'auth.signInProblem': {
    warm: 'No link could be sent. Nothing on this phone changes.',
    literal: 'The sign-in link could not be sent. Local data is unaffected.',
  },
  // SIGN IN WITH APPLE (implementation-brief §4, §10 spike #3; docs/DEVIATIONS.md auth-sign/A5).
  //
  // THE BUTTON'S OWN LABEL IS NOT IN THIS CATALOGUE, AND THAT IS THE ONE PLACE IT MAY NOT BE.
  // Apple's `ASAuthorizationAppleIDButton` draws its own text, in the user's own system language,
  // and Apple's Human Interface Guidelines require that exact wording, logo and proportion. A
  // catalogue string rendered in a `@carn/ui` Button would be a branding violation and an App Store
  // rejection risk. So the control speaks Apple's words; everything AROUND it speaks ours, which is
  // what these two strings are. A5 records the exception.
  //
  // `auth.appleExplain` is the second-person sentence that has to carry the honesty the button
  // cannot: the same narrow promise `auth.signInExplain` makes for the email path, because both
  // paths reach the same session and therefore the same push-only replica (A2).
  'auth.appleExplain': {
    warm: 'Or use your Apple ID. It keeps the same copy off this phone.',
    literal: 'Alternatively, sign in with an Apple ID. This enables the same backup upload.',
  },
  // A dismissed sheet gets NO string at all — see `AppleSignInCancelledError` in
  // `src/auth/clerkAdapter.ts`. This is only for a real failure, and it names what did not change,
  // exactly as `auth.signInProblem` does for the email path.
  'auth.appleProblem': {
    warm: 'Apple could not sign you in. Nothing on this phone changes.',
    literal: 'Apple sign-in could not be completed. Local data is unaffected.',
  },
  'auth.signedInAs': {
    warm: 'Signed in as {email}.',
    literal: 'Signed in as {email}.',
    slots: ['email'],
  },
  // The honest direction of travel — see this block's header for why it is not called "sync", and
  // for why it no longer denies the downward half. It names loops rather than "everything",
  // because the held set is real and `data.*` is where it is enumerated.
  'auth.backupOnly': {
    warm: 'Your loops travel up to the server, and changes travel back down to this phone.',
    literal: 'Task data uploads to the server, and changes on the server download to this device.',
  },
  'auth.signOut': { warm: 'Sign out', literal: 'Sign out' },
  // The question this audience actually has about a sign-out control, answered before it is asked.
  // Grounded in a structural law rather than a fresh promise (CLAUDE.md: history is append-only).
  'auth.signOutExplain': {
    warm: 'Everything on this phone stays exactly where it is.',
    literal: 'Signing out does not remove any data from this device.',
  },

  // -------------------------------------------------------------------------
  // Sync and system state — plain, never alarming
  // -------------------------------------------------------------------------
  /**
   * `sync.offline` USED TO BE HERE, AND IS DELETED (docs/RULINGS.md R103, R26).
   *
   * It read "Offline. Everything is saved here." / "Offline. Changes saved locally." R94 assigned
   * it to the home surface; R103 moved it to `src/screens/shopping/ShareLinkRow.tsx` — the only
   * surface in the app that makes a network request, and one that already renders `sync.syncing` —
   * with the instruction that if it could not honestly render there either, delete it. It cannot,
   * for two independent reasons:
   *
   *   1. **The sentence would be false on that surface.** Its substance is the local-write promise
   *      (implementation-brief §2: "the local write is the real write"), and nothing about a share
   *      link is written locally. Issuing mints a server-side token; revoking destroys one. The
   *      row's own failure branch says so correctly — `shareLink.failed`: "The link is unchanged."
   *      Telling a user their changes were "saved locally" when no link was made is R36's shape:
   *      two true-sounding clauses that jointly assert something that did not happen.
   *   2. **There is still no reachability signal to render.** A rejected request is
   *      indistinguishable from a 500 or a refused auth, and the app has no network-state
   *      dependency, so "offline" would be a guess presented as a fact.
   *
   * R26's gate exists to stop copy outliving the feature it was written for, and satisfying it by
   * finding a string a home is the failure mode rather than the fix. `SyncPhase` in
   * `src/stores/syncStatus.ts` keeps its `'offline'` member — it is the store's vocabulary for a
   * drain engine that does not exist yet, and nothing sets it. The string comes back with the
   * signal, not before.
   */
  'sync.syncing': { warm: 'Catching up', literal: 'Syncing' },
  'sync.synced': { warm: 'Up to date', literal: 'Synced' },
  'sync.failed': {
    warm: 'Could not reach the server. Your work is safe here; it will try again.',
    literal: 'Could not reach the server. Changes are saved locally and will retry.',
  },

  // -------------------------------------------------------------------------
  // Your data — what is here, what has left, and how to take it back
  // (`apps/mobile/src/screens/settings/DataSettings.tsx`)
  //
  // Authorised by implementation-brief §2 (the device is the source of truth; the outbox and the
  // pull cursor are the two facts these strings report), §3 (the privacy invariant — "the server
  // never receives raw location"), and product-concept §14 risk 1, which makes plain-English
  // privacy design "a product requirement, not legal boilerplate — this audience reads the
  // permissions screen". §12 makes trust the moat.
  //
  // EVERY SENTENCE HERE IS A CLAIM ABOUT A MECHANISM, WHICH IS THE HARDEST KIND TO KEEP TRUE.
  // `apps/mobile/test/data-settings.test.ts` checks the checkable ones against the code rather
  // than against this comment: what travels is read off `PUSHABLE_TABLES`, what is held is read
  // off the same set's complement, and the "never uploaded" list is asserted against the files
  // that make each one true. A string here that starts describing a different app fails there.
  //
  // WHY THE WORD "SYNC" IS AVOIDED IN THE USER-FACING FORMS AND USED FREELY IN THIS COMMENT.
  // `auth.*` may not say it (`copy-claims.test.ts`), and the reason was that the round trip was
  // half-built. The pull half landed (docs/DEVIATIONS.md, Workstream: pull-sync), so the word is
  // no longer a lie — but it is still a word that means everything and nothing to a reader who
  // has just asked "has any of my stuff actually gone anywhere". Two directions, named, with
  // counts, answer that; one noun does not.
  'data.title': { warm: 'Your data', literal: 'Your data' },
  // THIS SENTENCE IS RENDERED ON TWO OTHER SURFACES (`SettingsSurface.tsx`'s index row and
  // `AccountSettings.tsx`'s link), so it is a promise about what the destination contains. It
  // said "…and how to take it back" / "…and how to remove data" until DA6 deleted the removal
  // section: the page no longer contains that, and a row that promises a section which is not
  // there is the R26 species of dead copy pointing the other way — a live string describing a
  // screen that has changed underneath it. Corrected in the same commit that deleted the section.
  'data.contains': {
    warm: 'What is kept here, and what reaches the server.',
    literal: 'Local storage and server copies.',
  },

  // 1. The device, first — because "where is my work" is the question underneath the other three,
  //    and the answer does not depend on an account (implementation-brief §2).
  'data.local.title': { warm: 'On this phone', literal: 'On this device' },
  'data.local.explain': {
    warm: 'Everything you keep lives here first. Every screen reads from this phone, account or no account.',
    literal: 'All data is stored on this device first. Every screen reads local data, with or without an account.',
  },

  // 2. What has left, as counts and times rather than as a status word.
  //
  // THE LITERAL FORM WAS "What is uploaded to the server" — 30 characters at `loopTitle` (21px),
  // which at design-direction §4's 250% text lever is five lines of heading standing over three
  // short ledger rows, inverting the hierarchy it is supposed to establish (DA6). Shortened on the
  // literal side only; the warm form was already 23 characters and is unchanged, so a reader who
  // has one register set does not see the other one move.
  'data.server.title': { warm: 'What reaches the server', literal: 'Uploaded to the server' },
  'data.server.signedOut': {
    warm: 'Nothing. There is no account on this phone, so nothing has left it.',
    literal: 'Nothing. No account is signed in on this device, so no data has been uploaded.',
  },
  // THE LEDGER'S THREE ROW NAMES. Bare noun phrases, no `{count}` slot and therefore no singular
  // form: the figure is rendered BESIDE the name in its own tabular column rather than inside a
  // sentence, so there is no grammar for a count to agree with (DA6).
  //
  // THESE REPLACED THREE FULL SENTENCES — `data.sent.count` ("{count} changes uploaded from this
  // device."), `data.waiting` and `data.held.count` — and the sentences are DELETED rather than
  // left for a future surface, per R26's gate. The reason they had to go is design-direction §4:
  // "numbers and times in tabular figures throughout: time-blind users deserve numerals that sit
  // still." A numeral in the middle of a sentence cannot sit still, because the sentence in front
  // of it is a different length in each register and at each text scale. A column can.
  'data.figure.sent': { warm: 'Sent up', literal: 'Uploaded' },
  'data.figure.waiting': { warm: 'Waiting to go', literal: 'Queued for upload' },
  'data.figure.held': { warm: 'Held here', literal: 'Held on this device' },
  /**
   * THE FOURTH ROW (docs/DEVIATIONS.md, Workstream: outbox-quarantine). A bare noun phrase like the
   * three above it, for the reason stated there: the figure sits in its own tabular column.
   *
   * "Turned back" NAMES WHAT HAPPENED AND NOT WHAT IT MEANS ABOUT THE USER. The alternatives were
   * all worse in a specific way: "Stuck" and "Failed" report a state as a fault; "Rejected" is what
   * the server did to the person; "Not going" is a prediction the app cannot keep, because these
   * entries ARE retried daily and one of them may well go tomorrow. design-direction §1 puts
   * acknowledgement on the act — this row is the act, in the passive, with no adjective anywhere near
   * the person.
   *
   * THE LITERAL REGISTER MAY SAY "SERVER" and the warm one may not, which is the same division every
   * key in this block draws. Neither says "error".
   */
  'data.figure.quarantined': { warm: 'Turned back', literal: 'Refused by the server' },
  'data.sent.none': {
    warm: 'Nothing has gone up yet. It goes when the app can reach the server.',
    literal: 'No changes have been uploaded yet. Upload occurs when the server is reachable.',
  },
  'data.sent.when': { warm: 'Last sent {ago}.', literal: 'Last upload {ago}.', slots: ['ago'] },
  // The pull cursor, said as what it is: how far up the server's own clock this phone has read.
  // Not "last checked" — a check that finds nothing does not move it, and reporting it as a
  // check would make a quiet fortnight look like a broken one.
  'data.received.upTo': {
    warm: 'Changes from the server read up to {when}.',
    literal: 'Changes from the server received up to {when}.',
    slots: ['when'],
  },
  'data.received.none': {
    warm: 'Nothing has come back down yet. Nothing on the server is newer than what is here.',
    literal: 'No changes have been downloaded yet. The server holds nothing newer than this device.',
  },
  'data.rhythm': {
    warm: 'It sends and checks about once a minute while the app is open.',
    literal: 'Upload and download run about once per minute while the app is open.',
  },

  // THE MANUAL CONTROL (DA7). It sits directly above `data.rhythm`, and that pairing is the whole
  // reason the footnote survives the addition: the button answers "can I make it go now" and the
  // footnote answers "do I need to", which is the honest frame for a control the engine makes
  // redundant most of the time.
  //
  // THE WARM REGISTER MAY NOT SAY "SYNC", and that is this block's own standing rule rather than a
  // preference — the header above states it: the noun "means everything and nothing to a reader
  // who has just asked 'has any of my stuff actually gone anywhere'". Two directions, named, is
  // what the warm form says. The literal register is for the reader who wants the technical word.
  'data.sync.now': { warm: 'Send and check now', literal: 'Sync now' },
  // WHAT A FINISHED RUN MAY CLAIM, WHICH IS LESS THAN "Up to date".
  //
  // `sync.synced` ("Up to date" / "Synced") was the obvious key to reuse and it is the wrong one:
  // a run that completes with twenty entries still HELD on the device (`data.figure.held`, and
  // P4's reason) has not made anything up to date, and a page whose entire subject is the gap
  // between here and there is the last place that may round it off. R103's closing warning is
  // exactly this move — "satisfying the gate by finding the string a home is the failure mode,
  // not the fix."
  //
  // So the claim attaches to the ACT and then points at the ledger, which is the real answer and
  // which the user watched change: the figures above are current as of the press, whatever they
  // say.
  'data.sync.done': {
    warm: 'Sent and checked. The numbers above are current.',
    literal: 'Upload and download finished. The figures above are current.',
  },
  /**
   * THE THIRD OUTCOME: the server answered, and turned entries back.
   *
   * `data.sync.done` used to cover this, because the button branched on `reached` alone — and
   * `reached` means the round trip happened, not that anything moved. So a run in which the server
   * refused every single entry printed "Upload and download finished. The figures above are
   * current." immediately above "0 uploaded, 37 queued for upload". Each statement was true and
   * the pair was a contradiction, which is worse than either an error or silence: it teaches the
   * user that the numbers and the sentence do not have to agree.
   *
   * It names the ACT and not the person (design-direction §1), says where the work is, and makes
   * no promise about when — the keeper retries on its own and there is no control to offer.
   */
  'data.sync.refused': {
    warm: 'The server answered but would not take some of it. It stays here and goes again.',
    literal: 'The server responded but rejected some entries. They stay queued and will retry.',
  },
  /**
   * WIDENED WHEN SETTINGS BECAME SYNCABLE (docs/DEVIATIONS.md, Workstream: settings-sync).
   *
   * It used to name loops and their history alone, because that was the whole of what the drain
   * could send. Settings now travel too — the dial, the warm cap, a declared storm, how loud the
   * app is allowed to be, and the words the user has taught it — so the sentence has to say so or
   * it understates what leaves the phone, which on this page is the one direction of error that
   * matters. `apps/mobile/test/data-settings.test.ts` fails if this and `PUSHABLE_TABLES` disagree.
   *
   * "the words you have taught it" IS THE VOCABULARY, said as the user experiences it rather than
   * as a table name. It is named explicitly rather than folded into "settings" because a private
   * dictionary is the most personal thing in this list and a reader deciding whether to sign in
   * deserves to see it named (product-concept §11, §14 risk 1).
   */
  'data.travels': {
    warm: 'Your loops travel, and so does their whole history: capsules, session stones, endings. Your settings travel too, including the words you have taught it.',
    literal: 'Tasks travel, and so does their full history: saved context, work sessions, endings. Settings are also uploaded, including your vocabulary corrections.',
  },
  // The held set. A count the user can see is the difference between "nothing is wrong" and a
  // queue they were never told about (product-concept §11's predictability). The count itself is
  // now `data.figure.held`; this is the sentence that defuses it, and it stays directly under the
  // figure for the reason it always did — the number is the surprise, the reason is the answer.
  /**
   * NARROWED IN THE SAME CHANGE `data.travels` WIDENED (docs/DEVIATIONS.md, Workstream:
   * settings-sync).
   *
   * It used to name four things — settings, places, vocabulary and lists — because all four were
   * held behind the one `settings` entry the drain refused to send. Settings and vocabulary now
   * travel, so naming them here would be the false statement this page exists to avoid: a user
   * reading "your words wait here" while their dictionary is on the server is worse than no
   * sentence at all.
   *
   * PLACES ARE STILL HELD AND STILL NAMED, because the reason is real and specific: a place the
   * user deletes has no way to say so on the way up yet, so sending one risks it coming back. See
   * `SETTINGS_FIELDS_NOT_IN_ROW` in `@carn/sync` and docs/OPEN-QUESTIONS.md.
   */
  'data.held.explain': {
    warm: 'Places and your lists have no safe way up yet, so they wait here.',
    literal: 'Places and lists cannot be uploaded safely yet, so they remain queued here.',
  },
  /**
   * THE SENTENCE THAT DEFUSES THE FOURTH FIGURE, and it has three jobs in two clauses
   * (docs/DEVIATIONS.md, Workstream: outbox-quarantine).
   *
   * 1. **Say the work is safe, first.** This is the only figure on the page a reader could take as
   *    "something of mine has been lost", and the answer is that nothing has: brief §2 makes the
   *    local write the real write, and the entry is still on the phone. That clause leads.
   * 2. **Say what the app is doing about it, without a promise it cannot keep.** "tries again each
   *    day" is exactly true (`QUARANTINE_RETRY_MS`), where "will be sent later" would be a forward
   *    promise this surface has no standing to make — the same class of claim `data.sync.done` was
   *    corrected for.
   * 3. **Offer no action, because there is none.** design-direction §3 forbids alarm, and R36's rule
   *    is that a surface must not imply a control that does not exist. There is deliberately no
   *    "clear" button — see the workstream entry for why removing an unsent write is a separate
   *    decision, and DA6 for why this page's removal section was deleted in the first place.
   *
   * NO COUNT AND NO TIMESTAMP INSIDE IT. The figure carries the number, in its own column, and "each
   * day" is a rhythm rather than a countdown: a sentence saying "next try in 14 hours" would be the
   * staleness report DA2 exists to prevent.
   */
  'data.quarantined.explain': {
    warm: 'The server would not take these, so they are set aside here. Your work is still on this phone, and it tries again each day.',
    literal:
      'The server rejected these entries, so they are set aside on this device. The data remains stored locally, and upload is retried once a day.',
  },

  // 3. The floor: things that are not queued, not delayed, and not coming.
  'data.never.title': { warm: 'What never leaves this phone', literal: 'What is never uploaded' },
  'data.never.location': {
    warm: 'Where you are. Places are matched on this phone, and no coordinates are sent.',
    literal: 'Location. Place matching runs on this device, and no coordinates are uploaded.',
  },
  'data.never.media': {
    warm: 'Recordings and photos. The note that one exists travels; the file itself stays here.',
    literal: 'Audio and image files. A reference is uploaded; the file itself stays on this device.',
  },
  'data.never.surfacing': {
    warm: 'The rules for when something comes to you. Those are worked out here, every time.',
    literal: 'The conditions that decide when an item is shown. These are evaluated on this device.',
  },
  'data.never.trips': {
    warm: 'Shop visits. Counted here for your own figures, and sent nowhere.',
    literal: 'Shopping trip records. Stored on this device for local reporting only.',
  },

  // 4. Removal — BACK, AND FOR THE ONE REASON DA4 AND DA6 BOTH NAMED (docs/RULINGS.md R134).
  //
  // THE HISTORY MATTERS HERE MORE THAN ALMOST ANYWHERE ELSE IN THIS FILE. Six keys stood in this
  // slot and were deleted with the removal section (DA6). Before that, DA4 refused to build a
  // "Remove everything from the server" control at all, and its reason was not squeamishness: it
  // was that `DELETE /api/v1/account` tombstoned the SETTINGS ROW ONLY, so a control wired to it
  // "would be the most expensive false promise in the app, made to the audience §12 calls
  // 'permanently done with tools that exploited them'". DA6's closing line — "THE CONSTRAINT THAT
  // KILLED THE BUTTON HAS NOT CHANGED" — was true when it was written.
  //
  // IT HAS CHANGED. R134 rules that the endpoint erases for real: `functions/handlers/account.ts`
  // and `functions/src/lib/account-erasure.ts` now tombstone loops and capsules alongside
  // `schema.settings`, on the same per-field last-write-wins tombstone discipline the sync engine
  // uses, and clear the columns that carry the user's words. So the sentence these keys make is
  // finally true, which is the only condition DA4 ever set.
  //
  // WHAT THESE STRINGS MAY AND MAY NOT CLAIM, because the boundary is narrow and a future edit
  // will be tempted to round it off:
  //
  //   MAY   — every loop, its capsules, ITS WHOLE HISTORY, your settings and your taught words go
  //           from the server; the account is signed out after.
  //   MAY   — the support page is still there for someone who no longer has the device.
  //   MAY NOT — THAT A SHARED THING GOES, and this is the boundary that moved twice. These keys
  //            read "Remove all uploaded data" until a critic pass checked them against the merge
  //            (docs/reviews/erasure-critic-pass.md) and found four pushable tables surviving an
  //            erasure — `loop_events`, `session_stones`, `loop_terminals`, `loop_settlements`,
  //            with `loop_events.data` carrying `noted.previousNote` and `sorted.previous.title`,
  //            the user's own words. `RULINGS R207` settles that founder decision: erasure now
  //            reaches all four, so the change-log sentence these keys used to carry is FALSE and
  //            is gone. What is still true is smaller and is `data.remove.remains`'s whole job: a
  //            shared list, and anything passed to another person, is not this user's alone to
  //            remove, so it stays. `apps/mobile/test/data-settings.test.ts` fails a
  //            `data.remove.*` string that claims the log stays while
  //            `functions/src/lib/account-erasure.ts` clears `schema.loopEvents`, and failed the
  //            opposite claim before R207 — the gate reads the erasure and swings either way.
  //   MAY NOT — that this phone is emptied. It is not. The device is the source of truth
  //            (implementation-brief §2) and this control does not touch it; removing one loop at
  //            a time (D32) is the local route, and R135 purges a removed loop's content thirty
  //            days later. `apps/mobile/test/data-settings.test.ts` fails on a `data.*` string
  //            that attaches an erasure claim to the device.
  //   MAY NOT — that it can be undone. It cannot, and `data.remove.noUndo` says so before the
  //            press rather than after it. `D32`'s confirmation is the precedent for the SHAPE of
  //            an act with no inverse: an ordinary panel, an ordinary question, "keep it" first,
  //            no red, no warning glyph, no scrim — prominence, never threat (design-direction §3
  //            objection 3).
  'data.remove.title': { warm: 'Removing what you sent up', literal: 'Removing uploaded data' },
  /**
   * NAMES WHAT GOES. The things listed are exactly the nine tables `eraseAccount` clears — loops,
   * their four append-only child logs, capsules, the settings row, the taught words and the
   * recorded verdicts — said as the objects a user recognises. "its history" is the child logs,
   * added by `RULINGS R207`: the change log used to survive an erasure and now does not.
   * `data.remove.remains` is the other half of the sentence and is rendered directly under this one.
   */
  'data.remove.explain': {
    warm: 'This takes every loop, its capsules, its history and your settings off the server. This phone keeps its own copy.',
    // "saved context" is `data.travels`'s literal rendering of a capsule, reused rather than
    // invented: the metaphor layer is skin, never structure (product-concept §11).
    literal:
      'This removes every task, its saved context, its history and your settings from the server. The copy on this device is unaffected.',
  },
  /**
   * WHAT STAYS, SAID BY THE CONTROL ITSELF (docs/reviews/erasure-critic-pass.md; `RULINGS R207`).
   *
   * THIS SENTENCE CHANGED SUBJECT, AND KEPT ITS JOB. It said the change log stays on the server,
   * because before R207 four append-only child logs survived an erasure and `loop_events.data`
   * held earlier titles and notes. R207 erases them, so that sentence is now false and the key
   * says the one thing that is still true: a shared list and anything handed to another person are
   * not this user's alone to remove, so they stay (`account-erasure.ts`'s header, and the account
   * handler's, have always said so). A control that listed only what it took would be the DA4
   * failure again in a smaller size — "the most expensive false promise in the app" — which is why
   * the key survives the change rather than being deleted with the claim.
   */
  'data.remove.remains': {
    warm: 'What you share with someone else stays. It is not yours alone to take away.',
    literal: 'Anything you share with another person remains. It is not yours alone to remove.',
  },
  /** Said BEFORE the press, which is the whole difference between a warning and a fact. */
  'data.remove.noUndo': {
    warm: 'There is no way back from this one, and it signs you out when it is done.',
    literal: 'This cannot be undone. You are signed out when it finishes.',
  },
  /**
   * THE LABEL NAMES THE HISTORY NOW (`RULINGS R207`). It read "Remove uploaded tasks and settings",
   * which was the narrowest true thing to say while the change log survived. It survives no longer,
   * and a label that under-states what a control with no inverse does is the same defect as one
   * that over-states it, in the safer direction.
   */
  'data.remove.action': {
    warm: 'Remove your loops, their history and settings from the server',
    literal: 'Remove uploaded tasks, their history and settings',
  },
  'data.remove.question': {
    warm: 'Remove your loops, their history and settings from the server?',
    literal: 'Remove uploaded tasks, their history and settings?',
  },
  /** "Keep it" first, and it is the quiet, ordinary answer (D32's panel, same order). */
  'data.remove.cancel': { warm: 'Keep it', literal: 'Keep the data' },
  'data.remove.confirm': { warm: 'Yes, remove it', literal: 'Remove' },
  'data.remove.working': { warm: 'Removing it now.', literal: 'Removal in progress.' },
  /**
   * The acknowledgement. A statement of what happened, with no adjective about the person who
   * did it (design-direction §1) and no celebration attached (there is nothing here to celebrate:
   * `Celebration` is for `closed` and `released`).
   */
  'data.remove.done': {
    warm: 'Your loops, their history and settings are off the server. You are signed out.',
    literal: 'Uploaded tasks, their history and settings are removed. You are signed out.',
  },
  /**
   * The unreachable-server case, and it says what it did NOT do. A removal control that answered
   * with a vague apology would leave the user unable to tell whether their data went.
   */
  'data.remove.unreachable': {
    warm: 'The server cannot be reached, so nothing is removed. Try again in a moment.',
    literal: 'The server cannot be reached. Nothing is removed. Try again in a moment.',
  },
  /**
   * The fallback, and it is BACK TO ONE CASE (R134 build consequence 4; `RULINGS R207`). It carried
   * two while the change log survived an erasure — the lost phone, and the log — and R207 removes
   * the second, so the sentence goes back to the one thing the support page is still the only route
   * to: a person whose phone is at the bottom of a slurry pit and cannot press a button on it.
   */
  'data.remove.lostDevice': {
    warm: 'Lost the phone? The support page reaches a person.',
    literal: 'If you no longer have the device, use the support page.',
  },


  // -------------------------------------------------------------------------
  // Durations and elapsed time. Rendered in tabular figures (design-direction §4:
  // "time-blind users deserve numerals that sit still"). Deliberately coarse: an
  // exact minute count on a three-week-old loop is precision nobody asked for.
  // -------------------------------------------------------------------------
  'time.underMinute': { warm: 'under a minute', literal: 'less than 1 minute' },
  // Abbreviated forms are unit-invariant, so they need no singular; the linter only
  // demands a `one` form where a plural noun would otherwise appear.
  'time.minutes': {
    warm: '{count}m',
    literal: '{count} min',
    oneWarm: '1m',
    oneLiteral: '1 min',
    slots: ['count'],
  },
  'time.hours': {
    warm: '{count}h',
    literal: '{count} h',
    oneWarm: '1h',
    oneLiteral: '1 h',
    slots: ['count'],
  },
  'time.hoursMinutes': {
    warm: '{hours}h {minutes}m',
    literal: '{hours} h {minutes} min',
    slots: ['hours', 'minutes'],
  },
  /**
   * `TIME_OF_DAY_BANDS` in `@carn/core`, named — the {band} slot of
   * `release.observation.caughtAtTime` (RULINGS R37).
   *
   * They are ADVERBIAL PHRASES, not nouns, because the only sentence that uses them puts them
   * where an adverb goes: "Caught in the evening." A bare "Evening" would have forced the
   * catalogue to carry a sentence with a noun jammed into an adverb's slot, which is exactly the
   * class of grammatical error RULINGS R24 was written about.
   *
   * Coarse on purpose (product-concept §8.3's own example is "in the evening"), and identical in
   * both registers because there is no metaphor in them and no wryness to be had: a band the
   * user recognises is the only unit an observation about their own rhythm can honestly use.
   */
  'time.band.morning': { warm: 'in the morning', literal: 'in the morning' },
  'time.band.afternoon': { warm: 'in the afternoon', literal: 'in the afternoon' },
  'time.band.evening': { warm: 'in the evening', literal: 'in the evening' },
  'time.band.night': { warm: 'at night', literal: 'at night' },
  'ago.justNow': { warm: 'just now', literal: 'less than an hour ago' },
  'ago.hours': {
    warm: '{count}h ago',
    literal: '{count} hours ago',
    oneWarm: '1h ago',
    oneLiteral: '1 hour ago',
    slots: ['count'],
  },
  'ago.yesterday': { warm: 'yesterday', literal: '1 day ago' },
  'ago.days': {
    warm: '{count} days ago',
    literal: '{count} days ago',
    oneWarm: '1 day ago',
    oneLiteral: '1 day ago',
    slots: ['count'],
  },
  'ago.weeks': {
    warm: '{count} weeks ago',
    literal: '{count} weeks ago',
    oneWarm: '1 week ago',
    oneLiteral: '1 week ago',
    slots: ['count'],
  },

  // -------------------------------------------------------------------------
  // First run — 30 seconds, never a quiz (design-direction §3)
  // -------------------------------------------------------------------------
  /**
   * THE WORDMARK AT THE HEAD OF THE FLOW (docs/RULINGS.md R172).
   *
   * R172: "a quiet wordmark at the head of the flow. One mark, no ornament." First run renders
   * no glyph, wordmark or material gesture anywhere, and it is the one screen where a user does
   * not yet know what app they are in — so the product's own name goes at the top and nothing
   * else does. **No cartographic motif**, so design-direction §2 objection 3's live constraint
   * ("no contour line may appear as pure ornament") is untouched rather than argued around.
   *
   * IDENTICAL IN BOTH REGISTERS, and legitimately: this is a proper noun, not a sentence. The
   * `firstRun.welcome` line directly under it is where the two registers actually differ, and it
   * already carries the `self-reference` exception for naming the product at all.
   *
   * NO PROMPTING OR REASSURANCE COPY EITHER WAY (R172 clause 3; product-concept §11). The mark
   * says the product's name and stops — there is no tagline here, no greeting, and nothing about
   * the user's situation.
   */
  'firstRun.wordmark': { warm: 'Carn', literal: 'Carn' },
  /**
   * The positioning line, verbatim from product-concept §1. It is the one place the product
   * names itself, and it earns the exception.
   *
   * THE INVERSION IS IN BOTH REGISTERS NOW (`docs/reviews/apple-design-award-review.md` `C2`).
   * `docs/DEVIATIONS.md` D30 makes plain words the default, so the first sentence a new user
   * reads is the LITERAL form — and that form carried only the second half of the positioning
   * line. The start/finish contrast is the product thesis (product-concept §1; §12 requires the
   * wedge to be publicly legible), it contains no metaphor and no wryness, and it was reserved
   * for the register almost nobody sees first. Two short sentences, second person, nothing to
   * strip: there was never a reason for literal mode to lose it.
   *
   * "MOST TASK APPS", NOT "EVERY TO-DO APP", IN THE LITERAL FORM. The register whose job is
   * precision does not get to make a claim about every app that has ever shipped; the warm form
   * keeps §1's own hyperbole, which is what the warm register is for.
   *
   * `marketing.hero` IS DELIBERATELY UNTOUCHED. It quotes the same line for a different surface
   * under §12, where third-person description is the genre and the page carries the contrast in
   * its own copy around the hero. `C2`'s argument is specifically about the register a new user
   * meets inside the app.
   */
  'firstRun.welcome': {
    warm: 'Every to-do app helps you start. Carn helps you finish.',
    literal: 'Most task apps help you start. Carn saves your place in unfinished tasks so you can finish them.',
    allow: ['self-reference'],
    why: 'The positioning line from product-concept §1, quoted exactly in warm, and its literal-safe equivalent (ADA review C2). First run is the one place the product may name itself.',
  },
  //
  // `firstRun.intensityAsk` was DELETED here, not re-voiced. critique 5.4.7 required the
  // question to stop priming the answer, the fix was applied — and the string was rendered by
  // nothing, so the fix landed on dead copy while the screen leaned on
  // `settings.intensityExplain` instead (critique 1.3). The dial states its own purpose and the
  // two lines below it do the rest; a fourth prose block above the exit is what put the exit
  // below the fold in the first place.
  //
  // NOT "All levels are fully supported": support is something a vendor grants a
  // configuration, and it answers a doubt the user has not had yet — protesting too much, in
  // release-note language, about the one control §3 calls the most important in the app
  // (critique 3.3). design-direction §3's actual substance is that the two ends serve different
  // people: "some users need vivid stimulation to register anything while others are
  // overwhelmed by a drop shadow". A line that says so is one the user can act on.
  //
  // AND THE THIRD SENTENCE WENT, FOR THE SAME REASON THE SUPPORT MATRIX DID
  // (`docs/roadmap/onboarding.md` item 7). It read "Both are settings, not adjustments." — which
  // is three separate faults on the second screen-second of the product. It is WRYNESS, and it
  // shipped identically in both registers, where design-direction §1 says the literal mode
  // "strips even the wryness"; `lint.ts`'s identical-register check could not see it, because
  // that rule only fires when the warm form carries metaphor. It re-commits the protesting-
  // too-much the paragraph above claims to have fixed, in the same defensive shape, about the
  // same control. And "adjustments" is a term this product has not defined and never uses again,
  // so the sentence a literal-minded reader is asked to parse first turns on a word with no
  // referent. The two sentences that remain are §3's actual substance and say all of it.
  //
  // THE REGISTERS DIFFER BY IDIOM, NOT BY CONTENT: warm carries the colloquial form, literal the
  // plain one. Neither names a channel, because the dial's own `settings.intensityExplain` sits
  // directly above this line on the same screen (`IntensityDial.tsx`) and already lists all
  // seven of them — naming colour and movement again here would be the same fact twice.
  //
  // NEITHER FORM CLAIMS LEGIBILITY CHANGES WITH THE DIAL. Text contrast is fixed at accessible
  // levels at every setting (CLAUDE.md; design-direction §3), so "set it where you can read it"
  // would be a false statement about the control this screen exists to introduce.
  'firstRun.intensityNeither': {
    warm: 'Some people need plenty before anything registers. Some need much less.',
    literal: 'Some people need more to notice things. Some need much less.',
  },
  /**
   * `Settings.density` IS NAMED IN FIRST RUN (docs/RULINGS.md R156).
   *
   * At `'full'` the archive card is 90pt per row against 114pt — a ~21% density gain with no law
   * touched, no type shrunk and no ledger entry required, which is larger than every presentation
   * change in `AR1`–`AR3` combined. The lever already existed and nobody was told about it.
   *
   * THE DEFAULT DOES NOT MOVE. design-direction §4's "higher densities are opt-in, because hiding
   * can mean ceasing-to-exist" is untouched: this line says the lever exists and where it lives.
   * The user is TOLD, not switched.
   *
   * It sits beside the Intensity dial (R156) because first run already introduces the other
   * control that changes how much you see, and because a hint on the archive would be a prompt on
   * a surface product-concept §11 keeps prompt-free. It states a fact and offers nothing — no
   * "try", no "you can", no invitation to go and change something before capturing anything.
   *
   * This is the mitigation `R148` leaves owing for design-direction §4 objection 1's
   * forgetting-machine risk: a bounded ladder plus a discoverable lever.
   */
  'firstRun.density': {
    warm: 'Settings holds a density lever as well. It fits more on one screen.',
    literal: 'Settings also holds a density control. It fits more on one screen.',
  },
  // Now VISIBLE body text above the exit, not the exit's `accessibilityHint`. iOS ships
  // VoiceOver with "Speak Hints" off by default, so the only sentence stating that setup is
  // finished reached neither a sighted user nor a typical VoiceOver user (critique 1.3).
  'firstRun.done': { warm: 'That’s the whole setup.', literal: 'Setup complete.' },
  /**
   * The exit's own label.
   *
   * It used to be `capture.title` — "Catch it" / "Capture" — so the one control that leaves
   * setup asked the user to catch a thing that does not exist yet, and named its destination
   * instead of its action (critique 1.3). It says what pressing it does.
   */
  'firstRun.start': { warm: 'Start', literal: 'Start' },
  /**
   * implementation-brief §11's first definition-of-done clause: "a new user reaches first
   * capture in under 60 seconds without creating a password twice." So there is no account
   * wall before first capture, and the surface says so rather than leaving the user
   * wondering what they skipped.
   *
   * THE SECOND SENTENCE STATES A DEFAULT, NOT A FOREVER (`docs/reviews/apple-design-award-review.md`
   * `C1`). It read "Everything stays on this phone." — unconditionally — and the note here
   * justified that on two premises the auth workstream falsified: that nothing in `apps/mobile`
   * imports `packages/auth`, and that "the catalogue has no `auth.*` keys at all". Both are now
   * false. The `auth.*` family runs from this file, `SignInSection.tsx` ships, Clerk and Sign in
   * with Apple landed (`docs/DEVIATIONS.md`, Workstream: auth, `A1`/`A2`), and `auth.backupOnly`
   * states that work travels up to the server and changes travel back down. So the old sentence
   * stopped being true the moment a user found Settings, on the one screen where a reader
   * product-concept §12 calls "justifiably hostile" to being misled decides whether to trust the
   * app at all. `settings.placesExplain` is this catalogue's own precedent: a stale privacy
   * sentence is worse than none.
   *
   * IT NAMES THE CONDITION AND OFFERS NOTHING, and the distinction is load-bearing. Whether first
   * run *points* at the account is an open founder question (`docs/OPEN-QUESTIONS.md`, "The
   * onboarding fan-out", question 1: `R49`'s blocker is discharged and nobody recorded the
   * decision it was holding). `C1` authorises correcting the claim and explicitly does not decide
   * the pointer, so the deferred "sign in later" line does not return here: there is no link, no
   * control, no forward promise. Naming sign-in as the thing that changes the default is what
   * makes the sentence true, and the sentence holds whichever way question 1 is answered.
   */
  'firstRun.noAccount': {
    warm: 'No account needed. Everything stays on this phone unless you sign in.',
    literal: 'No account required. Data is stored on this device unless you sign in.',
  },

  // -------------------------------------------------------------------------
  // apps/web marketing page (implementation-brief §12: "apps/web (marketing +
  // shared-list web fallback)"; product-concept §12 gives the positioning line and
  // the ethics commitments). Deliberately small: the positioning line, what the
  // product does, and the ethics commitments that are genuinely differentiating.
  // No pricing, no testimonials, no screenshots, no store links, no launch date —
  // none of those are true yet, and product-concept §12 forbids fake urgency; a
  // marketing page implying a shipped product would be the first dishonest thing
  // in this repo.
  // -------------------------------------------------------------------------
  'marketing.hero': {
    warm: 'Every to-do app helps you start. Carn helps you finish.',
    literal: 'Carn saves your place in unfinished tasks so you can resume them.',
    allow: ['self-reference'],
    why: 'The positioning line, product-concept §12, quoted for marketing use. A marketing page is the other legitimate place (alongside firstRun.welcome) the product may name itself, since third-person description is the genre.',
  },
  'marketing.heroSub': {
    warm: 'Catch a thing in seconds. Put it down mid-swing. Pick it back up exactly where you left it.',
    literal: 'Capture takes seconds. Pausing a task keeps its context. Resuming restores it.',
  },
  'marketing.audience': {
    warm: 'Built for brains that start plenty and do not always finish. No diagnosis required.',
    literal: 'Designed for ADHD, autistic and AuDHD adults, and anyone who finds ordinary to-do apps difficult. No diagnosis is required.',
  },
  'marketing.whatItDoes.title': { warm: 'What it does', literal: 'What it does' },
  'marketing.whatItDoes.capture': {
    warm: 'One line, a photo or your voice. Nothing to file first.',
    literal: 'Capture by text, photo or voice. No categorisation is required first.',
  },
  'marketing.whatItDoes.resume': {
    warm: 'Put a task down mid-swing and it keeps your place: what it is, and what comes next.',
    literal: 'Pausing a task saves its context: the last step and the next one.',
  },
  'marketing.whatItDoes.sharedLists': {
    warm: 'A shopping list the whole house can add to, even the one person without the app.',
    literal: 'Shared shopping lists. A person without the app can still add items from a browser.',
  },
  'marketing.ethics.title': { warm: 'What it won’t do', literal: 'What it won’t do' },
  'marketing.ethics.noStreaks': {
    warm: 'Time away never costs you anything. Nothing here tracks a run of days.',
    literal: 'No penalty for time away. Continuity is not tracked.',
  },
  'marketing.ethics.noOverdue': {
    warm: 'A hard date gets size and place on the page, never an alarm colour.',
    literal: 'Fixed deadlines are shown by size and position, never by colour used as a warning.',
  },
  'marketing.ethics.sensoryFree': {
    warm: 'Every sensory and accessibility setting is free, always.',
    literal: 'All sensory and accessibility settings are free permanently.',
  },
  'marketing.ethics.cancelTwoTaps': {
    warm: 'Cancel a subscription in two taps, whenever you want.',
    literal: 'Cancel a subscription in two taps.',
  },
  'marketing.nonClinical': {
    warm: 'A tool, not a treatment. No symptom claims.',
    literal: 'A tool, not a treatment. No clinical claims are made.',
  },

  // -------------------------------------------------------------------------
  // apps/web site navigation — the footer on all four pages.
  //
  // Authorised by `docs/marketing/app-store-listing.md` §URLs, which requires a
  // Support URL that "must resolve and offer a contact route" and a Privacy Policy
  // URL, and names `apps/web` as the host of both. Four pages need a way between
  // them, and design-direction §4's fixed spatial grammar means the same four
  // links in the same order in the same place on every page.
  //
  // NOT `nav.*`. That prefix is the mobile app's five permanent tabs, and
  // `lint.ts`'s `collidesWithinSurface` checks every `nav.` key against the state
  // vocabulary printed on top of it. A website footer shares neither surface nor
  // that constraint, so it gets its own prefix rather than borrowing one whose
  // collision rule is about something else.
  //
  // `siteNav.terms` is LAST, and that position is the point. §4 forbids
  // rearrangement, and About/Privacy/Support are already three learned positions;
  // slotting Terms between Privacy and Support to group the legal pages together
  // would move Support for every reader who has already found it once. Appending
  // costs a slightly worse grouping and moves nothing (docs/DEVIATIONS.md W3).
  // -------------------------------------------------------------------------
  'siteNav.about': { warm: 'About', literal: 'About' },
  'siteNav.privacy': { warm: 'Privacy', literal: 'Privacy' },
  'siteNav.support': { warm: 'Support', literal: 'Support' },
  'siteNav.terms': { warm: 'Terms', literal: 'Terms' },

  // -------------------------------------------------------------------------
  // apps/web privacy page (`/privacy`).
  //
  // Authorised by `docs/marketing/app-store-listing.md` §URLs: "**Privacy Policy
  // URL** | Required. §14 risk 1: 'this audience reads the permissions screen.'
  // Plain English, not boilerplate." That instruction is why this is written in the
  // catalogue's own voice rather than pasted from a generator: the register laws are
  // exactly what "plain English, not boilerplate" means here, and a privacy policy
  // this audience cannot parse fails its own purpose.
  //
  // EVERY CLAIM BELOW IS SOURCED FROM CODE, NOT FROM THE BRIEF'S INTENT. The
  // temptation in a privacy policy is to restate the invariant one wishes were true.
  // Where the shipped behaviour is narrower than the ambition, this copy states the
  // shipped behaviour:
  //   - on-device location/audio/photo: `apps/mobile/app.config.ts` `infoPlist`
  //     usage strings, `src/native/photo.ts` (re-encode strips EXIF, RULINGS R116),
  //     `src/native/speech.ts` (on-device transcription).
  //   - what uploads: `packages/schema/src/pg/schema.ts` — the `capsules` table
  //     carries `voiceNoteTranscript` (text, uploads) and `voiceNoteUri`/`photoUri`
  //     (local file references; RULINGS R118 records that there is no media upload).
  //   - uploads only when signed in: `auth.deviceOnly` / `auth.signInExplain`.
  //   - the model route: `functions/src/lib/ai-gateway.ts` (Netlify AI Gateway to
  //     Anthropic) and `functions/handlers/ai-proxy.ts` (Plus-gated, per-request).
  //   - tier lookups: RevenueCat, via `functions/src/lib/entitlement.ts`.
  //   - sign-in: Clerk (`apps/mobile/src/auth/clerkAdapter.ts`).
  //   - notifications: local only — `app.config.ts` declares no
  //     `remote-notification` background mode (RULINGS R7).
  //   - no analytics: `apps/mobile/package.json` carries no analytics dependency.
  //   - the database region: `netlify.toml`'s Netlify Database comment records the
  //     region as a decision (RULINGS R136). `privacy.uploads.where` states it
  //     plainly and names the transfer safeguard rather than leaving a UK/EU reader
  //     to infer one from the processor's nationality.
  //   - the Art. 13 contact detail: a dedicated `support@usecarn.app`, named beside
  //     the controller's identity (RULINGS R141). `usecarn.app` is the product
  //     domain `apps/mobile/eas.json` already builds against.
  //   - not for under-13s: `privacy.age.body` (RULINGS R140), paired with
  //     `siteTerms.age.*` on `/terms`.
  //
  // THE REGISTERED POSTAL ADDRESS IS THE ONLY THING STILL NOT STATED. RULINGS R137
  // records it as deliberately unsupplied by founder decision — HJ Digital is a sole
  // trader and the address is a TODO, not an oversight — and it stays that way. No
  // placeholder, no "address on request", no support-form substitute: a placeholder
  // reads as a statement. This is the last hard blocker on submission.
  // -------------------------------------------------------------------------
  'privacy.title': { warm: 'Privacy', literal: 'Privacy' },
  'privacy.updated': {
    warm: 'Last updated {when}.',
    literal: 'Last updated {when}.',
    slots: ['when'],
  },
  'privacy.intro': {
    warm: 'This page says what stays on your phone, what leaves it, and who holds it.',
    literal: 'This page states what data stays on your device, what is transmitted, and who holds it.',
  },
  'privacy.summary.title': { warm: 'The short version', literal: 'Summary' },
  'privacy.summary.location': {
    warm: 'Your location stays on this phone. Arriving at a shop is worked out on the device.',
    literal: 'Your location stays on this device. Geofence arrival is evaluated on the device.',
  },
  'privacy.summary.audio': {
    warm: 'Your audio stays on this phone. A voice line is transcribed on the device.',
    literal: 'Your audio stays on this device. Voice recordings are transcribed on the device.',
  },
  'privacy.summary.photos': {
    warm: 'A photo you attach stays on this phone. The image file is not uploaded.',
    literal: 'Photos you attach stay on this device. Image files are not uploaded.',
  },
  'privacy.summary.noAccount': {
    warm: 'Without an account, nothing leaves the phone at all.',
    literal: 'Without an account, no data leaves the device.',
  },
  'privacy.summary.noTracking': {
    warm: 'No analytics, no advertising, and no tracking across other apps.',
    literal: 'There is no analytics, no advertising, and no cross-app tracking.',
  },
  'privacy.controller.title': { warm: 'Who holds your data', literal: 'Who holds your data' },
  'privacy.controller.body': {
    warm: 'HJ Digital is the data controller for Carn, and is based in the United Kingdom.',
    literal: 'HJ Digital is the data controller for Carn, and is based in the United Kingdom.',
  },
  'privacy.controller.law': {
    warm: 'The UK GDPR applies. So does the EU GDPR, if you are in the European Union.',
    literal: 'The UK GDPR applies. The EU GDPR also applies if you are in the European Union.',
  },
  // RULINGS R141 — the Art. 13 contact detail, named beside the controller's identity
  // rather than left to the support page alone. `usecarn.app` is the product domain
  // `apps/mobile/eas.json` already builds against, so this names a real address rather
  // than a placeholder domain.
  'privacy.controller.contact': {
    warm: 'Write to support@usecarn.app, which reaches a person.',
    literal: 'Email support@usecarn.app with any question about this page or your data.',
  },
  // RULINGS R140 — 13+ to use, mirrored on `/terms` by `siteTerms.age.*`. Answers the
  // shared-list question a child's data would otherwise raise, by stating there is none
  // to answer for: Carn does not knowingly collect or keep it.
  'privacy.age.title': { warm: 'Age', literal: 'Age' },
  'privacy.age.body': {
    warm: 'This app is not meant for anyone under 13. No child’s data is knowingly collected or kept.',
    literal: 'This app is not for anyone under 13. No child’s data is knowingly collected or kept.',
  },
  'privacy.onDevice.title': {
    warm: 'What never leaves your phone',
    literal: 'What never leaves your device',
  },
  'privacy.onDevice.location': {
    warm: 'Raw location is never sent anywhere. Only the shops you add are watched, never your movements.',
    literal: 'Raw location is never transmitted. Only the shops you add are monitored, not your movements.',
  },
  'privacy.onDevice.audio': {
    warm: 'A voice line is transcribed on the phone. The recording itself is never uploaded.',
    literal: 'Voice recordings are transcribed on the device. The audio file is never uploaded.',
  },
  'privacy.onDevice.photos': {
    warm: 'A photo is re-encoded on the phone, which strips its GPS tag. The file stays here.',
    literal: 'Photos are re-encoded on the device, which removes the GPS tag. Image files are not uploaded.',
  },
  'privacy.onDevice.why': {
    warm: 'This is built in, not promised. Raw location and raw audio have no route off the phone.',
    literal: 'This is enforced in the software, not by policy alone. Raw location and audio have no upload path.',
  },
  'privacy.uploads.title': {
    warm: 'What is uploaded, and when',
    literal: 'What is uploaded, and when',
  },
  'privacy.uploads.onlySignedIn': {
    warm: 'Nothing is uploaded until you sign in. Signing in is optional.',
    literal: 'No data is uploaded until you sign in. Signing in is optional.',
  },
  'privacy.uploads.what': {
    warm: 'Signed in, your text goes up: titles, notes, next steps, and place labels you type.',
    literal: 'When signed in, text is uploaded: task titles, notes, next steps, and place labels you type.',
  },
  'privacy.uploads.transcripts': {
    warm: 'A voice transcript is text, so it goes up with the rest. The recording does not.',
    literal: 'A voice transcript is text, so it is uploaded. The audio recording is not.',
  },
  'privacy.uploads.also': {
    warm: 'Session history, shopping lists, and your settings go up too.',
    literal: 'Session history, shopping lists, and your settings are also uploaded.',
  },
  'privacy.uploads.why': {
    warm: 'The reason is backup, and shared lists. There is no other use.',
    literal: 'The purpose is backup and shared lists. There is no other use.',
  },
  // RULINGS R136 — states the storage location plainly rather than leaving it to be
  // inferred from Netlify's nationality, and names the transfer safeguard for a UK or
  // EU reader. `netlify.toml` records the same region as a decision.
  //
  // THE TWO NAMES ARE LOAD-BEARING AND STAY. R136 makes naming the safeguard the whole
  // point — an unnamed safeguard is not a disclosure — so "UK IDTA" and "EU SCCs" cannot
  // be dropped for being jargon. What CAN carry the reader is the sentence around them:
  // the plain statement of what the thing is and what it does comes first, and the name
  // arrives second, as a label for something already understood. Do not reorder that.
  'privacy.uploads.where': {
    warm: 'What you upload sits on Netlify’s servers in the United States. A contract makes Netlify protect it the same way UK and EU law does. For the UK that contract is the UK IDTA. For the EU it is the EU SCCs.',
    literal: 'What you upload is stored on Netlify servers in the United States. A contract requires Netlify to give it the same protection UK and EU law gives it. For the UK, that contract is the UK IDTA — the International Data Transfer Addendum. For the EU, it is the EU SCCs — the Standard Contractual Clauses.',
  },
  'privacy.sharing.title': { warm: 'Shared lists', literal: 'Shared lists' },
  'privacy.sharing.link': {
    warm: 'A share link keeps its key after the #, which a browser never sends to a server.',
    literal: 'A share link holds its key in the URL fragment, which browsers do not transmit to servers.',
  },
  'privacy.sharing.visitor': {
    warm: 'Anyone with the link can read that list and add to it. Nothing else of yours.',
    literal: 'Anyone holding the link can read and add to that list only. No other data is exposed.',
  },
  'privacy.sharing.visitorId': {
    warm: 'A visitor browser keeps a random id, so a row can say who added it.',
    literal: 'A visitor browser stores a random identifier, to label who added an item.',
  },
  'privacy.sharing.revoke': {
    warm: 'You can revoke a link. The page then says so, and takes nothing further.',
    literal: 'You can revoke a share link. The page then reports that the link is no longer valid.',
  },
  'privacy.ai.title': { warm: 'AI features', literal: 'AI features' },
  'privacy.ai.what': {
    warm: 'Ask for AI sorting or a breakdown, and that text is sent to a model to answer it.',
    literal: 'If you request AI sorting or a task breakdown, that text is sent to a language model.',
  },
  'privacy.ai.route': {
    warm: 'The route is Netlify AI Gateway to Anthropic. Nothing else of yours travels with it.',
    literal: 'Requests are routed through Netlify AI Gateway to Anthropic. No other data is included.',
  },
  'privacy.ai.optIn': {
    warm: 'This is part of Carn Plus, and only ever happens when you ask for it.',
    literal: 'This is part of Carn Plus. No text is sent unless you request it.',
  },
  'privacy.ai.noProfile': {
    warm: 'No picture of you is built from those requests.',
    literal: 'No user profile is built from those requests.',
  },
  'privacy.payments.title': { warm: 'Subscriptions', literal: 'Subscriptions' },
  'privacy.payments.body': {
    warm: 'The App Store or Google Play takes the payment. No card details ever reach Carn.',
    literal: 'Payment is handled by the App Store or Google Play. Card details never reach Carn.',
  },
  'privacy.payments.revenuecat': {
    warm: 'RevenueCat keeps the record of whether a subscription is on, against your account id.',
    literal: 'RevenueCat stores subscription status against your account identifier.',
  },
  'privacy.signIn.title': { warm: 'Signing in', literal: 'Signing in' },
  'privacy.signIn.body': {
    warm: 'Sign-in is a link sent to your email address, run by Clerk. There is no password.',
    literal: 'Sign-in uses a link emailed to you, provided by Clerk. No password is stored.',
  },
  'privacy.signIn.what': {
    warm: 'Clerk keeps your email address, and the session for this device.',
    literal: 'Clerk stores your email address and this device session.',
  },
  'privacy.notifications.title': { warm: 'Notifications', literal: 'Notifications' },
  'privacy.notifications.body': {
    warm: 'Notifications are scheduled on the phone. Nothing is pushed to you from a server.',
    literal: 'Notifications are scheduled on the device. No server sends push notifications.',
  },
  'privacy.tracking.title': { warm: 'No tracking', literal: 'No tracking' },
  'privacy.tracking.body': {
    warm: 'There is no analytics kit, no advertising id, and no third-party tracker in the app.',
    literal: 'The app contains no analytics SDK, no advertising identifier, and no third-party trackers.',
  },
  'privacy.tracking.logs': {
    warm: 'Server logs record requests with your account id, and Netlify keeps them briefly.',
    literal: 'Server logs record requests with your account identifier. Netlify retains them briefly.',
  },
  'privacy.website.title': { warm: 'This website', literal: 'This website' },
  'privacy.website.body': {
    warm: 'This site sets no cookies, and runs no analytics.',
    literal: 'This website sets no cookies and runs no analytics.',
  },
  'privacy.website.form': {
    warm: 'A message you send from the support page reaches Netlify Forms, and then an inbox.',
    literal: 'Support messages are received by Netlify Forms and forwarded to an email inbox.',
  },
  'privacy.website.formWhat': {
    warm: 'That holds your email address and what you write, and nothing else.',
    literal: 'It stores your email address and the message text only.',
  },
  'privacy.keeping.title': {
    warm: 'How long things are kept',
    literal: 'How long data is kept',
  },
  'privacy.keeping.appendOnly': {
    warm: 'History is added to, never rewritten. A deletion marks a record, rather than erasing it.',
    literal: 'History is append-only. A deletion marks the record as deleted rather than erasing it.',
  },
  'privacy.keeping.recovery': {
    warm: 'A value you replace stays recoverable for {days} days, and then it goes.',
    literal: 'A replaced value is recoverable for {days} days, and is then removed.',
    slots: ['days'],
  },
  'privacy.rights.title': { warm: 'Your rights', literal: 'Your rights' },
  /**
   * THE IN-APP CONTROL IS NAMED FIRST NOW, AND THE SUPPORT PAGE IS THE FALLBACK (docs/RULINGS.md
   * R134 build consequence 4).
   *
   * This said erasure was a request through the support page, which was the honest sentence while
   * `DELETE /api/v1/account` tombstoned the settings row only — "honest, and a promise somebody
   * has to personally keep on a one-month statutory clock", as R134 puts it. The endpoint erases
   * for real now, so the page names the control that does it. The support route stays in
   * `privacy.rights.support` rather than being deleted: a person who has lost the device cannot
   * press anything in the app.
   */
  'privacy.rights.body': {
    warm: 'You can ask for a copy of your data, a correction, or erasure. A copy and a correction reach a person on the support page.',
    literal:
      'You can request a copy of your data, a correction, or erasure. Requests for a copy or a correction go through the support page.',
  },
  /**
   * NAMES WHAT THE CONTROL REACHES (docs/reviews/erasure-critic-pass.md; `RULINGS R207`).
   *
   * This said "the app performs erasure itself", which the critic pass narrowed because
   * `functions/src/lib/account-erasure.ts` left the four append-only child logs whole — an
   * unqualified Art. 17 claim on a privacy policy is the one kind of copy drift in this repository
   * that is a legal problem rather than a quality one. R207 widens the erasure to those logs, so
   * "their history" joins the list. It is still a LIST rather than the word "everything": a shared
   * list is not this user's alone to remove and stays, and `privacy.sharing.*` is where that lives.
   */
  'privacy.rights.erase': {
    warm: 'In the app, open Settings, then Your data. That takes your loops, their history and your settings off the server.',
    literal:
      'In the app, open Settings, then Your data. That control removes uploaded tasks, their history and your settings from the server.',
  },
  /**
   * The support route, for a lost device — and for that alone since `RULINGS R207`. It carried the
   * change log too, because the log survived an erasure and someone had to be able to ask for it;
   * the control reaches it now, so asking a person for it would send a reader to a slower route for
   * a thing two presses already do.
   */
  'privacy.rights.support': {
    warm: 'If the phone is gone, ask on the support page and a person will do it.',
    literal:
      'If you no longer have the device, ask on the support page and a person will do it for you.',
  },
  'privacy.rights.complain': {
    warm: 'You can also complain to the Information Commissioner’s Office in the United Kingdom.',
    literal: 'You can also complain to the Information Commissioner’s Office in the United Kingdom.',
  },
  'privacy.changes.title': { warm: 'Changes to this page', literal: 'Changes to this page' },
  'privacy.changes.body': {
    warm: 'A change here changes the date at the top of the page.',
    literal: 'Any change to this page updates the date shown at the top.',
  },

  // -------------------------------------------------------------------------
  // apps/web support page (`/support`).
  //
  // Authorised by `docs/marketing/app-store-listing.md` §URLs: "**Support URL** |
  // Required. Must resolve and offer a contact route. `apps/web` is the natural
  // host." The contact route is a Netlify Forms submission — recorded as a
  // deviation, since it is server behaviour outside `functions/` (implementation
  // brief §3 makes Netlify Functions the whole API).
  //
  // The form's copy carries no response-time promise. §12 forbids fake urgency, and
  // its mirror image — an invented service level nobody is staffed to meet — is a
  // promise this repository cannot keep. "A person reads every message" is a
  // commitment about who, which is true, rather than about when, which is not known.
  // -------------------------------------------------------------------------
  'support.title': { warm: 'Support', literal: 'Support' },
  'support.intro': {
    warm: 'Something not working, or a question about your data? Write, and a person reads it.',
    literal: 'Report a problem or ask a question here. A person reads every message.',
  },
  // RULINGS R141 — the dedicated address, named directly rather than left implicit in
  // the form's routing. `usecarn.app` is the product domain `apps/mobile/eas.json`
  // already builds against.
  'support.direct': {
    warm: 'Or write straight to support@usecarn.app.',
    literal: 'You can also email support@usecarn.app directly.',
  },
  'support.include.title': { warm: 'What helps', literal: 'What to include' },
  'support.include.body': {
    warm: 'Say what you expect, and what happens instead. Your phone and app version help too.',
    literal: 'Describe what you expect and what happens instead. Include your device and app version.',
  },
  'support.form.title': { warm: 'Send a message', literal: 'Send a message' },
  'support.form.emailLabel': { warm: 'Your email address', literal: 'Your email address' },
  'support.form.emailHint': {
    warm: 'Only so a reply can reach you.',
    literal: 'Used only to send you a reply.',
  },
  'support.form.messageLabel': { warm: 'What is happening', literal: 'What is happening' },
  'support.form.messagePlaceholder': {
    warm: 'What you expect, and what happens instead.',
    literal: 'What you expect, and what happens instead.',
  },
  'support.form.submit': { warm: 'Send', literal: 'Send' },
  'support.form.sending': { warm: 'Sending', literal: 'Sending' },
  'support.form.sent': {
    warm: 'That is sent. A reply comes to the address above.',
    literal: 'Your message is sent. A reply goes to the address above.',
  },
  'support.form.failed': {
    warm: 'The message could not be sent. Try again in a moment.',
    literal: 'The message could not be sent. Try again in a moment.',
  },
  'support.form.needEmail': {
    warm: 'An email address is needed, so a reply can reach you.',
    literal: 'An email address is required for a reply.',
  },
  'support.form.needMessage': {
    warm: 'A message is needed before this can send.',
    literal: 'A message is required before sending.',
  },
  'support.data.title': { warm: 'Your data', literal: 'Your data' },
  /**
   * SAME CHANGE, SAME REASON AS `privacy.rights.body` (docs/RULINGS.md R134 build consequence 4):
   * erasure is a control in the app, and this page is the fallback for the person who cannot
   * reach it. Asking someone to write a message for a thing they can do in two presses is the
   * kind of small dishonesty product-concept §12's audience notices.
   */
  'support.data.body': {
    warm: 'Ask here for a copy of your data or a correction. The privacy page says what is held.',
    literal: 'Request a copy of your data or a correction here. The privacy page lists what is held.',
  },
  /** Same scope, same reason as `privacy.rights.erase` (`RULINGS R207`). */
  'support.data.erase': {
    warm: 'In the app, open Settings, then Your data. That takes your loops, their history and your settings off the server.',
    literal:
      'To remove uploaded tasks, their history and your settings from the server, open the app: Settings, then Your data.',
  },
  /** Back to the lost device alone, with `privacy.rights.support` and for its reason (`R207`). */
  'support.data.lostDevice': {
    warm: 'If the phone is gone, ask here and a person will do it.',
    literal: 'If you no longer have the device, ask here and a person will do it for you.',
  },
  'support.accessibility.title': { warm: 'Accessibility', literal: 'Accessibility' },
  'support.accessibility.body': {
    warm: 'If something is hard to see, hear or reach, that is a fault. Say so and it gets fixed.',
    literal: 'Report anything difficult to see, hear or reach. It is treated as a defect.',
  },

  // -------------------------------------------------------------------------
  // apps/web terms of use page (`/terms`).
  //
  // FOUNDER-DIRECTED, and recorded in `docs/DEVIATIONS.md` **W3** rather than
  // presented as authorised: `docs/marketing/app-store-listing.md` §URLs names three
  // URLs and says "All three are built and live in `apps/web`". This is a fourth. The
  // requirement behind it is real — product-concept §12 makes Carn Plus an
  // auto-renewing subscription, and App Store Review 3.1.2 wants a functional Terms of
  // Use link wherever that is sold — but §URLs does not name the page, so W3 is the
  // entry that says so.
  //
  // `siteTerms.` AND NOT `terms.`. `terms` is already an exported `CopyEntry` map in
  // this file (the metaphor/literal glossary), and `lintAll` lints it under its own map
  // name — so `terms.released` is a real, citable thing today, and a copy key called
  // `terms.title` would make every future `terms.x` citation ambiguous. Same reasoning
  // as `siteNav.` above, for the same kind of reason.
  //
  // EVERY CLAUSE HERE IS A STATEMENT ABOUT THE SHIPPED BINARY, on the same rule the
  // `privacy.*` block sets out: a term describing behaviour the app does not have is
  // the one kind of copy drift in this repository that is a legal problem rather than a
  // quality one. What each clause rests on:
  //   - offline, device-first: implementation-brief §2 ("the local write is the real
  //     write"), and `apps/mobile/src/data/engine.ts`.
  //   - the store bills and refunds: `functions/src/lib/entitlement.ts` (RevenueCat
  //     reads the store's receipt; Carn never touches a card), `plus.cancelExplain`.
  //   - cancelling takes nothing away: `plus.cancelKeepsWork`, itself grounded in
  //     CLAUDE.md's append-only law.
  //   - a shared link is a capability anyone holding it can use, and is revocable:
  //     RULINGS **R67**, **R68**, and `sharedList.linkExpired`.
  //   - AI is opt-in and per-request: `functions/handlers/ai-proxy.ts`.
  //   - non-clinical: product-concept §12, and `marketing.nonClinical` already says it
  //     on `/`. It is repeated here because a listing claim and a contractual term are
  //     read by different people for different reasons.
  //   - erasure of uploaded data is a manual support request:
  //     `functions/handlers/account.ts` tombstones the settings row only, so promising
  //     a self-serve delete would be false (docs/DEVIATIONS.md **W2** item 2).
  //   - the governing-law clause: RULINGS **R133**. England and Wales, and its courts
  //     — Wales shares England's contract-law jurisdiction, and
  //     `docs/marketing/app-store-listing.md` records the founder as Welsh.
  //   - the liability cap: RULINGS **R138**. The last twelve months of Carn Plus fees
  //     paid, with `siteTerms.noWarranty.statutory` carrying the consumer-law override
  //     that already sat on the page.
  //   - the age clauses: RULINGS **R140**. 13+ to use, matching `privacy.age.body`;
  //     18+, or parental consent, to subscribe — thirteen is the UK's Art. 8 age of
  //     consent for information-society services.
  //
  // THERE IS DELIBERATELY NO ARBITRATION CLAUSE AND NO CLASS-ACTION WAIVER. RULINGS
  // **R139** confirms the absence as a decision rather than a gap: disputes go to the
  // courts of England and Wales per R133, and an arbitration clause unenforceable
  // against the actual UK/EU audience is decoration that reads as hostility on a page
  // whose voice is deliberately plain.
  //
  // A NOTE ON REGISTER, because a legal page is where the temptation to abandon it is
  // strongest: these strings pass the same linter as every other string in the product
  // — 22 words a sentence, second person, present tense, no metaphor in the literal
  // register. Contract boilerplate fails all four, and a reader who cannot parse the
  // agreement has not agreed to anything in any sense that matters. §14 risk 1 ("this
  // audience reads the permissions screen") applies here at least as hard as it does to
  // `/privacy`.
  // -------------------------------------------------------------------------
  'siteTerms.title': { warm: 'Terms of use', literal: 'Terms of use' },
  'siteTerms.updated': {
    warm: 'Last updated {when}.',
    literal: 'Last updated {when}.',
    slots: ['when'],
  },
  'siteTerms.intro': {
    warm: 'These terms are the agreement between you and HJ Digital about using Carn. By using it, you accept them.',
    literal:
      'These terms are the agreement between you and HJ Digital covering your use of Carn. By using it, you accept them.',
  },
  'siteTerms.who.title': { warm: 'Who provides Carn', literal: 'Who provides Carn' },
  'siteTerms.who.body': {
    warm: 'HJ Digital makes Carn, and is based in the United Kingdom.',
    literal: 'HJ Digital provides Carn and is based in the United Kingdom.',
  },
  'siteTerms.who.contact': {
    warm: 'The support page reaches a person, and is the place to write about these terms.',
    literal: 'Use the support page to contact a person about these terms.',
  },
  'siteTerms.notMedical.title': { warm: 'Not a medical device', literal: 'Not a medical device' },
  'siteTerms.notMedical.body': {
    warm: 'This is a tool, not a treatment. Nothing here diagnoses or treats anything.',
    literal: 'This app is a tool and not a treatment. It makes no clinical claims.',
  },
  'siteTerms.notMedical.advice': {
    warm: 'Use it alongside medical advice, never in place of it.',
    literal: 'Do not use this app in place of medical advice.',
  },
  'siteTerms.yourContent.title': {
    warm: 'Your things stay yours',
    literal: 'Your content stays yours',
  },
  'siteTerms.yourContent.body': {
    warm: 'What you write, photograph and say is yours. HJ Digital claims no ownership of it.',
    literal:
      'The text, photos and recordings you create remain yours. HJ Digital claims no ownership of them.',
  },
  'siteTerms.yourContent.device': {
    warm: 'The original lives on your phone. A copy uploads only once you sign in.',
    literal: 'The original is held on your device. A copy is uploaded only when you sign in.',
  },
  'siteTerms.account.title': { warm: 'Accounts', literal: 'Accounts' },
  'siteTerms.account.optional': {
    warm: 'An account is optional. Signing in adds a backup, and changes nothing else.',
    literal: 'An account is optional. Signing in adds a server backup and changes nothing else.',
  },
  'siteTerms.account.keep': {
    warm: 'Sign-in is a link sent to your email address. Keep that inbox to yourself.',
    literal: 'Sign-in uses a link emailed to you. Keep access to that email account secure.',
  },
  'siteTerms.account.one': {
    warm: 'An account is for one person. Do not hand it to someone else.',
    literal: 'An account is for one person. Do not share it with anyone else.',
  },
  // RULINGS R140 — 13+ to use, 18+ or parental consent to subscribe. Thirteen is the
  // UK's Art. 8 age of consent for information-society services. `privacy.age.body`
  // carries the matching not-for-under-13s statement. No age gate is built: a gate is
  // an interrogation at first run, which product-concept §11 exists to avoid.
  'siteTerms.age.title': { warm: 'Age', literal: 'Age' },
  'siteTerms.age.use': {
    warm: 'You must be at least 13 to use Carn.',
    literal: 'You must be at least 13 years old to use Carn.',
  },
  // Two sentences rather than one with "or have parental consent" wedged into the middle:
  // the threshold and the exception are separate facts, and a reader who is 18 can stop
  // after the first. Same two thresholds, same effect.
  'siteTerms.age.subscribe': {
    warm: 'You must be 18 to subscribe to Carn Plus. Under 18, a parent has to agree first.',
    literal: 'You must be 18 to subscribe to Carn Plus. If you are under 18, a parent must agree.',
  },
  'siteTerms.plus.title': { warm: 'Carn Plus', literal: 'Carn Plus' },
  'siteTerms.plus.free': {
    warm: 'The free tier stays free. Plus is an optional subscription on top of it.',
    literal: 'The free tier remains free. Carn Plus is an optional paid subscription.',
  },
  'siteTerms.plus.billing': {
    warm: 'The App Store or Google Play takes the payment, and renews it until you cancel.',
    literal:
      'The App Store or Google Play collects payment and renews the subscription until you cancel.',
  },
  'siteTerms.plus.cancel': {
    warm: 'Cancel in two taps. Everything you have already made stays exactly as it is.',
    literal: 'Cancellation takes two taps. Everything already saved remains exactly as it is.',
  },
  'siteTerms.plus.refunds': {
    warm: 'Refunds belong to the store. Ask the one you bought the subscription from.',
    literal: 'Refunds are handled by the store. Request one from the store you purchased through.',
  },
  'siteTerms.store.title': {
    warm: 'The store’s terms also apply',
    literal: 'The store’s terms also apply',
  },
  'siteTerms.store.body': {
    warm: 'A purchase through a store is also covered by that store’s own terms.',
    literal: 'A purchase made through an app store is also governed by that store’s terms.',
  },
  'siteTerms.store.notParty': {
    warm: 'Apple and Google are not party to this agreement. It is between you and HJ Digital.',
    literal:
      'Apple and Google are not parties to this agreement, which is between you and HJ Digital.',
  },
  'siteTerms.store.support': {
    warm: 'HJ Digital answers support for Carn, and the store does not.',
    literal: 'HJ Digital provides support for this app. The stores do not.',
  },
  'siteTerms.sharing.title': { warm: 'Shared lists', literal: 'Shared lists' },
  'siteTerms.sharing.link': {
    warm: 'A shared list link works for anyone holding it. You choose who gets one.',
    literal: 'A shared-list link grants access to anyone holding it. You choose who receives it.',
  },
  'siteTerms.sharing.responsible': {
    warm: 'What goes in a shared list, you are sharing. Send a link to people you trust.',
    literal:
      'You are responsible for what you add to a shared list, and for who you send a link to.',
  },
  'siteTerms.sharing.revoke': {
    warm: 'You can turn a link off at any time. Then it stops working.',
    literal: 'You can revoke a link at any time, and it then stops working.',
  },
  'siteTerms.sharing.misuse': {
    warm: 'Do not use a shared list to send anything unlawful, or to harass anyone.',
    literal: 'Do not use a shared list to send unlawful content or to harass anyone.',
  },
  'siteTerms.ai.title': { warm: 'AI features', literal: 'AI features' },
  'siteTerms.ai.optional': {
    warm: 'AI features are optional. You ask for them, and nothing is sent on its own.',
    literal: 'AI features are optional. A request is sent only when you ask for one.',
  },
  'siteTerms.ai.wrong': {
    warm: 'A model can be wrong. Check anything that matters before you act on it.',
    literal: 'Model output can be incorrect. Check anything important before relying on it.',
  },
  'siteTerms.ai.yours': {
    warm: 'A suggestion is a suggestion. The decision stays yours.',
    literal: 'Suggestions are suggestions. Decisions remain yours.',
  },
  'siteTerms.use.title': { warm: 'Fair use', literal: 'Acceptable use' },
  'siteTerms.use.lawful': {
    warm: 'Use Carn lawfully, and not to harm anyone.',
    literal: 'Use this app lawfully and not to harm other people.',
  },
  'siteTerms.use.service': {
    warm: 'Do not attack the service, overload it, or reach for other people’s data.',
    literal: 'Do not attack or overload the service, or attempt to access other people’s data.',
  },
  'siteTerms.use.resell': {
    warm: 'Do not resell Carn, or pass a copy of it off as your own.',
    literal: 'Do not resell this app or redistribute it as your own.',
  },
  'siteTerms.availability.title': { warm: 'Availability', literal: 'Availability' },
  'siteTerms.availability.offline': {
    warm: 'The app works offline. The write to your phone is the real write.',
    literal: 'The app works offline. The write to your device is the authoritative one.',
  },
  'siteTerms.availability.server': {
    warm: 'The server can go down for maintenance. Your phone keeps working while it is down.',
    literal:
      'The server can be unavailable for maintenance. The app continues to work on your device.',
  },
  'siteTerms.availability.change': {
    warm: 'Features can change between versions. The promises on the about page hold.',
    literal: 'Features can change between releases. The commitments on the about page hold.',
  },
  'siteTerms.noWarranty.title': {
    warm: 'What is not promised',
    literal: 'Limits of this agreement',
  },
  'siteTerms.noWarranty.asIs': {
    warm: 'The app comes as it is. Nothing here promises it is free of faults.',
    literal: 'The app is provided as it is, with no warranty that it is free of defects.',
  },
  // RULINGS R138 — a cap expressed as a number, replacing "as far as the law allows".
  // `siteTerms.noWarranty.statutory`, directly below, already carries the consumer-law
  // override this cap does not touch.
  //
  // "Liability is capped at" is the term of art and R138 does NOT fix it — it fixes the
  // twelve-month scope and the amount, and asks for "the page's existing plain register —
  // no legalese drift". So the clause says what the cap does to the reader (there is a
  // limit on what HJ Digital has to pay you) and then what the limit is. Same scope, same
  // amount, same consumer-law override on the line below.
  'siteTerms.noWarranty.liability': {
    warm: 'If you claim against HJ Digital, there is a limit on what it has to pay you. That limit is what you paid for Carn Plus in the twelve months before your claim.',
    literal: 'If you make a claim against HJ Digital, there is a limit on what it has to pay you. The limit is the Carn Plus fees you paid in the twelve months before that claim.',
  },
  'siteTerms.noWarranty.statutory': {
    warm: 'Your rights as a consumer stand, whatever this page says.',
    literal: 'Your statutory rights as a consumer are not affected by these terms.',
  },
  'siteTerms.noWarranty.backup': {
    warm: 'Keep your own copy of anything you cannot afford to lose.',
    literal: 'Keep your own backup of anything you cannot afford to lose.',
  },
  'siteTerms.ending.title': { warm: 'Ending this', literal: 'Ending this agreement' },
  'siteTerms.ending.you': {
    warm: 'You can stop at any time. Deleting the app removes what it holds on the phone.',
    literal:
      'You can stop using the app at any time. Deleting it removes the data held on your device.',
  },
  /**
   * CORRECTED WITH THE ENDPOINT, NOT LEFT TO GO STALE (docs/RULINGS.md R134).
   *
   * This said uploaded data "takes a request through the support page to remove", and the block
   * comment above this page's keys gives the reason it said so: "erasure of uploaded data is a
   * manual support request: `functions/handlers/account.ts` tombstones the settings row only, so
   * promising a self-serve delete would be false". R134 makes the self-serve delete real, which
   * makes the old sentence the false one. A contractual term describing behaviour the app no
   * longer has is the one kind of copy drift in this repository that is a legal problem rather
   * than a quality one — that rule is this block's own, and it applies in this direction too.
   *
   * `RULINGS R207` widens it again: the change log goes with the rest, so "the rest" the support
   * page covered is now a phone the reader no longer has.
   */
  'siteTerms.ending.uploaded': {
    warm: 'Uploaded loops, their history and settings can be removed from the app: Settings, then Your data. The support page covers a phone you no longer have.',
    literal:
      'Remove uploaded tasks, their history and settings from within the app: Settings, then Your data. Use the support page if you no longer have the device.',
  },
  'siteTerms.ending.provider': {
    warm: 'Access can end if these terms are broken. That is the only reason for it.',
    literal: 'Access can be withdrawn for a breach of these terms, and for no other reason.',
  },
  // RULINGS R133 — England and Wales, and its courts. Wales shares England's
  // contract-law jurisdiction, and `docs/marketing/app-store-listing.md` records the
  // founder as Welsh. Settles the jurisdiction half of `docs/DEVIATIONS.md` W1/W2's
  // unknown 1; R137's postal address is the remaining half.
  //
  // "Governing law" and "jurisdiction" are the conventional words and R133 fixes neither
  // — it fixes the place: England and Wales, and its courts. The heading asks the reader's
  // question instead, in the voice the rest of the page already uses ("What is not
  // promised", "Ending this"), and the body states the two facts as two short sentences.
  'siteTerms.law.title': { warm: 'Which law applies', literal: 'Which law applies' },
  'siteTerms.law.body': {
    warm: 'The law of England and Wales applies to these terms. Any dispute goes to the courts of England and Wales.',
    literal:
      'The law of England and Wales applies to these terms. Any dispute is decided by the courts of England and Wales.',
  },
  'siteTerms.changes.title': {
    warm: 'Changes to these terms',
    literal: 'Changes to these terms',
  },
  'siteTerms.changes.body': {
    warm: 'A change to these terms changes the date at the top.',
    literal: 'Any change to these terms updates the date shown at the top.',
  },
  'siteTerms.changes.continue': {
    warm: 'Carrying on using Carn after a change accepts the new terms.',
    literal: 'Continuing to use the app after a change means you accept the new terms.',
  },

  // -------------------------------------------------------------------------
  // apps/web shared-list fallback (product-concept §10.1: "a partner without the
  // app can still add 'bin bags' from a browser"; §10 objection 3: for shared
  // shopping lists the web fallback "is the difference between the feature
  // working in a real household and not"). Persona C (§3) is the user here: zero
  // learning curve, a plain list, no temperature, no capsules, no metaphor — so
  // this surface reuses `shopping.*` wherever it already fits and adds only the
  // handful of strings a bare add/check list needs that `shopping.*` does not
  // (that vocabulary was written for the in-app Shopping Mode surface, which has
  // a department-grouped, huge-target presentation this page deliberately omits).
  // -------------------------------------------------------------------------
  'sharedList.pageIntro': {
    warm: 'A shared shopping list. Add to it, or check something off.',
    literal: 'A shared shopping list. Add an item, or check one off.',
  },
  'sharedList.addLabel': { warm: 'Add an item', literal: 'Add an item' },
  'sharedList.addPlaceholder': { warm: 'e.g. milk, batteries', literal: 'e.g. milk, batteries' },
  'sharedList.addSubmit': { warm: 'Add it', literal: 'Add item' },
  'sharedList.addHint': {
    warm: 'Adds it to the list for everyone who has this link.',
    literal: 'Adds the item to the list. Visible to anyone with this link.',
  },
  'sharedList.added': { warm: '{item} added.', literal: '{item} added.', slots: ['item'] },

  /**
   * RULINGS **R68**, verbatim on both registers: "It must state the fact and the remedy
   * without blame on either party — warm: *'This link no longer works. Ask for a new one.'* /
   * literal: *'This share link has been revoked. Request a new link.'*"
   *
   * WHAT THIS REPLACES. A revoked link used to render `share.localOnly` — "This phone only
   * for now. Nothing leaves it yet." — on a page that can be a desktop browser, while the
   * add hint disappeared and the visitor's typed item went to `localStorage` and was never
   * queued. Two things wrong at once: the sentence was about the wrong device, and the screen
   * said nothing about why the list was empty. product-concept §3 defines persona C as
   * needing "zero learning curve", and R68's authority note names the standard exactly: "a
   * dead end with no explanation is exactly the zero-learning-curve failure §3 forbids."
   */
  'sharedList.linkExpired': {
    warm: 'This link no longer works. Ask for a new one.',
    literal: 'This share link has been revoked. Request a new link.',
  },
  /**
   * The 429, which used to render `sync.failed` ("Could not reach the server... it will try
   * again") — false in both halves, because the server was reached and answered, and the
   * retry is refused for as long as the window holds. Reachable by an honest household, not
   * only by an attacker, which is why it gets its own sentence rather than a shrug.
   *
   * Both halves are true of the code: the item is in `localStorage` and in the browser
   * outbox before the network is asked, and `drainOutbox` replays it on the next read that
   * succeeds (`apps/web/src/list/backend.ts`).
   */
  'sharedList.rateLimited': {
    warm: 'This link is busy just now. Your item is safe here and goes up when the link is free.',
    literal: 'The server is limiting requests on this link. Queued changes are retried automatically.',
  },
  /**
   * THE CHECKBOX'S ACCESSIBLE NAME MUST NAME THE ITEM.
   *
   * `aria-label={checked ? t('shopping.uncheck') : name}` put the same label on every checked
   * checkbox — an `aria-label` on the `<input>` overrides the associated `<label>`, so inside
   * the "N in the trolley" disclosure a VoiceOver user heard "Put it back on the list,
   * checked, checkbox" on every row with no way to tell milk from bin bags. `shopping.uncheck`
   * is the in-app surface's unslotted button label and was the wrong key for a per-row
   * accessible name. These two carry the verb AND the item, in both states, so the name is
   * never ambiguous and colour/strike-through is never the only signal (design-direction §3).
   */
  'sharedList.checkItem': { warm: 'Tick off {item}', literal: 'Check {item}', slots: ['item'] },
  'sharedList.uncheckItem': {
    warm: 'Put {item} back on the list',
    literal: 'Uncheck {item}',
    slots: ['item'],
  },
  /**
   * What a link visitor is told when the item they queued offline has since been taken off
   * the list by the household.
   *
   * The route answers a replayed add against a tombstoned id with `410 item_removed` (see
   * `functions/src/lib/share-link.ts`) instead of the old 200-with-the-removed-item's-name.
   * The browser drops the outbox entry rather than retrying forever, and says this. It states
   * the fact and stops: the household withdrew the item, and no wording here second-guesses
   * that decision or implies the visitor did anything wrong.
   */
  'sharedList.itemWithdrawn': {
    warm: '{item} is no longer on the list. Nothing is put back.',
    literal: '{item} has been removed from the list. It is not re-added.',
    slots: ['item'],
  },

  // -------------------------------------------------------------------------
  // The owner's share-link control (RULINGS R69)
  //
  // R69: "the shopping list surface gains a 'share this list' control that issues the
  // link and hands it to the OS share sheet, plus a row that states whether a link is
  // live and when it was issued, and a revoke action. The token is shown **once**, at
  // issue, because no route can read it back — and the surface must say so, since a user
  // who assumes they can find it again will lose it."
  //
  // A DELIBERATELY SEPARATE PREFIX from `share.`, which `SURFACE_MANIFESTS.share` in
  // `lint.ts` declares as one screen: these strings render on the SHOPPING surface beside
  // the list, not on the sharing screen, so folding them into `share.` would have the
  // collision law comparing them against the wrong set of siblings.
  //
  // What cannot be exercised today is stated on the surface itself, not only here:
  // `shareLink.unavailable`. RULINGS **R76** confirms the §10.1 walk is blocked on
  // implementation-brief §10 spike #3 (Clerk), and rules that the honest response is to
  // name the blocker rather than route around it — explicitly forbidding a review-only
  // token-issuing endpoint. So the control is built in full and says why it cannot run.
  // -------------------------------------------------------------------------
  'shareLink.title': { warm: 'Share this list', literal: 'Share this list' },
  'shareLink.explain': {
    warm: 'Anyone with the link can add to this list and tick things off. Nothing else.',
    literal: 'Anyone with the link can add items and check them off. They see nothing else.',
  },
  'shareLink.issue': { warm: 'Make a link', literal: 'Create a share link' },
  'shareLink.issueHint': {
    warm: 'You see the link once. Copy it or send it while it is on screen.',
    literal: 'The link is displayed once. Copy or send it before leaving this screen.',
  },
  /** The once-only warning R69 requires the surface to carry, not merely imply. */
  'shareLink.showOnce': {
    warm: 'This is the only time this link is on screen. Nothing here can read it back.',
    literal: 'This link is displayed once. No route can retrieve it afterwards.',
  },
  'shareLink.copy': { warm: 'Copy the link', literal: 'Copy the link' },
  'shareLink.copied': { warm: 'Link copied.', literal: 'Link copied to the clipboard.' },
  'shareLink.send': { warm: 'Send it', literal: 'Share the link' },
  /** `{ago}` is `formatAgo` (`@carn/ui`) — coarse on purpose, same as every other timestamp. */
  'shareLink.live': {
    warm: 'A link is live, made {ago}.',
    literal: 'A share link is active, created {ago}.',
    slots: ['ago'],
  },
  'shareLink.none': {
    warm: 'No link is live for this list.',
    literal: 'No share link is active for this list.',
  },
  'shareLink.revoke': { warm: 'Turn this link off', literal: 'Revoke this link' },
  'shareLink.revokeHint': {
    warm: 'The link stops opening the list. You can make a new one after.',
    literal: 'The link stops working immediately. A new link can be created afterwards.',
  },
  'shareLink.revoked': {
    warm: 'That link is off. It opens nothing now.',
    literal: 'The link is revoked. It no longer opens the list.',
  },
  /**
   * R76's limit, said on the surface. Not a placeholder and not an apology: the control is
   * complete, and this is the one fact a user needs about why it cannot run yet.
   */
  'shareLink.unavailable': {
    warm: 'Making a link needs an account, and accounts are not connected yet.',
    literal: 'Share links require a signed-in account. Account sign-in is not connected yet.',
  },
  'shareLink.failed': {
    warm: 'Could not reach the server. The link is unchanged.',
    literal: 'The request does not reach the server. No link is created or revoked.',
  },

  // -------------------------------------------------------------------------
  // The reflection surface — optional, off by default (design-direction §6)
  //
  // Every string here is written against product-concept §11's "Body-aware, not
  // surveillance": the interrupt and energy logs "exist to serve the user's self-knowledge
  // and are surfaced back gently... No productivity scores, no comparisons to other users,
  // no data used to pressure."
  //
  // So each metric gets exactly two strings: a LABEL naming the quantity, and a sentence
  // saying WHAT IT COUNTS. Neither ever says what the number means about the person. There is
  // no word here for good, better, improving, or behind, in either register — and a figure
  // this surface cannot phrase as a fact about the work is one this surface does not render.
  // -------------------------------------------------------------------------
  /**
   * ONE NAME FOR THIS PLACE, IN BOTH REGISTERS, EVERYWHERE.
   *
   * This started as "Looking back" / "Your figures" while the settings row that reaches it said
   * "Reflection" — so a user tapped one word and landed on a screen titled another. Design
   * review caught it in the rendered off state, where "Looking back" and "Reflection is off"
   * were stacked in one viewport. product-concept §11 makes interface predictability an
   * autism-critical requirement and a published promise, and design-direction §1's objection 2
   * is specifically about not making the reader disambiguate. One key, used as the screen
   * title, the settings row, the link into it, and the off-state heading. The warmth is spent
   * on the sentences instead.
   */
  'reflection.title': { warm: 'Reflection', literal: 'Reflection' },
  // Does NOT mention the phone: `reflection.local` immediately below says where this is worked
  // out, and review found the two lines saying it twice in the top third of the screen — which
  // also pushed the first figure further below the fold.
  'reflection.intro': {
    warm: 'Some figures about the work.',
    literal: 'Figures about your tasks.',
  },
  // implementation-brief §3's privacy invariant, and product-concept §14 risk 1
  // ("on-device processing wherever possible"), said on the surface rather than in a policy.
  'reflection.local': {
    warm: 'Nothing here leaves the phone. No scores, no comparisons.',
    literal: 'This stays on the phone. No scores, no comparisons.',
  },
  /**
   * ON, and the history is still being read (docs/RULINGS.md R40).
   *
   * Every figure on this surface is a fold over the whole append-only log, and R40 bounded what a
   * cold start reads so that boot-to-writable stops growing with the install. So this surface asks
   * for the rest of the log and says so for the fraction of a second it takes, rather than
   * publishing a rate over a fortnight and labelling it a lifetime — §13's whole metric philosophy
   * is that the denominator is the honesty. Not a progress claim: there is nothing to count down.
   */
  'reflection.loading': {
    warm: 'Reading back through it.',
    literal: 'Reading the task history.',
  },
  /**
   * THE THIRD STATE: THE READ DID NOT COME BACK (docs/RULINGS.md R112).
   *
   * `loadHistory()`'s failure path was `.catch(() => {})`, so `historyLoaded()` stayed false for
   * ever and this surface said "Reading back through it." for the life of the install — the same
   * defect the write-behind `console.error` was, which is a failure with a reassuring face on it.
   * There are three states, so there are three strings.
   *
   * The retry offer is REAL: the failed read no longer memoises itself, so leaving and returning
   * to this surface issues a fresh one (`src/data/sqlite.ts` `loadHistory`). A promise of a retry
   * that nothing performed would be the R80 species in the catalogue.
   */
  'reflection.unreadable': {
    warm: 'The history could not be read. Open this again and it has another go.',
    literal: 'The task history could not be read. Open this screen again to retry.',
  },
  // The off state. It explains and offers, and it appears only where the user went looking.
  'reflection.off.title': { warm: 'Reflection is off', literal: 'Reflection is off' },
  'reflection.off.body': {
    warm: 'Turn it on to see figures about your own work. Turn it off again whenever you like.',
    literal: 'Turn it on to see figures about your own tasks. Turn it off again whenever you like.',
  },
  'reflection.turnOn': { warm: 'Turn it on', literal: 'Turn it on' },
  'reflection.turnOff': { warm: 'Turn it off', literal: 'Turn it off' },
  // The settings row. A plain row among the others, with no badge and no invitation.
  // The settings row's own name is `reflection.title` — see the note there. This is only the
  // sentence under it.
  /**
   * Describes the SETTING, never its current state.
   *
   * It read "…Off unless you ask for it." — true of the default, and a flat contradiction of
   * the pill reading "On" eighty pixels to its right, which is exactly what the rendered
   * settings surface showed. The state is the switch's job and the switch says the word
   * (design-direction §3: state is never carried by colour alone).
   */
  'reflection.settingExplain': {
    warm: 'An optional surface with figures about your own work.',
    literal: 'An optional screen with figures about your own tasks.',
  },
  'reflection.open': { warm: 'Open reflection', literal: 'Open reflection' },
  /**
   * What `undefined` renders as — and the reason it is words rather than "0%".
   *
   * Every rate in @carn/core is `number | undefined`, where undefined means "no data yet",
   * "never a synthetic 0, because 'no data' and 'total failure' are different facts about a
   * new user" (core metrics.ts). A new user shown 0% has been told they failed at something
   * they have not done yet, which product-concept §2 axiom 3 rules out structurally.
   */
  'reflection.noFigure': { warm: 'No figure yet', literal: 'No figure yet' },
  /**
   * THE DENOMINATOR NAMES ITSELF, PER METRIC.
   *
   * This was one generic string, "{part} of {whole} counted." — and design review caught two
   * adjacent cards both reading "2 of 4 counted." about entirely different sets of four. It read
   * like a rendering fault, and worse, "counted" says nothing about WHAT the four were. On a
   * surface whose only value is that the figure can be trusted, the denominator has to say what
   * it is a denominator of, in the same words the card's own sentence just used.
   */
  'reflection.resumption.fraction': {
    warm: '{part} of {whole} you put down.',
    literal: '{part} of {whole} you paused.',
    slots: ['part', 'whole'],
  },
  'reflection.survival.fraction': {
    warm: '{part} of {whole} caught more than 30 days ago.',
    literal: '{part} of {whole} captured more than 30 days ago.',
    slots: ['part', 'whole'],
    allow: ['tense'],
    why: 'The fraction of tasks caught more than 30 days ago — a fact about the past (ADA review C5).',
  },
  'reflection.compost.fraction': {
    warm: '{part} of {whole} in compost.',
    literal: '{part} of {whole} long-idle tasks.',
    slots: ['part', 'whole'],
  },
  'reflection.lapse.fraction': {
    warm: '{part} of {whole} gaps of that length.',
    literal: '{part} of {whole} gaps of that length.',
    slots: ['part', 'whole'],
  },

  'reflection.resumption.label': { warm: 'Coming back', literal: 'Return to paused tasks' },
  'reflection.resumption.what': {
    warm: 'Counts the loops you put down and open again within 14 days.',
    literal: 'Counts the tasks you pause and open again within 14 days.',
  },
  /**
   * Why the north-star metric has no figure yet, stated instead of left blank.
   *
   * `resumptionRate` excludes a park whose 14 days have not elapsed from BOTH sides, so a
   * user in their first fortnight legitimately has no rate. Saying which is the difference
   * between a surface that is waiting and a surface that looks broken.
   */
  'reflection.resumption.waiting': {
    warm: '{immature} put down recently, still inside the 14 days.',
    literal: '{immature} paused recently, still inside the 14 days.',
    slots: ['immature'],
  },

  'reflection.reentry.label': { warm: 'Getting back into it', literal: 'Time to first action' },
  'reflection.reentry.what': {
    warm: 'The middle time between opening a capsule and doing the next thing.',
    literal: 'The median time between opening saved context and acting on the task.',
  },
  /**
   * The coverage figure, published next to the median because core insists on it: it exists
   * "precisely so a client that instruments only its fast path cannot quietly report an
   * excellent one" (core metrics.ts). Hiding it would make the median a boast.
   */
  'reflection.reentry.coverage': {
    warm: 'Measured on {part} of {whole} openings.',
    literal: 'Measured on {part} of {whole} openings.',
    slots: ['part', 'whole'],
  },

  'reflection.survival.label': {
    warm: 'Things that reach an ending',
    literal: 'Captures that reach a decision',
  },
  'reflection.survival.what': {
    warm: 'Counts what you caught that reaches an ending within 30 days. Letting go is an ending.',
    literal: 'Counts captures that reach a decision within 30 days. Releasing is a decision.',
    allow: ['tense'],
    why: 'The word the tense heuristic reads is "caught", naming the past captures being counted; what the sentence claims about them is present ("reaches an ending") (ADA review C5).',
  },

  'reflection.compost.label': {
    warm: 'Decisions in compost',
    literal: 'Decisions on long-idle tasks',
  },
  'reflection.compost.what': {
    warm: 'Counts the composting loops you bring back or let go.',
    literal: 'Counts the long-idle tasks you reactivate or release.',
  },

  'reflection.lapse.label': { warm: 'Coming back after time away', literal: 'Return after time away' },
  'reflection.lapse.what': {
    warm: 'Counts the gaps of 14 days or more where you come back within 60.',
    literal: 'Counts the gaps of 14 days or more where you return within 60.',
  },

  'reflection.trip.label': {
    warm: 'Shopping trips worked through',
    literal: 'Shopping trips completed',
  },
  'reflection.trip.what': {
    warm: 'Counts the trips where every item on the list gets ticked off.',
    literal: 'Counts the trips where every item on the list gets checked off.',
  },
  /**
   * The denominator, named (docs/RULINGS.md R98). product-concept §13 defines this figure as "% of
   * trips where the list is fully worked", so the whole is TRIPS — the ones that ended with
   * something on the list. A trip still in progress and a trip walked with an empty list are in
   * neither half (`tripsWorkedThrough` in the reflection report; core `tripCompletionRate` returns
   * `undefined` for the empty one rather than a perfect score).
   */
  'reflection.trip.fraction': {
    warm: '{part} of {whole} trips with nothing left on the list.',
    literal: '{part} of {whole} trips with every item checked off.',
    slots: ['part', 'whole'],
  },
  /**
   * NO TRIP ROWS ON THIS DEVICE — which since RULINGS R54 is a narrower fact than it was.
   *
   * It used to mean "trips cannot be recorded at all": a trip's start snapshot lived in the
   * ephemeral shopping-mode store, which implementation-brief §5 correctly fences to session
   * state, so nothing survived the app and no figure could exist. R54 decided the privacy question
   * and `shopping_trips` now persists the minimal row, so the row reports.
   *
   * What is left for this string is the empty case, and the wording had to change with it: "Trips
   * are not recorded yet" is no longer true of the app, only of this device's record. It stays a
   * DISTINCT state from an absent rate — see `ReflectionRow.unrecorded` — because "nothing has been
   * recorded" and "you walked no trip with anything on the list" are different answers, and a
   * surface whose only value is trust must not make a fresh install look like an unusual month.
   */
  'reflection.trip.pending': {
    warm: 'No trips are recorded here yet, so there is no figure to show.',
    literal: 'No shopping trips are recorded on this device, so there is no figure to show.',
  },

  // The personal cairn — the SECOND and last place the cairn may appear
  // (design-direction §6). Accumulative only: one stone per session on a loop that reached
  // an ending, and `loop.cairnNothingLost` says what that means out loud.
  'reflection.cairn.label': { warm: 'Your cairn', literal: 'Your session history' },
  'reflection.cairn.count': {
    warm: '{count} stones',
    literal: '{count} work sessions',
    oneWarm: '1 stone',
    oneLiteral: '1 work session',
    slots: ['count'],
  },
  'reflection.cairn.what': {
    warm: 'One stone for every session on a loop you finished or let go.',
    literal: 'One entry for every work session on a task you finished or released.',
  },

  // -------------------------------------------------------------------------
  // The warm cap's gentle trade (product-concept §5) — the other half of
  // `warmCap.prompt`, which had a question and no answers.
  //
  // §5: "Promoting a Loop to warm past the cap prompts a gentle trade ('want to let one of
  // these cool off?') — never a block." The promotion has ALREADY happened by the time
  // any of this renders: `warmCap.keepAll` is a first-class answer and the offer costs
  // nothing to walk away from. Nothing here counts at the user, and nothing here says no.
  // -------------------------------------------------------------------------
  /**
   * The accepting answer, naming the loop it would move — never "pick one from this list".
   *
   * `warmCapTradeOffer` picks the least-recently-touched warm loop the app is allowed to file, so
   * the offer is one named thing rather than a ranking exercise at the moment the user was doing
   * something else. Naming it is also what makes the offer safe to accept blind.
   *
   * THE TITLE COMES LAST, and that is not a stylistic preference. The first version read
   * "Let {title} cool off", which renders as "Let Check the water trough in the top field cool
   * off" — a user's own sentence wedged between a verb and its particle. Seen in a screenshot at
   * dial 4, where the label also wraps to two lines. A slot holding an arbitrary user sentence
   * belongs at the end of the label, never in the middle of a clause (the class of error
   * RULINGS R24 was written about).
   */
  'warmCap.letCool': {
    warm: 'Let it cool off: {title}',
    literal: 'Set to idle: {title}',
    slots: ['title'],
  },
  /** What accepting did, and that it took nothing away. */
  'warmCap.cooled': {
    warm: '{title} is cooling now. It is still here, further down.',
    literal: '{title} is idle now. It is still on the all-tasks screen.',
    slots: ['title'],
    allow: ['metaphor'],
    why: '"idle" is the literal register\'s own word for the state, established by warmCap.prompt; the metaphor list catches "cooling" in the warm form only.',
  },
  /** Said before either answer, so "never a block" is on the screen and not only in a doc. */
  'warmCap.tradeExplain': {
    warm: 'Nothing is blocked. This one stays in front of you either way.',
    literal: 'Nothing is blocked. This task stays active either way.',
  },

  // -------------------------------------------------------------------------
  // The Tier-2 note, solicited at a calm moment (product-concept §6.1, RULINGS R42).
  // `capsule.calmPrompt` and `capsule.addNote` existed with no entry point anywhere; these
  // are the field, its two answers, and the acknowledgement.
  // -------------------------------------------------------------------------
  'capsule.notePlaceholder': {
    warm: 'Bolts are in the jacket pocket, need a 13mm',
    literal: 'The step you stopped at, or the next one',
  },
  'capsule.noteSave': { warm: 'Keep the note', literal: 'Save the note' },
  /** Equal weight to keeping, and it ends the asking for this one (R42: at most once). */
  'capsule.noteSkip': { warm: 'Not now', literal: 'Not now' },
  'capsule.noteAdded': {
    warm: 'Kept on this one.',
    literal: 'Saved to this task.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },

  // -------------------------------------------------------------------------
  // The date and its reminder ladder, on the loop's own surface (product-concept §5,
  // RULINGS R39/R33).
  //
  // Capture records the KIND and a coarse WHEN with no picker (§7.1). This is where a hard
  // commitment gets the "calendar-grade treatment and escalating, user-configured reminders"
  // §5 promises. Every string here states a fact or offers a choice: there is no alarm word,
  // no count of days remaining, and no variant of "overdue" — the state does not exist
  // (product-concept §5) and prominence is carried by size and position instead
  // (design-direction §3, objection 3).
  // -------------------------------------------------------------------------
  'loop.dateSection': { warm: 'The date on this one', literal: 'Date' },
  /** The disclosure. Behind one press, because most loops never need it. */
  'loop.dateEdit': { warm: 'Set or change the date', literal: 'Set or change the date' },
  // `loop.dateNone` — "No date on this one." / "No date set." — is DELETED, not orphaned.
  // `DateEditor` rendered it under a heading, above a button already labelled "Set or change the
  // date": three lines to report that nothing had happened, and one of three such statements
  // stacked down a freshly-caught loop. See the `summary` branch in DateEditor.tsx for the
  // argument (product-concept §8.3, §6.3) and for the two precedents in the same directory.
  // R26's dead-key check is the reason it is removed here rather than left for later.
  'loop.dateSoftSet': {
    warm: 'A day you have in mind: {when}. It can slide.',
    literal: 'Intended date: {when}. It can move.',
    slots: ['when'],
  },
  'loop.dateClear': { warm: 'Take the date off', literal: 'Remove the date' },
  // -------------------------------------------------------------------------
  // Naming a specific day (product-concept §5's "calendar-grade treatment"; R39's refinement).
  //
  // The three coarse choices above stay — today, tomorrow, within the week — because they answer
  // most dates in one press. This is the other half: an appointment in November, a form due on
  // the 30th, a birthday four months out. The strings state the format and, when the entry
  // cannot be read, state WHY in one plain sentence. None of them is a warning: there is no
  // alarm colour in the token system and no count of days remaining anywhere
  // (design-direction §3; product-concept §5 — no overdue state exists).
  // -------------------------------------------------------------------------
  'loop.dateExact': { warm: 'Or name the day', literal: 'Or enter a date' },
  'loop.dateExact.explain': {
    warm: 'Any day ahead, however far.',
    literal: 'Any future date, however far ahead.',
  },
  'loop.dateExact.day': { warm: 'Day', literal: 'Day' },
  'loop.dateExact.month': { warm: 'Month', literal: 'Month' },
  'loop.dateExact.year': { warm: 'Year', literal: 'Year' },
  'loop.dateExact.set': { warm: 'Put it on this day', literal: 'Set this date' },
  'loop.dateExact.incomplete': {
    warm: 'Day, month, and a four-digit year.',
    literal: 'Enter day, month and four-digit year.',
  },
  'loop.dateExact.unreal': {
    warm: 'That day is not in that month.',
    literal: 'That day does not exist in that month.',
  },
  'loop.dateExact.past': {
    warm: 'Today, or a day ahead of today.',
    literal: 'Choose today or a later date.',
  },
  /** Spoken after the write, because the line that changes is above the fold on this surface. */
  'loop.dateExact.done': {
    warm: 'The date is now {when}.',
    literal: 'Date set to {when}.',
    slots: ['when'],
  },
  'loop.dateEarlier': { warm: 'A day earlier', literal: 'One day earlier' },
  'loop.dateLater': { warm: 'A day later', literal: 'One day later' },
  'loop.dateTimeOfDay': { warm: 'What time of day?', literal: 'Time of day' },
  'loop.dateTime.morning': { warm: 'Morning', literal: 'Morning' },
  'loop.dateTime.midday': { warm: 'Midday', literal: 'Midday' },
  'loop.dateTime.afternoon': { warm: 'Afternoon', literal: 'Afternoon' },
  'loop.dateTime.evening': { warm: 'Evening', literal: 'Evening' },
  'loop.dateTime.endOfDay': { warm: 'End of the day', literal: 'End of the day' },
  'loop.reminders': { warm: 'Reminders', literal: 'Reminders' },
  /**
   * The ladder is the user's, and every rung can be turned off (product-concept §5's
   * "user-configured"). It also says where reminders apply, because a soft date is an
   * intention and §5 forbids punishing one.
   */
  'loop.remindersExplain': {
    warm: 'Only for a date the world cares about. Turn any of them off.',
    literal: 'Fixed deadlines only. Each reminder can be turned off.',
  },
  'loop.reminders.week': { warm: 'A week before', literal: 'A week before' },
  'loop.reminders.day': { warm: 'The day before', literal: 'The day before' },
  'loop.reminders.twoHours': { warm: 'Two hours before', literal: 'Two hours before' },
  'loop.reminders.atTime': { warm: 'At the time itself', literal: 'At the deadline time' },
  'loop.remindersNone': { warm: 'No reminders on this one.', literal: 'No reminders set.' },
  /**
   * A rung whose moment is already behind the clock. Stated as arithmetic, not as a scolding:
   * core's `defaultReminderOffsetsMs` truncates the ladder to the rungs still ahead, and the
   * surface says so rather than showing a switch that cannot do anything.
   */
  'loop.remindersElapsed': {
    warm: 'This one comes before now, so there is nothing to set.',
    literal: 'This reminder time is earlier than now, so it cannot be set.',
  },

  // -------------------------------------------------------------------------
  // The Toss, from the loop it is about (product-concept §10, RULINGS R47).
  //
  // §10's gesture is "hand a single Loop to another person, capsule included" — an act
  // performed ON A LOOP. The share surface's stacked radio group of ten of the user's own
  // sentences was the wrong shape for it, so the entry point is here and the picker there
  // shrinks to recipients.
  // -------------------------------------------------------------------------
  'loop.handOver': { warm: 'Hand this over', literal: 'Transfer this task' },
  'loop.handOverHint': {
    warm: 'Opens sharing with this one already chosen.',
    literal: 'Opens the sharing screen with this task selected.',
  },
  /** What the share surface says when it was opened without a loop (R47's reduced picker). */
  'share.tossFromLoop': {
    warm: 'Open a loop and choose “hand this over”. It travels with its bookmark.',
    literal: 'Open a task and choose “transfer this task”. Its saved context travels with it.',
  },

  // -------------------------------------------------------------------------
  // The release observation, on the loop's own surface (RULINGS R57).
  //
  // §5's "one moment, then done" is why the claim, the explanation and the two buttons no
  // longer sit under the compost celebration. The insight lives where the loop lives.
  // -------------------------------------------------------------------------
  /**
   * Says plainly that the question can be ignored (R57).
   *
   * `compost.observationExplain` says how much to trust the guess; this says that walking
   * away is a complete answer. It is appended rather than folded into that key because the
   * compost card renders the same pair and belongs to another surface's owner.
   */
  'release.observationIgnorable': {
    warm: 'You can leave this question alone. Nothing happens if you do.',
    literal: 'Answering is optional. Nothing is recorded unless you keep the note.',
  },
  /** The kept note, shown on the ending it belongs to. A label, not a verdict. */
  'release.observationKept': { warm: 'Noted with the ending', literal: 'Noted with the release' },

  // -------------------------------------------------------------------------
  // Carn Plus — the subscription (product-concept §12, "Commercial strategy").
  //
  // §12: "The free tier must be genuinely usable forever... Paid ('Carn Plus') adds:
  // unlimited AI Sorting and breakdown, walkaround mode, full compost intelligence,
  // multiple shared lists, and wearable complications... the free/paid line is
  // therefore drawn at AI compute and convenience, never at core coping function.
  // Sensory and accessibility settings are free forever, categorically." Plus the
  // ethics constraints in the same section, DECIDED and non-negotiable: no
  // engagement-maximising dark patterns, no fake-urgency discount timers,
  // cancellation in two taps, trust is the moat.
  //
  // What that means for the words in this section specifically:
  //   - never imply the free tier is a trial, crippled, or a lesser state of being;
  //   - no scarcity, no countdown, no "limited time" (already unlintable — see
  //     URGENCY_WORDS in ./lint.ts — but the judgement call is recorded here too);
  //   - the dismiss/not-now affordance reads with the same weight as the buy
  //     affordance, never a diminutive "no thanks";
  //   - gate encounters state a fact and a working free path, never a dead end;
  //   - a completed purchase is a transaction, not an achievement — no
  //     `celebrate.*`-style acknowledgement and no praise, per design-direction §1's
  //     "acknowledgement attaches to the act, never the person," which applies here
  //     even though the "act" is spending money rather than finishing work.
  //   - "Carn Plus" is a product name, so it stays a name in both registers
  //     (product-concept §11's literal mode swaps METAPHOR for functional labels; a
  //     name is not a metaphor). Everything ELSE — the loops, capsules, sorting,
  //     compost words used to describe what it does — still goes through the
  //     normal warm/literal swap like anywhere else in the catalogue.
  //
  // FOUNDER DECISION (post-dates the first draft of this section): the free tier has
  // NO model-backed AI at all, not a monthly allowance that runs out and renews. Plus
  // is the only tier with AI Sorting/breakdown. What is free and complete is the
  // LOCAL HEURISTIC sorting path (`proposals.ts` runs on-device, no model, no
  // network) — product-concept §7.2 objection 2 makes "Sorting can be turned fully
  // manual" a trust requirement, and §15 requires the MVP phase to work fully
  // offline. So nowhere below may a string imply free AI that gets capped, used up,
  // or unlocked — the correct shape is always "AI sorting is a Plus thing; sorting
  // things yourself is the free thing, and it is not a lesser version of it."
  //
  // No price or currency is ever written into these strings: `{price}` is a slot
  // the caller fills with the store's own localized string at purchase time (App
  // Store / Play Store / RevenueCat), the same interpolation convention as every
  // other `{slot}` in this file. This package has no opinion on what a "5" or a
  // "£" means in any given storefront, and must not pretend to.
  //
  // ADVERSARIAL CRITIC PASS (`docs/reviews/plus-copy-critique.md`, FAIL, 2 S1/3 S2)
  // found and this section now fixes: `plus.offer.intro` claimed unnamed extra value
  // ("a few conveniences") the offer screen's own `PLUS_CAPABILITY_REGISTRY.shipping`
  // filter (DEVIATIONS M6, `packages/core/src/entitlements.ts`) proves false on first
  // render — fixed to say only what ships. (That key is now DELETED, DEVIATIONS M13:
  // "say only what ships" held, and then the list grew past it. See the comment left
  // in its place below.) `plus.gate.compostDepth` sold a capability
  // (`full-compost-intelligence`) that DEVIATIONS M4/M5 confirm has never shipped, on
  // a gate string outside the registry's protection — deleted, not reworded; see the
  // comment left in its place. Added `plus.cancelKeepsWork`, because nothing in the
  // family previously reassured a user that their own CONTENT survives cancellation,
  // only that free features do. `plus.feature.walkaround.label` now matches the
  // existing `origin.walkaround` warm/literal split instead of inventing a second one.
  // The general lesson for whoever extends this section next: a string that names a
  // capability is a claim the REST OF THE APP has to already be true, not just a
  // tone the linter can check — check `packages/core/src/entitlements.ts` and
  // `docs/DEVIATIONS.md`'s monetisation entries before naming one.
  // -------------------------------------------------------------------------

  // -- The offer screen --------------------------------------------------------
  /** A product name, not a metaphor. Stays "Carn Plus" in both registers. */
  'plus.offer.title': { warm: 'Carn Plus', literal: 'Carn Plus' },
  /*
   * `plus.offer.intro` WAS HERE AND IS DELETED (docs/DEVIATIONS.md M13).
   *
   * It read "Plus adds AI-generated sorting and task breakdown." and opened the offer screen.
   * Its own doc comment carried the instruction that killed it: "Restore 'and [named things]'
   * in the SAME commit that flips a second `PlusCapabilityDescriptor.shipping` to `true`."
   * TWO more have flipped since — `multiple-shared-lists` and `material-palettes` — and this
   * string did not move, so the loudest claim on the paywall named one of the three
   * capabilities the same screen was rendering directly beneath it. It undersold the product
   * by two thirds, which is a strange way for a marketing sentence to fail.
   *
   * The fix is not a better sentence. A summary above a generated list is a SECOND source of
   * capability copy, and DEVIATIONS M6 exists because a second source drifts — it drifted here
   * within two capability launches, in the direction its own comment warned about. The list is
   * the summary now, and it cannot disagree with itself.
   *
   * Do not reinstate a one-line summary here without also reinstating `aiSortingShips` in
   * `PlusSurface.tsx` (the gate that kept the old sentence from outliving the capability it
   * named) and declaring how it stays in step with the registry. "Say only what ships" was
   * never the hard part; staying true afterwards was.
   */
  /**
   * §12's own list of what free covers, "genuinely usable forever" — with sorting
   * things yourself named explicitly, because it is the item a user might fear they
   * are losing once AI sorting is named as a Plus feature two lines up. Naming it
   * here is the reassurance, not a footnote to it.
   */
  'plus.offer.staysFree': {
    warm: 'Capture, loops, capsules, sorting things yourself, the interrupt button and one list you can share stay free, forever.',
    literal:
      'Capture, tasks, saved context, reviewing captured items yourself, the interrupt button and one list you can share stay free, forever.',
  },
  /*
   * `plus.offer.lineDrawn` WAS HERE AND IS DELETED (docs/DEVIATIONS.md D52).
   *
   * It stated §12's line in its own terms — "the free and paid line is drawn at AI processing and
   * convenience; it never touches core coping functions" — as a second paragraph under
   * `staysFree` on the offer surface. Removed at founder instruction because the free-tier proof
   * had grown longer than the value it sits beneath, and this was the abstract half of it:
   * `staysFree` NAMES the free things, which is what §12 requires a reader be able to check
   * before the price; this restated the principle behind the naming.
   *
   * Deleted rather than left unwired, which is the same move M13 made on `plus.offer.intro` —
   * `test/references.test.ts` holds the catalogue to a falling ceiling of keys with no call site,
   * so an orphan here is not free. Recorded rather than silently dropped: if the line ever comes
   * back it belongs in the composition below, after `staysFree`.
   */
  /**
   * §12, close to verbatim: "Sensory and accessibility settings are free forever,
   * categorically." Stated plainly rather than folded into the general free-tier
   * line, because it is the one promise this audience will check first.
   */
  'plus.offer.accessibilityFree': {
    warm: 'Sensory and accessibility settings are free forever. No catch.',
    literal: 'Sensory and accessibility settings are free forever, categorically.',
  },
  /**
   * The decorative glyph beside `plus.offer.accessibilityFree` inside `<Callout>`
   * (`packages/ui/src/components/Callout.tsx`). `presentational` hides it from assistive
   * technology, so this never renders as visible or spoken text — it exists so the icon still
   * has a name in the accessibility tree's source rather than an inline literal (CLAUDE.md:
   * "never inline a user-visible string in a component"). Also used by `Gallery.tsx`'s
   * `Callout` demonstration, on the same principle the rest of the gallery follows: demo real
   * catalogue keys, never a placeholder string.
   */
  'plus.offer.accessibilityFree.icon': {
    warm: 'A promise that holds.',
    literal: 'This is a guarantee.',
  },

  // -- The five paid capabilities, §12's list in order --------------------------
  /**
   * Not "unlimited" — that word names a ceiling on something free also has some of,
   * and free has none. This is the ONLY tier with model-backed sorting at all.
   */
  /**
   * IT NO LONGER SAYS "AND BREAKDOWN", AND THAT IS A CORRECTION, NOT A TRIM
   * (`docs/DEVIATIONS.md` M6; `RULINGS R202`).
   *
   * The label used to carry product-concept §12's whole phrase, "AI Sorting and breakdown", and
   * the explain line promised "how to start them" — which is the `first-physical-action` task.
   * That task has a model behind it and no surface in front of it (R202: `capsule.nextAction` has
   * render sites but no writer and no screen that asks for one), so the claim was for something a
   * buyer could not reach. M6's rule is exact about what that is: "a bullet point for a feature
   * that does not exist is not a stale comment — it is the app taking money for a promise it
   * cannot keep."
   *
   * It was survivable while NOTHING on this row shipped and the whole capability was dark. It
   * stopped being survivable the moment half of it went live (`RULINGS R199`–`R202`), because the
   * row is now precisely half true and a buyer has no way to tell which half.
   *
   * The capability's ID still reads `unlimited-ai-sorting-and-breakdown` — §12's phrase, and the
   * domain's. `PlusSurface`'s `CAPABILITY_COPY` is the seam where a domain word and a user word
   * are allowed to differ, on the same precedent D53 set for "Unlimited lists". Put the breakdown
   * words back in the same change that gives breakdown a screen, not before.
   */
  'plus.feature.sorting.label': {
    warm: 'AI sorting and breakdown',
    literal: 'AI-generated sorting and task breakdown',
  },
  'plus.feature.sorting.explain': {
    warm: 'A model reads what you catch and suggests how to file it.',
    literal: 'A model generates sorting suggestions for captured items.',
  },
  /**
   * THE TWO THINGS THE MODEL ACTUALLY DOES TODAY, one per shipped proxy task (`RULINGS R202`:
   * `sorting-triage` and `duplicate-detection`). Each of these is checkable by a buyer within one
   * Sorting session, which is the standard M6 asks of anything on this screen.
   *
   * NEITHER LINE PROMISES THE MODEL ANSWERS EVERY CARD, because R199 is explicit that it does not:
   * the local heuristics answer first and the model speaks only for the items they had nothing to
   * say about. "Where they had nothing to say" is the honest shape of that and is why the second
   * clause is there rather than a flat "every capture gets a suggestion".
   */
  'plus.feature.sorting.point.title': {
    warm: 'Clearer wording for what you catch.',
    literal: 'Clearer titles for captured items.',
  },
  'plus.feature.sorting.point.duplicate': {
    warm: 'Spots two lines that mean one job.',
    literal: 'Detects duplicate captures.',
  },
  /**
   * THE TWO PULLED TASKS (`RULINGS R203`), added in the same change that gave them a screen —
   * which is the condition the label's own comment set for putting "breakdown" back. Both lines
   * say "ask", because both are buttons the user presses on a capsule rather than anything the
   * app does on its own, and a bullet that implied otherwise would oversell them in the one
   * direction this audience minds most.
   */
  'plus.feature.sorting.point.breakdown': {
    warm: 'Ask a stalled job for its first step.',
    literal: 'Request a suggested first action.',
  },
  'plus.feature.sorting.point.summary': {
    // "capsule" is a metaphor term the linter refuses in the literal register, and rightly: that
    // register exists for a reader who wants the mechanism, not the image.
    warm: 'Ask a voice note to sum itself up.',
    literal: 'Summarise an audio note.',
  },
  // Matches the existing `origin.walkaround` split (:377) — "Walkaround mode" is the
  // LITERAL form there, not a name repeated verbatim in both registers, and a reader who
  // has already seen the origin tag once should not meet the same feature named two
  // different ways in two places (critique minor note).
  'plus.feature.walkaround.label': { warm: 'A walkaround', literal: 'Walkaround mode' },
  /** product-concept §9.1: narrate a whole walk; each thing said becomes its own Loop. */
  'plus.feature.walkaround.explain': {
    warm: 'Talk through a whole walk. Each thing you mention becomes its own loop.',
    literal: 'Speak continuously during a walk. Each item you mention becomes a separate task.',
  },
  'plus.feature.compost.label': {
    warm: 'Full compost intelligence',
    literal: 'Full long-idle task insights',
  },
  'plus.feature.compost.explain': {
    warm: 'Deeper patterns on what tends to stall, and why.',
    literal: 'Deeper analysis of which tasks become long idle, and why.',
  },
  /**
   * NOT "multiple shared lists", which is §12's phrase and was this bullet's label until the
   * founder checked it against the gate it describes (docs/DEVIATIONS.md D53).
   *
   * SHARING IS NOT THE GATE. `apps/mobile/src/data/shoppingListGate.ts` counts LISTS —
   * `canCreateShoppingList(existingListCount, status)` — and a free user is stopped from making
   * a SECOND LIST whether or not either of them is ever shared. Sharing the one list they have
   * is free (`'one-shared-list'`, `FREE_FOREVER_CAPABILITIES`) and stays free. So the old label
   * named a restriction that does not exist and hid the one that does: a free user reading
   * "multiple shared lists" has no reason to think a private second list is unavailable, which
   * is the exact thing they will try and be refused.
   *
   * "Unlimited" is honest here in a way §12's own AI wording is not (`plus.feature.sorting.label`
   * above, and the note on it): free HAS one list, so there is a ceiling for Plus to remove, and
   * the gate has no second ceiling above one — R124: §12 says "multiple", not "two".
   */
  'plus.feature.lists.label': { warm: 'Unlimited lists', literal: 'Unlimited lists' },
  'plus.feature.lists.explain': {
    warm: 'Keep as many lists as you want, and share any of them. The free tier keeps one.',
    literal: 'Create any number of lists and share any of them. The free tier includes one list.',
  },
  'plus.feature.wearable.label': { warm: 'Wearable complications', literal: 'Wearable complications' },
  'plus.feature.wearable.explain': {
    warm: 'The interrupt button, right on your wrist.',
    literal: 'The interrupt button, available as a watch complication.',
  },
  /**
   * `'material-palettes'` (`@carn/core` `PLUS_CAPABILITIES`) is, per `entitlements.ts`'s own
   * doc comment, "the ONE new capability in this file that GENUINELY SHIPS today" — the paid
   * `MaterialId`s (`calch`, `glo`, `grug`, `ffion`) in `materials.ts`. `material.explain`
   * (settings surface, above) carries the promise this bullet must not contradict: a material
   * is colour only, never a sensory or accessibility outcome, so this bullet names it as a
   * choice of colour rather than as anything a free user cannot cope without.
   *
   * THE COUNT AND THE NAMES ARE `MATERIAL_TIER`'s, AND HAVE TO STAY THAT WAY (RULINGS R182,
   * amending `docs/DEVIATIONS.md` M11). This bullet read "Five more… limestone, coal, heather,
   * the tide and the foxglove" while R182 was moving **the tide — Llanw — to the free side**,
   * which put a now-free material on the screen that asks for money, named as a paid benefit,
   * three lines above `plus.freeForever`'s categorical promise. product-concept §12 forbids
   * exactly that on exactly this audience: a purchase induced by a false statement of the goods
   * is the "ND tax" §12 refuses, and M12's ethics argument for this screen (the subject is the
   * subscription, never the reader; no scarcity) is worth nothing while the list of goods is
   * wrong. Four materials are paid, so four are named — and `material.llanw.*` is not one of
   * them.
   */
  'plus.feature.materials.label': { warm: 'More materials', literal: 'Additional colour materials' },
  'plus.feature.materials.explain': {
    warm: 'Four more sets of colours, drawn from limestone, coal, heather and the foxglove.',
    literal:
      'Four more colour sets: limestone, coal, heather and foxglove. Colour only — your other display settings do not change.',
  },

  // -- Purchase actions ----------------------------------------------------------
  /**
   * WHERE THE MONEY GOES, STATED AS A FACT — never as an appeal (docs/DEVIATIONS.md M12).
   *
   * Founder request: the free tier is the bulk of the app, and saying that a subscription
   * funds it may convert a reader who is otherwise looking at a thin paid bundle (M4, M6).
   * The line is written to the narrowest version of that idea, and the phrasing is the whole
   * of the ethics argument:
   *
   *   - The SUBJECT is the subscription, not the reader. "Subscriptions pay for X" is a fact
   *     about a transaction. "Help keep Carn going" makes the reader responsible for an
   *     outcome, which is guilt with a friendly face and is the pattern §12 forbids on a
   *     population with impulse-control challenges.
   *   - NO SCARCITY. Nothing here says the work stops, or is at risk, or needs saving. That
   *     would be fake urgency (§12) and — since it is unfalsifiable — a claim
   *     `apps/mobile/test/copy-claims.test.ts` could never gate.
   *   - NO FIRST PERSON. `lint.ts` FIRST_PERSON forbids "we"/"our" outright, which is
   *     fortunate: "we" is what turns a funding fact into a relationship the reader is being
   *     asked to maintain.
   *
   * "including the free tier" is the part the founder asked for and the part that has to stay
   * true. It is true by construction today: everything in `plus.offer.staysFree` and every
   * member of `FREE_FOREVER_CAPABILITIES` (`packages/core/src/entitlements.ts`) is built and
   * maintained out of the same work. If Plus ever becomes the bulk of the app, this line goes.
   */
  'plus.offer.supportsUpkeep': {
    warm: 'Subscriptions pay for the work on Carn. That includes keeping the free tier free.',
    literal:
      'Subscriptions fund continued development and maintenance of Carn, including the free tier.',
  },
  /** `{price}` is the store's own localized string. Never a hardcoded number or symbol. */
  'plus.subscribeMonthly': {
    warm: 'Get Plus monthly — {price}',
    literal: 'Subscribe monthly for {price}',
    slots: ['price'],
  },
  'plus.subscribeAnnual': {
    warm: 'Get Plus yearly — {price}',
    literal: 'Subscribe yearly for {price}',
    slots: ['price'],
  },
  'plus.restore': { warm: 'Restore purchases', literal: 'Restore purchases' },
  /**
   * §12's ethics constraints have force of law here: no dark patterns, cancellation
   * in two taps. The word for "leave this screen" is the same plain "Not now" used
   * everywhere else in the catalogue (`compost.reviewLater`, `shopping.nearStoreLater`)
   * — not a smaller, apologetic, or guilt-adjacent variant. Rendering it is a UI
   * concern, but the copy is written to be the SAME control weight as the buy
   * buttons above it, not a footnote.
   */
  'plus.dismiss': { warm: 'Not now', literal: 'Not now' },
  'plus.dismiss.hint': {
    warm: 'Closes this. Everything free stays free.',
    literal: 'Closes this screen. Free features are unaffected.',
  },

  /**
   * THE TWO LEGAL LINKS ON THE PURCHASE SURFACE — ADA review A7; App Review 3.1.2 wants a
   * Terms of Use link wherever a subscription is sold, not only in App Store Connect's
   * metadata. `docs/marketing/app-store-listing.md` (`§URLs`) already names the pages these
   * point at: `apps/web/src/site/TermsPage.tsx` (`/terms`) and `apps/web/src/site/PrivacyPage
   * .tsx` (`/privacy`). Row labels match `siteTerms.title` / `privacy.title` exactly, so a
   * reader who taps through meets the same name they just read.
   *
   * NOUN PHRASES, LIKE `plus.dismiss` ABOVE — a destination's name, not a sentence about it,
   * which is the same shape every other row label on this screen already takes.
   */
  'plus.legal.terms': { warm: 'Terms of use', literal: 'Terms of use' },
  'plus.legal.privacy': { warm: 'Privacy', literal: 'Privacy policy' },
  /**
   * WHY THESE TWO EXIST INSTEAD OF REUSING `sync.failed`.
   *
   * The offer screen first shipped rendering `sync.failed` ("Could not reach the server.
   * Changes are saved locally and will retry.") for both an unreachable store and a failed
   * purchase. A design-review screenshot caught it: on a paywall there are no "changes" to
   * save and nothing to retry, so the one honest-looking line on the screen was describing a
   * mechanism that is not running. Reusing a calm line is not the same as reusing a TRUE one.
   *
   * Both say what did not happen and then what is still true, in that order — the same shape
   * every other non-blocking line in this catalogue uses (`warmCap.prompt`,
   * `plus.gate.aiSorting`). Neither blames the user or the network, and neither invites a
   * retry the surface cannot promise: the store being unreachable is not a state Carn can fix
   * from here, and pretending otherwise is how a dead end gets dressed as an action.
   */
  'plus.offerUnavailable': {
    warm: 'Plus is not reachable right now. Nothing is lost, and the free tier keeps working.',
    literal:
      'The store is not reachable right now. Nothing is lost, and all free features keep working.',
  },
  /**
   * "There is no charge" is the sentence a person actually wants when a purchase fails, and
   * stating it plainly is the opposite of the pattern §12 forbids — an audience "permanently
   * done with tools that exploited them" assumes the worst unless told otherwise.
   *
   * IT IS PHRASED IN THE PRESENT TENSE ON PURPOSE, AND THE LINTER IS WHY. The first draft read
   * "That did not go through. You have not been charged." — which `lintCatalogue` rejected twice
   * over: past tense outside acknowledgement copy, and "you have not", which the no-shame rule
   * catches because that construction is how an app tells someone what they failed to do. The
   * rule is blunt here (the sentence was reassuring, not accusing) but it is right that the
   * phrasing is avoidable, and the present-tense version is shorter and calmer anyway. Declaring
   * `allow: ['tense']` would have been the lazy answer to a law that was doing its job.
   */
  'plus.purchaseFailed': {
    warm: 'That purchase is not complete. There is no charge.',
    literal: 'The purchase is not complete. There is no charge.',
  },

  // -- Gate encounters: a fact and a working free path, never a dead end --------
  // Mirrors the warm cap's own rule (`warmCap.prompt`, `warmCap.tradeExplain`,
  // product-concept §5): a limit is a prompt, never a wall, and the sentence says
  // what still works before it says what does not.
  /**
   * Renamed from `plus.gate.aiAllowance` (founder decision): there is no monthly
   * meter to run out of, so "used up... back next month" was describing a mechanic
   * that does not exist. AI sorting is simply a Plus feature; sorting things
   * yourself is the free tier's own default, not a fallback for a missing quota,
   * and it is not a lesser version of what Plus does — it is a different mechanism
   * that already works in full (product-concept §7.2 objection 2, §15).
   */
  'plus.gate.aiSorting': {
    warm: 'AI sorting is part of Plus. Sorting things yourself is the default here, and it works in full.',
    literal:
      'AI-generated sorting is part of Plus. Manual review is the default here, and it works in full.',
  },
  'plus.gate.aiSorting.upgradeHint': {
    warm: 'Plus adds AI sorting, if that is ever what you want.',
    literal: 'Plus adds AI-generated sorting.',
  },
  /**
   * The free tier's own limit, per §12: one list, always usable, shareable at no cost.
   *
   * SAYS "LIST", NOT "SHARED LIST" (docs/DEVIATIONS.md D53), for the reason
   * `plus.feature.lists.label` carries in full: the gate this string explains counts lists, so a
   * user meets it on the second list whether or not they ever share one. The KEY keeps the older
   * name because `'multiple-shared-lists'` is what §12 and R124 call the capability and what
   * `@carn/core` types it as — renaming the key would put this string out of step with the six
   * files whose comments cite it by name, and the words on the screen are what had to be true.
   */
  'plus.gate.secondSharedList': {
    warm: 'One list on the free tier. Plus adds more, whenever you want them.',
    literal: 'The free tier includes one list. Plus adds additional lists.',
  },
  // `plus.gate.compostDepth` was REMOVED here (S1, `docs/reviews/plus-copy-critique.md`).
  // It asserted "the deeper patterns underneath are part of Plus" — a purchasable
  // capability. DEVIATIONS.md M4/M5 both confirm `full-compost-intelligence` has never
  // shipped (`packages/core/src/entitlements.ts` PLUS_CAPABILITY_REGISTRY:
  // `{ id: 'full-compost-intelligence', shipping: false }`), and `releaseObservation`
  // plus the Compost review mechanics are a compile-time FREE-FOREVER capability
  // (`'compost-review'` in `FREE_FOREVER_CAPABILITIES`) — never gated, never partial.
  // Unlike the offer screen's bullets, a gate string has no `shipping`-flag mechanism
  // protecting it, so this one sat one call site away from selling a subscriber a tap
  // that would find nothing. Do not re-add this key from product-concept §12's wording
  // alone — it returns, under its own name, only in the same commit that flips
  // `full-compost-intelligence` to `shipping: true` in the phase-2 build.
  /** Shared reassurance line, for any gate encounter to render alongside its own fact. */
  'plus.gate.neverBlocks': {
    warm: 'Nothing here is blocked. The free path still works, in full.',
    literal: 'Nothing here is blocked. The free tier continues to work in full.',
  },
  // Not "View Carn Plus" — three consecutive capitalised words reads as Title Case
  // (DEVIATIONS.md D1), so the sentence carries "Carn Plus" rather than opening with it.
  'plus.gate.seePlus': { warm: 'See what Plus adds', literal: 'See what Carn Plus adds' },

  // -- Subscription management, in Settings ---------------------------------------
  'plus.settings.title': { warm: 'Carn Plus', literal: 'Carn Plus' },
  'plus.status.free': { warm: 'Free', literal: 'Free' },
  'plus.status.active': { warm: 'Plus is on', literal: 'Plus is active' },
  /**
   * Calm, factual, no alarm colour implied by the words either (design-direction §1–2).
   *
   * PRESENT TENSE, FOR THE REASON `plus.purchaseFailed` RECORDS ONE LINE ABOVE. Both forms read
   * "Payment hasn't gone through yet" — present perfect, in a catalogue whose neighbouring key
   * documents rewriting exactly that construction rather than declaring `allow: ['tense']`,
   * because "declaring the exception would have been the lazy answer to a law that was doing its
   * job". `lint.ts`'s past-tense heuristic does not list "gone", so this one passed the gate and
   * broke the law anyway (design-direction §1: second person, present tense).
   *
   * AND IT WAS THE ONLY KEY IN THE CATALOGUE WITH A STRAIGHT APOSTROPHE — verified across all
   * 1,243 keys: the only other straight apostrophes in this file are inside `why` prose, which no
   * user reads. A typographic outlier in one status line is invisible until it renders beside
   * `plus.cancelExplain`'s curly ones. The present-tense form has no apostrophe to get wrong.
   *
   * IT NAMES THE RENEWAL. `inBillingGrace` in `@carn/core`'s `entitlements.ts` is "a card failed
   * and the store is retrying", which is a different fact from `plus.purchaseFailed`'s
   * first-purchase failure, and the two states were sharing a sentence shape that did not tell
   * them apart. Entitlement is unaffected while the store retries (same doc), so nothing here
   * warns, counts down or hurries: §12 forbids fake urgency, and this is a fact with the action
   * attached.
   */
  'plus.status.billingRetry': {
    warm: 'The renewal payment is not through. Update it when you get a moment.',
    literal: 'The renewal payment is incomplete. Update your payment method.',
  },
  'plus.manage': { warm: 'Manage subscription', literal: 'Manage subscription' },
  'plus.cancel': { warm: 'Cancel subscription', literal: 'Cancel subscription' },
  /** §12: "subscription cancellation in two taps." Stated as fact, not as a sales objection. */
  'plus.cancelExplain': {
    warm: 'Two taps, and it is done. Everything free stays exactly the same.',
    literal: 'Cancellation takes two taps. Free features stay exactly the same.',
  },
  /**
   * S2 (`docs/reviews/plus-copy-critique.md`): `plus.cancelExplain` answers "will my
   * free features still work" but never the narrower, more anxious question this
   * audience is likelier to actually have — what happens to the things made WHILE
   * paying. Grounded in an actual structural law rather than a new promise: CLAUDE.md
   * "Nothing is ever taken away by time. History is append-only," so this states what
   * the app already does, no more. Rendered alongside `plus.cancelExplain` (see
   * `compositions` below), never in place of it — one is about features, this is
   * about content, and this audience will check both.
   */
  'plus.cancelKeepsWork': {
    warm: 'Nothing you have made is taken away. Your loops, capsules and cairn stay exactly as they are.',
    literal:
      'Cancelling removes no content you have created. Your tasks, saved context and session history remain exactly as they are.',
  },

  // -- Purchase acknowledgement ----------------------------------------------------
  /**
   * A transaction, not an achievement. Read against `celebrate.closed` and
   * `celebrate.stoneAdded` on purpose: same plain, unhurried register, zero
   * exclamation, zero praise — but note what is missing relative to those. A closed
   * Loop earns "that one took some coming back to"; this earns nothing about the
   * person, because paying for a subscription is not an act of the user's the app
   * has any standing to remark on.
   */
  'plus.purchased': { warm: 'Plus is on.', literal: 'Plus is active.' },
  /** Not "the cap is gone" — there was never a meter. AI sorting turns on, new. */
  'plus.purchased.detail': {
    warm: 'AI sorting is on now. The rest of Plus is live too.',
    literal: 'AI-generated sorting is now available. The rest of Plus is active too.',
  },

  // ===========================================================================
  // Widgets (docs/specs/widgets-architecture.md — the working spec for this
  // workstream; authorised by product-concept §6.2, §6.3, §7.1, §8.2, §11 and
  // implementation-brief §3, §8).
  //
  // Two audiences, kept in two sub-groups below:
  //   `widget.<kind>.*`   — strings the WIDGET ITSELF draws (spec §1: "no user-visible
  //                         string may be hardcoded in Swift or Kotlin", "every string a
  //                         widget draws is resolved in TypeScript... and written into
  //                         the snapshot"). These are dedicated keys, not aliases of the
  //                         in-app strings they echo, because a widget's space budget is
  //                         its own constraint (a lock-screen circular accessory has room
  //                         for two or three words) and must be free to diverge in length
  //                         from the full-app copy without touching it. The one deliberate
  //                         exception is `widget.resume.coolingRow`, which the spec itself
  //                         asks to keep byte-for-byte identical to `home.coolingRow`
  //                         (product-concept §6.3 authorises exactly one quiet row; two
  //                         phrasings of the same fact would be a second row in disguise).
  //   `widget.surface.*`  — strings the IN-APP MANAGEMENT SURFACE draws (another agent
  //                         builds the screen; these are its words).
  // ===========================================================================

  // -- The Interrupt widget (product-concept §6.2) --------------------------------
  // Past tense on purpose, matching `interrupt.button` exactly: the widget names the
  // thing that just happened TO the user, not an instruction to act.
  'widget.interrupt.label': {
    warm: 'Life happened',
    literal: 'Interrupt',
    allow: ['tense'],
    why: 'Names the interrupting event, which is by definition already past. Same reasoning as interrupt.button (product-concept §6.2).',
  },
  /** Drawn the moment the tap is queued (spec §4: the widget re-renders optimistically, before the app is foregrounded to drain the intent). */
  'widget.interrupt.acknowledge': {
    warm: 'Parked.',
    literal: 'Paused.',
    allow: ['tense'],
    why: 'The widget\'s own acknowledgement of a park just performed — the same shape `interrupt.parked` uses (ADA review C5).',
  },
  // The state when there is nothing running to park (product-concept §6.2's "optionally,
  // the interrupting thing itself is captured as a new Loop"): a press still does
  // something, so the widget says what, rather than showing a disabled control with no
  // explanation — a widget has no hover state to reveal that on demand (spec §3.3).
  'widget.interrupt.idle': { warm: 'Catches something new', literal: 'Captures a new task' },

  // -- The Resume widget ("Where was I?", product-concept §6.3) -------------------
  'widget.resume.label': {
    warm: 'Where was I?',
    literal: 'Active task',
    allow: ['tense', 'first-person'],
    why: 'Same signature question as home.title, in the user\'s own voice (product-concept §6.3).',
  },
  'widget.resume.empty': { warm: 'Nothing warm', literal: 'No active task' },
  /** The bare "next" label, paired in the snapshot with the capsule's own next-action text (never composed by native code). Mirrors capsule.next. */
  'widget.resume.next': { warm: 'Next', literal: 'Next step' },
  /**
   * "THERE IS ONE OF THESE INSIDE" — the two words the widget family did not have
   * (docs/DEVIATIONS.md WG10 item 3).
   *
   * product-concept §6.3 names the photo and the voice line among the three things the
   * resumption surface shows, and the widget is the resumption surface a user meets most
   * often. It can show neither — a widget cannot play audio, and below about 44pt a
   * photograph is a smudge — so presence is the honest rendering. Until now the only words
   * for that were `widget.redacted.photo`/`voiceNote`, which say the content is being KEPT
   * BACK; on an un-redacted home-screen tile that is a false statement, so the mark was
   * simply not drawn and the two strongest recognition hooks were invisible.
   *
   * These are the present-and-not-drawn forms. They state what is in the capsule and stop:
   * no "tap to hear", no count, nothing about the user (design-direction §1). The
   * `widget.redacted.*` pair still covers the withheld case, which is a different fact.
   */
  'widget.resume.photoInside': { warm: 'A photo inside', literal: 'Includes a photo' },
  'widget.resume.voiceInside': { warm: 'A voice line inside', literal: 'Includes an audio note' },
  /**
   * THE QUIET COOLING ROW — `large` family only (product-concept §6.3 authorises exactly
   * one; §2 of the widgets spec forbids inventing a second phrasing of the same fact).
   * Kept identical to `home.coolingRow`, including its plural forms, rather than
   * duplicated with drift. Measured against the `large` family's width budget it fits
   * unshortened, so no shorter variant is needed here — if a future family needs one,
   * add it as `widget.resume.coolingRow.short` with a comment recording which family
   * forced it, rather than editing this entry.
   */
  'widget.resume.coolingRow': {
    warm: '{count} cooling — look when you’re ready',
    literal: '{count} idle tasks',
    oneWarm: '1 cooling — look when you’re ready',
    oneLiteral: '1 idle task',
    slots: ['count'],
  },

  // -- The Capture widget (product-concept §7.1) -----------------------------------
  'widget.capture.label': { warm: 'Catch', literal: 'Capture' },
  /** The hold-to-speak affordance (product-concept §7.1 — "hold the widget… and speak"). */
  'widget.capture.holdToSpeak': { warm: 'Hold and speak', literal: 'Hold to record' },
  /** Drawn on the widget itself after a hold-to-speak capture completes, mirroring capture.saved. */
  'widget.capture.captured': {
    warm: 'Caught.',
    literal: 'Saved.',
    allow: ['tense'],
    why: 'The widget\'s own capture confirmation — the same shape `capture.saved` uses (ADA review C5).',
  },

  // -- The Shopping widget (product-concept §8.2) ----------------------------------
  'widget.shopping.label': { warm: 'Shopping', literal: 'Shopping list' },
  'widget.shopping.remaining': {
    warm: '{count} left',
    literal: '{count} items remaining',
    oneWarm: '1 left',
    oneLiteral: '1 item remaining',
    slots: ['count'],
  },
  'widget.shopping.finished': { warm: 'List worked.', literal: 'All items checked.' },

  // -- Redaction stand-ins (widgets spec §5; implementation-brief §3 privacy invariant;
  // product-concept §14 names photos among the most sensitive data classes) -----------
  //
  // `packages/core` emits a structured redaction MARKER, never English — these are the
  // English for each marker. The design goal is discretion, not disclosure: a stand-in
  // states the CLASS of thing that is there ("a task", "a photo") rather than announcing
  // that something has been hidden, so the lock screen reads as a normal, calm widget
  // rather than an error state or a warning. "Kept private" is the one recurring phrase
  // across the family, deliberately: it is a fact about a setting the user chose, not an
  // apology and not a flag. None of these should be read by anyone as "something is
  // wrong" — because nothing is.
  //
  // NAMING NOTE for whoever wires `packages/core/src/widgets.ts`: these five classes
  // (title / next-action / photo / voice-note / shopping-item) are inferred from the
  // capsule fields product-concept §6.1 and §6.3 name, plus a generic fallback for a
  // marker that does not map cleanly onto one. Confirm the key names below match the
  // marker enum once it exists; rename in this file rather than growing a second set.
  'widget.redacted.title': { warm: 'Something warm, kept private', literal: 'A task, kept private' },
  'widget.redacted.next': { warm: 'A next step, kept private', literal: 'A next step, kept private' },
  'widget.redacted.photo': { warm: 'A photo, kept private', literal: 'A photo, kept private' },
  'widget.redacted.voiceNote': {
    warm: 'A voice note, kept private',
    literal: 'An audio note, kept private',
  },
  'widget.redacted.item': { warm: 'An item, kept private', literal: 'An item, kept private' },
  /** The fallback, for a marker that names no more specific class. */
  'widget.redacted.generic': { warm: 'Something, kept private', literal: 'Content, kept private' },

  // -- Unknown schema version (widgets spec §6) -------------------------------------
  // Native code that reads a `schemaVersion` it does not know renders this, never a
  // partial or guessed rendering of a payload it cannot trust. Not an error message —
  // it names the one thing that fixes it and stops.
  'widget.unknownVersion': { warm: 'Open the app to see this.', literal: 'Open the app to see this.' },

  // -- No payload at all (RULINGS R167, superseded by R209) --------------------------
  // R167 put a "nothing here yet" string on this branch, paired with the OS's own
  // default widget card. RULINGS R209 removes the string: the one state with no
  // payload has no theme and no string table to resolve one out of, so `NoPayloadState`
  // in `native/ios/CarnWidgets/Shell.swift` drew a hardcoded Swift literal to carry it
  // (`docs/DEVIATIONS.md` D-j) — the one exception spec §3 law 1 ever had. R209 trades
  // the exception for silence: the no-payload branch now draws the OS's own card and no
  // text at all, so `widget.empty` has no reader left and is not carried here as dead
  // vocabulary (`D35`'s "ship what the app uses"). See `NoPayloadState`'s own comment
  // for what that silence costs.

  // ===========================================================================
  // The in-app widgets management surface (widgets spec §7:
  // apps/mobile/src/screens/widgets/). Built by another agent; these are its words.
  // ===========================================================================
  'widget.surface.title': { warm: 'Widgets', literal: 'Widgets' },
  'widget.surface.intro': {
    warm: 'Keep the loop you need one glance away, without opening the app.',
    literal: 'Show task information on your home screen or lock screen without opening the app.',
  },

  // THE FOUR NAMES THE OS WIDGET PICKER SHOWS, and nothing else about a kind.
  //
  // Each says what the widget DOES FOR YOU, not what it is (widgets spec, "What to write" §B).
  // Their only reader is native: `GalleryNames.swift` interpolates
  // `widget.surface.<kind>.name` out of the payload table, and
  // `apps/mobile/test/widgets-bridge.test.ts` pins all four so a rename breaks in CI rather than
  // in every iPhone's widget gallery. The in-app management surface printed them until the four
  // kind blocks were removed from it (docs/DEVIATIONS.md §widget-gallery).
  //
  // THE FOUR MATCHING `.description` ENTRIES WERE DELETED WITH THOSE BLOCKS. They had exactly one
  // reader, that surface, and `GalleryNames.swift` states in its own words why the picker takes no
  // descriptions ("a description is exactly the kind of string that has two registers and no way to
  // pick one here"). A dual-register string nothing renders is the tax
  // `packages/copy/test/references.test.ts` exists to refuse.
  'widget.surface.interrupt.name': {
    warm: 'Life happened',
    literal: 'Interrupt',
    allow: ['tense'],
    why: 'Same event-naming reasoning as interrupt.button and widget.interrupt.label (product-concept §6.2).',
  },
  'widget.surface.resume.name': {
    warm: 'Where was I?',
    literal: 'Resume',
    allow: ['tense', 'first-person'],
    why: 'Same signature question as home.title and widget.resume.label (product-concept §6.3).',
  },
  'widget.surface.capture.name': { warm: 'Catch', literal: 'Capture' },
  'widget.surface.shopping.name': { warm: 'Shopping', literal: 'Shopping list' },

  // -- The privacy setting (`widgetPrivacy`, widgets spec §5) ----------------------
  'widget.surface.privacy.label': { warm: 'What shows on the lock screen', literal: 'Lock screen content' },
  'widget.surface.privacy.explain': {
    warm: 'Anyone holding your phone can see your lock screen. Choose how much of your task appears there.',
    literal: 'Your lock screen can be seen by anyone holding your phone. Choose how much task content is shown there.',
  },
  'widget.surface.privacy.optionRedacted': { warm: 'Keep it private', literal: 'Hide task content' },
  'widget.surface.privacy.optionRedacted.explain': {
    warm: 'Widgets show icons and counts, not the words.',
    literal: 'Widgets show icons and counts, not text content.',
  },
  'widget.surface.privacy.optionFull': { warm: 'Show it all', literal: 'Show task content' },
  'widget.surface.privacy.optionFull.explain': {
    warm: 'Widgets show the real title and next step.',
    literal: 'Widgets show the full task title and next step.',
  },

  // -- Per-platform "how to add this" guidance -------------------------------------
  // Literal enough to follow one-handed, on the phone, while doing it (widgets spec
  // "What to write" §B). iOS and Android genuinely differ, so these are separate
  // strings rather than one generic paragraph with a platform branch inside it.
  'widget.surface.platformIOS': { warm: 'On iPhone', literal: 'On iOS' },
  'widget.surface.platformAndroid': { warm: 'On Android', literal: 'On Android' },
  'widget.surface.sectionHomeScreen': { warm: 'Home screen', literal: 'Home screen' },
  'widget.surface.sectionLockScreen': { warm: 'Lock screen', literal: 'Lock screen' },
  'widget.surface.sectionQuickSettings': { warm: 'Quick settings tile', literal: 'Quick settings tile' },

  'widget.surface.addIOS.homeScreen': {
    warm: 'Touch and hold an empty space on your home screen. Tap the plus in the corner. Search for Carn. Pick a widget, then tap Add Widget.',
    literal: 'Touch and hold an empty area of the home screen. Tap the plus button. Search for Carn. Select a widget, then tap Add Widget.',
  },
  'widget.surface.addIOS.lockScreen': {
    warm: 'Touch and hold your lock screen, then tap Customize. Tap the widget area under the clock. Search for Carn, add a widget, then tap Done.',
    literal: 'Touch and hold the lock screen, then tap Customize. Tap the widget area below the clock. Search for Carn, add a widget, then tap Done.',
  },
  'widget.surface.addAndroid.homeScreen': {
    warm: 'Touch and hold an empty space on your home screen, then tap Widgets. Find Carn, touch and hold its widget, then drag it into place.',
    literal: 'Touch and hold an empty area of the home screen, then tap Widgets. Find Carn in the list, touch and hold its widget, then drag it to the home screen.',
  },
  /** implementation-brief §8: "home-screen widget + quick-settings tile on Android later." */
  'widget.surface.addAndroid.quickSettings': {
    warm: 'Open quick settings, then tap the pencil to edit. Drag the Carn tile in from the list below.',
    literal: 'Open the quick settings panel, then tap the edit icon. Drag the Carn tile up from the available list.',
  },

  // The OS does not tell an app which of its widgets are actually placed anywhere —
  // this surface must not pretend otherwise (widgets spec "What to write" §B).
  'widget.surface.placementUnknown': {
    warm: 'This screen cannot tell whether you have actually added a widget. Phones do not share that.',
    literal: 'This screen cannot detect whether a widget has been added to your device. That information is not available to apps.',
  },

  // -------------------------------------------------------------------------
  // ADDING TO A LOOP AFTER THE FACT (product-concept §7.1, §6.1; implementation-brief §8).
  //
  // §7.1 takes every decision out of capture — "no project, no date, no priority, no list" —
  // on the grounds that those are "separate cognitive acts". The separate act then has to be
  // reachable, and for the loop's own free text (`Loop.note`) and for a photo or voice note
  // added later it was not: the only Tier-2 path in the app is the calm-moment PROMPT
  // (`capsule.calmPrompt`, RULINGS R42), which the user cannot ask for.
  //
  // NOTHING HERE NAMES A LACK. Not "no description yet", not "0 photos" — an absence the user
  // has not filled in is rendered as nothing at all (docs/DEVIATIONS.md LD5), so every string
  // in this block names an act or states a fact about something that now exists.
  // -------------------------------------------------------------------------

  /** The passage's heading, at the foot of the loop's surface beside the date and the rename. */
  'loop.additions': { warm: 'Add to this one', literal: 'Add to this task' },

  // -- the loop's own free text ---------------------------------------------

  /**
   * The heading over the note itself, where it renders under the bookmark.
   *
   * "Notes" would collide with `capsule.addNote`'s Tier-2 note, which is a different thing in a
   * different place — that one is the next physical action, this one is standing reference. The
   * words name what the text IS (what you know about the job) rather than the field's type.
   */
  'loop.note': { warm: 'What you know about it', literal: 'Details' },
  'loop.note.add': { warm: 'Write what you know', literal: 'Add details' },
  'loop.note.edit': { warm: 'Change what you wrote', literal: 'Edit the details' },
  'loop.note.hint': {
    warm: 'Anything worth having to hand next time. Nothing is required.',
    literal: 'Any details worth keeping with this task. This field is optional.',
  },
  'loop.note.placeholder': {
    warm: 'Pump is a Grundfos, spares are in the second drawer',
    literal: 'Model numbers, phone numbers, measurements, anything else',
  },
  'loop.note.save': { warm: 'Keep this', literal: 'Save the details' },
  /** Same shape and same job as `loop.rename.cancel` two passages up ("Leave the name alone"). */
  'loop.note.cancel': { warm: 'Leave the words alone', literal: 'Cancel' },
  /**
   * Clearing is offered, and it is offered plainly rather than behind a warning.
   *
   * "Nothing is ever taken away" (design-direction §6) is a rule about what TIME removes. A
   * person deleting their own sentence is the opposite, the previous wording stays in the
   * append-only log either way (@carn/core `annotate`), and a confirmation here would treat an
   * ordinary edit as a hazard.
   */
  'loop.note.clear': { warm: 'Take the words out', literal: 'Delete the details' },
  'loop.noteSaved': {
    warm: 'Kept with this one.',
    literal: 'Saved to this task.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },
  'loop.noteCleared': {
    warm: 'Out it comes.',
    literal: 'The details are removed.',
  },

  // -- one link, raw (`RULINGS R178`) ---------------------------------------
  //
  // ONE FIELD, BESIDE THE NOTE THAT ALREADY HOLDS STRINGS. R178: "not a fetched title, not a
  // list." No string here promises a page title, a preview, a favicon or a description, because
  // there is none and there must not be: fetching one is a network read on a device whose
  // architecture is "no user interaction ever waits on the network" (implementation-brief §2) and
  // a disclosure to a third party of what this user is looking at (§3). What the user sees is the
  // address they pasted.
  //
  // AND NOTHING HERE CALLS IT A CAPTURE. Adding a link to a task that exists is not a new capture,
  // `CaptureChannel` stays a closed union of eight, and the copy must not blur the two.
  /**
   * The heading over the link where it renders, and the word the field is known by.
   *
   * The warm register names what it is FOR rather than its type, exactly as `loop.note` does
   * ("What you know about it"): the page the job needs. The literal register names the type,
   * because that is what the literal register is for.
   */
  'loop.link': { warm: 'A page that goes with it', literal: 'Link' },
  'loop.link.add': { warm: 'Add a link', literal: 'Add a link' },
  'loop.link.edit': { warm: 'Change the link', literal: 'Edit the link' },
  'loop.link.hint': {
    warm: 'One address, kept with this one. Nothing is fetched.',
    literal: 'One web address stored with this task. No page is loaded until you open it.',
  },
  'loop.link.placeholder': {
    warm: 'The manual, the parts list, the listing',
    literal: 'A web address, pasted in full',
  },
  'loop.link.save': { warm: 'Keep this link', literal: 'Save the link' },
  'loop.link.cancel': { warm: 'Leave the link alone', literal: 'Cancel' },
  /** Same argument as `loop.note.clear`: an ordinary edit, not a hazard, so no confirmation. */
  'loop.link.clear': { warm: 'Take the link out', literal: 'Delete the link' },
  /**
   * The control that opens it, and the only place in this family that touches the network — by
   * handing the address to the platform, at the moment the user asks, which is the one shape §2
   * permits.
   */
  'loop.link.open': { warm: 'Open it', literal: 'Open the link' },
  'loop.link.open.hint': {
    warm: 'Hands the address to whatever opens it on this phone.',
    literal: 'Opens the address in the default browser or app for it.',
  },
  /**
   * The platform declined to open it — usually a scheme nothing on the device handles, or a string
   * that is not an address at all.
   *
   * SAID PLAINLY, AND NOT AS THE USER'S ERROR. Nothing validates a link on the way in (R178: raw is
   * "the only shape that costs nothing to reason about"), so a string that will not open is a fact
   * about this device, not a mistake to be told off for. The address stays exactly as written.
   */
  'loop.link.cannotOpen': {
    warm: 'Nothing on this phone will open that. The address is still here.',
    literal: 'This address cannot be opened on this device. It remains saved on the task.',
  },
  'loop.linkSaved': {
    warm: 'Link kept with this one.',
    literal: 'The link is saved to this task.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },
  'loop.linkCleared': {
    warm: 'Link taken out.',
    literal: 'The link is removed.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },

  // -- a photo or a voice note, added later ---------------------------------
  //
  // These join the CAPSULE, not the note: §6.1's photo is "the half-disassembled pump, exactly
  // as left" and its voice note is "bolts are in my jacket pocket" — both are about where the
  // work stands, which is the capsule's whole job. The hints say so, because a control that
  // silently writes to a different object than the one above it is a control that lies.

  /**
   * Two controls, because they are two different acts and one of them is the point.
   *
   * §7.1's photo channel is the camera — "the user is standing in front of the broken hinge" —
   * and `src/capture/photo.ts` defaults to it for that reason. Adding a photo to a job from last
   * week is the other case: the picture already exists. Offering only the camera would make the
   * common retrospective case reachable only by re-photographing something.
   */
  'loop.addPhoto': { warm: 'Take a photo', literal: 'Take a photo' },
  'loop.addPhoto.hint': {
    warm: 'It joins your bookmark, so you see it first next time.',
    literal: 'The photo is added to this task and shown when you open it.',
  },
  'loop.choosePhoto': { warm: 'Choose a photo', literal: 'Choose a photo' },
  'loop.choosePhoto.hint': {
    warm: 'Pick one you already have. It joins your bookmark the same way.',
    literal: 'Select an existing image. It is added to this task in the same way.',
  },
  /**
   * The camera is not reachable. A fact about this device, not a failure of the user.
   *
   * Distinct from `capture.photoUnavailable` because that one is about capture — "you can still
   * type it" is the right next step there and the wrong one here, where the loop already exists.
   */
  'loop.addPhoto.unavailable': {
    warm: 'No camera to reach on this device. The rest of this still works.',
    literal: 'The camera is not available on this device. All other options still work.',
  },
  'loop.photoAdded': {
    warm: 'Photo kept with this one.',
    literal: 'The photo is added to this task.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },
  /**
   * Taking it back off, offered plainly (docs/DEVIATIONS.md LA6).
   *
   * `loop.note.clear`'s argument, applied to a picture: "nothing is ever taken away" is a rule
   * about what TIME removes, the capsule that carries the photo stays in the append-only log
   * (@carn/core `removeCapsuleMedia`), and a confirmation would treat a person's own photograph as
   * a hazard. The wording is the same family as "Take the words out" and "Take the link out",
   * because it is the same act on the third thing this surface can add.
   */
  'loop.removePhoto': { warm: 'Take the photo out', literal: 'Delete the photo' },
  'loop.removePhoto.hint': {
    warm: 'It comes off your bookmark. Nothing else about this one changes.',
    literal: 'Removes the photo from this task. Nothing else about the task changes.',
  },
  'loop.photoRemoved': {
    warm: 'Photo taken out.',
    literal: 'The photo is removed.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },

  'loop.addVoiceNote': { warm: 'Add a voice note', literal: 'Add an audio note' },
  'loop.addVoiceNote.hint': {
    warm: 'Up to fifteen seconds. It joins your bookmark with the words written out.',
    literal: 'Up to fifteen seconds. The audio is added to this task with a transcript.',
  },
  'loop.voiceNoteAdded': {
    warm: 'Voice note kept with this one.',
    literal: 'The audio note is added to this task.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },
  /**
   * The audio landed and the words did not (implementation-brief §6: "unrecognised audio always
   * survives as a playable attachment on the loop"). Stated, because a transcript that silently
   * never appears reads as the recording having failed.
   */
  'loop.voiceNoteNoWords': {
    warm: 'No words this time. The recording is here to play.',
    literal: 'No transcript is available. The audio is saved and can be played back.',
  },
  /**
   * Taking it back off (docs/DEVIATIONS.md LA6). `loop.removePhoto`'s argument, unchanged.
   *
   * THE HINT NAMES BOTH HALVES, and it has to. §7.3 makes the transcript the recording's silent
   * equivalent — one attachment seen two ways — so `removeCapsuleMedia` takes the words with the
   * audio, and a control that said only "the recording" would leave the user expecting a second
   * press that does not exist. Saying so is design-direction §3's rule that no outcome is carried
   * by one channel alone, applied to the outcome rather than the state.
   */
  'loop.removeVoiceNote': { warm: 'Take the voice note out', literal: 'Delete the audio note' },
  'loop.removeVoiceNote.hint': {
    warm: 'The recording and the written words both come off your bookmark.',
    literal: 'Removes the recording and its transcript from this task.',
  },
  'loop.voiceNoteRemoved': {
    warm: 'Voice note taken out.',
    literal: 'The audio note is removed.',
    allow: ['tense'],
    why: 'An acknowledgement of the act that just happened. design-direction §1 permits past tense there.',
  },
  /** Nothing to record with. Same shape as the photo's, and the same reason it is its own key. */
  'loop.addVoiceNote.unavailable': {
    warm: 'No microphone to reach on this device. Writing it works instead.',
    literal: 'Audio recording is not available on this device. Typing the details works instead.',
  },
} as const satisfies Record<string, CopyEntry>;

export type CopyKey = keyof typeof copy;

/**
 * A shopping department's id, DERIVED from the `dept.*` keys rather than declared twice.
 *
 * `DEFAULT_DEPARTMENTS` in `@carn/core` is the authority on the list and its default order
 * (RULINGS R22), and `@carn/copy` is a leaf package with no dependencies — it must not import
 * core, and it must not carry a second copy of the list either, which is exactly the drift
 * RULINGS R29 forbids for the intensity channels. So the ids are read back out of the keys:
 * adding `dept.deli` here makes `'deli'` a legal department to this package and nothing else has
 * to change, and ./shoppingWords.ts stops compiling on a typo.
 *
 * The name is `ShoppingDepartment` rather than `DepartmentId` on purpose: core exports
 * `DepartmentId` as the same closed union derived from `DEFAULT_DEPARTMENTS`, and two identically
 * named types that the compiler cannot compare across a package boundary is a footgun. That the
 * two agree is asserted once, in `packages/ui/src/components/departmentCopy.ts`, which is the
 * only place that imports both.
 */
export type ShoppingDepartment =
  Extract<CopyKey, `dept.${string}`> extends `dept.${infer Id}` ? Id : never;

/**
 * The key that names a department, or `undefined` for a department the user invented.
 *
 * A user-created department is USER DATA, like a loop's title: it is rendered as typed and it
 * is not a catalogue concern. Only the twelve defaults have names here.
 */
export function departmentNameKey(id: string): CopyKey | undefined {
  const key = `dept.${id}`;
  return key in copy ? (key as CopyKey) : undefined;
}

/**
 * Strings the UI renders as ONE utterance, declared so `lintCompositions` can judge the
 * sentence rather than its halves.
 *
 * Add a group whenever a surface stacks a label above a body, or concatenates two keys into
 * one line, or gives a screen reader both in a single accessible name. A review found
 * "Worth noticing" above "No sessions on this one." reassembling a sentence two earlier
 * critiques had condemned, with neither half violating anything on its own.
 */
export const compositions: readonly (readonly CopyKey[])[] = [
  ['compost.observationLabel', 'release.observation.noSessions'],
  ['compost.observationLabel', 'release.observation.interrupted'],
  // The comparative observation is HALF a sentence on its own: the fact, then the clause that
  // earns it. Both directions, both axes, all four under the surface's own label.
  ['compost.observationLabel', 'release.observation.caughtAtPlace', 'release.pattern.doesNotStick'],
  ['compost.observationLabel', 'release.observation.caughtAtPlace', 'release.pattern.sticks'],
  ['compost.observationLabel', 'release.observation.caughtAtTime', 'release.pattern.doesNotStick'],
  ['compost.observationLabel', 'release.observation.caughtAtTime', 'release.pattern.sticks'],
  ['capsule.label', 'capsule.none'],
  ['capsule.next', 'capsule.none'],
  // The OTHER half of the same fold (docs/DEVIATIONS.md top/D38): a loop caught and never opened
  // gets `capsule.notStarted`, under the same heading, and only the `capsule.none` pair was declared.
  ['capsule.label', 'capsule.notStarted'],
  /**
   * THE CAPSULE'S ABSENCE, THEN THE LOOP'S OWN NOTE (docs/DEVIATIONS.md LA1).
   *
   * `LoopNote` renders directly beneath `CapsuleDetail`, so on a loop that was never parked and
   * DOES carry a note these three lines are read as one utterance. The pair caught a real
   * contradiction the first time it was rendered — `capsule.none`'s literal form said "No note
   * here yet." above the user's own note — and it is declared so the next edit to either key has
   * to survive the adjacency rather than rediscover it in a screenshot.
   *
   * The note's own text is NOT in this group and cannot be: it is user content, not copy — the
   * same reason a list's name carries no entry (RULINGS R125 clause 3).
   */
  ['capsule.label', 'capsule.none', 'loop.note'],
  ['capsule.label', 'capsule.notStarted', 'loop.note'],
  // The loop's own cairn: its heading above its count line. The empty pair is deliberately NOT
  // declared — the section does not render at all at zero, so a group for it would assert a
  // rendering that no longer exists.
  ['loop.history', 'loop.sessionCount'],
  ['home.empty.title', 'home.empty.body'],
  ['home.empty.caught.title', 'home.empty.caught.body'],
  ['compost.title', 'compost.intro'],
  ['sorting.title', 'sorting.intro'],
  // The proposed-rhythm block, read top to bottom as one utterance: the heading, the axis label
  // the condition row renders, and the disclosure line under it (`PartBlock`). Declared because
  // the three lines are the whole of what a user reads before answering "Use this / Not this",
  // and because the composition is what makes the near-repetition visible — an earlier draft
  // headed the block "Looks like a job that comes round" directly above "When it comes round".
  ['sorting.proposedRhythm', 'surfacing.rhythm', 'sorting.rhythmKeepsGoing'],
  // The proposed energy-fit block, same three lines in the same order (`RULINGS R165`), and
  // declared for the same reason: the near-repetition between a heading and the axis label
  // directly under it is only visible when the two are read as one utterance. An earlier draft
  // headed this block "Suggested small job" directly above the literal axis label "Job-size
  // trigger".
  ['sorting.proposedEnergy', 'surfacing.energy', 'sorting.energyFits'],
  // The `plus.*` family (`docs/reviews/plus-copy-critique.md`, S2 — no composition
  // declared for a family with several documented render-together pairs).
  // The offer screen's free-tier proof, read in the order a user scrolls it — which since
  // docs/DEVIATIONS.md M13 is accessibility FIRST, and no longer opens the screen. `intro` left
  // this group because the key is deleted; `accessibilityFree` leads it because the `Callout`
  // now renders above the paragraph rather than below it. The order here is not decorative:
  // `lintCompositions` judges the sentence these make when read in sequence, so a group whose
  // order disagrees with `PlusSurface.tsx` lints a paragraph nobody sees. `lineDrawn` left this
  // group the same way `intro` did — the key is deleted (docs/DEVIATIONS.md D52), so the proof
  // is now two sentences and this checks both of them.
  ['plus.offer.accessibilityFree', 'plus.offer.staysFree'],
  // `plus.gate.neverBlocks` is documented at its own definition as the shared
  // reassurance line "for any gate encounter to render alongside its own fact" — so
  // each fact that exists gets its pairing declared here. `plus.gate.compostDepth`
  // is not among them: it was deleted (S1, same report) and has no pairing to check.
  ['plus.gate.aiSorting', 'plus.gate.neverBlocks'],
  ['plus.gate.secondSharedList', 'plus.gate.neverBlocks'],
  // The funding line closes the capability block (docs/DEVIATIONS.md M12, moved by M13), so the
  // sentence a reader actually assembles is the LAST CAPABILITY plus the funding fact — not the
  // funding fact plus a buy button, which is what this group checked while `supportsUpkeep` sat
  // inside the ask. Declared against every shipping capability's explain rather than only the
  // last one, because registry ORDER is not a promise: `PLUS_CAPABILITY_REGISTRY` decides which
  // row lands directly above this line, and a composition pinned to today's last row would stop
  // checking the real adjacency the moment a `shipping` flag flips.
  ['plus.feature.sorting.explain', 'plus.offer.supportsUpkeep'],
  ['plus.feature.lists.explain', 'plus.offer.supportsUpkeep'],
  ['plus.feature.materials.explain', 'plus.offer.supportsUpkeep'],
  // The purchase acknowledgement's two lines, same shape as `home.empty.title` +
  // `home.empty.body` above.
  ['plus.purchased', 'plus.purchased.detail'],
  // The cancellation screen's two reassurances — features, then content.
  ['plus.cancelExplain', 'plus.cancelKeepsWork'],
  // The account section's own render-together pairs (Workstream: auth, A1). Each control on that
  // surface is a label above an explain, and the signed-in state is a fact above its own limit —
  // which is the pair that most needs checking as one sentence, because "Signed in as you@example
  // .com. Your work travels up to the server..." is the whole claim a reader takes away.
  ['auth.signIn', 'auth.signInExplain'],
  ['auth.signOut', 'auth.signOutExplain'],
  ['auth.signedInAs', 'auth.backupOnly'],
  // The Apple alternative reads directly under the email control's own explain, so the two are one
  // utterance on screen — "…Shared lists need it too. Or use your Apple ID…" — and the composition
  // linter should judge that sentence rather than either half (auth-sign/A5).
  ['auth.signInExplain', 'auth.appleExplain'],
  // The "Your data" page (docs/DEVIATIONS.md, Workstream: data-surface). Every group below is
  // read off `DataSettings.tsx` in paint order, not guessed: three of them are a
  // `SettingsSection`'s title above its own `explain`, and the two long ones are runs of
  // consecutive `Text` nodes inside a single `View` — which a reader takes in as one paragraph
  // and which therefore have to survive the laws as one paragraph.
  ['data.title', 'data.contains'],
  ['data.local.title', 'data.local.explain'],
  ['data.server.title', 'data.server.signedOut'],
  // The heading and the scope statement now directly under it: `data.travels` moved into the
  // section's `explain` slot, because it says what the figures below are figures OF (DA6).
  ['data.server.title', 'data.travels'],
  // THE LEDGER, IN PAINT ORDER, as a populated device reads it: each row's figure name followed by
  // its own caption, then the boundary line under the card. A screen reader hears this run as one
  // passage and so does an eye scanning the numeral column, which is why it is declared as one
  // utterance rather than as six unrelated fragments.
  [
    'data.figure.sent',
    'data.sent.when',
    'data.figure.waiting',
    'data.figure.held',
    'data.held.explain',
    'data.received.upTo',
  ],
  /**
   * THE SAME RUN ON A DEVICE THAT HAS HAD ENTRIES TURNED BACK (Workstream: outbox-quarantine).
   *
   * Declared separately rather than added to the run above, because the two captions never render
   * together on most devices and the pair that needs judging as one utterance is this one: "Held
   * here… Places and your lists have no safe way up yet… Turned back… The server would not take
   * these…" is the worst-case reading of this page, and it has to add up to a description of the
   * app's own limits rather than to an accusation or an alarm.
   */
  [
    'data.figure.sent',
    'data.sent.when',
    'data.figure.waiting',
    'data.figure.held',
    'data.held.explain',
    'data.figure.quarantined',
    'data.quarantined.explain',
    'data.received.upTo',
  ],
  // The same run on a device that has never completed a round trip. The zero-cases are declared
  // separately because they never render alongside the forms they replace — and this is the
  // combination that most needs checking as one sentence, because a fresh install reads two
  // "nothing yet" statements in a row and they must not add up to an accusation.
  ['data.figure.sent', 'data.sent.none', 'data.figure.waiting', 'data.received.none'],
  // The manual control and each of its three outcomes, then the control and the footnote beneath
  // it — "Send and check now. It sends and checks about once a minute while the app is open." is
  // the utterance that has to read as reassurance rather than as a contradiction (DA7).
  ['data.sync.now', 'data.sync.done'],
  ['data.sync.now', 'sync.failed'],
  ['data.sync.now', 'data.sync.refused'],
  ['data.sync.now', 'data.rhythm'],
  // The outcome read against the figures it sits under — the combination that was actually wrong
  // on a device: "finished, the figures are current" over "0 uploaded, 37 waiting". Declared so
  // the refusal line is checked as one utterance WITH the ledger rather than on its own, because
  // on its own it read fine and only contradicted the numerals above it.
  ['data.figure.sent', 'data.figure.waiting', 'data.sync.refused'],
  // The floor, as one block of four statements under one heading.
  [
    'data.never.title',
    'data.never.location',
    'data.never.media',
    'data.never.surfacing',
    'data.never.trips',
  ],
  // THE REMOVAL SECTION, BACK (docs/RULINGS.md R134) — in paint order, and the reason it is
  // declared as a run rather than as pairs is that the whole point of this section is the
  // sentence a reader assembles before pressing anything: what it does, what it leaves, what it
  // does not touch, and that there is no way back. Any one of those read alone is either a threat
  // or an understatement. `data.remove.remains` joined the run when the critic pass found the four
  // child logs surviving an erasure the copy called total.
  ['data.remove.title', 'data.remove.explain', 'data.remove.remains', 'data.remove.noUndo'],
  // The control and each of its outcomes, the same shape `data.sync.now` uses above.
  ['data.remove.action', 'data.remove.working'],
  ['data.remove.action', 'data.remove.done'],
  ['data.remove.action', 'data.remove.unreachable'],
  // The last line of the section: the route for someone who cannot press the button, read
  // directly under the button (R134 keeps the support route as the fallback).
  ['data.remove.action', 'data.remove.lostDevice'],


  // -------------------------------------------------------------------------
  // Lists, plural, and the sections the user authors (RULINGS R124–R130).
  //
  // Forty keys landed with no composition declared, on surfaces that document their own
  // render-together sets in comments — the identical gap `docs/reviews/plus-copy-critique.md`
  // raised about `plus.*` one workstream earlier, found again in
  // `docs/reviews/lists-plural-critique.md` S2.5. Every group below was read off the surface
  // that renders it, not guessed: each is either a literal concatenation in code or two or more
  // blocks that are on screen at the same time, in the order they are painted.
  // -------------------------------------------------------------------------

  // apps/mobile/src/screens/lists/ListsIndexSurface.tsx — the index with nothing on it yet.
  ['lists.title', 'lists.empty'],
  /**
   * The index row's accessible name, concatenated at `ListsIndexSurface.tsx` with `'. '`:
   * `[entry.name, count, kindLabel, shared]`. `entry.name` is the user's own words and carries
   * no key (docs/DEVIATIONS.md L3), so what is checkable is the tail — and the tail is a whole
   * spoken sentence about the list. Both kinds, because a row is one or the other.
   */
  ['lists.itemCount', 'lists.kindShopping', 'lists.sharedMark'],
  ['lists.itemCount', 'lists.kindPlain', 'lists.sharedMark'],

  /**
   * apps/mobile/src/screens/lists/NewListSurface.tsx — each kind option's accessible name is
   * `${label}. ${explain}`, one string, which is a composition the linter had never seen. The
   * two lines are also painted one above the other inside the same card.
   */
  ['lists.kind.shopping', 'lists.kind.shopping.explain'],
  ['lists.kind.plain', 'lists.kind.plain.explain'],
  // The create button and the quiet line that says why it is refusing, rendered together for as
  // long as the name field is empty — which is the whole of the user's first visit.
  ['lists.create', 'lists.nameNeeded'],

  // apps/mobile/src/screens/lists/ListSurface.tsx — the archive confirmation, all three at once:
  // the reassurance, then the two answers.
  ['lists.archiveExplain', 'lists.archiveConfirm', 'lists.archiveCancel'],
  // The count line, and R127's done count beside it on a `plain` list. Declared because the two
  // numbers are read as one utterance by a screen reader (the row's own accessible name joins
  // them) and because a pair of counts is exactly the shape §11's no-scores law watches.
  ['shopping.remaining', 'lists.doneCount'],
  /**
   * CLEARING A WORKED LIST (docs/DEVIATIONS.md LC1) — four groups, each read off
   * `screens/lists/ClearChecked.tsx` rather than guessed at.
   *
   * The invitation is a fact then two answers, on screen together, in paint order; the
   * confirmation is a question, a reassurance and two answers, all four at once, which is
   * `lists.archiveExplain`'s group above and the same reason; the acknowledgement is a statement
   * beside the two controls that follow it.
   */
  ['shopping.allWorked', 'shopping.clear', 'shopping.clearLater'],
  [
    'shopping.clear.question',
    'shopping.clear.explain',
    'shopping.clear.confirm',
    'shopping.clear.cancel',
  ],
  ['shopping.cleared', 'shopping.clear.undo', 'shopping.clear.dismiss'],
  ['shopping.clear.undone', 'shopping.clear.dismiss'],
  // The doorway and the control directly under it, which `Trolley` renders as one block.
  ['shopping.checkedRow', 'shopping.clear'],
  /**
   * THE CLEARED SURFACE, WHICH THE FOUR GROUPS ABOVE DID NOT DESCRIBE (docs/DEVIATIONS.md LC2).
   *
   * The critic pass read the real render-together sets off the running build and found three of them
   * undeclared — including the one that joins to a contradiction. `lintCompositions` returns clean
   * over all three, so this was never a hidden law violation; it is the honesty defect that
   * function's own docblock exists to prevent ("only the surface knows what it puts next to what,
   * which is why the groups are declared"). A declared set is a claim about the surface, and three
   * of the claims were false.
   *
   * The asking-state set is no longer real — the invitation is now phase-gated, so it and the
   * question cannot both be on screen — and is deliberately not declared, because declaring a set
   * that cannot occur is the same defect in the other direction.
   */
  // Nothing left to get, nothing in the trolley, and the last shop put away: the empty sentence
  // sits directly above the count line's own numeral, with the standing way back at the foot.
  ['shopping.remaining', 'shopping.emptyCleared', 'shopping.clear.undo'],
  // The acknowledgement, read against the empty state it leaves behind. This is the join that was
  // a contradiction before `shopping.emptyCleared` existed: "nothing on the list yet" directly
  // above "Nothing is deleted."
  ['shopping.emptyCleared', 'shopping.cleared'],
  // The restored state: the invitation comes back the moment the items do, so it is on screen with
  // the acknowledgement of the undo that brought them.
  ['shopping.allWorked', 'shopping.clear.undone', 'shopping.clear.dismiss'],
  // The `R129` management band, read top to bottom, once anything has been cleared. This is the run
  // that showed `shopping.clear.undo` could not be reused here: "Undo that" between a store control
  // and "Rename this list" has no antecedent (LC2).
  ['shopping.clear.putBack', 'lists.rename', 'lists.archive'],
  // The add row: where the item is about to go, and the control that changes it.
  ['shopping.addToSection', 'shopping.changeSection', 'shopping.changeSection.hint'],
  /**
   * One item row's hint, literally joined with a space in `ItemRow.tsx` when the row offers the
   * move as well as the tap. Four groups because the first half is chosen by kind (R127) and by
   * whether the row is already checked, and each of the four combinations is a sentence somebody
   * hears.
   */

  /**
   * apps/mobile/src/screens/lists/SectionsSurface.tsx, top to bottom on a list nobody has added
   * a section to yet: the kind's explanation, the count, the honest "none of your own", and the
   * footnote about the ones that keep their names.
   *
   * THE TWO GROUPS DIFFER BY MORE THAN THE FIRST KEY, and that is the surface's own decision
   * rather than an omission here: `sections.empty` renders only on a `plain` list, because
   * "3 sections" above "No sections of your own yet" read as a contradiction on a shopping list
   * (see that file's comment). Both kinds end with `sections.builtIn`, because a `plain` list
   * still has the catch-all and the catch-all is built in.
   */
  ['sections.explainShopping', 'sections.count', 'sections.builtIn'],
  ['sections.explain', 'sections.count', 'sections.empty', 'sections.builtIn'],
  // The removal's second step: the reassurance, then the button that performs it and the hint
  // that button carries.
  ['sections.removeExplain', 'sections.remove', 'sections.remove.hint'],
];
